import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Collapse, Drawer, Form, Input, Modal, Select, Space, Table, Tabs, Tag, Tooltip, Popconfirm, message, Switch } from 'antd';
import {
  DownOutlined,
  CloseOutlined,
  FilterOutlined,
  ThunderboltOutlined,
  CameraOutlined,
  SearchOutlined,
  SaveOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import mermaid from 'mermaid';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { confirmDialog } from '../utils/confirm';
import {
  listWebhookDeliveries,
  listWebhookEndpoints,
  listWebhookEvents,
  listWebhookSubscriptions,
  saveWebhookSubscription,
} from '../services/webhookConsumerApi';
import MappingReviewPanel from '../components/AhriDashboard/MappingReviewPanel';
import SettingsHistoryPanel from '../components/AhriDashboard/SettingsHistoryPanel';

const createEmptyWhereTree = () => ({ logic: 'AND', conditions: [], groups: [] });

const MAPPING_V3_PHASE_LABELS = {
  outdoorModelNumber: 'Outdoor',
  indoorModelNumber: 'Indoor',
  furnaceModelNumber: 'Furnace',
  reconcile: 'Reconcile',
};
// Mirrors `PHASES` + the trailing reconcile step in
// backend/services/ahri_mapping_v3/job.py -- used only to render "Phase N of 4".
const MAPPING_V3_PHASE_ORDER = ['outdoorModelNumber', 'indoorModelNumber', 'furnaceModelNumber', 'reconcile'];

// Phases 1-3 and reconcile (phase 4) return different-shaped result objects
// (run_field_phase: distinct_count/bulk_exact_matched_count/matched_count/
// rows_updated; run_reconcile_phase: processed/written/reverse_verify_cleared/
// status_changed, or {skipped: reason} when nothing was touched) -- this
// normalizes both into one row shape so the shared table stays honest about
// what each phase's counters actually mean, rather than showing '—' for
// mismatched field names.
function mappingV3RowValues(phaseName, phaseResult) {
  if (!phaseResult || typeof phaseResult !== 'object') {
    return { distinct: '—', bulkExact: '—', matched: '—', rowsUpdated: '—', reverseCleared: '—', statusChanged: '—', note: null };
  }
  if (phaseResult.skipped) {
    return { distinct: '—', bulkExact: '—', matched: '—', rowsUpdated: '—', reverseCleared: '—', statusChanged: '—', note: phaseResult.skipped };
  }
  if (phaseName === 'reconcile') {
    // While the phase is still running, `progress_cb` (backend/services/
    // ahri_mapping_v3/job.py) writes {processed, total, ...} after every
    // chunk -- show "processed / total" so it's visibly climbing instead of
    // looking stuck, then just the final count once processed === total.
    const processed = phaseResult.processed;
    const total = phaseResult.total;
    const distinct =
      total != null && processed != null && total !== processed
        ? `${processed} / ${total}`
        : processed ?? '—';
    return {
      distinct,
      bulkExact: '—',
      matched: '—',
      rowsUpdated: phaseResult.written ?? '—',
      reverseCleared: phaseResult.reverse_verify_cleared ?? '—',
      statusChanged: phaseResult.status_changed ?? '—',
      note: null,
    };
  }
  if (phaseResult.distinct_count == null && phaseResult.total != null) {
    // Still mid-run: `progress_cb` (backend/services/ahri_mapping_v3/phases.py)
    // has only written {processed, total} so far -- the full
    // distinct_count/bulk_exact_matched_count/... shape lands once the phase
    // finishes and overwrites this entry.
    const processed = phaseResult.processed;
    const total = phaseResult.total;
    return {
      distinct: total !== processed ? `${processed} / ${total}` : processed,
      bulkExact: '—',
      matched: '—',
      rowsUpdated: '—',
      reverseCleared: '—',
      statusChanged: '—',
      note: null,
    };
  }
  return {
    distinct: phaseResult.distinct_count ?? '—',
    bulkExact: phaseResult.bulk_exact_matched_count ?? '—',
    matched: phaseResult.matched_count ?? '—',
    rowsUpdated: phaseResult.rows_updated ?? '—',
    reverseCleared: '—',
    statusChanged: '—',
    note: null,
  };
}

const DEFAULT_LOOKUP_TRANSFORM = {
  trim_after_plus: true,
  truncate_last_chars: 2,
  asterisk_replacement: '.',
  dot_wildcard_enabled: true,
};
const DEFAULT_MATCH_PRODUCT_FIELDS = [
  'id',
  'accountId',
  'productCode',
  'imageUrl',
  'manufacturerName',
  'manufacturerPartNumber',
  'name',
];
const DEFAULT_PRODUCT_IMAGE_BASE_URL = 'https://www.gearypacific.com//ASSETS/IMAGES/ITEMS/ZOOM/';
// No built-in filters: every System Shop facet (brand, cooling capacity,
// SEER2/HSPF2/EER2, etc.) must be configured on this screen
// (system_shop_filters) -- no field name is assumed/seeded here. Removing
// the last remaining filter below (removeSystemShopFilter) now correctly
// leaves zero filters instead of silently reseeding a hardcoded list.
const DEFAULT_SYSTEM_SHOP_FILTERS = [];
const DEFAULT_SYSTEM_SHOP_SEARCH = {
  enabled: true,
  placeholder: 'Search by model number, brand, or AHRI ID',
  query_fields: ['search_text^1'],
  phrase_fields: [],
  min_match: '2<75%',
  operator: 'AND',
  tie: 0.1,
  qs: 1,
  ps: 2,
  bq: [],
  bf: [],
  suggest_fields: [
    'rows_referenceid',
    'rows_outdoormodelnumber',
    'rows_indoormodelnumber',
    'rows_outdoorbrandname',
    'rows_indoorbrandname',
  ],
  suggest_limit: 8,
  show_tooltips: false,
  interaction_mode: 'filters',
  interaction_modes: ['filters', 'qa'],
  allow_mode_switch: true,
};
const SYSTEM_SHOP_MATCH_TYPE_OPTIONS = [
  { value: 'complete', label: 'Complete product matches' },
  { value: 'partial', label: 'Partial product matches' },
  { value: 'none', label: 'No product matches' },
];

const normalizeSystemShopMatchTypes = (value, { includeUnmatched = false } = {}) => {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
    ? value.split(/[\s,|]+/)
    : [];
  const aliases = {
    complete_match: 'complete',
    'complete-match': 'complete',
    partial_match: 'partial',
    'partial-match': 'partial',
    unmatched: 'none',
    no_match: 'none',
    'no-match': 'none',
  };
  const selected = rawValues
    .map((entry) => aliases[String(entry || '').trim()] || aliases[String(entry || '').trim().toLowerCase()] || String(entry || '').trim().toLowerCase())
    .filter((entry, index, list) => SYSTEM_SHOP_MATCH_TYPE_OPTIONS.some((option) => option.value === entry) && list.indexOf(entry) === index);
  if (selected.length) {
    return SYSTEM_SHOP_MATCH_TYPE_OPTIONS.map((option) => option.value).filter((entry) => selected.includes(entry));
  }
  return includeUnmatched ? SYSTEM_SHOP_MATCH_TYPE_OPTIONS.map((option) => option.value) : ['complete', 'partial'];
};
const DEFAULT_SYSTEM_SHOP_QUERY_TYPES = [
  {
    id: '',
    name: 'default',
    label: 'Default',
    search_engine: 'any',
    def_type: 'edismax',
    fl: '',
    rows: '',
    wt: '',
    debug: false,
    query_fields: ['search_text^1'],
    phrase_fields: [],
    min_match: '2<75%',
    operator: 'AND',
    tie: 0.1,
    qs: 1,
    ps: 2,
    ps2: '',
    bq: [],
    bf: [],
    use_raw_q: true,
  },
];
const SYSTEM_SHOP_WEBHOOK_EVENT_TYPES = [
  { key: 'system_shop.search_requested', label: 'Search Requested' },
  { key: 'system_shop.route_matched', label: 'Route Matched' },
  { key: 'system_shop.flow_completed', label: 'Flow Completed' },
  { key: 'system_shop.flow_failed', label: 'Flow Failed' },
  { key: 'system_shop.ui_click', label: 'UI Click' },
];

const AHRI_SYSTEM_DEFAULT_COPY_MODULES = [
  { key: 'connection', label: 'AHRI Connection' },
  { key: 'program_schemas', label: 'Program Schemas' },
  { key: 'solr', label: 'Solr' },
  { key: 'match_lookup', label: 'Match Lookup' },
  { key: 'attribute_import', label: 'Attribute Import' },
  { key: 'system_shop_filters', label: 'System Shop Filters' },
  { key: 'eco_rebates', label: 'Eco Rebates' },
];

const AHRI_SECTION_SOURCE_KEYS = {
  openai: ['openai', 'vectorize_enabled'],
  pinecone: ['pinecone', 'vectorize_enabled'],
  connection: [
    'token_url',
    'grant_type',
    'client_id',
    'client_secret',
    'scope',
    'resource',
    'data_url',
    'userUniqueId',
    'content_type',
    'page_size',
    'threads',
    'mapping_threads',
    'mapping_v2_max_trim',
    'mapping_v2_min_length',
    'verify_ssl',
    'use_system_proxy',
    'proxy_url',
    'access_token_override',
  ],
  schemas: ['program_schemas', 'program_names', 'outdoor_brand_names', 'indoor_brand_names'],
  solr: ['solr'],
  match_lookup: ['match_product_lookup', 'lookup_transform', 'product_image_fallback_base_url'],
  attr_import: ['product_attribute_import', 'lookup_transform'],
  system_shop: [
    'system_shop_filters',
    'system_shop_search',
    'system_shop_advanced_filters',
    'system_shop_filters_settings',
    'system_shop_query_types',
    'product_query_types',
    'query_types',
    'system_shop_query_flows',
    'system_shop_theme',
    'match_facets',
    'model_status_filters',
    'system_builder',
  ],
  eco_rebates: ['eco_rebates'],
};
// Labels for the settingsSection nav -- also used to title the scoped
// History drawer (AHRI_SECTION_SOURCE_KEYS has no entry for 'exports' or
// 'system_defaults' since neither saves through put_ahri_config, so History
// shows the unfiltered timeline for those two).
const AHRI_SETTINGS_SECTION_LABELS = {
  openai: 'OpenAI',
  pinecone: 'Pinecone',
  connection: 'AHRI Connection',
  schemas: 'Program Schemas',
  solr: 'Solr',
  match_lookup: 'Match Lookup',
  attr_import: 'Attribute Import',
  system_shop: 'System Shop Filters',
  exports: 'Exports',
  eco_rebates: 'Eco Rebates',
  system_defaults: 'System Defaults',
};
const DEFAULT_PRODUCT_QUERY_TYPES = [
  {
    id: '',
    name: 'default',
    label: 'Default',
    search_engine: 'any',
    def_type: 'edismax',
    fl: '',
    rows: '',
    wt: '',
    debug: false,
    query_fields: ['search_text^1'],
    phrase_fields: [],
    min_match: '2<75%',
    operator: 'AND',
    tie: 0.1,
    qs: 1,
    ps: 2,
    ps2: '',
    bq: [],
    bf: [],
    use_raw_q: true,
  },
];

const DEFAULT_PRODUCT_ATTR_IMPORT = {
  outdoor_model: [{ source_attribute: '', target_attribute: '' }],
  indoor_model: [{ source_attribute: '', target_attribute: '' }],
  furnace_model: [{ source_attribute: '', target_attribute: '' }],
};

const normalizeAttrImportList = (value) => {
  const list = Array.isArray(value) ? value : value && typeof value === 'object' ? [value] : [];
  if (!list.length) {
    return [{ source_attribute: '', target_attribute: '' }];
  }
  return list.map((entry) => ({
    source_attribute: typeof entry?.source_attribute === 'string' ? entry.source_attribute : '',
    target_attribute: typeof entry?.target_attribute === 'string' ? entry.target_attribute : '',
  }));
};

const ensureProductAttrImport = (value) => {
  if (!value || typeof value !== 'object') {
    return { ...DEFAULT_PRODUCT_ATTR_IMPORT };
  }
  return {
    outdoor_model: normalizeAttrImportList(value.outdoor_model),
    indoor_model: normalizeAttrImportList(value.indoor_model),
    furnace_model: normalizeAttrImportList(value.furnace_model),
  };
};

const normalizeSolrRowField = (field) => {
  const raw = String(field || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  const hasRows = lower.startsWith('rows_');
  const hasProdAttr = lower.startsWith('prodattr_');
  if (hasRows || hasProdAttr) {
    const prefix = hasRows ? 'rows_' : 'prodattr_';
    const rest = raw.slice(prefix.length);
    const cleanedRest = rest
      .split('')
      .map((ch) => (/[a-zA-Z0-9]/.test(ch) ? ch : '_'))
      .join('')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
    return cleanedRest ? `${prefix}${cleanedRest.toLowerCase()}` : '';
  }
  const cleaned = raw
    .split('')
    .map((ch) => (/[a-zA-Z0-9]/.test(ch) ? ch : '_'))
    .join('')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  return cleaned ? `rows_${cleaned.toLowerCase()}` : '';
};

const parseValueDisplayMapText = (raw) => {
  const text = String(raw || '').trim();
  if (!text) {
    return null;
  }
  const map = {};
  text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const idx = line.indexOf('=');
      if (idx === -1) return;
      const rawKey = line.slice(0, idx).trim();
      const rawValue = line.slice(idx + 1).trim();
      if (rawKey) {
        map[rawKey] = rawValue || rawKey;
      }
    });
  return Object.keys(map).length ? map : null;
};

const formatValueDisplayMapText = (map) =>
  Object.entries(map || {})
    .map(([key, value]) => `${key}=${value}`)
    .join('\n');

// Splits a group's value list on commas, but treats commas inside double quotes
// as literal (e.g. a value like "29,000") rather than as separators. Lets values
// that themselves contain a comma (thousand separators, etc.) round-trip intact.
const splitGroupValues = (raw) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  const text = String(raw || '');
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result.map((v) => v.trim()).filter(Boolean);
};

const quoteGroupValueIfNeeded = (value) =>
  value.includes(',') || value.includes('"') ? `"${value.replace(/"/g, '""')}"` : value;

const joinGroupValues = (values) => (values || []).map(quoteGroupValueIfNeeded).join(',');

const parseValueGroupsText = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return [];
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf('=');
      if (idx === -1) return null;
      const label = line.slice(0, idx).trim();
      const values = splitGroupValues(line.slice(idx + 1));
      return label && values.length ? { label, values } : null;
    })
    .filter(Boolean);
};

const formatValueGroupsText = (groups) =>
  (groups || [])
    .map((group) => `${group.label}=${joinGroupValues(group.values)}`)
    .join('\n');

const SORT_ORDER_OPTIONS = ['asc', 'desc', 'count_asc', 'count_desc', 'custom'];

// Mirrors ahri_v6/src/domain/filters.js's parseFractionValue -- parses size-like
// strings ("1/2 inch", "1-1/2\"", "3 inch") into a decimal for numeric sort.
const parseFractionValue = (value) => {
  const text = String(value ?? '').trim().toLowerCase();
  if (!text) return NaN;
  const match = text.match(
    /^(\d+(?:\.\d+)?)?\s*[-\s]\s*(\d+)\s*\/\s*(\d+)|^(\d+)\s*\/\s*(\d+)|^(\d+(?:\.\d+)?)/
  );
  if (!match) return NaN;
  if (match[2] !== undefined && match[3] !== undefined) {
    const whole = match[1] ? Number(match[1]) : 0;
    const denominator = Number(match[3]);
    return denominator ? whole + Number(match[2]) / denominator : whole;
  }
  if (match[4] !== undefined && match[5] !== undefined) {
    const denominator = Number(match[5]);
    return denominator ? Number(match[4]) / denominator : NaN;
  }
  if (match[6] !== undefined) return Number(match[6]);
  return NaN;
};

const getPreviewValuesForFields = (fields, preview, filterSortConfig = {}) => {
  const totals = new Map();
  (fields || []).forEach((field) => {
    const raw = String(field || '');
    const items = raw.startsWith('prodattr_')
      ? preview?.productAttributeFacets?.[raw]?.values || []
      : preview?.facets?.[raw] || [];
    items.forEach((item) => {
      const key = String(item?.value ?? '').trim();
      if (!key) return;
      totals.set(key, (totals.get(key) || 0) + (typeof item?.count === 'number' ? item.count : 0));
    });
  });
  const items = [...totals.entries()].map(([value, count]) => ({ value, count }));
  // Sort the admin's candidate-value list the SAME way this attribute's values
  // are sorted for shoppers (sort_order/value_type/custom_value_order) instead
  // of a fixed count-desc order, so the admin finds values where they expect
  // them while setting up display maps/groups.
  const { sortOrder = 'count_desc', valueType = 'string', customOrder = [] } = filterSortConfig;
  const byValueAsc = (a, b) =>
    String(a.value).localeCompare(String(b.value), undefined, { numeric: true, sensitivity: 'base' });
  if (sortOrder === 'count_asc' || sortOrder === 'count_desc') {
    const dir = sortOrder === 'count_desc' ? -1 : 1;
    return items.sort((a, b) => (a.count !== b.count ? dir * (a.count - b.count) : byValueAsc(a, b)));
  }
  if (sortOrder === 'custom') {
    const indexMap = new Map((Array.isArray(customOrder) ? customOrder : []).map((v, i) => [String(v), i]));
    return items.sort((a, b) => {
      const aIdx = indexMap.has(String(a.value)) ? indexMap.get(String(a.value)) : Infinity;
      const bIdx = indexMap.has(String(b.value)) ? indexMap.get(String(b.value)) : Infinity;
      return aIdx !== bIdx ? aIdx - bIdx : byValueAsc(a, b);
    });
  }
  if (valueType === 'number' || valueType === 'fraction') {
    return items.sort((a, b) => {
      const aNum = parseFractionValue(a.value);
      const bNum = parseFractionValue(b.value);
      const aValid = Number.isFinite(aNum);
      const bValid = Number.isFinite(bNum);
      if (!aValid && !bValid) return byValueAsc(a, b);
      if (!aValid) return 1;
      if (!bValid) return -1;
      return sortOrder === 'desc' ? bNum - aNum : aNum - bNum;
    });
  }
  return items.sort((a, b) => (sortOrder === 'desc' ? -byValueAsc(a, b) : byValueAsc(a, b)));
};

// Total records with a value in any of `fields`, deduped by distinct value
// the same way getPreviewValuesForFields is (so a value present under more
// than one underlying field isn't double-counted). Returns null when no
// facet data is cached yet for these fields, so callers can render "—"
// instead of a misleading 0.
const getRecordCountForFields = (fields, preview) => {
  if (!Array.isArray(fields) || !fields.length) return null;
  const totals = new Map();
  let sawAnyField = false;
  fields.forEach((field) => {
    const raw = String(field || '');
    const items = raw.startsWith('prodattr_')
      ? preview?.productAttributeFacets?.[raw]?.values
      : preview?.facets?.[raw];
    if (!Array.isArray(items)) return;
    sawAnyField = true;
    items.forEach((item) => {
      const key = String(item?.value ?? '').trim();
      if (!key) return;
      totals.set(key, (totals.get(key) || 0) + (typeof item?.count === 'number' ? item.count : 0));
    });
  });
  if (!sawAnyField) return null;
  let sum = 0;
  totals.forEach((count) => { sum += count; });
  return sum;
};

const normalizeSystemShopFilters = (value, options = {}) => {
  const { keepEmpty = false, fallbackToDefault = true, includeRaw = true } = options;
  if (!Array.isArray(value)) {
    return fallbackToDefault
      ? DEFAULT_SYSTEM_SHOP_FILTERS.map((entry) => ({ ...entry, fields: [...entry.fields] }))
      : [];
  }
  const normalized = value
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => {
      const key = String(entry.key || entry.id || entry.name || '').trim();
      const label = String(entry.label || entry.title || '').trim() || key;
      let derivedKey = key;
      if (!derivedKey && label) {
        derivedKey = label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
      }
      const rawFields = entry.fields || entry.attributes || entry.attribute_fields || [];
      const fields = Array.isArray(rawFields)
        ? rawFields.map((field) => normalizeSolrRowField(field)).filter(Boolean)
        : String(rawFields || '')
            .split(/[\n,]+/)
            .map((field) => normalizeSolrRowField(field))
            .filter(Boolean);
      const valueType = String(entry.value_type || entry.type || 'string').toLowerCase();
      const sortOrderRaw = String(entry.sort_order || entry.sort || 'asc').toLowerCase();
      const sortOrder = SORT_ORDER_OPTIONS.includes(sortOrderRaw) ? sortOrderRaw : 'asc';
      const customValueOrder = Array.isArray(entry.custom_value_order)
        ? entry.custom_value_order.map((v) => String(v).trim()).filter(Boolean)
        : [];
      const rawDisplayMap = entry.value_display_map || entry.display_map || entry.value_map || {};
      const rawDisplayText = entry.value_display_map_raw;
      let valueDisplayMap = {};
      if (typeof rawDisplayText === 'string') {
        valueDisplayMap = parseValueDisplayMapText(rawDisplayText) || {};
      } else if (typeof rawDisplayMap === 'string') {
        try {
          valueDisplayMap = JSON.parse(rawDisplayMap);
        } catch {
          valueDisplayMap = {};
        }
      } else if (rawDisplayMap && typeof rawDisplayMap === 'object') {
        valueDisplayMap = rawDisplayMap;
      }
      // Prefer the structured `value_groups` array whenever it actually has
      // entries -- `value_groups_raw` can legitimately be an empty string
      // alongside a populated `value_groups` list (e.g. a group added via the
      // clickable-chip builder rather than typed as raw text), so checking
      // only "is value_groups_raw a string" silently discarded real groups
      // whenever the two drifted out of sync.
      const rawGroupsText = entry.value_groups_raw;
      const hasGroupsArray = Array.isArray(entry.value_groups) && entry.value_groups.length > 0;
      const valueGroups = hasGroupsArray
        ? entry.value_groups
            .filter((group) => group && typeof group === 'object')
            .map((group) => ({
              label: String(group.label || '').trim(),
              values: Array.isArray(group.values)
                ? group.values.map((v) => String(v).trim()).filter(Boolean)
                : [],
            }))
            .filter((group) => group.label && group.values.length)
        : typeof rawGroupsText === 'string' && rawGroupsText.trim()
          ? parseValueGroupsText(rawGroupsText)
          : [];
      const result = {
        key: derivedKey,
        label: label || derivedKey,
        fields,
        value_type: ['number', 'fraction'].includes(valueType) ? valueType : 'string',
        sort_order: sortOrder,
        custom_value_order: customValueOrder,
        value_display_map: valueDisplayMap || {},
        value_groups: valueGroups,
        show_ungrouped_values: entry.show_ungrouped_values !== false,
        multi_select: entry.multi_select === true || entry.multi_select === 'true',
      };
      if (includeRaw && typeof rawDisplayText === 'string') {
        result.value_display_map_raw = rawDisplayText;
      }
      if (includeRaw) {
        result.value_groups_raw = formatValueGroupsText(valueGroups);
      }
      return result;
    })
    .filter((entry) => (keepEmpty ? true : entry.key));
  if (normalized.length) {
    return normalized;
  }
  return fallbackToDefault
    ? DEFAULT_SYSTEM_SHOP_FILTERS.map((entry) => ({ ...entry, fields: [...entry.fields] }))
    : [];
};

const normalizeSystemShopSearch = (value) => {
  if (!value || typeof value !== 'object') {
    return { ...DEFAULT_SYSTEM_SHOP_SEARCH, query_fields: [...DEFAULT_SYSTEM_SHOP_SEARCH.query_fields] };
  }
  const listify = (raw, fallback) => {
    if (Array.isArray(raw)) {
      return raw.map((item) => String(item).trim()).filter(Boolean);
    }
    if (typeof raw === 'string') {
      return raw
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return fallback;
  };
  const enabled =
    typeof value.enabled === 'boolean'
      ? value.enabled
      : typeof value.enabled === 'string'
      ? value.enabled.trim().toLowerCase() === 'true'
      : DEFAULT_SYSTEM_SHOP_SEARCH.enabled;
  const operatorRaw = String(value.operator ?? DEFAULT_SYSTEM_SHOP_SEARCH.operator).toUpperCase();
  const operator = operatorRaw === 'NONE' ? '' : operatorRaw;
  const rawModes = Array.isArray(value.interaction_modes) ? value.interaction_modes : [];
  const interactionModes = rawModes
    .map((mode) => String(mode || '').trim().toLowerCase())
    .filter((mode, idx, arr) => (mode === 'filters' || mode === 'qa') && arr.indexOf(mode) === idx);
  const normalizedModes = interactionModes.length ? interactionModes : [...DEFAULT_SYSTEM_SHOP_SEARCH.interaction_modes];
  let interactionMode = String(value.interaction_mode || DEFAULT_SYSTEM_SHOP_SEARCH.interaction_mode).trim().toLowerCase();
  if (interactionMode !== 'filters' && interactionMode !== 'qa') {
    interactionMode = DEFAULT_SYSTEM_SHOP_SEARCH.interaction_mode;
  }
  if (!normalizedModes.includes(interactionMode)) {
    interactionMode = normalizedModes[0];
  }
  const allowModeSwitch =
    typeof value.allow_mode_switch === 'boolean'
      ? value.allow_mode_switch
      : typeof value.allow_mode_switch === 'string'
      ? value.allow_mode_switch.trim().toLowerCase() === 'true'
      : DEFAULT_SYSTEM_SHOP_SEARCH.allow_mode_switch;
  return {
    enabled,
    placeholder: String(value.placeholder || DEFAULT_SYSTEM_SHOP_SEARCH.placeholder),
    query_fields: listify(value.query_fields, [...DEFAULT_SYSTEM_SHOP_SEARCH.query_fields]),
    phrase_fields: listify(value.phrase_fields, []),
    min_match: String(value.min_match || DEFAULT_SYSTEM_SHOP_SEARCH.min_match),
    operator: operator === 'OR' ? 'OR' : operator === 'AND' ? 'AND' : '',
    tie: Number.isFinite(Number(value.tie)) ? Number(value.tie) : DEFAULT_SYSTEM_SHOP_SEARCH.tie,
    qs: Number.isFinite(Number(value.qs)) ? Number(value.qs) : DEFAULT_SYSTEM_SHOP_SEARCH.qs,
    ps: Number.isFinite(Number(value.ps)) ? Number(value.ps) : DEFAULT_SYSTEM_SHOP_SEARCH.ps,
    bq: listify(value.bq, []),
    bf: listify(value.bf, []),
    suggest_fields: listify(value.suggest_fields, [...DEFAULT_SYSTEM_SHOP_SEARCH.suggest_fields]),
    suggest_limit: Number.isFinite(Number(value.suggest_limit))
      ? Math.max(1, Number(value.suggest_limit))
      : DEFAULT_SYSTEM_SHOP_SEARCH.suggest_limit,
    show_tooltips:
      typeof value.show_tooltips === 'boolean'
        ? value.show_tooltips
        : typeof value.show_tooltips === 'string'
        ? value.show_tooltips.trim().toLowerCase() === 'true'
        : DEFAULT_SYSTEM_SHOP_SEARCH.show_tooltips,
    query_type: String(value.query_type || '').trim(),
    interaction_mode: interactionMode,
    interaction_modes: normalizedModes,
    allow_mode_switch: allowModeSwitch,
  };
};

const normalizeQueryFlows = (value) => {
  if (!Array.isArray(value)) return [];
  const allowedEngines = new Set(['solr', 'opensearch', 'shadow', 'any']);
  return value
    .filter((flow) => flow && typeof flow === 'object')
    .map((flow) => ({
      id: String(flow.id || ''),
      name: String(flow.name || '').trim() || 'Untitled Flow',
      search_engine: allowedEngines.has(String(flow.search_engine || flow.engine || 'any').toLowerCase())
        ? String(flow.search_engine || flow.engine || 'any').toLowerCase()
        : 'any',
      nodes: Array.isArray(flow.nodes) ? flow.nodes : [],
      edges: Array.isArray(flow.edges) ? flow.edges : [],
    }))
    .filter((flow) => flow.id);
};

const normalizeQueryTypes = (value, defaults) => {
  if (!Array.isArray(value)) {
    return defaults.map((entry) => ({
      ...entry,
      id: String(entry.id || entry.query_type_id || ''),
      search_engine: String(entry.search_engine || 'any').toLowerCase(),
      query_fields: [...entry.query_fields],
      phrase_fields: [...entry.phrase_fields],
    }));
  }
  const listify = (raw) => {
    if (Array.isArray(raw)) {
      return raw.map((item) => String(item).trim()).filter(Boolean);
    }
    if (typeof raw === 'string') {
      return raw
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };
  const allowedEngines = new Set(['solr', 'opensearch', 'shadow', 'any']);
  const normalized = value
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => {
      const name = String(entry.name || entry.key || '').trim();
      if (!name) return null;
      const label = String(entry.label || entry.title || name).trim() || name;
      const operatorRaw = String(entry.operator ?? 'AND').trim().toUpperCase();
      const operator = operatorRaw === 'NONE' ? '' : operatorRaw;
      const useRaw = entry.use_raw_q;
      const debugRaw = entry.debug;
      const rowsRaw = entry.rows;
      return {
        id: String(entry.id || entry.query_type_id || '').trim(),
        name,
        label,
        search_engine: allowedEngines.has(String(entry.search_engine || entry.engine || 'any').toLowerCase())
          ? String(entry.search_engine || entry.engine || 'any').toLowerCase()
          : 'any',
        def_type: String(entry.def_type || entry.defType || 'edismax').trim() || 'edismax',
        fl: typeof entry.fl === 'string' ? entry.fl : '',
        rows: rowsRaw === '' || rowsRaw === null || rowsRaw === undefined ? '' : Number(rowsRaw),
        wt: typeof entry.wt === 'string' ? entry.wt : '',
        debug: typeof debugRaw === 'boolean' ? debugRaw : false,
        query_fields: listify(entry.query_fields || entry.qf),
        phrase_fields: listify(entry.phrase_fields || entry.pf),
        min_match: String(entry.min_match || entry.mm || '').trim(),
        operator: operator === 'OR' ? 'OR' : operator === 'AND' ? 'AND' : '',
        tie: Number.isFinite(Number(entry.tie)) ? Number(entry.tie) : 0,
        qs: Number.isFinite(Number(entry.qs)) ? Number(entry.qs) : 1,
        ps: Number.isFinite(Number(entry.ps)) ? Number(entry.ps) : 2,
        ps2: entry.ps2 === '' || entry.ps2 === null || entry.ps2 === undefined ? '' : Number(entry.ps2),
        bq: listify(entry.bq),
        bf: listify(entry.bf),
        use_raw_q: typeof useRaw === 'boolean' ? useRaw : true,
      };
    })
    .filter(Boolean);
  if (normalized.length) {
    return normalized;
  }
  return defaults.map((entry) => ({
    ...entry,
    id: String(entry.id || entry.query_type_id || ''),
    search_engine: String(entry.search_engine || 'any').toLowerCase(),
    query_fields: [...entry.query_fields],
    phrase_fields: [...entry.phrase_fields],
  }));
};

const normalizeQueryTypesList = (value, systemDefaults, productDefaults, options = {}) => {
  const { fallback = true } = options;
  if (Array.isArray(value) && value.length) {
    return value
      .filter((entry) => entry && typeof entry === 'object')
      .map((entry) => {
        const target = String(entry.target || entry.type || '').toLowerCase();
        const base = target === 'product' ? productDefaults[0] : systemDefaults[0];
        return {
          target: target === 'product' ? 'product' : 'ahri',
          ...base,
          ...entry,
        };
      });
  }
  if (!fallback) {
    return [];
  }
  const systemList = normalizeQueryTypes(systemDefaults, systemDefaults).map((entry) => ({ ...entry, target: 'ahri' }));
  const productList = normalizeQueryTypes(productDefaults, productDefaults).map((entry) => ({ ...entry, target: 'product' }));
  return [...systemList, ...productList];
};

const splitQueryTypes = (list) => {
  const system = [];
  const product = [];
  (list || []).forEach((entry) => {
    const target = String(entry?.target || '').toLowerCase();
    if (target === 'product') {
      product.push(entry);
    } else {
      system.push(entry);
    }
  });
  return { system, product };
};

const normalizeSystemShopFilterSettings = (value) => {
  if (!value || typeof value !== 'object') {
    return {
      include_unmatched: false,
      match_types: ['complete', 'partial'],
      exclude_zero_in_requested_warehouse: true,
      show_fully_available_only: false,
      show_score: false,
      show_score_debug: false,
      enable_integrated_search: true,
      enable_image_search: true,
      order: [],
      use_variant_based_display: false,
      brand_filter_dimension: 'manufacturer',
      show_brand_row: true,
    };
  }
  const includeUnmatched =
    typeof value.include_unmatched === 'boolean'
      ? value.include_unmatched
      : typeof value.include_unmatched === 'string'
      ? value.include_unmatched.trim().toLowerCase() === 'true'
      : false;
  const matchTypes = normalizeSystemShopMatchTypes(value.match_types, { includeUnmatched });
  const excludeZeroInRequestedWarehouse =
    typeof value.exclude_zero_in_requested_warehouse === 'boolean'
      ? value.exclude_zero_in_requested_warehouse
      : typeof value.exclude_zero_in_requested_warehouse === 'string'
      ? value.exclude_zero_in_requested_warehouse.trim().toLowerCase() === 'true'
      : true;
  const showFullyAvailableOnly =
    typeof value.show_fully_available_only === 'boolean'
      ? value.show_fully_available_only
      : typeof value.show_fully_available_only === 'string'
      ? value.show_fully_available_only.trim().toLowerCase() === 'true'
      : false;
  const showScore =
    typeof value.show_score === 'boolean'
      ? value.show_score
      : typeof value.show_score === 'string'
      ? value.show_score.trim().toLowerCase() === 'true'
      : false;
  const showScoreDebug =
    typeof value.show_score_debug === 'boolean'
      ? value.show_score_debug
      : typeof value.show_score_debug === 'string'
      ? value.show_score_debug.trim().toLowerCase() === 'true'
      : false;
  const order = Array.isArray(value.order)
    ? value.order
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
          type: String(item.type || '').trim() || 'standard',
          key: String(item.key || '').trim(),
        }))
        .filter((item) => item.key)
    : [];
  const enableIntegratedSearch =
    typeof value.enable_integrated_search === 'boolean'
      ? value.enable_integrated_search
      : typeof value.enable_integrated_search === 'string'
      ? value.enable_integrated_search.trim().toLowerCase() === 'true'
      : true;
  const enableImageSearch =
    typeof value.enable_image_search === 'boolean'
      ? value.enable_image_search
      : typeof value.enable_image_search === 'string'
      ? value.enable_image_search.trim().toLowerCase() === 'true'
      : true;
  const useVariantBasedDisplay =
    typeof value.use_variant_based_display === 'boolean'
      ? value.use_variant_based_display
      : typeof value.use_variant_based_display === 'string'
      ? value.use_variant_based_display.trim().toLowerCase() === 'true'
      : false;
  const brandFilterDimensionRaw = String(value.brand_filter_dimension || '').trim().toLowerCase();
  const brandFilterDimension = brandFilterDimensionRaw === 'brand' ? 'brand' : 'manufacturer';
  const showBrandRow =
    typeof value.show_brand_row === 'boolean'
      ? value.show_brand_row
      : typeof value.show_brand_row === 'string'
      ? value.show_brand_row.trim().toLowerCase() === 'true'
      : true;
  return {
    include_unmatched: matchTypes.includes('none'),
    match_types: matchTypes,
    exclude_zero_in_requested_warehouse: excludeZeroInRequestedWarehouse,
    show_fully_available_only: showFullyAvailableOnly,
    show_score: showScore,
    show_score_debug: showScoreDebug,
    enable_integrated_search: enableIntegratedSearch,
    enable_image_search: enableImageSearch,
    order,
    use_variant_based_display: useVariantBasedDisplay,
    brand_filter_dimension: brandFilterDimension,
    show_brand_row: showBrandRow,
  };
};

const buildSystemShopFilterOrder = (standardFilters, advancedFilters, orderRaw) => {
  const standard = normalizeSystemShopFilters(standardFilters, {
    includeRaw: true,
    keepEmpty: true,
    fallbackToDefault: false,
  });
  const advanced = normalizeSystemShopAdvancedFilters(advancedFilters, {
    keepEmpty: true,
    fallbackToDefault: false,
  });
  const settings = normalizeSystemShopFilterSettings({ order: orderRaw });
  const configuredOrder = settings.order || [];

  const byId = new Map();
  const sourceOrder = [];

  standard.forEach((entry) => {
    const key = String(entry?.key || '').trim();
    if (!key) return;
    const id = `standard:${key}`;
    if (byId.has(id)) return;
    byId.set(id, { type: 'standard', key });
    sourceOrder.push(id);
  });

  advanced.forEach((entry) => {
    const key = String(entry?.key || '').trim();
    if (!key) return;
    const id = `advanced:${key}`;
    if (byId.has(id)) return;
    byId.set(id, { type: 'advanced', key });
    sourceOrder.push(id);
  });

  const result = [];
  const used = new Set();

  configuredOrder.forEach((entry) => {
    const type = String(entry?.type || '').trim().toLowerCase() || 'standard';
    const key = String(entry?.key || '').trim();
    if (!key) return;
    const id = `${type}:${key}`;
    if (!byId.has(id) || used.has(id)) return;
    result.push(byId.get(id));
    used.add(id);
  });

  sourceOrder.forEach((id) => {
    if (used.has(id)) return;
    result.push(byId.get(id));
    used.add(id);
  });

  return result;
};

const parseAdvancedFilterValues = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return [];
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf('=');
      if (idx === -1) {
        return { label: line, value: line, query: '' };
      }
      const label = line.slice(0, idx).trim();
      const query = line.slice(idx + 1).trim();
      return { label: label || query, value: label || query, query };
    })
    .filter((entry) => entry.label);
};

const formatAdvancedFilterValues = (values) =>
  (values || []).map((entry) => `${entry.label}=${entry.query || ''}`).join('\n');

const normalizeSystemShopAdvancedFilters = (value, options = {}) => {
  const { keepEmpty = false, fallbackToDefault = false } = options;
  if (!Array.isArray(value)) {
    return fallbackToDefault ? [] : [];
  }
  const normalized = value
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => {
      const key = String(entry.key || entry.id || entry.name || '').trim();
      const label = String(entry.label || entry.title || '').trim() || key;
      const rawValues = entry.values_raw || entry.values || [];
      const values = Array.isArray(rawValues)
        ? rawValues
            .filter((item) => item && typeof item === 'object')
            .map((item) => ({
              label: String(item.label || item.value || '').trim(),
              value: String(item.value || item.label || '').trim(),
              query: String(item.query || '').trim(),
            }))
            .filter((item) => item.label)
        : parseAdvancedFilterValues(rawValues);
      return {
        key: key || label,
        label,
        values,
        values_raw: entry.values_raw || '',
      };
    })
    .filter((entry) => (keepEmpty ? true : entry.key));
  return normalized.length ? normalized : [];
};

const InfoHint = ({ label, showAlways = false }) => (
  <span
    className={`info-hint ms-2 align-middle${showAlways ? ' info-hint--always' : ''}`}
    aria-label={label}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      role="img"
      aria-hidden="true"
      style={{ verticalAlign: '-2px' }}
    >
      <path d="M8 1.333a6.667 6.667 0 1 0 0 13.334A6.667 6.667 0 0 0 8 1.333Zm0 1.2a5.467 5.467 0 1 1 0 10.934A5.467 5.467 0 0 1 8 2.533Zm-.02 2.4a.82.82 0 1 0 0 1.64.82.82 0 0 0 0-1.64Zm-1 3.02h2v4.1h-2v-4.1Z" />
    </svg>
    <span className="info-hint-bubble" role="tooltip">
      {label}
    </span>
    <span className="visually-hidden">{label}</span>
  </span>
);

const SettingToggle = ({ id, title, description, checked, onChange, children, indent = false }) => (
  <div className={`setting-toggle${indent ? ' setting-toggle--indent' : ''}`}>
    <div className="setting-toggle-row">
      <label className="setting-toggle-label" htmlFor={id}>
        {title}
      </label>
      <Switch id={id} checked={!!checked} onChange={onChange} size="small" />
    </div>
    {description && <div className="form-text setting-toggle-desc">{description}</div>}
    {children}
  </div>
);

const ensureMatchLookupConfig = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const rawFields = Array.isArray(source.product_fields) ? source.product_fields : [];
  const cleaned = [];
  rawFields.forEach((field) => {
    if (typeof field !== 'string') return;
    const name = field.trim();
    if (name && !cleaned.includes(name)) {
      cleaned.push(name);
    }
  });
  if (!cleaned.length) {
    cleaned.push(...DEFAULT_MATCH_PRODUCT_FIELDS);
  } else {
    if (!cleaned.includes('productCode')) cleaned.push('productCode');
    if (!cleaned.includes('imageUrl')) cleaned.push('imageUrl');
  }
  const enforce =
    typeof source.enforce_complete_match === 'boolean'
      ? source.enforce_complete_match
      : true;
  const mappingField = (() => {
    const raw = typeof source.mapping_field === 'string' ? source.mapping_field.trim() : '';
    if (!raw) return 'productCode';
    const lower = raw.toLowerCase();
    if (['manufacturer_part_number', 'manufacturerpartnumber', 'mfr_part_number', 'mfr_part'].includes(lower)) {
      return 'manufacturerPartNumber';
    }
    return 'productCode';
  })();
  if (!cleaned.includes(mappingField)) cleaned.push(mappingField);

  return {
    product_fields: cleaned,
    enforce_complete_match: enforce,
    mapping_field: mappingField,
  };
};


const normalizeWhereTree = (tree) => {
  const base = createEmptyWhereTree();
  if (!tree || typeof tree !== 'object') {
    return base;
  }
  const logic = String(tree.logic || '').toUpperCase() === 'OR' ? 'OR' : 'AND';
  const conditions = Array.isArray(tree.conditions)
    ? tree.conditions
        .filter((cond) => cond && typeof cond === 'object')
        .map((cond) => ({
          field: typeof cond.field === 'string' ? cond.field : '',
          op: typeof cond.op === 'string' ? cond.op : 'eq',
          value: cond.hasOwnProperty('value') ? cond.value : '',
        }))
    : [];
  const groups = Array.isArray(tree.groups)
    ? tree.groups
        .filter((grp) => grp && typeof grp === 'object')
        .map((grp) => normalizeWhereTree(grp))
    : [];
  return { logic, conditions, groups };
};

const hasWhereContent = (tree) => {
  if (!tree || typeof tree !== 'object') return false;
  if (Array.isArray(tree.conditions) && tree.conditions.some((cond) => cond && cond.field)) {
    return true;
  }
  if (Array.isArray(tree.groups)) {
    return tree.groups.some((grp) => hasWhereContent(grp));
  }
  return false;
};

const generateUuid = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (err) {
    // ignore and fallback
  }
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return template.replace(/[xy]/g, (ch) => {
    const rnd = Math.floor(Math.random() * 16);
    const val = ch === 'x' ? rnd : ((rnd & 0x3) | 0x8);
    return val.toString(16);
  });
};

const flowStepLabel = (step) => {
  if (step.type === 'condition') {
    return `If results ${step.operator || '>='} ${step.threshold ?? 0}`;
  }
  if (step.type === 'flow') {
    return `Flow: ${step.flowId || 'Select flow'}`;
  }
  return `Query: ${step.queryType || step.queryTypeId || 'default'}`;
};

const buildMermaidDiagram = (steps) => {
  if (!Array.isArray(steps) || steps.length === 0) return '';
  const safeId = (id) => String(id || '').replace(/[^a-zA-Z0-9_]/g, '_');
  const label = (text) => String(text || '').replace(/"/g, "'");
  const lines = ['flowchart TD'];
  const endNodeId = 'END_NODE';
  const idMap = new Map();
  let needsEnd = false;

  steps.forEach((step, index) => {
    const rawId = step.id || `node_${index}`;
    const id = safeId(rawId) || `node_${index}`;
    idMap.set(step.id || rawId, id);
    lines.push(`  ${id}["${label(flowStepLabel(step))}"]`);
  });

  const resolveId = (raw, indexFallback) => {
    if (!raw) return '';
    if (idMap.has(raw)) return idMap.get(raw);
    const fallback = safeId(raw) || `node_${indexFallback}`;
    if (!idMap.has(raw)) {
      idMap.set(raw, fallback);
      lines.push(`  ${fallback}["${label(raw)}"]`);
    }
    return fallback;
  };

  steps.forEach((step, index) => {
    const source = resolveId(step.id || `node_${index}`, index);
    if (step.type === 'query') {
      if (step.next) {
        lines.push(`  ${source} --> ${resolveId(step.next, index + 1000)}`);
      } else {
        needsEnd = true;
        lines.push(`  ${source} --> ${endNodeId}`);
      }
    } else {
      if (step.nextTrue) {
        lines.push(`  ${source} -- True --> ${resolveId(step.nextTrue, index + 2000)}`);
      } else {
        needsEnd = true;
        lines.push(`  ${source} -- True --> ${endNodeId}`);
      }
      if (step.nextFalse) {
        lines.push(`  ${source} -- False --> ${resolveId(step.nextFalse, index + 3000)}`);
      } else {
        needsEnd = true;
        lines.push(`  ${source} -- False --> ${endNodeId}`);
      }
    }
  });

  if (needsEnd) {
    lines.push(`  ${endNodeId}["End"]`);
  }
  return lines.join('\n');
};

const AhriData = () => {
  const { getAuthHeaders, account } = useAuth();
  const [cfg, setCfg] = useState({
    token_url: '', grant_type: 'client_credentials', client_id: '', client_secret: '', scope: '', resource: '',
    data_url: '', userUniqueId: '', content_type: 'application/json', program_names: [],
    outdoor_brand_names: [], indoor_brand_names: [],
    pinecone: { api_key: '', host: '', index: '' },
    openai: { api_key: '', base_url: '', embedding_model: 'text-embedding-3-small', completions_model: 'gpt-4.1' },
    solr: { base_url: '', username: '', password: '', verify_ssl: true, shards: 1, replicas: 1, sync_variants_on_import: false },
    page_size: 200, threads: 2, mapping_threads: 2, mapping_v2_max_trim: 4, mapping_v2_min_length: 4,
    vectorize_enabled: true,
    // Network/security
    verify_ssl: true, use_system_proxy: true, proxy_url: '',
    // Optional manual bearer token to bypass OAuth call
    access_token_override: '',
    lookup_transform: { ...DEFAULT_LOOKUP_TRANSFORM },
    product_image_fallback_base_url: DEFAULT_PRODUCT_IMAGE_BASE_URL,
    match_product_lookup: ensureMatchLookupConfig(),
    product_attribute_import: ensureProductAttrImport(),
    system_shop_filters: normalizeSystemShopFilters(),
    system_shop_search: normalizeSystemShopSearch(),
    query_types: [],
    system_shop_query_types: normalizeQueryTypes(null, DEFAULT_SYSTEM_SHOP_QUERY_TYPES),
    product_query_types: normalizeQueryTypes(null, DEFAULT_PRODUCT_QUERY_TYPES),
    system_shop_filters_settings: normalizeSystemShopFilterSettings(),
    system_shop_advanced_filters: normalizeSystemShopAdvancedFilters(),
    system_shop_query_flows: [],
  });
  const [ahriSourceMap, setAhriSourceMap] = useState({});
  const [activeTab, setActiveTab] = useState('settings');
  const [systemShopEventEndpoints, setSystemShopEventEndpoints] = useState([]);
  const [systemShopSelectedEndpointId, setSystemShopSelectedEndpointId] = useState('');
  const [systemShopEvents, setSystemShopEvents] = useState([]);
  const [systemShopEventsLoading, setSystemShopEventsLoading] = useState(false);
  const [systemShopEventsError, setSystemShopEventsError] = useState('');
  const [systemShopEventSearch, setSystemShopEventSearch] = useState('');
  const [systemShopEventStartAt, setSystemShopEventStartAt] = useState('');
  const [systemShopEventEndAt, setSystemShopEventEndAt] = useState('');
  const [systemShopEventDetailOpen, setSystemShopEventDetailOpen] = useState(false);
  const [systemShopSelectedEvent, setSystemShopSelectedEvent] = useState(null);
  const [systemShopWebhookSubs, setSystemShopWebhookSubs] = useState([]);
  const [systemShopWebhookSubSaving, setSystemShopWebhookSubSaving] = useState('');
  const [systemShopWebhookDeliveryStats, setSystemShopWebhookDeliveryStats] = useState({
    pending: 0,
    delivered: 0,
    failed: 0,
  });
  const [systemShopWebhookDeliveries, setSystemShopWebhookDeliveries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [polling, setPolling] = useState(false);
  const [solrImportState, setSolrImportState] = useState({});
  const [mappingState, setMappingState] = useState({});
  const [mappingJobs, setMappingJobs] = useState([]);
  const [mappingJobsLoading, setMappingJobsLoading] = useState(false);
  const [mappingJobsError, setMappingJobsError] = useState('');
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState('');
  const [mappingStarting, setMappingStarting] = useState(false);
  const [mappingStopping, setMappingStopping] = useState(false);
  const [mappingScopeSelection, setMappingScopeSelection] = useState('full');
  const [mappingIdsInput, setMappingIdsInput] = useState('');
  const [mappingV2State, setMappingV2State] = useState({});
  const [mappingV2Jobs, setMappingV2Jobs] = useState([]);
  const [mappingV2JobsLoading, setMappingV2JobsLoading] = useState(false);
  const [mappingV2JobsError, setMappingV2JobsError] = useState('');
  const [mappingV2Loading, setMappingV2Loading] = useState(false);
  const [mappingV2Error, setMappingV2Error] = useState('');
  const [mappingV2Starting, setMappingV2Starting] = useState(false);
  const [mappingV2Stopping, setMappingV2Stopping] = useState(false);
  const [mappingV2BaselineStarting, setMappingV2BaselineStarting] = useState(false);
  const [mappingV2ScopeSelection, setMappingV2ScopeSelection] = useState('full');
  const [mappingV2IdsInput, setMappingV2IdsInput] = useState('');
  const [mappingV3State, setMappingV3State] = useState({});
  const [mappingV3Loading, setMappingV3Loading] = useState(false);
  const [mappingV3Error, setMappingV3Error] = useState('');
  const [mappingV3Starting, setMappingV3Starting] = useState(false);
  const [mappingV3Stopping, setMappingV3Stopping] = useState(false);
  const [mappingSubTab, setMappingSubTab] = useState('v2');
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportMatchStatus, setExportMatchStatus] = useState(['all']);
  const [exportProgram, setExportProgram] = useState([]);
  const [exportBrand, setExportBrand] = useState([]);
  const [exportTab, setExportTab] = useState('create');
  const [exportBrandOptions, setExportBrandOptions] = useState([]);
  const [exportBrandLoading, setExportBrandLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportJobs, setExportJobs] = useState([]);
  const [exportJobsLoading, setExportJobsLoading] = useState(false);
  const [exportJobsError, setExportJobsError] = useState('');
  const [exportCount, setExportCount] = useState(null);
  const [exportCountLoading, setExportCountLoading] = useState(false);
  const exportCountKeyRef = useRef('');
  const exportPollRef = useRef(null);
  const exportBatchesLoadedRef = useRef(false);
  const [systemShopTab, setSystemShopTab] = useState('filters');
  const [savingSystemShopFilters, setSavingSystemShopFilters] = useState(false);
  const [flowListId, setFlowListId] = useState('');
  const [flowName, setFlowName] = useState('');
  const [flowEngine, setFlowEngine] = useState('any');
  const [flowView, setFlowView] = useState('list');
  const [flowSteps, setFlowSteps] = useState([]);
  const [flowSelectedStepId, setFlowSelectedStepId] = useState('');
  const [flowTestQuery, setFlowTestQuery] = useState('');
  const [flowTestCustomerId, setFlowTestCustomerId] = useState('');
  const [flowTestResult, setFlowTestResult] = useState(null);
  const [flowTestLoading, setFlowTestLoading] = useState(false);
  const flowTestRunIdRef = useRef(0);
  const flowTestAbortRef = useRef(null);
  const [flowMermaidSvg, setFlowMermaidSvg] = useState('');
  const [flowMermaidError, setFlowMermaidError] = useState('');
  const [routingView, setRoutingView] = useState('list');
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [routesError, setRoutesError] = useState('');
  const [routeFormError, setRouteFormError] = useState('');
  const [routeDraft, setRouteDraft] = useState(null);
  const [routeDeleteId, setRouteDeleteId] = useState(null);
  const [routeTest, setRouteTest] = useState({
    input: '{\n  "queryText": ""\n}',
    loading: false,
    error: '',
    result: null,
  });
  const routeLoadedRef = useRef(false);
  const [schemaEditorTab, setSchemaEditorTab] = useState('ahri');
  const [schemaSectionTab, setSchemaSectionTab] = useState('fields');
  const [schemaData, setSchemaData] = useState({ ahri: null, products: null });
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterDrawerWidth, setFilterDrawerWidth] = useState(() =>
    typeof window === 'undefined' ? 720 : Math.round(window.innerWidth * 0.6)
  );
  const [filterDrawerResizing, setFilterDrawerResizing] = useState(false);
  const filterDrawerResizeRef = useRef({ startX: 0, startWidth: 720 });
  const displayMapTextAreaRef = useRef(null);
  const [editingFilter, setEditingFilter] = useState(null);
  const [filterDraft, setFilterDraft] = useState(null);
  const [availableFacetFields, setAvailableFacetFields] = useState({
    loading: false,
    error: '',
    variantMode: false,
    brandFilterDimension: '',
    fields: [],
  });
  const [advancedFilterTest, setAdvancedFilterTest] = useState({
    loading: false,
    results: [],
    error: '',
  });
  const [filterValuePreview, setFilterValuePreview] = useState({
    loading: false,
    error: '',
    facets: {},
    productAttributeFacets: {},
    loadedAt: null,
  });
  const [groupBuilderSelected, setGroupBuilderSelected] = useState([]);
  const [groupBuilderLabel, setGroupBuilderLabel] = useState('');
  const previewValues = useMemo(
    () =>
      getPreviewValuesForFields(filterDraft?.fields, filterValuePreview, {
        sortOrder: filterDraft?.sort_order || 'count_desc',
        valueType: filterDraft?.value_type || 'string',
        customOrder: filterDraft?.custom_value_order || [],
      }),
    [filterDraft?.fields, filterDraft?.sort_order, filterDraft?.value_type, filterDraft?.custom_value_order, filterValuePreview]
  );
  const configuredGroups = useMemo(
    () => parseValueGroupsText(filterDraft?.value_groups_raw),
    [filterDraft?.value_groups_raw]
  );
  const previewValueSet = useMemo(
    () => new Set(previewValues.map((item) => item.value)),
    [previewValues]
  );
  const parsedDisplayMapEntries = useMemo(
    () => parseValueDisplayMapText(filterDraft?.value_display_map_raw) || {},
    [filterDraft?.value_display_map_raw]
  );
  const hasSampleData = !filterValuePreview.loading && !filterValuePreview.error && previewValueSet.size > 0;
  const unmatchedDisplayMapValues = useMemo(
    () => (hasSampleData ? Object.keys(parsedDisplayMapEntries).filter((key) => !previewValueSet.has(key)) : []),
    [hasSampleData, parsedDisplayMapEntries, previewValueSet]
  );
  const groupValueIssues = useMemo(() => {
    if (!hasSampleData) return { unmatched: [], overlapping: [] };
    const occurrences = new Map();
    configuredGroups.forEach((group) => {
      group.values.forEach((value) => {
        occurrences.set(value, (occurrences.get(value) || 0) + 1);
      });
    });
    const unmatched = [...new Set(configuredGroups.flatMap((g) => g.values))].filter(
      (v) => !previewValueSet.has(v)
    );
    const overlapping = [...occurrences.entries()].filter(([, count]) => count > 1).map(([value]) => value);
    return { unmatched, overlapping };
  }, [hasSampleData, configuredGroups, previewValueSet]);
  const hasAdvancedValidationIssues =
    unmatchedDisplayMapValues.length > 0 ||
    groupValueIssues.unmatched.length > 0 ||
    groupValueIssues.overlapping.length > 0;
  const [draggingCustomValue, setDraggingCustomValue] = useState(null);
  useEffect(() => {
    if (filterDraft?.sort_order !== 'custom') return;
    const existing = Array.isArray(filterDraft.custom_value_order) ? filterDraft.custom_value_order : [];
    const seen = new Set(existing);
    const missing = previewValues.map((item) => item.value).filter((v) => !seen.has(v));
    if (!missing.length) return;
    setFilterDraft((prev) =>
      prev ? { ...prev, custom_value_order: [...(prev.custom_value_order || []), ...missing] } : prev
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDraft?.sort_order, previewValues]);
  const [schemaSearch, setSchemaSearch] = useState('');
  const [schemaDrawerOpen, setSchemaDrawerOpen] = useState(false);
  const [schemaDrawerMode, setSchemaDrawerMode] = useState('add');
  const [schemaDrawerType, setSchemaDrawerType] = useState('field');
  const [, setSchemaDrawerRecord] = useState(null);
  const [schemaDrawerSchema, setSchemaDrawerSchema] = useState('ahri');
  const [schemaForm] = Form.useForm();
  const [schemaDrawerWidth, setSchemaDrawerWidth] = useState(() => {
    if (typeof window === 'undefined') return 520;
    return Math.round(window.innerWidth * 0.5);
  });
  const [schemaDrawerResizing, setSchemaDrawerResizing] = useState(false);
  const schemaResizeRef = useRef({ startX: 0, startWidth: 0 });
  const persistConfigRef = useRef(null);
  const [queryTypePanelMode, setQueryTypePanelMode] = useState(null); // add | edit | test
  const [queryTypePanelIndex, setQueryTypePanelIndex] = useState(null);
  const [queryTypeDraft, setQueryTypeDraft] = useState(null);
  const [queryTypeEngineTab, setQueryTypeEngineTab] = useState('all');
  const [queryTypeDrawerWidth, setQueryTypeDrawerWidth] = useState(() =>
    typeof window === 'undefined' ? 520 : Math.round(window.innerWidth * 0.3)
  );
  const [queryTypeDrawerResizing, setQueryTypeDrawerResizing] = useState(false);
  const queryTypeResizeRef = useRef({ startX: 0, startWidth: 0 });
  const [draggingShopFilter, setDraggingShopFilter] = useState(null);
  const [queryTypeTest, setQueryTypeTest] = useState({
    target: 'ahri',
    name: '',
    input: '',
    customer_id_erp: '',
    loading: false,
    error: '',
    successMessage: '',
    total: 0,
    results: [],
    page: 1,
    page_size: 10,
    responseRaw: '',
    search_engine: 'solr',
    search_request: null,
    solr_request: null,
    showRaw: false,
    expandAllFields: false,
  });
  const resetQueryTypeTestOutputs = useCallback((prev) => ({
    ...prev,
    loading: false,
    error: '',
    successMessage: '',
    total: 0,
    results: [],
    page: 1,
    responseRaw: '',
    search_request: null,
    solr_request: null,
    showRaw: false,
  }), []);
  useEffect(() => {
    if (queryTypePanelMode !== 'test') return;
    const target = queryTypeTest.target === 'product' ? 'product' : 'ahri';
    const available = (cfg.query_types || [])
      .filter((entry) => (target === 'product' ? entry?.target === 'product' : entry?.target !== 'product'))
      .map((entry) => String(entry?.name || '').trim())
      .filter(Boolean);
    if (!available.length) return;
    if (!available.includes(String(queryTypeTest.name || '').trim())) {
      setQueryTypeTest((prev) => ({ ...prev, name: available[0] }));
    }
  }, [cfg.query_types, queryTypePanelMode, queryTypeTest.name, queryTypeTest.target]);
  useEffect(() => {
    if (queryTypePanelMode !== 'test') return;
    setQueryTypeTest((prev) => resetQueryTypeTestOutputs(prev));
  }, [queryTypePanelMode, queryTypeTest.target, queryTypeTest.name, resetQueryTypeTestOutputs]);
  // Product Attributes Sync state
  const [attrSyncState, setAttrSyncState] = useState({});
  const [attrSyncLoading, setAttrSyncLoading] = useState(false);
  const [attrSyncError, setAttrSyncError] = useState('');
  const [attrSyncStarting, setAttrSyncStarting] = useState(false);
  const [attrSyncStopping, setAttrSyncStopping] = useState(false);
  const [attrSyncMode, setAttrSyncMode] = useState('replace');
  const [attrSyncClearExisting, setAttrSyncClearExisting] = useState(true);
  const [attrSyncMappedOnly, setAttrSyncMappedOnly] = useState(true);
  const [attrSyncBatchSize, setAttrSyncBatchSize] = useState(100);
  // AHRI Variant Resync state (background job triggered automatically by a
  // bulk product import or a single product edit -- see
  // start_ahri_variant_resync_job in backend/api/ahri.py)
  const [variantResyncState, setVariantResyncState] = useState({});
  const [variantResyncLoading, setVariantResyncLoading] = useState(false);
  const [variantResyncError, setVariantResyncError] = useState('');
  // AHRI System Variant Backfill state (Story 4.1)
  const [variantBackfillProgress, setVariantBackfillProgress] = useState({});
  const [variantBackfillLoading, setVariantBackfillLoading] = useState(false);
  const [variantBackfillError, setVariantBackfillError] = useState('');
  const [variantBackfillStarting, setVariantBackfillStarting] = useState(false);
  const [variantBackfillPolling, setVariantBackfillPolling] = useState(false);
  const [matchCheckState, setMatchCheckState] = useState({});
  const [matchCheckLoading, setMatchCheckLoading] = useState(false);
  const [matchCheckError, setMatchCheckError] = useState('');
  const [matchCheckStarting, setMatchCheckStarting] = useState(false);
  const [matchCheckStopping, setMatchCheckStopping] = useState(false);
  const [matchCheckBatchSize, setMatchCheckBatchSize] = useState(200);
  const [matchCheckCommitEachBatch, setMatchCheckCommitEachBatch] = useState(false);
  const [programOptions, setProgramOptions] = useState([]);
  const [productFieldOptions, setProductFieldOptions] = useState([]);
  const [productFieldOptionsLoading, setProductFieldOptionsLoading] = useState(false);
  const [productFieldOptionsError, setProductFieldOptionsError] = useState('');
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [embDebug, setEmbDebug] = useState(false);
  const [mapV2Id, setMapV2Id] = useState('');
  const [mapV2Loading, setMapV2Loading] = useState(false);
  const [mapV2Error, setMapV2Error] = useState('');
  const [mapV2Result, setMapV2Result] = useState(null);
  const [mapV2ShowDebug, setMapV2ShowDebug] = useState(false);
  const brandOptions = useMemo(() => {
    const set = new Set();
    (cfg.outdoor_brand_names || []).forEach((name) => {
      if (typeof name === 'string' && name.trim()) set.add(name.trim());
    });
    (cfg.indoor_brand_names || []).forEach((name) => {
      if (typeof name === 'string' && name.trim()) set.add(name.trim());
    });
    return Array.from(set);
  }, [cfg.outdoor_brand_names, cfg.indoor_brand_names]);
  const exportBrandList = exportBrandOptions.length > 0 ? exportBrandOptions : brandOptions;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...getAuthHeaders() }),
    [getAuthHeaders]
  );
  const matchLookupCfg = ensureMatchLookupConfig(cfg.match_product_lookup);
  const showSearchTooltips = !!cfg.system_shop_search?.show_tooltips;
  const schemaEndpoints = useMemo(
    () => ({
      ahri: `${API_BASE_URL}/api/v1/ahri/solr/schema`,
      products: `${API_BASE_URL}/api/v1/products/solr/schema`,
    }),
    []
  );
  const updateMatchLookup = useCallback((partial) => {
    setCfg((prev) => ({
      ...prev,
      match_product_lookup: {
        ...ensureMatchLookupConfig(prev.match_product_lookup),
        ...partial,
      },
    }));
  }, []);

  const loadSchema = useCallback(
    async (schemaKey) => {
      const endpoint = schemaEndpoints[schemaKey];
      if (!endpoint) return;
      setSchemaLoading(true);
      setSchemaError('');
      try {
        const resp = await fetch(endpoint, { headers: getAuthHeaders() });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `HTTP ${resp.status}`);
        }
        setSchemaData((prev) => ({ ...prev, [schemaKey]: data?.schema || {} }));
      } catch (err) {
        setSchemaError(String(err?.message || err));
      } finally {
        setSchemaLoading(false);
      }
    },
    [schemaEndpoints, getAuthHeaders]
  );

  const loadAvailableFacetFields = useCallback(async () => {
    setAvailableFacetFields((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-shop/available-facet-fields`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      setAvailableFacetFields({
        loading: false,
        error: '',
        variantMode: !!data?.variant_mode,
        brandFilterDimension: data?.brand_filter_dimension || '',
        fields: Array.isArray(data?.fields) ? data.fields : [],
      });
    } catch (err) {
      setAvailableFacetFields((prev) => ({ ...prev, loading: false, error: String(err?.message || err) }));
    }
  }, [getAuthHeaders]);

  const loadFilterValuePreview = useCallback(async () => {
    setFilterValuePreview((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-shop/filters-cache`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      const cache = data?.cache || {};
      setFilterValuePreview({
        loading: false,
        error: '',
        facets: cache.facets || {},
        productAttributeFacets: cache.product_attribute_facets || {},
        loadedAt: data?.updated_at || null,
      });
    } catch (err) {
      setFilterValuePreview((prev) => ({ ...prev, loading: false, error: String(err?.message || err) }));
    }
  }, [getAuthHeaders]);

  const openSchemaDrawer = useCallback(
    (schemaKey, type, mode, record = null) => {
      setSchemaDrawerSchema(schemaKey);
      setSchemaDrawerType(type);
      setSchemaDrawerMode(mode);
      setSchemaDrawerRecord(record);
      schemaForm.resetFields();
      if (record) {
        schemaForm.setFieldsValue({
          name: record.name || record.source || '',
          type: record.type || '',
          stored: record.stored,
          indexed: record.indexed,
          multiValued: record.multiValued,
          docValues: record.docValues,
          source: record.source,
          dest: record.dest,
          maxChars: record.maxChars,
          class: record.class,
          json: JSON.stringify(record, null, 2),
        });
      }
      setSchemaDrawerOpen(true);
    },
    [schemaForm]
  );

  const submitSchemaDrawer = useCallback(async () => {
    const values = await schemaForm.validateFields();
    const endpointBase = schemaEndpoints[schemaDrawerSchema];
    if (!endpointBase) return;
    const target = `${endpointBase}/${schemaDrawerType}`;
    const method = schemaDrawerMode === 'edit' ? 'PATCH' : 'POST';
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    let payload = {};
    if (values.json) {
      try {
        payload = JSON.parse(values.json);
      } catch (err) {
        message.error('Invalid JSON in Advanced JSON field.');
        return;
      }
    } else if (schemaDrawerType === 'copy-field') {
      payload = { source: values.source, dest: values.dest };
      if (values.maxChars) payload.maxChars = Number(values.maxChars);
    } else if (schemaDrawerType === 'field-type') {
      payload = { name: values.name, class: values.class };
    } else {
      payload = {
        name: values.name,
        type: values.type,
        stored: !!values.stored,
        indexed: !!values.indexed,
        multiValued: !!values.multiValued,
        docValues: !!values.docValues,
      };
    }
    try {
      const resp = await fetch(target, {
        method,
        headers,
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      message.success('Schema updated');
      setSchemaDrawerOpen(false);
      await loadSchema(schemaDrawerSchema);
    } catch (err) {
      message.error(String(err?.message || err));
    }
  }, [
    schemaForm,
    schemaEndpoints,
    schemaDrawerSchema,
    schemaDrawerType,
    schemaDrawerMode,
    getAuthHeaders,
    loadSchema,
  ]);

  const deleteSchemaItem = useCallback(
    async (schemaKey, type, record) => {
      const endpointBase = schemaEndpoints[schemaKey];
      if (!endpointBase) return;
      let url = `${endpointBase}/${type}`;
      if (type === 'copy-field') {
        url += `?source=${encodeURIComponent(record.source || '')}&dest=${encodeURIComponent(record.dest || '')}`;
      } else {
        const name = record.name || record.source;
        url += `?name=${encodeURIComponent(name || '')}`;
      }
      try {
        const resp = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `HTTP ${resp.status}`);
        }
        message.success('Schema updated');
        await loadSchema(schemaKey);
      } catch (err) {
        message.error(String(err?.message || err));
      }
    },
    [schemaEndpoints, getAuthHeaders, loadSchema]
  );

  const schemaSectionLabels = {
    fields: 'Fields',
    fieldTypes: 'Field Types',
    dynamicFields: 'Dynamic Fields',
    copyFields: 'Copy Fields',
  };

  const flowList = useMemo(
    () => normalizeQueryFlows(cfg.system_shop_query_flows),
    [cfg.system_shop_query_flows]
  );
  const queryTypeRows = useMemo(() => {
    const rows = Array.isArray(cfg.query_types) ? cfg.query_types : [];
    const activeTab = String(queryTypeEngineTab || 'all').toLowerCase();
    if (activeTab === 'all') return rows;
    return rows.filter((entry) => String(entry?.search_engine || 'any').toLowerCase() === activeTab);
  }, [cfg.query_types, queryTypeEngineTab]);
  const systemShopQueryTypeOptions = useMemo(() => cfg.query_types || [], [cfg.query_types]);
  const systemShopEventEndpointOptions = useMemo(() => {
    if (!Array.isArray(systemShopEventEndpoints)) return [];
    return systemShopEventEndpoints.filter((endpoint) => {
      const name = String(endpoint?.name || '').toLowerCase();
      const desc = String(endpoint?.description || '').toLowerCase();
      const key = String(endpoint?.endpoint_key || '').toLowerCase();
      return name.includes('system shop') || desc.includes('system shop') || key.includes('system_shop');
    });
  }, [systemShopEventEndpoints]);

  const loadSystemShopEventEndpoints = useCallback(async () => {
    setSystemShopEventsError('');
    try {
      const resp = await listWebhookEndpoints(getAuthHeaders());
      const rows = Array.isArray(resp?.data?.items)
        ? resp.data.items
        : (Array.isArray(resp?.data) ? resp.data : []);
      setSystemShopEventEndpoints(rows);
      setSystemShopSelectedEndpointId((prev) => {
        if (prev && rows.some((endpoint) => String(endpoint?.id) === String(prev))) return prev;
        const preferred = rows.find((endpoint) => {
          const name = String(endpoint?.name || '').toLowerCase();
          const desc = String(endpoint?.description || '').toLowerCase();
          const key = String(endpoint?.endpoint_key || '').toLowerCase();
          return name.includes('system shop') || desc.includes('system shop') || key.includes('system_shop');
        });
        if (preferred?.id != null) return String(preferred.id);
        return rows[0]?.id != null ? String(rows[0].id) : '';
      });
    } catch (err) {
      setSystemShopEventsError(String(err?.response?.data?.detail || err?.message || err));
      setSystemShopEventEndpoints([]);
      setSystemShopSelectedEndpointId('');
    }
  }, [getAuthHeaders]);

  const loadSystemShopEvents = useCallback(async () => {
    if (!systemShopSelectedEndpointId) {
      setSystemShopEvents([]);
      return;
    }
    setSystemShopEventsLoading(true);
    setSystemShopEventsError('');
    try {
      const params = {
        limit: 100,
      };
      const searchText = String(systemShopEventSearch || '').trim();
      if (searchText) params.search = searchText;
      if (systemShopEventStartAt) params.start_at = systemShopEventStartAt;
      if (systemShopEventEndAt) params.end_at = systemShopEventEndAt;
      const resp = await listWebhookEvents(systemShopSelectedEndpointId, getAuthHeaders(), params);
      const rows = Array.isArray(resp?.data?.items)
        ? resp.data.items
        : (Array.isArray(resp?.data) ? resp.data : []);
      setSystemShopEvents(rows);
    } catch (err) {
      setSystemShopEvents([]);
      setSystemShopEventsError(String(err?.response?.data?.detail || err?.message || err));
    } finally {
      setSystemShopEventsLoading(false);
    }
  }, [
    getAuthHeaders,
    systemShopSelectedEndpointId,
    systemShopEventSearch,
    systemShopEventStartAt,
    systemShopEventEndAt,
  ]);

  const loadSystemShopWebhookSubs = useCallback(async () => {
    if (!systemShopSelectedEndpointId) {
      setSystemShopWebhookSubs([]);
      return;
    }
    try {
      const resp = await listWebhookSubscriptions(systemShopSelectedEndpointId, getAuthHeaders());
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      setSystemShopWebhookSubs(rows);
    } catch (err) {
      setSystemShopWebhookSubs([]);
      setSystemShopEventsError(String(err?.response?.data?.detail || err?.message || err));
    }
  }, [getAuthHeaders, systemShopSelectedEndpointId]);

  const loadSystemShopWebhookDeliveryStats = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (systemShopSelectedEndpointId) params.endpoint_id = Number(systemShopSelectedEndpointId);
      const resp = await listWebhookDeliveries(getAuthHeaders(), params);
      const rows = Array.isArray(resp?.data) ? resp.data : [];
      setSystemShopWebhookDeliveries(rows);
      const next = { pending: 0, delivered: 0, failed: 0 };
      rows.forEach((row) => {
        const status = String(row?.status || '').toLowerCase();
        if (status === 'pending' || status === 'retry' || status === 'processing') next.pending += 1;
        else if (status === 'delivered') next.delivered += 1;
        else if (status === 'failed') next.failed += 1;
      });
      setSystemShopWebhookDeliveryStats(next);
    } catch (err) {
      setSystemShopWebhookDeliveryStats({ pending: 0, delivered: 0, failed: 0 });
      setSystemShopWebhookDeliveries([]);
    }
  }, [getAuthHeaders, systemShopSelectedEndpointId]);

  const toggleSystemShopWebhookEvent = useCallback(async (eventType, enabled) => {
    if (!systemShopSelectedEndpointId) return;
    setSystemShopWebhookSubSaving(eventType);
    try {
      await saveWebhookSubscription(
        systemShopSelectedEndpointId,
        { event_type: eventType, enabled: !!enabled, filters_json: {} },
        getAuthHeaders()
      );
      await loadSystemShopWebhookSubs();
      await loadSystemShopWebhookDeliveryStats();
    } catch (err) {
      setSystemShopEventsError(String(err?.response?.data?.detail || err?.message || err));
    } finally {
      setSystemShopWebhookSubSaving('');
    }
  }, [getAuthHeaders, loadSystemShopWebhookDeliveryStats, loadSystemShopWebhookSubs, systemShopSelectedEndpointId]);

  const createEmptyRouteDraft = useCallback(
    (order = 0) => ({
      id: null,
      route_name: '',
      route_order: order,
      enabled: true,
      query_flow_id: '',
      query_flow_name: '',
      when_clause_text: JSON.stringify({ all: [] }, null, 2),
      input_mapping_text: JSON.stringify({ query: { from: 'input.queryText', default: '' } }, null, 2),
      stop_on_match: true,
    }),
    []
  );

  const loadRoutes = useCallback(async () => {
    setRoutesLoading(true);
    setRoutesError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-shop/routes`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      setRoutes(Array.isArray(data?.routes) ? data.routes : []);
    } catch (err) {
      setRoutesError(String(err?.message || err));
    } finally {
      setRoutesLoading(false);
    }
  }, [getAuthHeaders]);

  const openRouteEditor = useCallback(
    (route = null) => {
      setRouteFormError('');
      if (!route) {
        const nextOrder = routes.length ? Math.max(...routes.map((r) => Number(r.route_order || 0))) + 1 : 1;
        setRouteDraft(createEmptyRouteDraft(nextOrder));
      } else {
        setRouteDraft({
          id: route.id,
          route_name: route.route_name || '',
          route_order: Number(route.route_order || 0),
          enabled: !!route.enabled,
          query_flow_id:
            route.query_flow_id
            || (flowList.find((flow) => flow.name === route.query_flow_name)?.id || ''),
          query_flow_name: route.query_flow_name || '',
          when_clause_text: JSON.stringify(route.when_clause || {}, null, 2),
          input_mapping_text: JSON.stringify(route.input_mapping || {}, null, 2),
          stop_on_match: route.stop_on_match !== false,
        });
      }
      setRoutingView('editor');
      setRouteTest((prev) => ({ ...prev, error: '', result: null }));
    },
    [createEmptyRouteDraft, flowList, routes]
  );

  const updateRouteDraft = useCallback((field, value) => {
    setRouteDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, []);

  const saveRoute = useCallback(async () => {
    if (!routeDraft) return;
    setRouteFormError('');
    const routeName = String(routeDraft.route_name || '').trim();
    const flowId = String(routeDraft.query_flow_id || '').trim();
    const flowName = String(routeDraft.query_flow_name || '').trim();
    if (!routeName) {
      setRouteFormError('Route name is required.');
      return;
    }
    if (!flowId) {
      setRouteFormError('Query flow is required.');
      return;
    }
    let whenClause = {};
    let inputMapping = {};
    try {
      whenClause = routeDraft.when_clause_text ? JSON.parse(routeDraft.when_clause_text) : {};
    } catch (err) {
      setRouteFormError('Invalid JSON in conditions.');
      return;
    }
    try {
      inputMapping = routeDraft.input_mapping_text ? JSON.parse(routeDraft.input_mapping_text) : {};
    } catch (err) {
      setRouteFormError('Invalid JSON in input mapping.');
      return;
    }
    const payload = {
      route_name: routeName,
      route_order: Number(routeDraft.route_order || 0),
      enabled: !!routeDraft.enabled,
      query_flow_id: flowId,
      query_flow_name: flowName,
      when_clause: whenClause,
      input_mapping: inputMapping,
      stop_on_match: !!routeDraft.stop_on_match,
    };
    const target = routeDraft.id
      ? `${API_BASE_URL}/api/v1/ahri/system-shop/routes/${routeDraft.id}`
      : `${API_BASE_URL}/api/v1/ahri/system-shop/routes`;
    const method = routeDraft.id ? 'PUT' : 'POST';
    setRoutesLoading(true);
    try {
      const resp = await fetch(target, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      await loadRoutes();
      setRoutingView('list');
      setRouteDraft(null);
    } catch (err) {
      setRouteFormError(String(err?.message || err));
    } finally {
      setRoutesLoading(false);
    }
  }, [routeDraft, getAuthHeaders, loadRoutes]);

  const deleteRoute = useCallback(
    async (routeId) => {
      if (!routeId) return;
      setRoutesLoading(true);
      setRoutesError('');
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-shop/routes/${routeId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `HTTP ${resp.status}`);
        }
        setRouteDeleteId(null);
        await loadRoutes();
      } catch (err) {
        setRoutesError(String(err?.message || err));
      } finally {
        setRoutesLoading(false);
      }
    },
    [getAuthHeaders, loadRoutes]
  );

  const runRouteTest = useCallback(async () => {
    setRouteTest((prev) => ({ ...prev, loading: true, error: '', result: null }));
    let payload = {};
    try {
      payload = routeTest.input ? JSON.parse(routeTest.input) : {};
    } catch (err) {
      setRouteTest((prev) => ({ ...prev, loading: false, error: 'Invalid JSON payload.' }));
      return;
    }
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-shop/routes/execute`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      setRouteTest((prev) => ({ ...prev, loading: false, result: data }));
    } catch (err) {
      setRouteTest((prev) => ({ ...prev, loading: false, error: String(err?.message || err) }));
    }
  }, [getAuthHeaders, routeTest.input]);

  const buildGraphFromSteps = useCallback((steps) => {
    const nodes = steps.map((step, idx) => ({
      id: step.id,
      type: step.type,
      data: {
        queryType: step.queryType,
        queryTypeId: step.queryTypeId,
        page_size: step.pageSize,
        operator: step.operator,
        threshold: step.threshold,
        conditionType: step.conditionType,
        field: step.field,
        fieldOperator: step.fieldOperator,
        fieldValue: step.fieldValue,
        flowId: step.flowId,
        isStart: !!step.isStart,
      },
      position: { x: 0, y: idx * 80 },
    }));
    const edges = [];
    steps.forEach((step) => {
      if ((step.type === 'query' || step.type === 'flow') && step.next) {
        edges.push({ id: `e_${step.id}_${step.next}`, source: step.id, target: step.next });
      }
      if (step.type === 'condition') {
        if (step.nextTrue) {
          edges.push({
            id: `e_${step.id}_${step.nextTrue}_t`,
            source: step.id,
            target: step.nextTrue,
            data: { outcome: 'true' },
          });
        }
        if (step.nextFalse) {
          edges.push({
            id: `e_${step.id}_${step.nextFalse}_f`,
            source: step.id,
            target: step.nextFalse,
            data: { outcome: 'false' },
          });
        }
      }
    });
    return { nodes, edges };
  }, []);

  const buildStepsFromGraph = useCallback((nodes, edges) => {
    const edgeMap = {};
    edges.forEach((edge) => {
      edgeMap[edge.source] = edgeMap[edge.source] || [];
      edgeMap[edge.source].push(edge);
    });
    return (nodes || []).map((node) => {
      const outs = edgeMap[node.id] || [];
      const step = {
        id: node.id,
        type: node.type || 'query',
        queryType: node.data?.queryType || '',
        queryTypeId: node.data?.queryTypeId || node.data?.query_type_id || '',
        pageSize: node.data?.page_size || 20,
        operator: node.data?.operator || '>=',
        threshold: node.data?.threshold ?? 1,
        conditionType: node.data?.conditionType || 'count',
        field: node.data?.field || '',
        fieldOperator: node.data?.fieldOperator || 'exists',
        fieldValue: node.data?.fieldValue || '',
        flowId: node.data?.flowId || '',
        isStart: !!node.data?.isStart,
        next: '',
        nextTrue: '',
        nextFalse: '',
      };
      if (step.type === 'query' || step.type === 'flow') {
        step.next = outs[0]?.target || '';
      } else {
        step.nextTrue = outs.find((e) => e.data?.outcome === 'true')?.target || '';
        step.nextFalse = outs.find((e) => e.data?.outcome === 'false')?.target || '';
      }
      return step;
    });
  }, []);

  const selectFlowById = useCallback((flowId) => {
    const flow = flowList.find((f) => f.id === flowId);
    if (!flow) return;
    setFlowListId(flow.id);
    setFlowName(flow.name || 'Untitled Flow');
    setFlowEngine(String(flow.search_engine || 'any').toLowerCase() || 'any');
    const steps = buildStepsFromGraph(flow.nodes || [], flow.edges || []);
    setFlowSteps(steps);
    setFlowSelectedStepId('');
    setFlowTestResult(null);
  }, [flowList, buildStepsFromGraph]);

  const createFlow = () => {
    const id = generateUuid();
    const newFlow = {
      id,
      name: 'New Flow',
      search_engine: 'any',
      nodes: [],
      edges: [],
    };
    const next = [...flowList, newFlow];
    setCfg((prev) => ({ ...prev, system_shop_query_flows: next }));
    setFlowListId(id);
    setFlowName('New Flow');
    setFlowEngine('any');
    setFlowSteps([]);
    setFlowView('editor');
  };

  const saveFlow = async () => {
    if (!flowListId) return;
    const graph = buildGraphFromSteps(flowSteps);
    const next = flowList.map((flow) =>
      flow.id === flowListId
        ? { ...flow, name: flowName || 'Untitled Flow', search_engine: flowEngine || 'any', nodes: graph.nodes, edges: graph.edges }
        : flow
    );
    const nextCfg = { ...cfg, system_shop_query_flows: next };
    setCfg(nextCfg);
    const ok = await persistConfig(nextCfg, { silent: true });
    if (ok) {
      message.success('Flow saved');
    } else {
      message.error('Failed to save flow');
    }
  };

  const addQueryNode = () => {
    const id = `node_${Date.now()}`;
    const step = {
      id,
      type: 'query',
      queryType: '',
      queryTypeId: '',
      pageSize: 20,
      isStart: flowSteps.length === 0,
      next: '',
      nextTrue: '',
      nextFalse: '',
    };
    setFlowSteps((prev) => [...prev, step]);
  };

  const addConditionNode = () => {
    const id = `node_${Date.now()}`;
    const step = {
      id,
      type: 'condition',
      operator: '>=',
      threshold: 1,
      conditionType: 'count',
      field: '',
      fieldOperator: 'exists',
      fieldValue: '',
      isStart: false,
      next: '',
      nextTrue: '',
      nextFalse: '',
    };
    setFlowSteps((prev) => [...prev, step]);
  };

  const addFlowNode = () => {
    const id = `node_${Date.now()}`;
    const step = {
      id,
      type: 'flow',
      flowId: '',
      isStart: flowSteps.length === 0,
      next: '',
      nextTrue: '',
      nextFalse: '',
    };
    setFlowSteps((prev) => [...prev, step]);
    setFlowSelectedStepId(id);
  };

  useEffect(() => () => {
    if (flowTestAbortRef.current) {
      try {
        flowTestAbortRef.current.abort();
      } catch {
        // ignore
      }
    }
  }, []);

  const runFlowTest = async () => {
    if (!flowListId || !flowTestQuery.trim()) return;
    flowTestRunIdRef.current += 1;
    const runId = flowTestRunIdRef.current;
    if (flowTestAbortRef.current) {
      try {
        flowTestAbortRef.current.abort();
      } catch {
        // ignore
      }
    }
    const controller = new AbortController();
    flowTestAbortRef.current = controller;
    setFlowTestResult(null);
    setFlowTestLoading(true);
    try {
      const customerIdErp = String(flowTestCustomerId || '').trim();
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-shop/flows/execute`, {
        method: 'POST',
        headers,
        signal: controller.signal,
        body: JSON.stringify({
          flow_id: flowListId,
          query: flowTestQuery,
          ...(customerIdErp ? { customer_id_erp: customerIdErp } : {}),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (runId !== flowTestRunIdRef.current) return;
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      setFlowTestResult(data);
    } catch (err) {
      if (runId !== flowTestRunIdRef.current) return;
      if (err?.name === 'AbortError') return;
      message.error(String(err?.message || err));
      setFlowTestResult(null);
    } finally {
      if (runId !== flowTestRunIdRef.current) return;
      setFlowTestLoading(false);
      flowTestAbortRef.current = null;
    }
  };

  const getSchemaSectionData = (schemaKey, section) => {
    const schema = schemaData[schemaKey] || {};
    if (section === 'fields') return schema.fields || [];
    if (section === 'fieldTypes') return schema.field_types || [];
    if (section === 'dynamicFields') return schema.dynamic_fields || [];
    if (section === 'copyFields') return schema.copy_fields || [];
    return [];
  };

  const renderSchemaTable = (schemaKey, sectionKey) => {
    const data = getSchemaSectionData(schemaKey, sectionKey);
    const searchTerm = (schemaSearch || '').trim().toLowerCase();
    const filteredData = searchTerm
      ? data.filter((row) => {
          if (!row) return false;
          try {
            return JSON.stringify(row).toLowerCase().includes(searchTerm);
          } catch {
            return false;
          }
        })
      : data;
    const columnsBySection = {
      fields: [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Type', dataIndex: 'type', key: 'type' },
        { title: 'Stored', dataIndex: 'stored', key: 'stored', render: (v) => (v ? 'Yes' : 'No') },
        { title: 'Indexed', dataIndex: 'indexed', key: 'indexed', render: (v) => (v ? 'Yes' : 'No') },
        { title: 'Multi', dataIndex: 'multiValued', key: 'multiValued', render: (v) => (v ? 'Yes' : 'No') },
        { title: 'DocValues', dataIndex: 'docValues', key: 'docValues', render: (v) => (v ? 'Yes' : 'No') },
      ],
      dynamicFields: [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Type', dataIndex: 'type', key: 'type' },
        { title: 'Stored', dataIndex: 'stored', key: 'stored', render: (v) => (v ? 'Yes' : 'No') },
        { title: 'Indexed', dataIndex: 'indexed', key: 'indexed', render: (v) => (v ? 'Yes' : 'No') },
        { title: 'Multi', dataIndex: 'multiValued', key: 'multiValued', render: (v) => (v ? 'Yes' : 'No') },
        { title: 'DocValues', dataIndex: 'docValues', key: 'docValues', render: (v) => (v ? 'Yes' : 'No') },
      ],
      fieldTypes: [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Class', dataIndex: 'class', key: 'class' },
        {
          title: 'Analyzer',
          key: 'analyzer',
          render: (_, record) => (record.analyzer ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>),
        },
      ],
      copyFields: [
        { title: 'Source', dataIndex: 'source', key: 'source' },
        { title: 'Dest', dataIndex: 'dest', key: 'dest' },
        { title: 'MaxChars', dataIndex: 'maxChars', key: 'maxChars' },
      ],
    };
    const typeMap = {
      fields: 'field',
      dynamicFields: 'dynamic-field',
      fieldTypes: 'field-type',
      copyFields: 'copy-field',
    };
    const columns = [
      ...columnsBySection[sectionKey],
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space>
            <Button size="small" onClick={() => openSchemaDrawer(schemaKey, typeMap[sectionKey], 'edit', record)}>
              Edit
            </Button>
            <Popconfirm
              title="Delete this schema item?"
              onConfirm={() => deleteSchemaItem(schemaKey, typeMap[sectionKey], record)}
            >
              <Button size="small" danger>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];
    return (
      <Table
        rowKey={(record) => record.name || `${record.source}-${record.dest}`}
        columns={columns}
        dataSource={filteredData}
        size="small"
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />
    );
  };
  const updateAttrImport = useCallback((slot, index, field, value) => {
    setCfg((prev) => {
      const base = ensureProductAttrImport(prev.product_attribute_import);
      const list = Array.isArray(base[slot]) ? [...base[slot]] : [{ source_attribute: '', target_attribute: '' }];
      const nextEntry = { ...(list[index] || { source_attribute: '', target_attribute: '' }), [field]: value };
      list[index] = nextEntry;
      return {
        ...prev,
        product_attribute_import: {
          ...base,
          [slot]: list,
        },
      };
    });
  }, []);
  const addAttrImportRow = useCallback((slot) => {
    setCfg((prev) => {
      const base = ensureProductAttrImport(prev.product_attribute_import);
      const list = Array.isArray(base[slot]) ? [...base[slot]] : [];
      list.push({ source_attribute: '', target_attribute: '' });
      return {
        ...prev,
        product_attribute_import: {
          ...base,
          [slot]: list,
        },
      };
    });
  }, []);
  const removeAttrImportRow = useCallback((slot, index) => {
    setCfg((prev) => {
      const base = ensureProductAttrImport(prev.product_attribute_import);
      const list = Array.isArray(base[slot]) ? [...base[slot]] : [];
      if (list.length <= 1) {
        list[0] = { source_attribute: '', target_attribute: '' };
      } else {
        list.splice(index, 1);
      }
      return {
        ...prev,
        product_attribute_import: {
          ...base,
          [slot]: list,
        },
      };
    });
  }, []);
  const addSystemShopAdvancedFilter = useCallback(() => {
    setCfg((prev) => {
      const filters = normalizeSystemShopAdvancedFilters(prev.system_shop_advanced_filters, {
        keepEmpty: true,
        fallbackToDefault: false,
      });
      filters.push({
        key: '',
        label: '',
        values: [],
        values_raw: '',
      });
      return { ...prev, system_shop_advanced_filters: filters };
    });
  }, []);
  const removeSystemShopAdvancedFilter = useCallback((index) => {
    setCfg((prev) => {
      const filters = normalizeSystemShopAdvancedFilters(prev.system_shop_advanced_filters, {
        keepEmpty: true,
        fallbackToDefault: false,
      });
      if (filters.length <= 1) {
        return {
          ...prev,
          system_shop_advanced_filters: [],
        };
      }
      filters.splice(index, 1);
      return { ...prev, system_shop_advanced_filters: filters };
    });
  }, []);
  const addSystemShopFilter = useCallback(() => {
    setCfg((prev) => {
      const filters = normalizeSystemShopFilters(prev.system_shop_filters, {
        keepEmpty: true,
        fallbackToDefault: false,
        includeRaw: true,
      });
      filters.push({
        key: '',
        label: '',
        fields: [],
        value_type: 'string',
        sort_order: 'asc',
        value_display_map: {},
      });
      return { ...prev, system_shop_filters: filters };
    });
  }, []);
  const removeSystemShopFilter = useCallback((index) => {
    setCfg((prev) => {
      const filters = normalizeSystemShopFilters(prev.system_shop_filters, {
        keepEmpty: true,
        fallbackToDefault: false,
        includeRaw: true,
      });
      if (filters.length <= 1) {
        return {
          ...prev,
          system_shop_filters: DEFAULT_SYSTEM_SHOP_FILTERS.map((entry) => ({
            ...entry,
            fields: [...entry.fields],
          })),
        };
      }
      filters.splice(index, 1);
      return { ...prev, system_shop_filters: filters };
    });
  }, []);
  const buildQueryTypeTemplate = useCallback((target = 'ahri') => {
    const base =
      target === 'product'
        ? (DEFAULT_PRODUCT_QUERY_TYPES[0] || {})
        : (DEFAULT_SYSTEM_SHOP_QUERY_TYPES[0] || {});
    return {
      id: generateUuid(),
      name: '',
      label: '',
      search_engine: 'any',
      def_type: 'edismax',
      fl: '',
      rows: '',
      wt: '',
      debug: false,
      query_fields: ['search_text^1'],
      phrase_fields: [],
      min_match: '',
      operator: 'AND',
      tie: 0,
      qs: 1,
      ps: 2,
      ps2: '',
      bq: [],
      bf: [],
      use_raw_q: true,
      target,
      ...base,
    };
  }, []);
  const openQueryTypePanel = useCallback((mode, index = null) => {
    setQueryTypePanelMode(mode);
    setQueryTypePanelIndex(index);
    if (mode === 'add') {
      const draft = buildQueryTypeTemplate('ahri');
      const engineFromTab = String(queryTypeEngineTab || 'all').toLowerCase();
      draft.search_engine = engineFromTab === 'all' ? 'any' : engineFromTab;
      setQueryTypeDraft(draft);
      return;
    }
    if (index === null) {
      setQueryTypeDraft(null);
      return;
    }
    const list = normalizeQueryTypesList(
      cfg.query_types,
      DEFAULT_SYSTEM_SHOP_QUERY_TYPES,
      DEFAULT_PRODUCT_QUERY_TYPES
    );
    setQueryTypeDraft({ ...list[index] });
  }, [buildQueryTypeTemplate, cfg.query_types, queryTypeEngineTab]);
  const cloneQueryType = useCallback((index) => {
    const list = normalizeQueryTypesList(
      cfg.query_types,
      DEFAULT_SYSTEM_SHOP_QUERY_TYPES,
      DEFAULT_PRODUCT_QUERY_TYPES
    );
    const source = list[index];
    if (!source) return;
    const baseName = String(source.name || 'query_type').trim() || 'query_type';
    const baseLabel = String(source.label || source.name || 'Query Type').trim() || 'Query Type';
    const cloned = {
      ...source,
      id: generateUuid(),
      name: `${baseName}_copy`,
      label: `${baseLabel} Copy`,
    };
    setQueryTypePanelMode('add');
    setQueryTypePanelIndex(null);
    setQueryTypeDraft(cloned);
  }, [cfg.query_types]);
  const saveQueryTypeDraft = useCallback(() => {
    if (!queryTypeDraft) return;
    setCfg((prev) => {
      const list = normalizeQueryTypesList(
        prev.query_types,
        DEFAULT_SYSTEM_SHOP_QUERY_TYPES,
        DEFAULT_PRODUCT_QUERY_TYPES
      );
      if (queryTypePanelMode === 'add') {
        list.push({ ...queryTypeDraft, id: String(queryTypeDraft.id || '').trim() || generateUuid() });
      } else if (queryTypePanelIndex !== null && queryTypePanelIndex >= 0) {
        list[queryTypePanelIndex] = {
          ...queryTypeDraft,
          id: String(queryTypeDraft.id || '').trim() || String(list[queryTypePanelIndex]?.id || '').trim() || generateUuid(),
        };
      }
      const next = { ...prev, query_types: list };
      setTimeout(() => {
        if (persistConfigRef.current) {
          persistConfigRef.current(next, { silent: true });
        }
      }, 0);
      return next;
    });
    setQueryTypePanelMode(null);
    setQueryTypePanelIndex(null);
    setQueryTypeDraft(null);
  }, [queryTypeDraft, queryTypePanelIndex, queryTypePanelMode]);
  const runQueryTypeTest = useCallback(async (pageOverride = null) => {
    const target = String(queryTypeTest.target || 'ahri');
    const name = String(queryTypeTest.name || '').trim();
    const input = String(queryTypeTest.input || '').trim();
    const customerIdErp = String(queryTypeTest.customer_id_erp || '').trim();
    const searchEngine = target === 'product' ? String(queryTypeTest.search_engine || 'solr') : 'solr';
    const page = pageOverride ?? queryTypeTest.page ?? 1;
    const pageSize = queryTypeTest.page_size ?? 10;
    const selectedTypes = (cfg.query_types || []).filter((entry) =>
      target === 'product' ? entry?.target === 'product' : entry?.target !== 'product'
    );
    const selectedConfig = selectedTypes.find((entry) => String(entry?.name || '').trim() === name) || null;
    if (!input) {
      setQueryTypeTest((prev) => ({ ...prev, error: 'Enter a search string to test.' }));
      return;
    }
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    setQueryTypeTest((prev) => ({
      ...prev,
      loading: true,
      error: '',
      successMessage: '',
      total: 0,
      results: [],
      responseRaw: '',
      search_request: null,
      solr_request: null,
    }));
    try {
      const endpoint =
        target === 'product'
          ? (searchEngine === 'opensearch'
            ? `${API_BASE_URL}/api/v1/products/opensearch/search`
            : `${API_BASE_URL}/api/v1/products/solr/search`)
          : `${API_BASE_URL}/api/v1/ahri/solr/search`;
      const payload = {
        query: input,
        page,
        page_size: pageSize,
        ...(target === 'product' ? { include_attribute_facets: false } : { include_product_attr_facets: false, context: 'system_shop' }),
        ...(customerIdErp ? { customer_id_erp: customerIdErp } : {}),
      };
      if (selectedConfig) {
        payload.search_config = selectedConfig;
      } else if (name) {
        payload.query_type = name;
      }
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = String(data?.detail || '');
        if (detail.includes('OpenSearch HTTP 404')) {
          throw new Error('Product search index is being prepared. Please try again in a moment.');
        }
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      const collectionMessage = data?.collection_created && data?.message ? String(data.message) : '';
      setQueryTypeTest((prev) => ({
        ...prev,
        loading: false,
        results: Array.isArray(data?.docs) ? data.docs : [],
        total: Number(data?.numFound || 0),
        page: Number(data?.page || page),
        page_size: Number(data?.page_size || pageSize),
        error: '',
        successMessage: collectionMessage,
        responseRaw: JSON.stringify(data, null, 2),
        search_request: data?.search_request || data?.solr_request || null,
        solr_request: data?.solr_request || null,
      }));
    } catch (err) {
      setQueryTypeTest((prev) => ({ ...prev, loading: false, error: String(err) }));
    }
  }, [cfg.query_types, getAuthHeaders, queryTypeTest.customer_id_erp, queryTypeTest.input, queryTypeTest.name, queryTypeTest.page, queryTypeTest.page_size, queryTypeTest.search_engine, queryTypeTest.target]);

  const removeQueryType = useCallback((index) => {
    setCfg((prev) => {
      const list = normalizeQueryTypesList(
        prev.query_types,
        DEFAULT_SYSTEM_SHOP_QUERY_TYPES,
        DEFAULT_PRODUCT_QUERY_TYPES
      );
      if (list.length <= 1) {
        return { ...prev, query_types: [] };
      }
      list.splice(index, 1);
      return { ...prev, query_types: list };
    });
  }, []);
  const combinedSystemShopFilters = useMemo(() => {
    const standardFilters = normalizeSystemShopFilters(cfg.system_shop_filters, {
      includeRaw: true,
      keepEmpty: true,
      fallbackToDefault: false,
    });
    const advancedFilters = normalizeSystemShopAdvancedFilters(cfg.system_shop_advanced_filters, {
      keepEmpty: true,
      fallbackToDefault: false,
    });
    const settings = normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings);
    const order = settings.order || [];
    const byKey = new Map();
    standardFilters.forEach((entry, index) => {
      const key = entry.key || '';
      const id = `standard:${key || `row_${index}`}`;
      byKey.set(id, { id, type: 'standard', key, label: entry.label || key, index, data: entry });
    });
    advancedFilters.forEach((entry, index) => {
      const key = entry.key || '';
      const id = `advanced:${key || `row_${index}`}`;
      byKey.set(id, { id, type: 'advanced', key, label: entry.label || key, index, data: entry });
    });
    const ordered = [];
    order.forEach((item) => {
      const orderedId = `${item.type}:${item.key}`;
      if (byKey.has(orderedId)) {
        ordered.push(byKey.get(orderedId));
        byKey.delete(orderedId);
      }
    });
    return [...ordered, ...Array.from(byKey.values())];
  }, [cfg.system_shop_filters, cfg.system_shop_advanced_filters, cfg.system_shop_filters_settings]);

  const handleCombinedFilterDragStart = (id) => setDraggingShopFilter(id);
  const handleShopFilterDragOver = (event) => event.preventDefault();
  const handleCombinedFilterDrop = (targetId) => {
    if (!draggingShopFilter || draggingShopFilter === targetId) return;
    const list = [...combinedSystemShopFilters];
    const fromIndex = list.findIndex((item) => item.id === draggingShopFilter);
    const toIndex = list.findIndex((item) => item.id === targetId);
    if (fromIndex === -1 || toIndex === -1) return;
    const next = [...list];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    const nextOrder = next
      .filter((item) => item.key)
      .map((item) => ({ type: item.type, key: item.key }));
    setCfg((prev) => ({
      ...prev,
      system_shop_filters_settings: {
        ...normalizeSystemShopFilterSettings(prev.system_shop_filters_settings),
        order: nextOrder,
      },
    }));
    setDraggingShopFilter(targetId);
  };

  const openFilterDrawer = (item) => {
    if (!item) return;
    setEditingFilter(item);
    setAdvancedFilterTest({ loading: false, results: [], error: '' });
    if (item.type === 'standard') {
      void loadAvailableFacetFields();
      void loadFilterValuePreview();
      setGroupBuilderSelected([]);
      setGroupBuilderLabel('');
      setFilterDraft({
        type: 'standard',
        key: item.data.key || '',
        label: item.data.label || '',
        fields: Array.isArray(item.data.fields) ? item.data.fields : [],
        value_type: item.data.value_type || 'string',
        sort_order: item.data.sort_order || 'asc',
        custom_value_order: Array.isArray(item.data.custom_value_order) ? item.data.custom_value_order : [],
        value_display_map_raw:
          typeof item.data.value_display_map_raw === 'string'
            ? item.data.value_display_map_raw
            : formatValueDisplayMapText(item.data.value_display_map),
        value_groups_raw:
          typeof item.data.value_groups_raw === 'string'
            ? item.data.value_groups_raw
            : formatValueGroupsText(item.data.value_groups),
        show_ungrouped_values: item.data.show_ungrouped_values !== false,
        multi_select: item.data.multi_select === true,
      });
    } else {
      setFilterDraft({
        type: 'advanced',
        key: item.data.key || '',
        label: item.data.label || '',
        values_raw: item.data.values_raw || formatAdvancedFilterValues(item.data.values),
      });
    }
    setFilterDrawerOpen(true);
  };

  const handleInsertDisplayMapValue = (value) => {
    setFilterDraft((prev) => {
      const text = prev?.value_display_map_raw || '';
      const alreadyPresent = text
        .split(/\n+/)
        .some((line) => line.split('=')[0].trim() === value);
      if (alreadyPresent) return prev;
      const trimmed = text.replace(/\s+$/, '');
      const nextText = trimmed ? `${trimmed}\n${value}=` : `${value}=`;
      return { ...prev, value_display_map_raw: nextText };
    });
    setTimeout(() => displayMapTextAreaRef.current?.focus?.(), 0);
  };

  const toggleGroupBuilderValue = (value) => {
    setGroupBuilderSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleAddGroup = () => {
    const label = groupBuilderLabel.trim();
    if (!label || !groupBuilderSelected.length) return;
    setFilterDraft((prev) => {
      const text = (prev?.value_groups_raw || '').replace(/\s+$/, '');
      const line = `${label}=${joinGroupValues(groupBuilderSelected)}`;
      const nextText = text ? `${text}\n${line}` : line;
      return { ...prev, value_groups_raw: nextText };
    });
    setGroupBuilderSelected([]);
    setGroupBuilderLabel('');
  };

  const handleRemoveGroup = (index) => {
    setFilterDraft((prev) => {
      const groups = parseValueGroupsText(prev?.value_groups_raw);
      const nextGroups = groups.filter((_, i) => i !== index);
      return { ...prev, value_groups_raw: formatValueGroupsText(nextGroups) };
    });
  };

  const moveCustomValue = (index, direction) => {
    setFilterDraft((prev) => {
      const list = [...(prev?.custom_value_order || [])];
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= list.length) return prev;
      const [moved] = list.splice(index, 1);
      list.splice(nextIndex, 0, moved);
      return { ...prev, custom_value_order: list };
    });
  };

  const handleCustomValueDrop = (targetValue) => {
    if (!draggingCustomValue || draggingCustomValue === targetValue) return;
    setFilterDraft((prev) => {
      const list = [...(prev?.custom_value_order || [])];
      const fromIndex = list.indexOf(draggingCustomValue);
      const toIndex = list.indexOf(targetValue);
      if (fromIndex === -1 || toIndex === -1) return prev;
      const next = [...list];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { ...prev, custom_value_order: next };
    });
    setDraggingCustomValue(targetValue);
  };

  const saveFilterDraft = (persist = false) => {
    if (!editingFilter || !filterDraft) {
      setFilterDrawerOpen(false);
      return;
    }
    setCfg((prev) => {
      let next = { ...prev };
      if (filterDraft.type === 'standard') {
        const fields = (Array.isArray(filterDraft.fields) ? filterDraft.fields : [])
          .map((field) => String(field || '').trim())
          .filter(Boolean);
        const parsedMap = parseValueDisplayMapText(filterDraft.value_display_map_raw);
        const parsedGroups = parseValueGroupsText(filterDraft.value_groups_raw);
        const filters = normalizeSystemShopFilters(prev.system_shop_filters, {
          keepEmpty: true,
          fallbackToDefault: false,
          includeRaw: true,
        });
        const updated = filters.map((entry, idx) =>
          idx === editingFilter.index
            ? {
                ...entry,
                key: filterDraft.key,
                label: filterDraft.label,
                fields,
                value_type: filterDraft.value_type,
                sort_order: filterDraft.sort_order,
                custom_value_order: filterDraft.sort_order === 'custom'
                  ? (Array.isArray(filterDraft.custom_value_order) ? filterDraft.custom_value_order.filter(Boolean) : [])
                  : [],
                value_display_map_raw: filterDraft.value_display_map_raw || '',
                value_display_map: parsedMap || {},
                value_groups_raw: filterDraft.value_groups_raw || '',
                value_groups: parsedGroups,
                show_ungrouped_values: filterDraft.show_ungrouped_values !== false,
                multi_select: filterDraft.multi_select === true,
              }
            : entry
        );
        next = { ...prev, system_shop_filters: updated };
      } else {
        const filters = normalizeSystemShopAdvancedFilters(prev.system_shop_advanced_filters, {
          keepEmpty: true,
          fallbackToDefault: false,
        });
        const updated = filters.map((entry, idx) =>
          idx === editingFilter.index
            ? {
                ...entry,
                key: filterDraft.key,
                label: filterDraft.label,
                values_raw: filterDraft.values_raw || '',
                values: parseAdvancedFilterValues(filterDraft.values_raw),
              }
            : entry
        );
        next = { ...prev, system_shop_advanced_filters: updated };
      }
      if (persist) {
        setTimeout(() => {
          persistConfig(next, { silent: true });
        }, 0);
      }
      return next;
    });
    setFilterDrawerOpen(false);
  };

  const handleSaveFilterDraftClick = () => {
    if (filterDraft?.type === 'standard' && hasAdvancedValidationIssues) {
      Modal.confirm({
        title: 'Some configured values may not match your data',
        content: (
          <div>
            <p>
              These values were checked against a live sample of this field's indexed values and may not
              work as expected:
            </p>
            <ul className="mb-2">
              {unmatchedDisplayMapValues.length > 0 && (
                <li>
                  Display Map value(s) not found in sample data: {unmatchedDisplayMapValues.join(', ')}
                </li>
              )}
              {groupValueIssues.unmatched.length > 0 && (
                <li>
                  Group value(s) not found in sample data: {groupValueIssues.unmatched.join(', ')}
                </li>
              )}
              {groupValueIssues.overlapping.length > 0 && (
                <li>
                  Value(s) assigned to more than one group: {groupValueIssues.overlapping.join(', ')}
                </li>
              )}
            </ul>
            <p className="mb-0">Save anyway?</p>
          </div>
        ),
        okText: 'Save Anyway',
        cancelText: 'Go Back and Fix',
        onOk: () => saveFilterDraft(true),
      });
      return;
    }
    saveFilterDraft(true);
  };

  const runAdvancedFilterTest = async () => {
    if (!filterDraft || filterDraft.type !== 'advanced') return;
    const values = parseAdvancedFilterValues(filterDraft.values_raw || '');
    if (!values.length) {
      setAdvancedFilterTest({
        loading: false,
        results: [],
        error: 'Add at least one value with a query to test.',
      });
      return;
    }
    setAdvancedFilterTest({ loading: true, results: [], error: '' });
    try {
      const matchTypes = normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings).match_types;
      const requests = values.map(async (entry) => {
        if (!entry.query) {
          return { ...entry, count: null, error: 'Missing Solr query' };
        }
        const payload = {
          query: '*:*',
          page: 1,
          page_size: 1,
          filters: { _raw_fq: [entry.query] },
          include_product_attr_facets: false,
          context: 'system_shop',
          match_types: matchTypes,
        };
        const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/search`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          return { ...entry, count: null, error: data?.detail || resp.statusText || 'Search failed' };
        }
        return { ...entry, count: Number(data?.numFound || 0) };
      });
      const results = await Promise.all(requests);
      setAdvancedFilterTest({ loading: false, results, error: '' });
    } catch (err) {
      setAdvancedFilterTest({
        loading: false,
        results: [],
        error: err?.message || 'Failed to run test',
      });
    }
  };

  const handleAddStandardFilter = () => {
    const current = normalizeSystemShopFilters(cfg.system_shop_filters, { includeRaw: true, keepEmpty: true, fallbackToDefault: false });
    const nextIndex = current.length;
    addSystemShopFilter();
    const draft = {
      type: 'standard',
      key: '',
      label: '',
      fields: [],
      value_type: 'string',
      sort_order: 'asc',
      custom_value_order: [],
      value_display_map_raw: '',
      value_groups_raw: '',
      show_ungrouped_values: true,
      multi_select: false,
    };
    setEditingFilter({ type: 'standard', key: '', label: '', index: nextIndex, data: draft });
    setFilterDraft(draft);
    setGroupBuilderSelected([]);
    setGroupBuilderLabel('');
    void loadAvailableFacetFields();
    void loadFilterValuePreview();
    setFilterDrawerOpen(true);
  };

  const handleAddAdvancedFilter = () => {
    const current = normalizeSystemShopAdvancedFilters(cfg.system_shop_advanced_filters, { keepEmpty: true, fallbackToDefault: false });
    const nextIndex = current.length;
    addSystemShopAdvancedFilter();
    const draft = {
      type: 'advanced',
      key: '',
      label: '',
      values_raw: '',
    };
    setEditingFilter({ type: 'advanced', key: '', label: '', index: nextIndex, data: draft });
    setFilterDraft(draft);
    setAdvancedFilterTest({ loading: false, results: [], error: '' });
    setFilterDrawerOpen(true);
  };
  const [logOpen, setLogOpen] = useState(false);
  const [logText, setLogText] = useState('');
  const [, setSchemasText] = useState('[]');
  const [, setSchemasError] = useState('');
  const [schemaProg, setSchemaProg] = useState('');
  const [schemaLines, setSchemaLines] = useState('');
  const [schemaProductFields, setSchemaProductFields] = useState([]);
  const [schemaSelectedFields, setSchemaSelectedFields] = useState([]);
  const [schemaCollection, setSchemaCollection] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerWidthPct, setDrawerWidthPct] = useState(50); // default 50%
  const [resizing, setResizing] = useState(false);
  const [qbTree, setQbTree] = useState(createEmptyWhereTree());
  const [whereJsonText, setWhereJsonText] = useState(JSON.stringify(createEmptyWhereTree(), null, 2));
  const [whereJsonDirty, setWhereJsonDirty] = useState(false);
  const [whereJsonError, setWhereJsonError] = useState('');
  const [settingsSection, setSettingsSection] = useState('openai'); // vertical settings tabs
  const [settingsHistoryOpen, setSettingsHistoryOpen] = useState(false);
  const [systemDefaultClients, setSystemDefaultClients] = useState([]);
  const [enableCopyDefaults, setEnableCopyDefaults] = useState(false);
  const [systemDefaultSelections, setSystemDefaultSelections] = useState({});
  const [systemDefaultCopying, setSystemDefaultCopying] = useState({});
  const renderSystemDefaultTag = useCallback((keys) => {
    const list = Array.isArray(keys) ? keys : [keys];
    const sources = list
      .map((key) => ahriSourceMap[key])
      .filter((source) => source === 'system' || source === 'client');
    const hasSystem = sources.includes('system');
    if (!hasSystem) return null;
    const hasClient = sources.includes('client');
    return <Tag color={hasClient ? 'gold' : 'default'}>{hasClient ? 'Includes system default' : 'System default'}</Tag>;
  }, [ahriSourceMap]);
  useEffect(() => {
    if (activeTab !== 'settings' || settingsSection !== 'system_shop' || systemShopTab !== 'routing') {
      return;
    }
    if (!routeLoadedRef.current) {
      routeLoadedRef.current = true;
      loadRoutes();
    }
  }, [activeTab, settingsSection, systemShopTab, loadRoutes]);

  const filterListPreviewLoadedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'settings' || settingsSection !== 'system_shop' || systemShopTab !== 'filters') {
      return;
    }
    if (!filterListPreviewLoadedRef.current) {
      filterListPreviewLoadedRef.current = true;
      void loadFilterValuePreview();
    }
  }, [activeTab, settingsSection, systemShopTab, loadFilterValuePreview]);

  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [verifyData, setVerifyData] = useState(null);
  const [solrDeleting, setSolrDeleting] = useState(false);
  const [productSolrDeleting, setProductSolrDeleting] = useState(false);
  const [programPurgeName, setProgramPurgeName] = useState('');
  const [programPurging, setProgramPurging] = useState(false);
  const [brandPurgeName, setBrandPurgeName] = useState('');
  const [brandDeleteModalOpen, setBrandDeleteModalOpen] = useState(false);
  const [brandDeleteStep, setBrandDeleteStep] = useState(1);
  const [brandImpact, setBrandImpact] = useState(null);
  const [brandImpactLoading, setBrandImpactLoading] = useState(false);
  const [brandImpactError, setBrandImpactError] = useState('');
  const [brandConfirmText, setBrandConfirmText] = useState('');
  const [brandPurging, setBrandPurging] = useState(false);
  const lookupTransform = useMemo(
    () => ({ ...DEFAULT_LOOKUP_TRANSFORM, ...(cfg.lookup_transform || {}) }),
    [cfg.lookup_transform]
  );
  const updateLookupTransform = useCallback((partial) => {
    setCfg((prev) => ({
      ...prev,
      lookup_transform: {
        ...DEFAULT_LOOKUP_TRANSFORM,
        ...(prev.lookup_transform || {}),
        ...partial,
      },
    }));
  }, []);
  const [sampleProgram, setSampleProgram] = useState('');
  const [sampleFields, setSampleFields] = useState([]);
  const [sampleResults, setSampleResults] = useState([]);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState('');
  const deleteSolrCollection = async () => {
    const ok = await confirmDialog({
      title: 'Delete AHRI collection',
      content: 'Delete the AHRI data collection for this account? This cannot be undone.',
    });
    if (!ok) return;
    setSolrDeleting(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/collection/delete`, {
        method: 'POST',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        window.alert(`Delete failed: ${data?.detail || resp.status}`);
      } else {
        window.alert('AHRI Solr collection deleted. Run a full import to recreate it.');
      }
    } catch (error) {
      window.alert(`Delete failed: ${error}`);
    } finally {
      setSolrDeleting(false);
    }
  };
  const deleteProductCollection = async () => {
    const ok = await confirmDialog({
      title: 'Delete Products collection',
      content: 'Delete the Products data collection for this account? This cannot be undone.',
    });
    if (!ok) return;
    setProductSolrDeleting(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/solr/collection/delete`, {
        method: 'POST',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        window.alert(`Delete failed: ${data?.detail || resp.status}`);
      } else {
        window.alert('Products Solr collection deleted. Run a product full import to recreate it.');
      }
    } catch (error) {
      window.alert(`Delete failed: ${error}`);
    } finally {
      setProductSolrDeleting(false);
    }
  };
  const handleProgramPurge = async () => {
    const program = (programPurgeName || '').trim();
    if (!program) {
      window.alert('Enter a program name to delete.');
      return;
    }
    const ok = await confirmDialog({
      title: 'Delete program data',
      content: `Delete AHRI data for program "${program}" from Postgres and Solr? This cannot be undone.`,
    });
    if (!ok) return;
    setProgramPurging(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/program/${encodeURIComponent(program)}`, {
        method: 'DELETE',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        window.alert(`Program delete failed: ${data?.detail || resp.status}`);
      } else {
        const dbCount = typeof data?.db_deleted === 'number' ? data.db_deleted : '0';
        const solrStatus = data?.solr_request_sent ? 'Solr delete submitted.' : 'Solr delete failed or not configured.';
        window.alert(`Deleted ${dbCount} DB rows for ${program}. ${solrStatus}`);
      }
    } catch (err) {
      window.alert(`Program delete failed: ${err}`);
    } finally {
      setProgramPurging(false);
    }
  };
  const openBrandDeleteModal = async () => {
    const brand = (brandPurgeName || '').trim();
    if (!brand) {
      window.alert('Enter or select a brand to delete.');
      return;
    }
    setBrandDeleteStep(1);
    setBrandConfirmText('');
    setBrandImpact(null);
    setBrandImpactError('');
    setBrandDeleteModalOpen(true);
    setBrandImpactLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/brand/${encodeURIComponent(brand)}/impact`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      setBrandImpact(data);
    } catch (err) {
      setBrandImpactError(String(err?.message || err));
    } finally {
      setBrandImpactLoading(false);
    }
  };
  const closeBrandDeleteModal = () => {
    if (brandPurging) return;
    setBrandDeleteModalOpen(false);
    setBrandDeleteStep(1);
    setBrandConfirmText('');
    setBrandImpact(null);
    setBrandImpactError('');
  };
  const handleBrandPurge = async () => {
    const brand = (brandPurgeName || '').trim();
    if (!brand || brandConfirmText.trim().toLowerCase() !== brand.toLowerCase()) {
      return;
    }
    setBrandPurging(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/brand/${encodeURIComponent(brand)}`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ confirm_brand_name: brand }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        window.alert(`Brand delete failed: ${data?.detail || resp.status}`);
      } else {
        const dbCount = typeof data?.db_deleted === 'number' ? data.db_deleted : 0;
        const productCount = typeof data?.products_updated === 'number' ? data.products_updated : 0;
        const solrStatus = data?.solr_request_sent ? 'Solr delete submitted.' : 'Solr delete failed or not configured.';
        window.alert(`Deleted ${dbCount} AHRI rows for "${brand}". Updated ${productCount} product records. ${solrStatus}`);
        setBrandDeleteModalOpen(false);
        setBrandPurgeName('');
        setExportBrandOptions((prev) => prev.filter((b) => b.toLowerCase() !== brand.toLowerCase()));
      }
    } catch (err) {
      window.alert(`Brand delete failed: ${err}`);
    } finally {
      setBrandPurging(false);
      setBrandDeleteStep(1);
      setBrandConfirmText('');
    }
  };
  const [verifyProgram, setVerifyProgram] = useState('');
  const [confirmSchemaOpen, setConfirmSchemaOpen] = useState(false);
  const [pendingSchema, setPendingSchema] = useState(null);
  const activeJob = useMemo(() => {
    if (!Array.isArray(jobs) || jobs.length === 0) return null;
    return jobs.find((job) => ['running', 'pending', 'queued', 'cancelling'].includes(job.status));
  }, [jobs]);
  const activeJobProgress = useMemo(() => {
    if (!activeJob) return { percent: 0, label: 'No import running' };
    const total = Number(activeJob.total || 0);
    const progress = Number(activeJob.progress || 0);
    if (!total || total <= 0 || progress <= 0) {
      return { percent: 0, label: 'Preparing...' };
    }
    const pct = Math.min(100, Math.round((progress / total) * 100));
    return { percent: pct, label: `${progress} / ${total}` };
  }, [activeJob]);

  const mappingStatus = mappingState?.status || '';
  const mappingStats = useMemo(() => {
    const total = Number(mappingState?.total || 0);
    const processed = Number(mappingState?.processed || 0);
    let percent = 0;
    if (total > 0) {
      percent = Math.min(100, Math.round((processed / total) * 100));
    } else if ((mappingStatus === 'completed' || mappingStatus === 'failed') && processed > 0) {
      percent = 100;
    }
    return { total, processed, percent };
  }, [mappingState, mappingStatus]);
  const mappingMatches = Number(mappingState?.matches || 0);
  const mappingCompleteMatches = Number(mappingState?.complete_matches || 0);
  const mappingUpdatedProducts = Number(mappingState?.updated_products || 0);
  const mappingScopeState = mappingState?.scope || 'full';
  const mappingRequestedIds = Array.isArray(mappingState?.requested_ids) ? mappingState.requested_ids : [];
  const mappingRequestedIdsMore = Number(mappingState?.requested_ids_more || 0);
  const mappingRequestedIdsTotal =
    Number(mappingState?.requested_ids_total || 0) || (mappingRequestedIds.length || 0);
  const mappingIdsParsedCount = useMemo(() => {
    if (!mappingIdsInput) return 0;
    return mappingIdsInput
      .split(/[\s,]+/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0).length;
  }, [mappingIdsInput]);
  const mappingV2Status = mappingV2State?.status || '';
  const mappingV2Stats = useMemo(() => {
    const total = Number(mappingV2State?.total || 0);
    const processed = Number(mappingV2State?.processed || 0);
    let percent = 0;
    if (total > 0) {
      percent = Math.min(100, Math.round((processed / total) * 100));
    } else if ((mappingV2Status === 'completed' || mappingV2Status === 'failed') && processed > 0) {
      percent = 100;
    }
    return { total, processed, percent };
  }, [mappingV2State, mappingV2Status]);
  const mappingV2Matches = Number(mappingV2State?.matches || 0);
  const mappingV2CompleteMatches = Number(mappingV2State?.complete_matches || 0);
  const mappingV2ScopeState = mappingV2State?.scope || 'full';
  const mappingV2RequestedIds = Array.isArray(mappingV2State?.requested_ids) ? mappingV2State.requested_ids : [];
  const mappingV2RequestedIdsMore = Number(mappingV2State?.requested_ids_more || 0);
  const mappingV2RequestedIdsTotal =
    Number(mappingV2State?.requested_ids_total || 0) || (mappingV2RequestedIds.length || 0);
  const mappingV2IdsParsedCount = useMemo(() => {
    if (!mappingV2IdsInput) return 0;
    return mappingV2IdsInput
      .split(/[\s,]+/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0).length;
  }, [mappingV2IdsInput]);
  const mappingV3Status = mappingV3State?.status || '';
  const mappingV3Phase = mappingV3State?.phase || '';
  const mappingV3PhaseResults = mappingV3State?.phase_results || {};
  const mappingV3TouchedCount = Number(mappingV3State?.touched_count || 0);
  // "x of y" for whichever phase is currently running -- field phases
  // (outdoor/indoor/furnace) and reconcile all write {processed, total} into
  // their own phase_results entry while in flight (backend/services/
  // ahri_mapping_v3/{phases,reconcile}.py progress_cb), so this just reads
  // whichever one is current rather than assuming reconcile's shape.
  const mappingV3CurrentPhaseProgress = useMemo(() => {
    const current = mappingV3PhaseResults?.[mappingV3Phase];
    const total = Number(current?.total || 0);
    const processed = Number(current?.processed || 0);
    if (!total) return null;
    return { processed, total, percent: Math.min(100, Math.round((processed / total) * 100)) };
  }, [mappingV3PhaseResults, mappingV3Phase]);
  // Coarse "phase N of 4" indicator so the spinner isn't the only signal
  // between phases (e.g. once a field phase's bulk-exact step finishes but
  // before its Solr-fallback loop has processed enough misses to report
  // progress yet).
  const mappingV3PhaseStep = useMemo(() => {
    const index = MAPPING_V3_PHASE_ORDER.indexOf(mappingV3Phase);
    if (index < 0) return null;
    return { index: index + 1, total: MAPPING_V3_PHASE_ORDER.length };
  }, [mappingV3Phase]);

  useEffect(() => {
    const onMove = (e) => {
      if (!resizing) return;
      const vw = Math.max(window.document.documentElement.clientWidth || 0, window.innerWidth || 0);
      const fromRightPx = Math.max(0, vw - (e.clientX || 0));
      let pct = (fromRightPx / vw) * 100;
      if (pct < 50) pct = 50;
      if (pct > 75) pct = 75;
      setDrawerWidthPct(pct);
    };
    const onUp = () => setResizing(false);
    if (resizing) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  const schemaFieldOptions = useMemo(() => {
    const names = new Set();
    (schemaLines || '')
      .split('\n')
      .forEach((line) => {
        const match = String(line || '').match(/^\s*([A-Za-z_][A-Za-z0-9_]*)\s*:/);
        if (match && match[1]) {
          names.add(match[1]);
        }
      });
    return Array.from(names);
  }, [schemaLines]);

  useEffect(() => {
    setSchemaProductFields((prev) => prev.filter((name) => schemaFieldOptions.includes(name)));
    setSchemaSelectedFields((prev) => {
      const filtered = prev.filter((name) => schemaFieldOptions.includes(name));
      if (filtered.length === 0 && schemaFieldOptions.length > 0) {
        return [...schemaFieldOptions];
      }
      return filtered;
    });
  }, [schemaFieldOptions]);

  useEffect(() => {
    if (!drawerOpen || whereJsonDirty) return;
    const normalized = normalizeWhereTree(qbTree);
    const nextText = JSON.stringify(normalized, null, 2);
    if (nextText !== whereJsonText) {
      setWhereJsonText(nextText);
    }
  }, [drawerOpen, qbTree, whereJsonDirty, whereJsonText]);

  useEffect(() => {
    const schemaSet = new Set();
    if (Array.isArray(cfg.program_schemas)) {
      cfg.program_schemas.forEach((schema) => {
        const prog = schema?.program;
        if (typeof prog === 'string' && prog.trim()) {
          schemaSet.add(prog.trim());
        }
      });
    }
    const legacySet = new Set();
    if (Array.isArray(cfg.program_names)) {
      cfg.program_names.forEach((prog) => {
        if (typeof prog === 'string' && prog.trim()) {
          legacySet.add(prog.trim());
        }
      });
    }
    const schemaOptions = Array.from(schemaSet);
    const legacyOptions = Array.from(legacySet);
    const nextOptions = schemaOptions.length > 0 ? schemaOptions : legacyOptions;
    const finalOptions = nextOptions.length > 0 ? nextOptions : ['AC'];
    setProgramOptions(finalOptions);
    setSelectedPrograms((prev) => {
      const filtered = prev.filter((prog) => finalOptions.includes(prog));
      if (filtered.length > 0) {
        return filtered;
      }
      return finalOptions;
    });
  }, [cfg.program_schemas, cfg.program_names]);

  const programLookupFieldMap = useMemo(() => {
    const map = {};
    (cfg.program_schemas || []).forEach((schema) => {
      const prog = (schema?.program || '').trim();
      if (!prog) return;
      const fields = Array.isArray(schema?.product_lookup_fields)
        ? schema.product_lookup_fields.filter((f) => typeof f === 'string' && f.trim())
        : [];
      map[prog] = fields;
    });
    return map;
  }, [cfg.program_schemas]);

  useEffect(() => {
    if (!sampleProgram && programOptions.length > 0) {
      setSampleProgram(programOptions[0]);
    }
  }, [programOptions, sampleProgram]);

  useEffect(() => {
    const available = programLookupFieldMap[sampleProgram] || [];
    setSampleFields((prev) => {
      if (!prev || prev.length === 0) {
        return available;
      }
      const filtered = prev.filter((field) => available.includes(field));
      if (filtered.length === prev.length) {
        return prev;
      }
      return filtered.length ? filtered : available;
    });
  }, [sampleProgram, programLookupFieldMap]);

  const loadConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, { headers: getAuthHeaders() });
      if (r.ok) {
        const j = await r.json();
        const cfgLoaded = { ...j?.config };
        const loadedSourceMap = j?.source_map && typeof j.source_map === 'object' ? j.source_map : {};
        setAhriSourceMap(loadedSourceMap);
        // If the openai section was inherited from system defaults and still carries the
        // demo placeholder key, clear it so the user is prompted to enter their own key
        // rather than silently saving the placeholder on the next save.
        if (loadedSourceMap.openai === 'system' && cfgLoaded.openai?.api_key &&
            (cfgLoaded.openai.api_key === 'demo-key-123' || cfgLoaded.openai.api_key.startsWith('demo-'))) {
          cfgLoaded.openai = { ...cfgLoaded.openai, api_key: '' };
        }
        cfgLoaded.lookup_transform = {
          ...DEFAULT_LOOKUP_TRANSFORM,
          ...(cfgLoaded?.lookup_transform || {})
        };
        cfgLoaded.product_attribute_import = ensureProductAttrImport(cfgLoaded?.product_attribute_import);
        cfgLoaded.system_shop_filters = normalizeSystemShopFilters(cfgLoaded?.system_shop_filters);
        cfgLoaded.system_shop_search = normalizeSystemShopSearch(cfgLoaded?.system_shop_search);
        cfgLoaded.system_shop_query_flows = normalizeQueryFlows(cfgLoaded?.system_shop_query_flows);
        const combinedQueryTypes = normalizeQueryTypesList(
          cfgLoaded?.query_types,
          DEFAULT_SYSTEM_SHOP_QUERY_TYPES,
          DEFAULT_PRODUCT_QUERY_TYPES,
          { fallback: false }
        );
        const split = splitQueryTypes(combinedQueryTypes);
        cfgLoaded.query_types = combinedQueryTypes;
        if (combinedQueryTypes.length) {
          cfgLoaded.system_shop_query_types = normalizeQueryTypes(
            split.system,
            DEFAULT_SYSTEM_SHOP_QUERY_TYPES
          );
          cfgLoaded.product_query_types = normalizeQueryTypes(split.product, DEFAULT_PRODUCT_QUERY_TYPES);
        } else {
          cfgLoaded.system_shop_query_types = normalizeQueryTypes(
            cfgLoaded?.system_shop_query_types,
            DEFAULT_SYSTEM_SHOP_QUERY_TYPES
          );
          cfgLoaded.product_query_types = normalizeQueryTypes(
            cfgLoaded?.product_query_types,
            DEFAULT_PRODUCT_QUERY_TYPES
          );
        }
        cfgLoaded.system_shop_filters_settings = normalizeSystemShopFilterSettings(cfgLoaded?.system_shop_filters_settings);
        cfgLoaded.system_shop_advanced_filters = normalizeSystemShopAdvancedFilters(cfgLoaded?.system_shop_advanced_filters);
        cfgLoaded.system_shop_filters_settings = {
          ...cfgLoaded.system_shop_filters_settings,
          order: buildSystemShopFilterOrder(
            cfgLoaded.system_shop_filters,
            cfgLoaded.system_shop_advanced_filters,
            cfgLoaded.system_shop_filters_settings?.order
          ),
        };
        if (typeof cfgLoaded.mapping_threads !== 'number') {
          cfgLoaded.mapping_threads = 2;
        }
        if (typeof cfgLoaded.mapping_v2_max_trim !== 'number') {
          cfgLoaded.mapping_v2_max_trim = 4;
        }
        if (typeof cfgLoaded.mapping_v2_min_length !== 'number') {
          cfgLoaded.mapping_v2_min_length = 4;
        }
        setCfg(prev => ({ ...prev, ...cfgLoaded }));
        try {
          setSchemasText(JSON.stringify(cfgLoaded?.program_schemas || [], null, 2));
          setSchemasError('');
        } catch {
          setSchemasText('[]');
          setSchemasError('');
        }
      }
    } catch {}
  }, [getAuthHeaders]);

  const loadAhriSystemDefaultClients = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-defaults`, { headers: getAuthHeaders() });
      if (!resp.ok) return;
      const data = await resp.json().catch(() => ({}));
      const copyEnabled = data?.enable_copy_defaults === true;
      setEnableCopyDefaults(copyEnabled);
      const clients = Array.isArray(data?.clients) ? data.clients : [];
      setSystemDefaultClients(copyEnabled ? clients : []);
      setSystemDefaultSelections((prev) => {
        const next = { ...prev };
        if (!copyEnabled) {
          AHRI_SYSTEM_DEFAULT_COPY_MODULES.forEach((moduleItem) => {
            next[moduleItem.key] = '';
          });
          return next;
        }
        AHRI_SYSTEM_DEFAULT_COPY_MODULES.forEach((moduleItem) => {
          if (next[moduleItem.key]) return;
          if (clients.length) {
            next[moduleItem.key] = String(clients[0].id);
          }
        });
        return next;
      });
    } catch {
      // ignore and keep local UI usable
    }
  }, [getAuthHeaders]);

  const copyAhriSystemDefaultFromClient = useCallback(
    async (moduleKey) => {
      const sourceAccountId = Number(systemDefaultSelections[moduleKey] || 0);
      if (!sourceAccountId) {
        alert('Select a client before copying.');
        return;
      }
      setSystemDefaultCopying((prev) => ({ ...prev, [moduleKey]: true }));
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/system-defaults/copy-from-client`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            module: moduleKey,
            source_account_id: sourceAccountId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          alert(data?.detail || `Copy failed (HTTP ${resp.status})`);
          return;
        }
        alert(`Copied ${moduleKey} defaults from client #${sourceAccountId}.`);
        await loadConfig();
      } catch (error) {
        alert(`Copy failed: ${error}`);
      } finally {
        setSystemDefaultCopying((prev) => ({ ...prev, [moduleKey]: false }));
      }
    },
    [getAuthHeaders, loadConfig, systemDefaultSelections]
  );

  useEffect(() => {
    if (activeTab !== 'settings') return;
    loadAhriSystemDefaultClients();
  }, [activeTab, loadAhriSystemDefaultClients]);

  useEffect(() => {
    if (settingsSection === 'system_defaults' && !enableCopyDefaults) {
      setSettingsSection('openai');
    }
  }, [enableCopyDefaults, settingsSection]);

  const loadJobs = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/jobs`, { headers: getAuthHeaders() });
      if (r.ok) {
        const j = await r.json();
        setJobs(j.jobs || []);
      }
    } catch {}
  }, [getAuthHeaders]);

  const loadSolrImportState = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/import/state`, { headers: getAuthHeaders() });
      if (r.ok) {
        const j = await r.json();
        setSolrImportState(j.state || {});
      }
    } catch {}
  }, [getAuthHeaders]);

  const loadMappingState = useCallback(async () => {
    try {
      setMappingLoading(true);
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/state`, { headers: getAuthHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setMappingState(data?.state || {});
        setMappingError('');
      } else {
        const data = await resp.json().catch(() => ({}));
        setMappingError(data?.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      setMappingError(String(err));
    } finally {
      setMappingLoading(false);
    }
  }, [getAuthHeaders]);

  const loadMappingJobs = useCallback(async () => {
    try {
      setMappingJobsLoading(true);
      setMappingJobsError('');
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/jobs`, { headers: getAuthHeaders() });
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setMappingJobs(Array.isArray(data?.jobs) ? data.jobs : []);
      } else {
        const data = await resp.json().catch(() => ({}));
        setMappingJobsError(data?.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      setMappingJobsError(String(err));
    } finally {
      setMappingJobsLoading(false);
    }
  }, [getAuthHeaders]);

  const loadMappingV2State = useCallback(async () => {
    try {
      setMappingV2Loading(true);
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping-v2/state`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setMappingV2State(data?.state || {});
        setMappingV2Error('');
      } else {
        setMappingV2Error(data?.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      setMappingV2Error(String(err));
    } finally {
      setMappingV2Loading(false);
    }
  }, [getAuthHeaders]);

  const loadMappingV2Jobs = useCallback(async () => {
    try {
      setMappingV2JobsLoading(true);
      setMappingV2JobsError('');
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping-v2/jobs`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setMappingV2Jobs(Array.isArray(data?.jobs) ? data.jobs : []);
      } else {
        setMappingV2JobsError(data?.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      setMappingV2JobsError(String(err));
    } finally {
      setMappingV2JobsLoading(false);
    }
  }, [getAuthHeaders]);

  const startProductMapping = useCallback(async () => {
    if (mappingStarting) return;
    const payload = {};
    // Pass the max threads selected in the Mapping section so backend
    // uses the same value for this job without requiring a separate Save.
    if (typeof cfg.mapping_threads === 'number') {
      payload.mapping_threads = cfg.mapping_threads;
    }
    if (mappingScopeSelection === 'subset') {
      const parsedIds = (mappingIdsInput || '')
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
      if (parsedIds.length === 0) {
        setMappingError('Enter at least one AHRI ID to run a targeted mapping job.');
        return;
      }
      payload.ahri_ids = parsedIds;
    }
    setMappingStarting(true);
    setMappingError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setMappingError(data?.detail || 'Unable to start mapping');
      } else {
        await loadMappingState();
        await loadMappingJobs();
      }
    } catch (err) {
      setMappingError(String(err));
    } finally {
      setMappingStarting(false);
    }
  }, [cfg.mapping_threads, headers, loadMappingJobs, loadMappingState, mappingStarting, mappingScopeSelection, mappingIdsInput]);

  const stopProductMapping = useCallback(async () => {
    if (mappingStopping) return;
    setMappingStopping(true);
    setMappingError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/stop`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setMappingError(data?.detail || 'Unable to stop mapping job');
      } else {
        await loadMappingState();
        await loadMappingJobs();
      }
    } catch (err) {
      setMappingError(String(err));
    } finally {
      setMappingStopping(false);
    }
  }, [headers, loadMappingJobs, loadMappingState, mappingStopping]);

  const buildMappingV2JobSettings = useCallback(() => {
    const clamp = (val, min, max, fallback) => {
      const n = parseInt(String(val ?? ''), 10);
      if (Number.isNaN(n)) return fallback;
      return Math.min(max, Math.max(min, n));
    };
    return {
      mapping_threads: clamp(cfg.mapping_threads, 1, 10, 2),
      mapping_v2_max_trim: clamp(cfg.mapping_v2_max_trim, 0, 8, 4),
      mapping_v2_min_length: clamp(cfg.mapping_v2_min_length, 1, 8, 4),
    };
  }, [cfg.mapping_threads, cfg.mapping_v2_max_trim, cfg.mapping_v2_min_length]);

  const persistMappingV2JobSettings = useCallback(async () => {
    const settings = buildMappingV2JobSettings();
    const nextCfg = { ...cfg, ...settings };
    setCfg(nextCfg);
    const persist = persistConfigRef.current;
    if (persist) {
      await persist(nextCfg, { silent: true });
    }
    return settings;
  }, [buildMappingV2JobSettings, cfg]);

  const startProductMappingV2 = useCallback(async () => {
    if (mappingV2Starting) return;
    const settings = await persistMappingV2JobSettings();
    const payload = {
      max_trim: settings.mapping_v2_max_trim,
      min_length: settings.mapping_v2_min_length,
      mapping_threads: settings.mapping_threads,
    };
    if (mappingV2ScopeSelection === 'subset') {
      const parsedIds = (mappingV2IdsInput || '')
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
      if (parsedIds.length === 0) {
        setMappingV2Error('Enter at least one AHRI ID to run a targeted mapping job.');
        return;
      }
      payload.ahri_ids = parsedIds;
    }
    setMappingV2Starting(true);
    setMappingV2Error('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping-v2/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setMappingV2Error(data?.detail || 'Unable to start mapping v2');
      } else {
        await loadMappingV2State();
        await loadMappingV2Jobs();
      }
    } catch (err) {
      setMappingV2Error(String(err));
    } finally {
      setMappingV2Starting(false);
    }
  }, [
    headers,
    loadMappingV2Jobs,
    loadMappingV2State,
    mappingV2IdsInput,
    mappingV2ScopeSelection,
    mappingV2Starting,
    persistMappingV2JobSettings,
  ]);

  const startProductMappingV2Baseline = useCallback(async () => {
    const v2Busy = mappingV2Status === 'running' || mappingV2Status === 'queued';
    if (mappingV2BaselineStarting || v2Busy) return;
    setMappingV2BaselineStarting(true);
    setMappingV2Error('');
    try {
      const sampleResp = await fetch(
        `${API_BASE_URL}/api/v1/ahri/product-mapping-v2/baseline-sample?limit=500`,
        { headers: getAuthHeaders() },
      );
      const sampleData = await sampleResp.json().catch(() => ({}));
      if (!sampleResp.ok) {
        setMappingV2Error(sampleData?.detail || `Baseline sample failed (HTTP ${sampleResp.status})`);
        return;
      }
      const settings = await persistMappingV2JobSettings();
      const payload = {
        ...(sampleData?.suggested_start_payload || {}),
        baseline: true,
        max_trim: settings.mapping_v2_max_trim,
        min_length: settings.mapping_v2_min_length,
        mapping_threads: settings.mapping_threads,
      };
      const startResp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping-v2/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const startData = await startResp.json().catch(() => ({}));
      if (!startResp.ok) {
        setMappingV2Error(startData?.detail || 'Unable to start baseline mapping v2');
      } else {
        await loadMappingV2State();
        await loadMappingV2Jobs();
      }
    } catch (err) {
      setMappingV2Error(String(err));
    } finally {
      setMappingV2BaselineStarting(false);
    }
  }, [
    headers,
    loadMappingV2Jobs,
    loadMappingV2State,
    mappingV2Status,
    mappingV2BaselineStarting,
    persistMappingV2JobSettings,
  ]);

  const stopProductMappingV2 = useCallback(async () => {
    if (mappingV2Stopping) return;
    setMappingV2Stopping(true);
    setMappingV2Error('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping-v2/stop`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setMappingV2Error(data?.detail || 'Unable to stop mapping v2 job');
      } else {
        await loadMappingV2State();
        await loadMappingV2Jobs();
      }
    } catch (err) {
      setMappingV2Error(String(err));
    } finally {
      setMappingV2Stopping(false);
    }
  }, [headers, loadMappingV2Jobs, loadMappingV2State, mappingV2Stopping]);

  // === Product Mapping V3 (beta, bulk-first, ships alongside v1/v2) ===
  const loadMappingV3State = useCallback(async () => {
    try {
      setMappingV3Loading(true);
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/mapping-v3/state`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setMappingV3State(data?.state || {});
        setMappingV3Error('');
      } else {
        setMappingV3Error(data?.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      setMappingV3Error(String(err));
    } finally {
      setMappingV3Loading(false);
    }
  }, [getAuthHeaders]);

  const startProductMappingV3 = useCallback(async () => {
    if (mappingV3Starting) return;
    setMappingV3Starting(true);
    setMappingV3Error('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/mapping-v3/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setMappingV3Error(data?.detail || 'Unable to start mapping v3');
      } else {
        await loadMappingV3State();
      }
    } catch (err) {
      setMappingV3Error(String(err));
    } finally {
      setMappingV3Starting(false);
    }
  }, [headers, loadMappingV3State, mappingV3Starting]);

  const stopProductMappingV3 = useCallback(async () => {
    if (mappingV3Stopping) return;
    setMappingV3Stopping(true);
    setMappingV3Error('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/mapping-v3/stop`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setMappingV3Error(data?.detail || 'Unable to stop mapping v3 job');
      } else {
        await loadMappingV3State();
      }
    } catch (err) {
      setMappingV3Error(String(err));
    } finally {
      setMappingV3Stopping(false);
    }
  }, [headers, loadMappingV3State, mappingV3Stopping]);

  // === Product Attributes Sync ===
  const loadAttrSyncState = useCallback(async () => {
    try {
      setAttrSyncLoading(true);
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-attributes-sync/state`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setAttrSyncState(data?.state || {});
      }
    } catch (err) {
      console.error('loadAttrSyncState error:', err);
    } finally {
      setAttrSyncLoading(false);
    }
  }, [getAuthHeaders]);

  const startAttrSync = useCallback(async () => {
    if (attrSyncStarting) return;
    setAttrSyncStarting(true);
    setAttrSyncError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-attributes-sync/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sync_mode: attrSyncMode,
          clear_existing: attrSyncClearExisting,
          mapped_only: attrSyncMappedOnly,
          batch_size: attrSyncBatchSize,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setAttrSyncError(data?.detail || `Unable to start attributes sync (HTTP ${resp.status})`);
      } else {
        await loadAttrSyncState();
      }
    } catch (err) {
      setAttrSyncError(String(err));
    } finally {
      setAttrSyncStarting(false);
    }
  }, [attrSyncStarting, headers, loadAttrSyncState, attrSyncMode, attrSyncClearExisting, attrSyncMappedOnly, attrSyncBatchSize]);

  const stopAttrSync = useCallback(async () => {
    if (attrSyncStopping) return;
    setAttrSyncStopping(true);
    setAttrSyncError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-attributes-sync/stop`, {
        method: 'POST',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setAttrSyncError(data?.detail || 'Unable to stop attributes sync job');
      } else {
        await loadAttrSyncState();
      }
    } catch (err) {
      setAttrSyncError(String(err));
    } finally {
      setAttrSyncStopping(false);
    }
  }, [attrSyncStopping, headers, loadAttrSyncState]);

  const attrSyncStatus = attrSyncState?.status || '';
  const attrSyncActive = ['running', 'queued', 'cancelling'].includes(attrSyncStatus);

  // === AHRI Variant Resync (auto-triggered by product imports/edits) ===
  const loadVariantResyncState = useCallback(async () => {
    try {
      setVariantResyncLoading(true);
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/variant-resync/state`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setVariantResyncState(data?.state || {});
      } else {
        setVariantResyncError(data?.detail || `Unable to load variant resync status (HTTP ${resp.status})`);
      }
    } catch (err) {
      console.error('loadVariantResyncState error:', err);
    } finally {
      setVariantResyncLoading(false);
    }
  }, [getAuthHeaders]);

  const variantResyncStatus = variantResyncState?.status || '';
  const variantResyncActive = ['running', 'queued'].includes(variantResyncStatus);

  // === AHRI System Variant Backfill (Story 4.1) ===
  const loadVariantBackfillProgress = useCallback(async () => {
    try {
      setVariantBackfillLoading(true);
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/variants/backfill/status`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        const nextProgress = data?.progress || {};
        setVariantBackfillProgress(nextProgress);
        if (['completed', 'failed'].includes(nextProgress.status)) {
          setVariantBackfillPolling(false);
        }
      }
    } catch (err) {
      console.error('loadVariantBackfillProgress error:', err);
    } finally {
      setVariantBackfillLoading(false);
    }
  }, [getAuthHeaders]);

  const startVariantBackfill = useCallback(async () => {
    if (variantBackfillStarting) return;
    setVariantBackfillStarting(true);
    setVariantBackfillError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/variants/backfill`, {
        method: 'POST',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setVariantBackfillError(data?.detail || `Unable to start variant backfill (HTTP ${resp.status})`);
      } else {
        // The job runs in a background thread, so this response can race it --
        // progress may still read "pending" here even though the job is live.
        // Poll regardless of the reported status; loadVariantBackfillProgress
        // stops polling once it observes a terminal status.
        setVariantBackfillProgress(data?.progress || {});
        setVariantBackfillPolling(true);
      }
    } catch (err) {
      setVariantBackfillError(String(err));
    } finally {
      setVariantBackfillStarting(false);
    }
  }, [variantBackfillStarting, headers]);

  const variantBackfillStatus = variantBackfillProgress?.status || '';
  const variantBackfillActive = variantBackfillStatus === 'running' || variantBackfillPolling;

  const loadMatchCheckState = useCallback(async () => {
    try {
      setMatchCheckLoading(true);
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/match-check/state`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setMatchCheckState(data?.state || {});
      }
    } catch (err) {
      console.error('loadMatchCheckState error:', err);
    } finally {
      setMatchCheckLoading(false);
    }
  }, [getAuthHeaders]);

  const startMatchCheck = useCallback(async () => {
    if (matchCheckStarting) return;
    setMatchCheckStarting(true);
    setMatchCheckError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/match-check/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ batch_size: matchCheckBatchSize, commit_each_batch: matchCheckCommitEachBatch }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setMatchCheckError(data?.detail || `Unable to start match check (HTTP ${resp.status})`);
      } else {
        await loadMatchCheckState();
      }
    } catch (err) {
      setMatchCheckError(String(err));
    } finally {
      setMatchCheckStarting(false);
    }
  }, [matchCheckStarting, headers, loadMatchCheckState, matchCheckBatchSize, matchCheckCommitEachBatch]);

  const stopMatchCheck = useCallback(async () => {
    if (matchCheckStopping) return;
    setMatchCheckStopping(true);
    setMatchCheckError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/match-check/stop`, {
        method: 'POST',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setMatchCheckError(data?.detail || 'Unable to stop match check job');
      } else {
        await loadMatchCheckState();
      }
    } catch (err) {
      setMatchCheckError(String(err));
    } finally {
      setMatchCheckStopping(false);
    }
  }, [matchCheckStopping, headers, loadMatchCheckState]);

  const matchCheckStatus = matchCheckState?.status || '';
  const matchCheckActive = ['running', 'queued', 'cancelling'].includes(matchCheckStatus);
  useEffect(() => {
    if (!matchCheckActive) {
      return undefined;
    }
    const timer = setInterval(() => {
      loadMatchCheckState();
    }, 3000);
    return () => clearInterval(timer);
  }, [loadMatchCheckState, matchCheckActive]);

  const runSamplePreview = useCallback(async () => {
    if (!sampleProgram) {
      setSampleError('Select a program to preview.');
      return;
    }
    setSampleLoading(true);
    setSampleError('');
    setSampleResults([]);
    try {
      const body = {
        program: sampleProgram,
        fields: sampleFields && sampleFields.length ? sampleFields : undefined,
        limit: 5,
        transform: lookupTransform,
      };
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/sample`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setSampleError(data?.detail || `Unable to run preview (HTTP ${resp.status})`);
        setSampleResults([]);
      } else {
        setSampleResults(Array.isArray(data?.samples) ? data.samples : []);
      }
    } catch (err) {
      setSampleError(String(err));
    } finally {
      setSampleLoading(false);
    }
  }, [sampleProgram, sampleFields, lookupTransform, headers]);

  const runMappingV2 = useCallback(async () => {
    const ahriId = (mapV2Id || '').trim();
    if (!ahriId) {
      setMapV2Error('Enter an AHRI ID to test.');
      return;
    }
    const settings = buildMappingV2JobSettings();
    const maxTrim = settings.mapping_v2_max_trim;
    const minLen = settings.mapping_v2_min_length;
    setMapV2Loading(true);
    setMapV2Error('');
    setMapV2Result(null);
    try {
      const payload = {
        ahri_id: ahriId,
        max_trim: Number.isFinite(maxTrim) ? maxTrim : undefined,
        min_length: Number.isFinite(minLen) ? minLen : undefined,
      };
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-map-lookup-v2`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      setMapV2Result(data);
    } catch (err) {
      setMapV2Error(String(err?.message || err));
    } finally {
      setMapV2Loading(false);
    }
  }, [buildMappingV2JobSettings, mapV2Id, headers]);


  const viewMappingJobLog = useCallback(async (jobId) => {
    if (!jobId) return;
    // Prefer in-memory log for the currently running job so entries (like query preview) appear immediately
    if (mappingState?.job_id && mappingState.job_id === jobId && Array.isArray(mappingState?.log)) {
      const lines = mappingState.log || [];
      setLogText(lines.length ? lines.join('\n') : 'No log entries yet.');
      setLogOpen(true);
      return;
    }
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/job/${jobId}`, { headers: getAuthHeaders() });
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const log = data?.job?.log;
        setLogText(log && log.length ? log : 'No log entries found for this job.');
        setLogOpen(true);
      } else {
        const data = await resp.json().catch(() => ({}));
        alert(data?.detail || `Unable to load log (HTTP ${resp.status})`);
      }
    } catch (err) {
      alert(`Unable to load log: ${err}`);
    }
  }, [getAuthHeaders, mappingState]);

  const deleteMappingJob = useCallback(async (jobId) => {
    if (!jobId) return;
    const ok = await confirmDialog({
      title: 'Delete mapping job',
      content: 'Delete this mapping job record?',
    });
    if (!ok) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-mapping/job/${jobId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        alert(data?.detail || `Delete failed (HTTP ${resp.status})`);
      } else {
        await loadMappingJobs();
      }
    } catch (err) {
      alert(`Delete failed: ${err}`);
    }
  }, [getAuthHeaders, loadMappingJobs]);

  useEffect(() => {
    (async () => {
      await loadConfig();
      await loadJobs();
    })();
  }, [loadConfig, loadJobs]);

  useEffect(() => {
    if (!polling) return;
    const id = setInterval(loadJobs, 5000);
    return () => clearInterval(id);
  }, [polling, loadJobs]);

  useEffect(() => {
    if (activeTab !== 'sync') return;
    loadSolrImportState();
  }, [activeTab, loadSolrImportState]);

  useEffect(() => {
    if (!polling || activeTab !== 'sync') return;
    const id = setInterval(loadSolrImportState, 5000);
    return () => clearInterval(id);
  }, [polling, activeTab, loadSolrImportState]);

  useEffect(() => {
    if (activeTab === 'mapping') {
      loadMappingState();
      loadMappingJobs();
      loadMappingV2State();
      loadMappingV2Jobs();
      loadMappingV3State();
      loadAttrSyncState();
      loadVariantResyncState();
    }
  }, [activeTab, loadMappingJobs, loadMappingState, loadMappingV2Jobs, loadMappingV2State, loadMappingV3State, loadAttrSyncState, loadVariantResyncState]);

  useEffect(() => {
    if (activeTab !== 'system_shop_events') return;
    loadSystemShopEventEndpoints();
  }, [activeTab, loadSystemShopEventEndpoints]);

  useEffect(() => {
    if (activeTab !== 'system_shop_events' || !systemShopSelectedEndpointId) return;
    loadSystemShopEvents();
    loadSystemShopWebhookSubs();
    loadSystemShopWebhookDeliveryStats();
  }, [
    activeTab,
    systemShopSelectedEndpointId,
    loadSystemShopEvents,
    loadSystemShopWebhookSubs,
    loadSystemShopWebhookDeliveryStats,
  ]);

  useEffect(() => {
    if (activeTab !== 'system_shop_events' || !systemShopSelectedEndpointId) return undefined;
    const id = setInterval(() => {
      loadSystemShopEvents();
      loadSystemShopWebhookDeliveryStats();
    }, 15000);
    return () => clearInterval(id);
  }, [activeTab, systemShopSelectedEndpointId, loadSystemShopEvents, loadSystemShopWebhookDeliveryStats]);

  const mappingActive = mappingStatus === 'running' || mappingStatus === 'queued';
  const mappingV2Active = mappingV2Status === 'running' || mappingV2Status === 'queued' || mappingV2Status === 'cancelling';
  const mappingV3Active = mappingV3Status === 'running' || mappingV3Status === 'queued' || mappingV3Status === 'cancelling';

  useEffect(() => {
    if (activeTab !== 'mapping' || !mappingActive) return;
    const id = setInterval(() => {
      loadMappingState();
    }, 5000);
    return () => clearInterval(id);
  }, [activeTab, mappingActive, loadMappingState]);

  useEffect(() => {
    if (activeTab !== 'mapping' || !mappingV2Active) return;
    const id = setInterval(() => {
      loadMappingV2State();
    }, 5000);
    return () => clearInterval(id);
  }, [activeTab, mappingV2Active, loadMappingV2State]);

  useEffect(() => {
    if (activeTab !== 'mapping' || !mappingV3Active) return;
    const id = setInterval(() => {
      loadMappingV3State();
    }, 5000);
    return () => clearInterval(id);
  }, [activeTab, mappingV3Active, loadMappingV3State]);

  useEffect(() => {
    if (activeTab !== 'mapping' || !attrSyncActive) return;
    const id = setInterval(() => {
      loadAttrSyncState();
    }, 3000);
    return () => clearInterval(id);
  }, [activeTab, attrSyncActive, loadAttrSyncState]);

  useEffect(() => {
    // Unlike the other jobs on this tab, this one isn't started by a click
    // here -- a bulk product import or a single product edit anywhere else
    // in the app can kick it off at any time. Poll continuously while this
    // tab is open (slowly while idle, quickly once a job is actually
    // running) so an admin sees it start without having triggered it.
    if (activeTab !== 'mapping') return;
    const id = setInterval(() => {
      loadVariantResyncState();
    }, variantResyncActive ? 3000 : 12000);
    return () => clearInterval(id);
  }, [activeTab, variantResyncActive, loadVariantResyncState]);

  useEffect(() => {
    if (activeTab !== 'mapping' || mappingSubTab !== 'variant-backfill') return;
    loadVariantBackfillProgress();
  }, [activeTab, mappingSubTab, loadVariantBackfillProgress]);

  useEffect(() => {
    if (activeTab !== 'mapping' || !(variantBackfillActive || variantBackfillPolling)) return;
    const id = setInterval(() => {
      loadVariantBackfillProgress();
    }, 3000);
    return () => clearInterval(id);
  }, [activeTab, variantBackfillActive, variantBackfillPolling, loadVariantBackfillProgress]);

  useEffect(() => {
    const handleResize = () => {
      const next = Math.round(window.innerWidth * 0.5);
      setSchemaDrawerWidth((prev) => Math.min(prev, Math.round(window.innerWidth * 0.75)) || next);
      setQueryTypeDrawerWidth((prev) => {
        const maxWidth = Math.round(window.innerWidth * 0.75);
        const minWidth = 360;
        const fallback = Math.round(window.innerWidth * 0.3);
        return Math.max(minWidth, Math.min(maxWidth, prev || fallback));
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!schemaDrawerResizing) return undefined;
    const onMove = (event) => {
      const delta = schemaResizeRef.current.startX - event.clientX;
      const rawWidth = schemaResizeRef.current.startWidth + delta;
      const maxWidth = Math.round(window.innerWidth * 0.75);
      const minWidth = 360;
      setSchemaDrawerWidth(Math.max(minWidth, Math.min(maxWidth, rawWidth)));
    };
    const onUp = () => setSchemaDrawerResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [schemaDrawerResizing]);

  useEffect(() => {
    if (!queryTypeDrawerResizing) return undefined;
    const onMove = (event) => {
      const delta = queryTypeResizeRef.current.startX - event.clientX;
      const rawWidth = queryTypeResizeRef.current.startWidth + delta;
      const maxWidth = Math.round(window.innerWidth * 0.75);
      const minWidth = 360;
      setQueryTypeDrawerWidth(Math.max(minWidth, Math.min(maxWidth, rawWidth)));
    };
    const onUp = () => setQueryTypeDrawerResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [queryTypeDrawerResizing]);


  useEffect(() => {
    if (activeTab !== 'settings' || settingsSection !== 'system_shop' || systemShopTab !== 'schema') {
      return;
    }
    loadSchema(schemaEditorTab);
  }, [activeTab, settingsSection, systemShopTab, schemaEditorTab, loadSchema]);

  useEffect(() => {
    if (!flowListId && flowList.length > 0) {
      selectFlowById(flowList[0].id);
    }
  }, [flowListId, flowList, selectFlowById]);

  useEffect(() => {
    mermaid.initialize({ startOnLoad: false, theme: 'default' });
  }, []);

  useEffect(() => {
    const diagram = buildMermaidDiagram(flowSteps);
    if (!diagram) {
      setFlowMermaidSvg('');
      setFlowMermaidError('');
      return;
    }
    const renderId = `flow_${Date.now()}`;
    let cancelled = false;
    mermaid
      .render(renderId, diagram)
      .then(({ svg }) => {
        if (cancelled) return;
        setFlowMermaidSvg(svg);
        setFlowMermaidError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setFlowMermaidSvg('');
        setFlowMermaidError(String(err?.message || err));
      });
    return () => {
      cancelled = true;
    };
  }, [flowSteps]);

  useEffect(() => {
    if (!filterDrawerResizing) return undefined;
    const onMove = (event) => {
      const delta = filterDrawerResizeRef.current.startX - event.clientX;
      const rawWidth = filterDrawerResizeRef.current.startWidth + delta;
      const maxWidth = Math.round(window.innerWidth * 0.75);
      const minWidth = 420;
      setFilterDrawerWidth(Math.max(minWidth, Math.min(maxWidth, rawWidth)));
    };
    const onUp = () => setFilterDrawerResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [filterDrawerResizing]);

  useEffect(() => {
    if (activeTab !== 'mapping') return;
    if (!mappingActive && mappingStatus) {
      loadMappingJobs();
    }
    if (!mappingV2Active && mappingV2Status) {
      loadMappingV2Jobs();
    }
  }, [activeTab, mappingActive, mappingStatus, loadMappingJobs, mappingV2Active, mappingV2Status, loadMappingV2Jobs]);

  const loadProductFieldOptions = useCallback(async () => {
    try {
      setProductFieldOptionsLoading(true);
      setProductFieldOptionsError('');
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/solr/schema/fields`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      const options = Array.isArray(data?.fields) ? data.fields : [];
      setProductFieldOptions(options);
    } catch (err) {
      setProductFieldOptionsError(String(err?.message || err));
      setProductFieldOptions([]);
    } finally {
      setProductFieldOptionsLoading(false);
    }
  }, [getAuthHeaders]);

  const loadExportBrands = useCallback(async () => {
    if (exportBrandLoading) return;
    setExportBrandLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/brands`, { headers: getAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      const list = Array.isArray(data?.brands) ? data.brands.filter((b) => typeof b === 'string' && b.trim()) : [];
      setExportBrandOptions(list);
    } catch (err) {
      setExportBrandOptions([]);
    } finally {
      setExportBrandLoading(false);
    }
  }, [exportBrandLoading, getAuthHeaders]);

  useEffect(() => {
    if (activeTab !== 'settings' || (settingsSection !== 'exports' && settingsSection !== 'solr')) return undefined;
    if (exportBrandOptions.length === 0 && !exportBrandLoading) {
      loadExportBrands();
    }
    return undefined;
  }, [activeTab, settingsSection, exportBrandOptions.length, exportBrandLoading, loadExportBrands]);

  useEffect(() => {
    if (settingsSection !== 'match_lookup') return undefined;
    if (productFieldOptions.length === 0 && !productFieldOptionsLoading && !productFieldOptionsError) {
      loadProductFieldOptions();
    }
    return undefined;
  }, [settingsSection, productFieldOptions.length, productFieldOptionsLoading, productFieldOptionsError, loadProductFieldOptions]);

  const handleProgramSelectChange = (event) => {
    const values = Array.from(event.target.selectedOptions).map((option) => option.value);
    setSelectedPrograms(values);
  };
  const handleSelectAllPrograms = () => {
    setSelectedPrograms(programOptions);
  };

  const handleClearPrograms = () => {
    setSelectedPrograms([]);
  };

  const handleMappingThreadsChange = (value) => {
    const n = onNum(value, 1, 10);
    setCfg((prev) => ({
      ...prev,
      mapping_threads: n === '' ? prev.mapping_threads || 1 : n,
    }));
  };

  const handleMappingV2MaxTrimChange = (value) => {
    const n = onNum(value, 0, 8);
    setCfg((prev) => ({
      ...prev,
      mapping_v2_max_trim: n === '' ? prev.mapping_v2_max_trim ?? 4 : n,
    }));
  };

  const handleMappingV2MinLengthChange = (value) => {
    const n = onNum(value, 1, 8);
    setCfg((prev) => ({
      ...prev,
      mapping_v2_min_length: n === '' ? prev.mapping_v2_min_length ?? 4 : n,
    }));
  };

  const persistConfig = useCallback(async (config, options = {}) => {
    const combinedQueryTypes = normalizeQueryTypesList(
      config.query_types,
      DEFAULT_SYSTEM_SHOP_QUERY_TYPES,
      DEFAULT_PRODUCT_QUERY_TYPES,
      { fallback: false }
    );
    const split = splitQueryTypes(combinedQueryTypes);
    const systemTypes = combinedQueryTypes.length
      ? normalizeQueryTypes(split.system, DEFAULT_SYSTEM_SHOP_QUERY_TYPES)
      : normalizeQueryTypes(config.system_shop_query_types, DEFAULT_SYSTEM_SHOP_QUERY_TYPES);
    const productTypes = combinedQueryTypes.length
      ? normalizeQueryTypes(split.product, DEFAULT_PRODUCT_QUERY_TYPES)
      : normalizeQueryTypes(config.product_query_types, DEFAULT_PRODUCT_QUERY_TYPES);
    const payload = {
      ...config,
      system_shop_search: normalizeSystemShopSearch(config.system_shop_search),
      system_shop_filters: normalizeSystemShopFilters(config.system_shop_filters, { includeRaw: false }),
      system_shop_advanced_filters: normalizeSystemShopAdvancedFilters(config.system_shop_advanced_filters),
      system_shop_filters_settings: {
        ...normalizeSystemShopFilterSettings(config.system_shop_filters_settings),
        order: buildSystemShopFilterOrder(
          config.system_shop_filters,
          config.system_shop_advanced_filters,
          config.system_shop_filters_settings?.order
        ),
      },
      query_types: combinedQueryTypes,
      system_shop_query_types: systemTypes,
      product_query_types: productTypes,
    };
    const r = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload),
    });
    if (!options.silent) {
      alert(r.ok ? 'Saved' : 'Save failed');
    }
    return r.ok;
  }, [headers]);

  useEffect(() => {
    persistConfigRef.current = persistConfig;
  }, [persistConfig]);

  const save = async () => {
    await persistConfig(cfg);
  };

  const saveSystemShopFilters = async () => {
    setSavingSystemShopFilters(true);
    try {
      await persistConfig(cfg);
    } finally {
      setSavingSystemShopFilters(false);
    }
  };

  const startSync = async () => {
    if (!selectedPrograms.length) {
      alert('Select at least one program to sync.');
      return;
    }
    const payload = { programs: selectedPrograms };
    const r = await fetch(`${API_BASE_URL}/api/v1/ahri/sync/start`, { method: 'POST', headers, body: JSON.stringify(payload) });
    const j = await r.json();
    if (r.ok) {
      setPolling(true);
      await loadJobs();
      alert(`Sync started (job ${j.job_id})`);
    } else {
      alert(`Sync failed to start: ${j?.detail || r.status}`);
    }
  };

  const cancelJob = async (jobId) => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/job/${jobId}/cancel`, { method: 'POST', headers });
      if (r.ok) {
        setPolling(true);
        await loadJobs();
      } else {
        const j = await r.json().catch(()=>({}));
        alert(`Cancel failed: ${j?.detail || r.status}`);
      }
    } catch (e) {
      alert('Cancel failed');
    }
  };

  const deleteJob = async (jobId) => {
    const ok = await confirmDialog({
      title: 'Delete job',
      content: 'Delete this job?',
    });
    if (!ok) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/job/${jobId}`, { method: 'DELETE', headers });
      if (r.ok) {
        await loadJobs();
      } else {
        const j = await r.json().catch(()=>({}));
        alert(`Delete failed: ${j?.detail || r.status}`);
      }
    } catch (e) {
      alert('Delete failed');
    }
  };
  const startEmbeddings = async () => {
    const r = await fetch(`${API_BASE_URL}/api/v1/ahri/embeddings/start`, { method: 'POST', headers, body: JSON.stringify({ debug: embDebug }) });
    const j = await r.json();
    if (r.ok) {
      setPolling(true);
      await loadJobs();
      alert(`Embedding started (job ${j.job_id})`);
    } else {
      alert(`Embedding failed to start: ${j?.detail || r.status}`);
    }
  };

  const exportAhriPartMatches = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    setExportError('');
    try {
      const fmt = exportFormat === 'xlsx' ? 'xlsx' : 'csv';
      const statusList = Array.isArray(exportMatchStatus) ? exportMatchStatus : [];
      const payload = {
        format: fmt,
        match_status: statusList.length ? statusList : ['all'],
        programs: Array.isArray(exportProgram) ? exportProgram : [],
        brands: Array.isArray(exportBrand) ? exportBrand : [],
      };
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/exports/start`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `Export failed (HTTP ${resp.status})`);
      }
      setExportTab('batches');
      await loadExportJobs();
    } catch (err) {
      setExportError(String(err?.message || err));
    } finally {
      setExportLoading(false);
    }
  };

  const loadExportJobs = useCallback(async () => {
    if (exportJobsLoading) return;
    setExportJobsLoading(true);
    setExportJobsError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/exports`, { headers });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      setExportJobs(Array.isArray(data?.jobs) ? data.jobs : []);
    } catch (err) {
      setExportJobsError(String(err?.message || err));
      setExportJobs([]);
    } finally {
      setExportJobsLoading(false);
    }
  }, [exportJobsLoading, headers]);

  const downloadExportJob = async (jobId, fileName) => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/exports/${jobId}/download`, { headers });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.detail || `Download failed (HTTP ${resp.status})`);
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `ahri_part_matches_${jobId}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportJobsError(String(err?.message || err));
    }
  };

  const loadExportCount = useCallback(async (payload) => {
    if (exportCountLoading) return;
    setExportCountLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/exports/count`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      setExportCount(typeof data?.count === 'number' ? data.count : 0);
    } catch (err) {
      setExportCount(null);
    } finally {
      setExportCountLoading(false);
    }
  }, [exportCountLoading, headers]);

  useEffect(() => {
    if (activeTab !== 'settings' || settingsSection !== 'exports' || exportTab !== 'create') return undefined;
    const statusList = Array.isArray(exportMatchStatus) ? exportMatchStatus : [];
    const hasStatusFilter = statusList.some((s) => String(s).toLowerCase() !== 'all');
    const hasProgramFilter = Array.isArray(exportProgram) && exportProgram.length > 0;
    const hasBrandFilter = Array.isArray(exportBrand) && exportBrand.length > 0;
    if (!hasStatusFilter && !hasProgramFilter && !hasBrandFilter) {
      setExportCount(null);
      exportCountKeyRef.current = '';
      return undefined;
    }
    const payload = {
      match_status: statusList.length ? statusList : ['all'],
      programs: Array.isArray(exportProgram) ? exportProgram : [],
      brands: Array.isArray(exportBrand) ? exportBrand : [],
    };
    const key = JSON.stringify(payload);
    if (exportCountKeyRef.current === key) return undefined;
    exportCountKeyRef.current = key;
    loadExportCount(payload);
    return undefined;
  }, [activeTab, settingsSection, exportTab, exportMatchStatus, exportProgram, exportBrand, loadExportCount]);

  const hasActiveExportJobs = useMemo(
    () =>
      (exportJobs || []).some((job) =>
        ['queued', 'running', 'pending', 'processing'].includes(String(job?.status || '').toLowerCase())
      ),
    [exportJobs]
  );

  useEffect(() => {
    if (activeTab !== 'settings' || settingsSection !== 'exports' || exportTab !== 'batches') return undefined;
    if (!exportBatchesLoadedRef.current) {
      loadExportJobs();
      exportBatchesLoadedRef.current = true;
    }
    if (exportPollRef.current) {
      clearInterval(exportPollRef.current);
      exportPollRef.current = null;
    }
    if (hasActiveExportJobs) {
      exportPollRef.current = setInterval(() => {
        loadExportJobs();
      }, 10000);
    }
    return () => {
      if (exportPollRef.current) {
        clearInterval(exportPollRef.current);
        exportPollRef.current = null;
      }
    };
  }, [activeTab, settingsSection, exportTab, loadExportJobs, hasActiveExportJobs]);

  useEffect(() => {
    if (activeTab !== 'settings' || settingsSection !== 'exports') {
      exportBatchesLoadedRef.current = false;
    }
  }, [activeTab, settingsSection, exportTab]);

  const onNum = (val, min, max) => {
    const n = parseInt(val || '0', 10);
    if (isNaN(n)) return '';
    let m = n;
    if (typeof min === 'number') m = Math.max(min, m);
    if (typeof max === 'number') m = Math.min(max, m);
    return m;
  };

  return (
    <div className="container-fluid py-4">
      <h3 className="mb-3">AHRI Data</h3>

      {/* Tabs header */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'sync' ? 'active' : ''}`}
            onClick={() => setActiveTab('sync')}
          >
            Data Sync
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'mapping' ? 'active' : ''}`}
            onClick={() => setActiveTab('mapping')}
          >
            Product Mapping
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'mapping_review' ? 'active' : ''}`}
            onClick={() => setActiveTab('mapping_review')}
          >
            Match Review
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'system_shop_events' ? 'active' : ''}`}
            onClick={() => setActiveTab('system_shop_events')}
          >
            System Shop Events
          </button>
        </li>
      </ul>

      {/* Tab content */}
      {activeTab === 'settings' && (
        <div className="row">
          <div className="col-12 d-flex justify-content-end mb-2">
            <Button onClick={() => setSettingsHistoryOpen(true)}>History</Button>
          </div>
          <div className="col-12 col-md-3 col-lg-2 mb-3">
            <ul className="nav nav-pills flex-column">
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'openai' ? 'active' : ''}`} onClick={()=>setSettingsSection('openai')}>OpenAI</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'pinecone' ? 'active' : ''}`} onClick={()=>setSettingsSection('pinecone')}>Pinecone</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'connection' ? 'active' : ''}`} onClick={()=>setSettingsSection('connection')}>AHRI Connection</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'schemas' ? 'active' : ''}`} onClick={()=>setSettingsSection('schemas')}>Program Schemas</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'solr' ? 'active' : ''}`} onClick={()=>setSettingsSection('solr')}>Solr</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'match_lookup' ? 'active' : ''}`} onClick={()=>setSettingsSection('match_lookup')}>Match Lookup</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'attr_import' ? 'active' : ''}`} onClick={()=>setSettingsSection('attr_import')}>Attribute Import</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'system_shop' ? 'active' : ''}`} onClick={()=>setSettingsSection('system_shop')}>System Shop Filters</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'exports' ? 'active' : ''}`} onClick={()=>setSettingsSection('exports')}>Exports</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${settingsSection === 'eco_rebates' ? 'active' : ''}`} onClick={()=>setSettingsSection('eco_rebates')}>Eco Rebates</button>
              </li>
              <li className="nav-item">
                {enableCopyDefaults && (
                  <button className={`nav-link ${settingsSection === 'system_defaults' ? 'active' : ''}`} onClick={()=>setSettingsSection('system_defaults')}>System Defaults</button>
                )}
              </li>
            </ul>
          </div>
          <div className="col-12 col-md-9 col-lg-10">
            {settingsSection === 'openai' && (
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0">OpenAI (AHRI)</h6>
                    {renderSystemDefaultTag(AHRI_SECTION_SOURCE_KEYS.openai)}
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">API Key</label>
                      <input type="password" className="form-control" value={cfg.openai?.api_key || ''} onChange={(e)=>setCfg({...cfg, openai: { ...(cfg.openai||{}), api_key: e.target.value }})} />
                      {cfg.openai?.api_key && (cfg.openai.api_key === 'demo-key-123' || cfg.openai.api_key.startsWith('demo-')) && (
                        <div className="text-warning small mt-1">⚠ This looks like a placeholder key — replace it with your real OpenAI API key.</div>
                      )}
                      {cfg.openai?.api_key && (cfg.openai.api_key.includes(' ') || cfg.openai.api_key.length > 200 || cfg.openai.api_key.startsWith('Image model') || cfg.openai.api_key.startsWith('Error')) && (
                        <div className="text-danger small mt-1">⛔ This field appears to contain error text, not an API key. Clear it and paste only the key from platform.openai.com.</div>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Base URL (optional)</label>
                      <input className="form-control" value={cfg.openai?.base_url || ''} onChange={(e)=>setCfg({...cfg, openai: { ...(cfg.openai||{}), base_url: e.target.value }})} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Embedding Model</label>
                      <input className="form-control" value={cfg.openai?.embedding_model || ''} onChange={(e)=>setCfg({...cfg, openai: { ...(cfg.openai||{}), embedding_model: e.target.value }})} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Completions Model</label>
                      <input className="form-control" value={cfg.openai?.completions_model || ''} onChange={(e)=>setCfg({...cfg, openai: { ...(cfg.openai||{}), completions_model: e.target.value }})} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">System Prompt</label>
                      <textarea className="form-control" rows={3} value={cfg.openai?.system_prompt || ''} onChange={(e)=>setCfg({...cfg, openai: { ...(cfg.openai||{}), system_prompt: e.target.value }})} />
                    </div>
                    <div className="col-12">
                      <hr className="my-2" />
                      <div className="fw-semibold small mb-2 text-muted">Image Search — Progressive Fallback</div>
                      <div className="form-text mb-2">
                        When no results are found for the extracted model number, the server strips characters from the right one at a time and retries until a result is found or the limits below are reached.
                      </div>
                      <div className="row g-3">
                        <div className="col-sm-3">
                          <label className="form-label">Max Trim</label>
                          <input
                            type="number"
                            className="form-control"
                            min={0}
                            max={10}
                            value={cfg.image_search?.max_trim ?? 4}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              setCfg({ ...cfg, image_search: { ...(cfg.image_search || {}), max_trim: isNaN(n) ? 4 : Math.max(0, Math.min(n, 10)) } });
                            }}
                          />
                          <div className="form-text">Max chars trimmed from right (0–10). Default: 4.</div>
                        </div>
                        <div className="col-sm-3">
                          <label className="form-label">Min Length</label>
                          <input
                            type="number"
                            className="form-control"
                            min={1}
                            max={20}
                            value={cfg.image_search?.min_length ?? 6}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              setCfg({ ...cfg, image_search: { ...(cfg.image_search || {}), min_length: isNaN(n) ? 6 : Math.max(1, Math.min(n, 20)) } });
                            }}
                          />
                          <div className="form-text">Stop trimming below this length (1–20). Default: 6.</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 d-flex justify-content-end">
                      <button className="btn btn-primary" onClick={save}>Save</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {settingsSection === 'pinecone' && (
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0">Pinecone (AHRI)</h6>
                    {renderSystemDefaultTag(AHRI_SECTION_SOURCE_KEYS.pinecone)}
                  </div>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" id="vec_enabled" checked={!!cfg.vectorize_enabled} onChange={(e)=>setCfg({...cfg, vectorize_enabled: e.target.checked})} />
                        <label className="form-check-label" htmlFor="vec_enabled">
                          Enable vectorizing to Pinecone (when off, sync stores only to DB)
                        </label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">API Key</label>
                      <input type="password" className="form-control" value={cfg.pinecone?.api_key || ''} onChange={(e)=>setCfg({...cfg, pinecone: { ...(cfg.pinecone||{}), api_key: e.target.value }})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Host</label>
                      <input className="form-control" value={cfg.pinecone?.host || ''} onChange={(e)=>setCfg({...cfg, pinecone: { ...(cfg.pinecone||{}), host: e.target.value }})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Index</label>
                      <input className="form-control" value={cfg.pinecone?.index || ''} onChange={(e)=>setCfg({...cfg, pinecone: { ...(cfg.pinecone||{}), index: e.target.value }})} />
                    </div>
                    <div className="col-12 d-flex justify-content-end">
                      <button className="btn btn-primary" onClick={save}>Save</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {settingsSection === 'connection' && (
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0">AHRI Connection</h6>
                    {renderSystemDefaultTag(AHRI_SECTION_SOURCE_KEYS.connection)}
                  </div>
                  <p className="text-muted small mb-4">
                    Configure the OAuth credentials and the GraphQL endpoint used for syncing AHRI data.
                  </p>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="text-uppercase text-muted small fw-bold">OAuth Credentials</div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Token URL</label>
                      <input className="form-control" value={cfg.token_url} onChange={(e)=>setCfg({...cfg, token_url: e.target.value})} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Grant Type</label>
                      <input className="form-control" value={cfg.grant_type} onChange={(e)=>setCfg({...cfg, grant_type: e.target.value})} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Scope</label>
                      <input className="form-control" value={cfg.scope} onChange={(e)=>setCfg({...cfg, scope: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Resource (App ID URI)</label>
                      <input
                        className="form-control"
                        placeholder="api://resource-app-id"
                        value={cfg.resource || ''}
                        onChange={(e)=>setCfg({...cfg, resource: e.target.value})}
                      />
                      <div className="form-text">Used when Scope is empty.</div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Client ID</label>
                      <input className="form-control" value={cfg.client_id} onChange={(e)=>setCfg({...cfg, client_id: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Client Secret</label>
                      <input type="password" className="form-control" value={cfg.client_secret} onChange={(e)=>setCfg({...cfg, client_secret: e.target.value})} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Access Token Override (optional)</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder="Paste a bearer token to bypass OAuth token call"
                        value={cfg.access_token_override || ''}
                        onChange={(e)=>setCfg({...cfg, access_token_override: e.target.value})}
                      />
                      <div className="form-text">If provided, the app uses this token directly until it expires.</div>
                    </div>
                    <div className="col-12">
                      <hr />
                      <div className="text-uppercase text-muted small fw-bold">Data Fetch Endpoint</div>
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Data URL</label>
                      <input className="form-control" value={cfg.data_url} onChange={(e)=>setCfg({...cfg, data_url: e.target.value})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">userUniqueId</label>
                      <input className="form-control" value={cfg.userUniqueId} onChange={(e)=>setCfg({...cfg, userUniqueId: e.target.value})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Content-Type</label>
                      <input className="form-control" value={cfg.content_type} onChange={(e)=>setCfg({...cfg, content_type: e.target.value})} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Page Size</label>
                      <input type="number" min={1} className="form-control" value={cfg.page_size} onChange={(e)=>{
                        const v = onNum(e.target.value, 1);
                        setCfg({...cfg, page_size: v});
                      }} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Parallel Threads (max 5)</label>
                      <input type="number" min={1} max={5} className="form-control" value={cfg.threads} onChange={(e)=>{
                        const v = onNum(e.target.value, 1, 5);
                        setCfg({...cfg, threads: v});
                      }} />
                    </div>
                    <div className="col-12">
                      <hr />
                      <div className="text-uppercase text-muted small fw-bold">Network & Proxy</div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-check form-switch mt-1">
                        <input className="form-check-input" type="checkbox" id="verify_ssl" checked={!!cfg.verify_ssl} onChange={(e)=>setCfg({...cfg, verify_ssl: e.target.checked})} />
                        <label className="form-check-label" htmlFor="verify_ssl">Verify SSL certificates</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-check form-switch mt-1">
                        <input className="form-check-input" type="checkbox" id="use_system_proxy" checked={!!cfg.use_system_proxy} onChange={(e)=>setCfg({...cfg, use_system_proxy: e.target.checked})} />
                        <label className="form-check-label" htmlFor="use_system_proxy">Use system proxy</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Proxy URL (optional)</label>
                      <input
                        className="form-control"
                        placeholder="https://user:pass@proxy.host:port"
                        value={cfg.proxy_url || ''}
                        onChange={(e)=>setCfg({...cfg, proxy_url: e.target.value})}
                      />
                    </div>
                    <div className="col-12 d-flex justify-content-end">
                      <button className="btn btn-primary" onClick={save}>Save</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {settingsSection === 'solr' && (
              <>
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0">SolrCloud (Search & Facets)</h6>
                    {renderSystemDefaultTag(AHRI_SECTION_SOURCE_KEYS.solr)}
                  </div>
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label">Base URL</label>
                      <input
                        className="form-control"
                        placeholder="https://solr-example:8983/solr"
                        value={cfg.solr?.base_url || ''}
                        onChange={(e)=>setCfg({...cfg, solr: { ...(cfg.solr||{}), base_url: e.target.value }})}
                      />
                      <div className="form-text">Point to the SolrCloud root (typically ends with /solr).</div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Verify SSL</label>
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="solr_verify_ssl"
                          checked={cfg.solr?.verify_ssl ?? true}
                          onChange={(e)=>setCfg({...cfg, solr: { ...(cfg.solr||{}), verify_ssl: e.target.checked }})}
                        />
                        <label className="form-check-label" htmlFor="solr_verify_ssl">Verify HTTPS certificates</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Username (optional)</label>
                      <input
                        className="form-control"
                        value={cfg.solr?.username || ''}
                        onChange={(e)=>setCfg({...cfg, solr: { ...(cfg.solr||{}), username: e.target.value }})}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Password (optional)</label>
                      <input
                        type="password"
                        className="form-control"
                        value={cfg.solr?.password || ''}
                        onChange={(e)=>setCfg({...cfg, solr: { ...(cfg.solr||{}), password: e.target.value }})}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Shards</label>
                      <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={cfg.solr?.shards ?? 1}
                        onChange={(e)=>{
                          const v = onNum(e.target.value, 1);
                          setCfg({...cfg, solr: { ...(cfg.solr||{}), shards: v }});
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Replicas</label>
                      <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={cfg.solr?.replicas ?? 1}
                        onChange={(e)=>{
                          const v = onNum(e.target.value, 1);
                          setCfg({...cfg, solr: { ...(cfg.solr||{}), replicas: v }});
                        }}
                      />
                    </div>
                    <div className="col-12">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="solr_sync_variants_on_import"
                          checked={!!cfg.solr?.sync_variants_on_import}
                          onChange={(e)=>setCfg({...cfg, solr: { ...(cfg.solr||{}), sync_variants_on_import: e.target.checked }})}
                        />
                        <label className="form-check-label" htmlFor="solr_sync_variants_on_import">
                          Also sync product combination (AHRI System Variant) docs when Full/Delta Import runs
                        </label>
                      </div>
                      <div className="form-text">
                        When enabled, every Full or Delta Import from the AHRI Solr tab also pushes <code>ahri_system_variants</code> rows into Solr as <code>doc_type: ahri_system_variant</code> documents, so variant data (brand/availability) stays in sync without a separate manual step.
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="alert alert-info mb-0">
                        Each account gets its own collection named <code>ahri_&lt;accountId&gt;</code>. These settings are required for indexing and Solr search UI.
                      </div>
                    </div>
                    <div className="col-12 d-flex flex-wrap justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={deleteSolrCollection}
                        disabled={solrDeleting}
                      >
                        {solrDeleting ? 'Deleting…' : 'Delete AHRI Data Collection'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-warning"
                        onClick={deleteProductCollection}
                        disabled={productSolrDeleting}
                      >
                        {productSolrDeleting ? 'Deleting…' : 'Delete Products Collection'}
                      </button>
                      <button type="button" className="btn btn-primary" onClick={save}>
                        Save
                      </button>
                    </div>
                    <div className="col-12">
                      <hr />
                      <label className="form-label fw-semibold">Delete Data by Program</label>
                      <div className="row g-2 align-items-end">
                        <div className="col-md-6">
                          <input
                            className="form-control"
                            list="ahri-program-list"
                            placeholder="Enter program (e.g., AC, ASHP…) or pick below"
                            value={programPurgeName}
                            onChange={(e)=>setProgramPurgeName(e.target.value)}
                          />
                          <datalist id="ahri-program-list">
                            {programOptions.map((prog) => (
                              <option key={prog} value={prog} />
                            ))}
                          </datalist>
                        </div>
                        <div className="col-md-6">
                          <button
                            type="button"
                            className="btn btn-outline-danger w-100"
                            onClick={handleProgramPurge}
                            disabled={programPurging}
                          >
                            {programPurging ? 'Deleting Program Data…' : 'Delete Program Data'}
                          </button>
                          <div className="form-text">
                            Removes AHRI records for the selected program from PostgreSQL and Solr.
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <hr />
                      <label className="form-label fw-semibold">Delete Data by Brand</label>
                      <div className="row g-2 align-items-end">
                        <div className="col-md-6">
                          <input
                            className="form-control"
                            list="ahri-brand-purge-list"
                            placeholder="Enter or pick a brand"
                            value={brandPurgeName}
                            onChange={(e) => setBrandPurgeName(e.target.value)}
                          />
                          <datalist id="ahri-brand-purge-list">
                            {exportBrandList.map((b) => (
                              <option key={b} value={b} />
                            ))}
                          </datalist>
                        </div>
                        <div className="col-md-6">
                          <button
                            type="button"
                            className="btn btn-outline-danger w-100"
                            onClick={openBrandDeleteModal}
                          >
                            Delete Brand Data…
                          </button>
                          <div className="form-text">
                            Reviews impact, then permanently removes AHRI records for the selected brand from PostgreSQL and Solr, and prunes matched product references.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">Lookup Transform Rules</h6>
                  <p className="text-muted small mb-4">
                    Configure how AHRI lookup values are cleaned before matching them against product part numbers.
                  </p>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="lt-trim-plus"
                          checked={!!lookupTransform.trim_after_plus}
                          onChange={(e)=>updateLookupTransform({ trim_after_plus: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="lt-trim-plus">
                          Trim everything after "+"
                        </label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Max fallback trim (chars)</label>
                      <input
                        type="number"
                        min={0}
                        className="form-control"
                        value={lookupTransform.truncate_last_chars ?? 0}
                        onChange={(e)=>{
                          const next = Math.max(0, Number(e.target.value) || 0);
                          updateLookupTransform({ truncate_last_chars: next });
                        }}
                      />
                      <div className="form-text">
                        When no match is found, the matcher retries by trimming up to this many trailing characters.
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Replace "*" with</label>
                      <input
                        className="form-control"
                        maxLength={1}
                        value={lookupTransform.asterisk_replacement ?? ''}
                        onChange={(e)=>updateLookupTransform({ asterisk_replacement: e.target.value.slice(0, 1) })}
                      />
                      <div className="form-text">Leave blank to keep "*".</div>
                    </div>
                    <div className="col-12">
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="lt-dot-wildcard"
                          checked={!!lookupTransform.dot_wildcard_enabled}
                          onChange={(e)=>updateLookupTransform({ dot_wildcard_enabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="lt-dot-wildcard">
                          Treat "." as a single-character wildcard
                        </label>
                      </div>
                      <div className="form-text">
                        When enabled, any "." in the cleaned value triggers a regex search for single-character wildcards.
                      </div>
                    </div>
                  <hr />
                  <div className="mb-3">
                    <div className="d-flex flex-column flex-lg-row align-items-lg-end gap-3">
                      <div className="flex-grow-1">
                        <label className="form-label">Program for Sample Preview</label>
                        <select
                          className="form-select"
                          value={sampleProgram}
                          onChange={(e)=>setSampleProgram(e.target.value)}
                        >
                          {programOptions.map((prog) => (
                            <option key={prog} value={prog}>{prog}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-grow-1">
                        <label className="form-label">Fields</label>
                        <select
                          className="form-select"
                          multiple
                          value={sampleFields}
                          onChange={(e)=>{
                            const values = Array.from(e.target.selectedOptions).map(opt => opt.value);
                            setSampleFields(values);
                          }}
                          size={Math.min(6, Math.max(3, (programLookupFieldMap[sampleProgram] || []).length))}
                        >
                          {(programLookupFieldMap[sampleProgram] || []).map((field) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                        <div className="mt-2 d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={()=>setSampleFields(programLookupFieldMap[sampleProgram] || [])}
                          >
                            Select All
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={()=>setSampleFields([])}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="form-text">Hold Cmd/Ctrl to select multiple fields.</div>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          disabled={sampleLoading || !(programLookupFieldMap[sampleProgram] || []).length}
                          onClick={runSamplePreview}
                        >
                          {sampleLoading ? 'Running…' : 'Preview 5 Records'}
                        </button>
                      </div>
                    </div>
                    {sampleError && <div className="alert alert-danger mt-3 mb-0">{sampleError}</div>}
                    {!sampleLoading && !sampleError && sampleResults.length === 0 && (
                      <div className="text-muted small mt-3">Run the preview to see sample transformations.</div>
                    )}
                    {sampleLoading && (
                      <div className="text-muted small mt-3">Loading sample records…</div>
                    )}
                    {!sampleLoading && sampleResults.length > 0 && (
                      <div className="table-responsive mt-3">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th style={{ minWidth: 120 }}>AHRI ID</th>
                              <th style={{ minWidth: 80 }}>Program</th>
                              <th style={{ minWidth: 140 }}>Field</th>
                              <th style={{ minWidth: 160 }}>Original Value</th>
                              <th style={{ minWidth: 160 }}>Transformed Value</th>
                              <th style={{ minWidth: 90 }}>Pattern Type</th>
                              <th style={{ minWidth: 200 }}>Pattern</th>
                              <th style={{ minWidth: 220 }}>Fallback Attempts</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sampleResults.map((sample) =>
                              (sample.fields || []).map((fieldEntry, idx) => (
                                <tr key={`${sample.ahri_id}-${fieldEntry.field}-${idx}`}>
                                  <td>{idx === 0 ? sample.ahri_id : ''}</td>
                                  <td>{idx === 0 ? sample.program : ''}</td>
                                  <td><code>{fieldEntry.field}</code></td>
                                  <td>{fieldEntry.original || <span className="text-muted">—</span>}</td>
                                  <td>{fieldEntry.transformed || <span className="text-muted">—</span>}</td>
                                  <td>{fieldEntry.pattern_type || '-'}</td>
                                  <td className="text-break">
                                    {fieldEntry.pattern || <span className="text-muted">n/a</span>}
                                  </td>
                                  <td className="text-break">
                                    {Array.isArray(fieldEntry.attempts) && fieldEntry.attempts.length > 0 ? (
                                      <ul className="mb-0 ps-3 small">
                                        {fieldEntry.attempts.map((attempt, attemptIdx) => (
                                          <li key={`${fieldEntry.field}-attempt-${attemptIdx}`}>
                                            <span className="text-muted">
                                              {attempt.fallback ? 'Fallback → ' : ''}
                                              {attempt.value}
                                            </span>
                                            {attempt.pattern && (
                                              <div>
                                                <code>{attempt.pattern}</code>
                                                <span className="text-muted"> ({attempt.pattern_type})</span>
                                              </div>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              </div>
              </>
            )}
            {settingsSection === 'match_lookup' && (
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0">Match Lookup API</h6>
                    {renderSystemDefaultTag(AHRI_SECTION_SOURCE_KEYS.match_lookup)}
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Fallback Product Image Base URL</label>
                      <input
                        className="form-control"
                        placeholder={DEFAULT_PRODUCT_IMAGE_BASE_URL}
                        value={cfg.product_image_fallback_base_url || ''}
                        onChange={(e)=>setCfg({...cfg, product_image_fallback_base_url: e.target.value})}
                      />
                      <div className="form-text">
                        Used to build complete image URLs when Solr only stores a filename.
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Mapping Field</label>
                      <select
                        className="form-select"
                        value={matchLookupCfg.mapping_field || 'productCode'}
                        onChange={(e) => updateMatchLookup({ mapping_field: e.target.value })}
                      >
                        <option value="productCode">productCode</option>
                        <option value="manufacturerPartNumber">manufacturerPartNumber</option>
                      </select>
                      <div className="form-text">
                        Choose which product field to use when matching AHRI data to products.
                      </div>
                    </div>
                    <div className="col-md-6 d-flex align-items-center">
                      <div className="form-check form-switch mt-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="match_lookup_complete_only"
                          checked={!!matchLookupCfg.enforce_complete_match}
                          onChange={(e)=>updateMatchLookup({ enforce_complete_match: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="match_lookup_complete_only">
                          Require <code>ahri_complete_match = true</code>
                        </label>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Product Fields (one per line)</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={(matchLookupCfg.product_fields || []).join('\n')}
                        onChange={(e)=>{
                          const entries = e.target.value
                            .split(/[\n,]/)
                            .map((part) => part.trim())
                            .filter(Boolean);
                          updateMatchLookup({ product_fields: entries });
                        }}
                      />
                      <div className="form-text">
                        These fields are passed to Solr's <code>fl</code> parameter when fetching mapped products.
                      </div>
                    </div>
                    <div className="col-12 d-flex justify-content-end">
                      <button className="btn btn-primary" onClick={save}>Save</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {settingsSection === 'attr_import' && (
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0">Product Attribute Import</h6>
                    {renderSystemDefaultTag(AHRI_SECTION_SOURCE_KEYS.attr_import)}
                  </div>
                  <p className="text-muted small mb-3">
                    Map product attributes into named AHRI attributes during the Product Attributes Sync job.
                  </p>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="fw-semibold">Outdoor Model Number</div>
                    </div>
                    {(cfg.product_attribute_import?.outdoor_model || []).map((row, idx) => (
                      <React.Fragment key={`outdoor-${idx}`}>
                        <div className="col-md-5">
                          <label className="form-label">Part Attribute Name</label>
                          <input
                            className="form-control"
                            value={row?.source_attribute || ''}
                            onChange={(e)=>updateAttrImport('outdoor_model', idx, 'source_attribute', e.target.value)}
                          />
                        </div>
                        <div className="col-md-5">
                          <label className="form-label">AHRI Attribute Name</label>
                          <input
                            className="form-control"
                            value={row?.target_attribute || ''}
                            onChange={(e)=>updateAttrImport('outdoor_model', idx, 'target_attribute', e.target.value)}
                          />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                          <button
                            type="button"
                            className="btn btn-outline-danger w-100"
                            onClick={() => removeAttrImportRow('outdoor_model', idx)}
                          >
                            Remove
                          </button>
                        </div>
                      </React.Fragment>
                    ))}
                    <div className="col-12">
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addAttrImportRow('outdoor_model')}>
                        Add mapping
                      </button>
                    </div>

                    <div className="col-12">
                      <hr className="my-2" />
                      <div className="fw-semibold">Indoor Model Number</div>
                    </div>
                    {(cfg.product_attribute_import?.indoor_model || []).map((row, idx) => (
                      <React.Fragment key={`indoor-${idx}`}>
                        <div className="col-md-5">
                          <label className="form-label">Part Attribute Name</label>
                          <input
                            className="form-control"
                            value={row?.source_attribute || ''}
                            onChange={(e)=>updateAttrImport('indoor_model', idx, 'source_attribute', e.target.value)}
                          />
                        </div>
                        <div className="col-md-5">
                          <label className="form-label">AHRI Attribute Name</label>
                          <input
                            className="form-control"
                            value={row?.target_attribute || ''}
                            onChange={(e)=>updateAttrImport('indoor_model', idx, 'target_attribute', e.target.value)}
                          />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                          <button
                            type="button"
                            className="btn btn-outline-danger w-100"
                            onClick={() => removeAttrImportRow('indoor_model', idx)}
                          >
                            Remove
                          </button>
                        </div>
                      </React.Fragment>
                    ))}
                    <div className="col-12">
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addAttrImportRow('indoor_model')}>
                        Add mapping
                      </button>
                    </div>

                    <div className="col-12">
                      <hr className="my-2" />
                      <div className="fw-semibold">Furnace Model Number</div>
                    </div>
                    {(cfg.product_attribute_import?.furnace_model || []).map((row, idx) => (
                      <React.Fragment key={`furnace-${idx}`}>
                        <div className="col-md-5">
                          <label className="form-label">Part Attribute Name</label>
                          <input
                            className="form-control"
                            value={row?.source_attribute || ''}
                            onChange={(e)=>updateAttrImport('furnace_model', idx, 'source_attribute', e.target.value)}
                          />
                        </div>
                        <div className="col-md-5">
                          <label className="form-label">AHRI Attribute Name</label>
                          <input
                            className="form-control"
                            value={row?.target_attribute || ''}
                            onChange={(e)=>updateAttrImport('furnace_model', idx, 'target_attribute', e.target.value)}
                          />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                          <button
                            type="button"
                            className="btn btn-outline-danger w-100"
                            onClick={() => removeAttrImportRow('furnace_model', idx)}
                          >
                            Remove
                          </button>
                        </div>
                      </React.Fragment>
                    ))}
                    <div className="col-12">
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => addAttrImportRow('furnace_model')}>
                        Add mapping
                      </button>
                    </div>
                    <div className="col-12">
                      <div className="form-text">
                        Leave a row blank to skip it. These mappings are applied when you run Product Attributes Sync.
                      </div>
                    </div>
                    <div className="col-12 d-flex justify-content-end">
                      <button className="btn btn-primary" onClick={save}>Save</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {settingsSection === 'system_shop' && (
              <>
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <h6 className="mb-0">System Shop Settings</h6>
                      {renderSystemDefaultTag(AHRI_SECTION_SOURCE_KEYS.system_shop)}
                    </div>
                    <div className="text-muted small">
                      Manage filters and tune search relevance for the System Shop experience.
                    </div>
                    <ul className="nav nav-tabs mt-3">
                      <li className="nav-item">
                        <button
                          className={`nav-link ${systemShopTab === 'filters' ? 'active' : ''}`}
                          onClick={() => setSystemShopTab('filters')}
                        >
                          Search/Filter settings
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${systemShopTab === 'tuner' ? 'active' : ''}`}
                          onClick={() => setSystemShopTab('tuner')}
                        >
                          Search Tuner
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${systemShopTab === 'schema' ? 'active' : ''}`}
                          onClick={() => setSystemShopTab('schema')}
                        >
                          Schema Editor
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${systemShopTab === 'query_types' ? 'active' : ''}`}
                          onClick={() => setSystemShopTab('query_types')}
                        >
                          Query Types
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${systemShopTab === 'query_flows' ? 'active' : ''}`}
                          onClick={() => setSystemShopTab('query_flows')}
                        >
                          Query Flows
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${systemShopTab === 'routing' ? 'active' : ''}`}
                          onClick={() => setSystemShopTab('routing')}
                        >
                          Routing
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>

                {systemShopTab === 'filters' && (
                  <div className="shop-filters-content">
                    <div className="card shop-filters-section mb-3">
                      <div className="card-body">
                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
                          <div className="shop-filters-section-title">
                            <span className="shop-filters-section-icon"><FilterOutlined /></span>
                            <div>
                              <div>Filter List</div>
                              <div className="text-muted small fw-normal">
                                Configure filter options, display labels, order, and value sorting.
                              </div>
                            </div>
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddStandardFilter}>
                              Add Filter
                            </button>
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddAdvancedFilter}>
                              Add Advanced Filter
                            </button>
                          </div>
                        </div>
                        <div className="table-responsive shop-filters-table d-none d-md-block">
                          <table className="table table-sm align-middle">
                            <thead>
                              <tr>
                                <th style={{ width: 70 }}>Drag</th>
                                <th style={{ minWidth: 200 }}>Label</th>
                                <th style={{ minWidth: 120 }}>Type</th>
                                <th style={{ minWidth: 160 }}>Key</th>
                                <th style={{ width: 100 }}>Records</th>
                                <th style={{ width: 140 }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {combinedSystemShopFilters.map((item) => {
                                const rowId = item.id;
                                const recordCount = getRecordCountForFields(item.data?.fields, filterValuePreview);
                                return (
                                  <tr
                                    key={`system-shop-filter-${rowId}`}
                                    draggable
                                    onDragStart={() => handleCombinedFilterDragStart(rowId)}
                                    onDragOver={handleShopFilterDragOver}
                                    onDrop={() => handleCombinedFilterDrop(rowId)}
                                    className={draggingShopFilter === rowId ? 'table-primary' : ''}
                                    style={{ cursor: 'grab' }}
                                  >
                                    <td>
                                      <span className="text-muted" title="Drag to reorder" aria-hidden="true">
                                        ⋮⋮
                                      </span>
                                      <span className="visually-hidden">Drag to reorder</span>
                                    </td>
                                    <td>{item.label || '-'}</td>
                                    <td className="text-capitalize">{item.type}</td>
                                    <td>{item.key || '-'}</td>
                                    <td className="text-muted">
                                      {recordCount === null ? '—' : recordCount.toLocaleString()}
                                    </td>
                                    <td>
                                      <div className="btn-group btn-group-sm">
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary"
                                          onClick={() => openFilterDrawer(item)}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger"
                                          onClick={() =>
                                            item.type === 'standard'
                                              ? removeSystemShopFilter(item.index)
                                              : removeSystemShopAdvancedFilter(item.index)
                                          }
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                              {combinedSystemShopFilters.length === 0 && (
                                <tr>
                                  <td colSpan="6" className="text-muted text-center">
                                    No filters configured yet.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="shop-filters-cards d-md-none">
                          {combinedSystemShopFilters.map((item) => {
                            const rowId = item.id;
                            const recordCount = getRecordCountForFields(item.data?.fields, filterValuePreview);
                            return (
                              <div
                                key={`system-shop-filter-card-${rowId}`}
                                className={`shop-filter-card${draggingShopFilter === rowId ? ' shop-filter-card--dragging' : ''}`}
                                draggable
                                onDragStart={() => handleCombinedFilterDragStart(rowId)}
                                onDragOver={handleShopFilterDragOver}
                                onDrop={() => handleCombinedFilterDrop(rowId)}
                              >
                                <div className="shop-filter-card-drag" title="Drag to reorder" aria-hidden="true">⋮⋮</div>
                                <div className="shop-filter-card-body">
                                  <div className="shop-filter-card-top">
                                    <span className="shop-filter-card-label">{item.label || '-'}</span>
                                    <span className="shop-filter-card-type text-capitalize">{item.type}</span>
                                  </div>
                                  {item.key && <div className="shop-filter-card-key">{item.key}</div>}
                                  <div className="shop-filter-card-key text-muted">
                                    {recordCount === null ? 'Records: —' : `Records: ${recordCount.toLocaleString()}`}
                                  </div>
                                  <div className="shop-filter-card-actions">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={() => openFilterDrawer(item)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() =>
                                        item.type === 'standard'
                                          ? removeSystemShopFilter(item.index)
                                          : removeSystemShopAdvancedFilter(item.index)
                                      }
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {combinedSystemShopFilters.length === 0 && (
                            <div className="text-muted text-center py-3">No filters configured yet.</div>
                          )}
                        </div>
                        <div className="alert alert-info small mb-0">
                          Filters appear in the same order as listed. Save settings, then refresh the System Shop page.
                        </div>
                      </div>
                    </div>

                    <div className="card shop-filters-section mb-3">
                      <div className="card-body">
                        <div className="shop-filters-section-title mb-3">
                          <span className="shop-filters-section-icon"><ThunderboltOutlined /></span>
                          <div>
                            <div>Match &amp; Availability</div>
                            <div className="text-muted small fw-normal">
                              Control which AHRI matches and stock conditions are surfaced to shoppers.
                            </div>
                          </div>
                        </div>
                        <div className="mb-1">
                          <div className="fw-semibold mb-2">AHRI product match filter</div>
                          <div className="row g-2">
                            {SYSTEM_SHOP_MATCH_TYPE_OPTIONS.map((option) => {
                              const settings = normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings);
                              const selectedTypes = settings.match_types || ['complete', 'partial'];
                              const isChecked = selectedTypes.includes(option.value);
                              return (
                                <div className="col-12 col-md-4" key={`system-shop-match-${option.value}`}>
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`system_shop_match_${option.value}`}
                                      checked={isChecked}
                                      onChange={(e) =>
                                        setCfg((prev) => {
                                          const current = normalizeSystemShopFilterSettings(prev.system_shop_filters_settings);
                                          const currentTypes = current.match_types || ['complete', 'partial'];
                                          const nextTypes = e.target.checked
                                            ? [...new Set([...currentTypes, option.value])]
                                            : currentTypes.filter((entry) => entry !== option.value);
                                          const normalizedTypes = normalizeSystemShopMatchTypes(nextTypes.length ? nextTypes : currentTypes);
                                          return {
                                            ...prev,
                                            system_shop_filters_settings: {
                                              ...current,
                                              match_types: normalizedTypes,
                                              include_unmatched: normalizedTypes.includes('none'),
                                            },
                                          };
                                        })
                                      }
                                    />
                                    <label className="form-check-label" htmlFor={`system_shop_match_${option.value}`}>
                                      {option.label}
                                    </label>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="form-text">
                            System Shop applies this match filter to results, facets, and advanced filter counts. At least one option is kept selected.
                          </div>
                        </div>
                        <div className="mt-2">
                          <SettingToggle
                            id="system_shop_exclude_zero_requested_warehouse"
                            title="Exclude systems with requested warehouse availability ≤ 0"
                            description={<>When enabled, warehouse searches return only AHRI rows where <code>rowd_available_at_&lt;warehouse&gt; &gt; 0</code>.</>}
                            checked={normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings).exclude_zero_in_requested_warehouse}
                            onChange={(checked) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_filters_settings: {
                                  ...normalizeSystemShopFilterSettings(prev.system_shop_filters_settings),
                                  exclude_zero_in_requested_warehouse: checked,
                                },
                              }))
                            }
                          />
                          <SettingToggle
                            id="system_shop_show_fully_available_only"
                            title="Show Only Fully Available Systems"
                            description={<>When enabled (and a branch is selected on the System Shop), only AHRI systems where every
                              mapped product is in stock at that branch are returned (<code>rowd_available_at_&lt;branch&gt; = 100</code>).
                              Without a branch context the filter is skipped automatically.</>}
                            checked={normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings).show_fully_available_only}
                            onChange={(checked) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_filters_settings: {
                                  ...normalizeSystemShopFilterSettings(prev.system_shop_filters_settings),
                                  show_fully_available_only: checked,
                                },
                              }))
                            }
                          />
                          <SettingToggle
                            id="system_shop_show_brand_row"
                            title="Show Indoor/Outdoor Brand Row"
                            description="When enabled, each system card shows an Outdoor/Indoor brand line below the AHRI ID
                              (e.g. 'Outdoor: American Standard® Indoor: TRANE®'). Turn off to hide that row for shoppers
                              who shouldn't see brand detail."
                            checked={normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings).show_brand_row}
                            onChange={(checked) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_filters_settings: {
                                  ...normalizeSystemShopFilterSettings(prev.system_shop_filters_settings),
                                  show_brand_row: checked,
                                },
                              }))
                            }
                          />
                          <SettingToggle
                            id="system_shop_use_variant_based_display"
                            title="Enable Variant-Based System Shop"
                            description="When enabled, System Shop search groups results by AHRI system (one card per certificate,
                              even with multiple matched product combinations) and facets/filters on the dimension
                              chosen below, instead of the legacy certificate-level brand pair. Existing storefronts
                              are unaffected until this is turned on for their account (AD-8)."
                            checked={normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings).use_variant_based_display}
                            onChange={(checked) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_filters_settings: {
                                  ...normalizeSystemShopFilterSettings(prev.system_shop_filters_settings),
                                  use_variant_based_display: checked,
                                },
                              }))
                            }
                          >
                            {normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings).use_variant_based_display && (
                              <div className="mt-2">
                                <label className="form-label" htmlFor="system_shop_brand_filter_dimension">
                                  Brand filter dimension
                                </label>
                                <select
                                  className="form-select"
                                  id="system_shop_brand_filter_dimension"
                                  style={{ maxWidth: 280 }}
                                  value={normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings).brand_filter_dimension}
                                  onChange={(e) =>
                                    setCfg((prev) => ({
                                      ...prev,
                                      system_shop_filters_settings: {
                                        ...normalizeSystemShopFilterSettings(prev.system_shop_filters_settings),
                                        brand_filter_dimension: e.target.value,
                                      },
                                    }))
                                  }
                                >
                                  <option value="manufacturer">Manufacturer (default -- always populated)</option>
                                  <option value="brand">Brand (requires brand_name set on products)</option>
                                </select>
                                <div className="form-text">
                                  Exactly one of manufacturer or brand drives the storefront's single "Brand" filter --
                                  never both at once. Brand is nullable and not auto-backfilled on products, so an
                                  account that hasn't started setting it yet should stay on manufacturer.
                                </div>
                              </div>
                            )}
                          </SettingToggle>
                          <SettingToggle
                            id="system_shop_show_score"
                            title="Show ranking score on system cards"
                            description="Displays the computed sort score chip on each AHRI system card in the System Shop."
                            checked={!!cfg.system_shop_filters_settings?.show_score}
                            onChange={(checked) =>
                              setCfg((prev) => {
                                const next = {
                                  ...normalizeSystemShopFilterSettings(prev.system_shop_filters_settings),
                                  show_score: checked,
                                };
                                if (!checked) next.show_score_debug = false;
                                return { ...prev, system_shop_filters_settings: next };
                              })
                            }
                          >
                            {!!cfg.system_shop_filters_settings?.show_score && (
                              <SettingToggle
                                id="system_shop_show_score_debug"
                                title="Enable score debug (click to inspect)"
                                description="When enabled, users can click on the score chip to see the scoring formula breakdown and field values."
                                checked={!!cfg.system_shop_filters_settings?.show_score_debug}
                                indent
                                onChange={(checked) =>
                                  setCfg((prev) => ({
                                    ...prev,
                                    system_shop_filters_settings: {
                                      ...normalizeSystemShopFilterSettings(prev.system_shop_filters_settings),
                                      show_score_debug: checked,
                                    },
                                  }))
                                }
                              />
                            )}
                          </SettingToggle>
                          <SettingToggle
                            id="system_shop_enable_integrated_search"
                            title="Enable Integrated Search"
                            description="When enabled, the System Shop searches both AHRI records and your product catalog simultaneously,
                              showing results in side-by-side tabs. Disable to restrict the widget to AHRI-only searches."
                            checked={normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings).enable_integrated_search}
                            onChange={(checked) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_filters_settings: {
                                  ...normalizeSystemShopFilterSettings(prev.system_shop_filters_settings),
                                  enable_integrated_search: checked,
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card shop-filters-section mb-3">
                      <div className="card-body">
                        <div className="shop-filters-section-title mb-3">
                          <span className="shop-filters-section-icon"><CameraOutlined /></span>
                          <div>
                            <div>Image Search</div>
                            <div className="text-muted small fw-normal">
                              Let shoppers snap a nameplate photo instead of typing a model number.
                            </div>
                          </div>
                        </div>
                        <SettingToggle
                          id="system_shop_enable_image_search"
                          title="Enable Image Search"
                          description="When enabled, users can photograph a device nameplate and the widget will extract the model number
                            to pre-fill the search. Requires the image extraction API to be configured. Disable to hide the
                            camera icon and image upload option."
                          checked={normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings).enable_image_search}
                          onChange={(checked) =>
                            setCfg((prev) => ({
                              ...prev,
                              system_shop_filters_settings: {
                                ...normalizeSystemShopFilterSettings(prev.system_shop_filters_settings),
                                enable_image_search: checked,
                              },
                            }))
                          }
                        />
                        {normalizeSystemShopFilterSettings(cfg.system_shop_filters_settings).enable_image_search && (
                          <div className="mt-3 p-3 border rounded bg-light">
                            <div className="fw-semibold small mb-1">Progressive Trim Fallback</div>
                            <div className="form-text mb-3">
                              When no results are found for the extracted model number, the widget strips characters from the
                              right one at a time and retries the search until a result is found or the limits below are reached.
                            </div>
                            <div className="row g-3">
                              <div className="col-sm-4">
                                <label className="form-label small fw-semibold" htmlFor="image_search_max_trim">Max Trim</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  id="image_search_max_trim"
                                  min={0}
                                  max={10}
                                  value={cfg.image_search?.max_trim ?? 4}
                                  onChange={(e) => {
                                    const n = parseInt(e.target.value, 10);
                                    setCfg((prev) => ({ ...prev, image_search: { ...(prev.image_search || {}), max_trim: isNaN(n) ? 4 : Math.max(0, Math.min(n, 10)) } }));
                                  }}
                                />
                                <div className="form-text">
                                  Maximum characters to trim from the right of the model number (0–10). Default: 4.
                                </div>
                              </div>
                              <div className="col-sm-4">
                                <label className="form-label small fw-semibold" htmlFor="image_search_min_length">Min Length</label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  id="image_search_min_length"
                                  min={1}
                                  max={20}
                                  value={cfg.image_search?.min_length ?? 6}
                                  onChange={(e) => {
                                    const n = parseInt(e.target.value, 10);
                                    setCfg((prev) => ({ ...prev, image_search: { ...(prev.image_search || {}), min_length: isNaN(n) ? 6 : Math.max(1, Math.min(n, 20)) } }));
                                  }}
                                />
                                <div className="form-text">
                                  Stop trimming when the query reaches this length (1–20). Default: 6.
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="card shop-filters-section mb-3">
                      <div className="card-body">
                        <div className="shop-filters-section-title mb-3">
                          <span className="shop-filters-section-icon"><SearchOutlined /></span>
                          <div>
                            <div>Search Basics</div>
                            <div className="text-muted small fw-normal">
                              Turn search on, set the hint text, and control suggestions.
                            </div>
                          </div>
                        </div>
                        <div className="row g-3 mb-3">
                          <div className="col-md-6">
                            <div className="form-check form-switch mt-1">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="system_shop_search_tooltips"
                                checked={!!cfg.system_shop_search?.show_tooltips}
                                onChange={(e) =>
                                  setCfg((prev) => ({
                                    ...prev,
                                    system_shop_search: {
                                      ...normalizeSystemShopSearch(prev.system_shop_search),
                                      show_tooltips: e.target.checked,
                                    },
                                  }))
                                }
                              />
                              <label className="form-check-label" htmlFor="system_shop_search_tooltips">
                                Always show help text
                              </label>
                            </div>
                            <div className="form-text">
                              Turn on to keep all tooltips visible without hovering.
                            </div>
                          </div>
                        </div>
                        <div className="row g-3">
                          <div className="col-md-4">
                            <div className="form-check form-switch mt-1">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="system_shop_search_enabled"
                                checked={!!cfg.system_shop_search?.enabled}
                                onChange={(e) =>
                                  setCfg((prev) => ({
                                    ...prev,
                                    system_shop_search: {
                                      ...normalizeSystemShopSearch(prev.system_shop_search),
                                      enabled: e.target.checked,
                                    },
                                  }))
                                }
                              />
                              <label className="form-check-label" htmlFor="system_shop_search_enabled">
                                Show search bar
                                <InfoHint
                                  showAlways={showSearchTooltips}
                                  label="Technical: enabled. Values: on/off. Effect: shows or hides the search box in System Shop."
                                />
                              </label>
                            </div>
                          </div>
                          <div className="col-md-8">
                            <label className="form-label">
                              Search box hint
                              <InfoHint
                                showAlways={showSearchTooltips}
                                label="Technical: placeholder. Values: any text. Effect: hint shown inside the search box before typing."
                              />
                            </label>
                            <input
                              className="form-control"
                              value={cfg.system_shop_search?.placeholder || ''}
                              onChange={(e) =>
                                setCfg((prev) => ({
                                  ...prev,
                                  system_shop_search: {
                                    ...normalizeSystemShopSearch(prev.system_shop_search),
                                    placeholder: e.target.value,
                                  },
                                }))
                              }
                            />
                            <div className="form-text">
                              Text shown before users start typing.
                            </div>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">
                              Query type
                              <InfoHint
                                showAlways={showSearchTooltips}
                                label="Technical: query_type. Values: query type name. Effect: uses the selected query profile when searching."
                              />
                            </label>
                            <select
                              className="form-select"
                              value={cfg.system_shop_search?.query_type || ''}
                              onChange={(e) =>
                                setCfg((prev) => ({
                                  ...prev,
                                  system_shop_search: {
                                    ...normalizeSystemShopSearch(prev.system_shop_search),
                                    query_type: e.target.value,
                                  },
                                }))
                              }
                            >
                              <option value="">Default (no override)</option>
                              {(cfg.query_types || []).filter((entry) => entry?.target !== 'product').map((entry, idx) => {
                                const key = String(entry?.name || '').trim() || `row_${idx}`;
                                return (
                                  <option key={`system-shop-query-${key}`} value={entry?.name || ''}>
                                    {entry?.label || entry?.name || 'Unnamed'}
                                  </option>
                                );
                              })}
                            </select>
                            <div className="form-text">
                              Choose a saved query profile for System Shop searches.
                            </div>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">
                              Interaction default
                              <InfoHint
                                showAlways={showSearchTooltips}
                                label="Technical: interaction_mode. Values: filters or qa. Effect: controls how users interact with filter selection when System Shop opens."
                              />
                            </label>
                            <select
                              className="form-select"
                              value={cfg.system_shop_search?.interaction_mode || 'filters'}
                              onChange={(e) =>
                                setCfg((prev) => ({
                                  ...prev,
                                  system_shop_search: {
                                    ...normalizeSystemShopSearch(prev.system_shop_search),
                                    interaction_mode: e.target.value,
                                  },
                                }))
                              }
                            >
                              <option value="filters">Filters</option>
                              <option value="qa">Q&amp;A</option>
                            </select>
                            <div className="form-text">
                              Starting experience shown in System Shop.
                            </div>
                          </div>
                          <div className="col-md-8">
                            <label className="form-label">
                              Interaction options
                              <InfoHint
                                showAlways={showSearchTooltips}
                                label="Technical: interaction_modes. Values: filters and/or qa. Effect: controls which interaction modes users can switch between."
                              />
                            </label>
                            <div className="d-flex flex-wrap gap-3">
                              {[
                                { key: 'filters', label: 'Filters mode' },
                                { key: 'qa', label: 'Q&A mode' },
                              ].map((mode) => {
                                const current = normalizeSystemShopSearch(cfg.system_shop_search);
                                const checked = (current.interaction_modes || []).includes(mode.key);
                                return (
                                  <div className="form-check" key={`system-shop-interaction-${mode.key}`}>
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`system_shop_search_interaction_${mode.key}`}
                                      checked={checked}
                                      onChange={(e) =>
                                        setCfg((prev) => {
                                          const normalized = normalizeSystemShopSearch(prev.system_shop_search);
                                          let nextModes = Array.isArray(normalized.interaction_modes)
                                            ? [...normalized.interaction_modes]
                                            : ['filters', 'qa'];
                                          if (e.target.checked) {
                                            if (!nextModes.includes(mode.key)) nextModes.push(mode.key);
                                          } else {
                                            nextModes = nextModes.filter((entry) => entry !== mode.key);
                                          }
                                          if (!nextModes.length) nextModes = ['filters'];
                                          const nextDefault = nextModes.includes(normalized.interaction_mode)
                                            ? normalized.interaction_mode
                                            : nextModes[0];
                                          return {
                                            ...prev,
                                            system_shop_search: {
                                              ...normalized,
                                              interaction_modes: nextModes,
                                              interaction_mode: nextDefault,
                                            },
                                          };
                                        })
                                      }
                                    />
                                    <label className="form-check-label" htmlFor={`system_shop_search_interaction_${mode.key}`}>
                                      {mode.label}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="form-text">
                              Enable one or both interaction styles. Users can switch only between enabled modes.
                            </div>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">
                              Allow customer to choose mode
                              <InfoHint
                                showAlways={showSearchTooltips}
                                label="Technical: allow_mode_switch. Values: true or false. Effect: when enabled and multiple modes are active, users can switch between Filters and Q&A in the widget."
                              />
                            </label>
                            <div className="form-check mt-1">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="system_shop_search_allow_mode_switch"
                                checked={!!normalizeSystemShopSearch(cfg.system_shop_search).allow_mode_switch}
                                onChange={(e) =>
                                  setCfg((prev) => ({
                                    ...prev,
                                    system_shop_search: {
                                      ...normalizeSystemShopSearch(prev.system_shop_search),
                                      allow_mode_switch: e.target.checked,
                                    },
                                  }))
                                }
                              />
                              <label className="form-check-label" htmlFor="system_shop_search_allow_mode_switch">
                                Show mode switch in widget
                              </label>
                            </div>
                            <div className="form-text">
                              If off, widget locks to the default interaction mode.
                            </div>
                          </div>
                          <div className="col-md-8">
                            <label className="form-label">
                              Typeahead sources
                              <InfoHint
                                showAlways={showSearchTooltips}
                                label="Technical: suggest_fields (typeahead fields). Values: Solr field names. Effect: suggestions are pulled from these fields."
                              />
                            </label>
                            <textarea
                              className="form-control"
                              rows={3}
                              value={(cfg.system_shop_search?.suggest_fields || []).join('\n')}
                              onChange={(e) =>
                                setCfg((prev) => ({
                                  ...prev,
                                  system_shop_search: {
                                    ...normalizeSystemShopSearch(prev.system_shop_search),
                                    suggest_fields: e.target.value
                                      .split(/[\n,]+/)
                                      .map((field) => field.trim())
                                      .filter(Boolean),
                                  },
                                }))
                              }
                            />
                            <div className="form-text">
                              Fields used to generate suggestion dropdowns.
                            </div>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">
                              Suggestion count
                              <InfoHint
                                showAlways={showSearchTooltips}
                                label="Technical: suggest_limit (max suggestions). Values: integer >= 1. Effect: limits the number of typeahead suggestions."
                              />
                            </label>
                            <input
                              type="number"
                              min={1}
                              className="form-control"
                              value={cfg.system_shop_search?.suggest_limit ?? 8}
                              onChange={(e) =>
                                setCfg((prev) => ({
                                  ...prev,
                                  system_shop_search: {
                                    ...normalizeSystemShopSearch(prev.system_shop_search),
                                    suggest_limit: Number(e.target.value),
                                  },
                                }))
                              }
                            />
                            <div className="form-text">
                              Maximum number of suggestions shown.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="shop-filters-save-bar">
                      <div className="shop-filters-save-bar-inner">
                        <span className="shop-filters-save-hint d-none d-sm-inline">
                          Save, then refresh System Shop to see changes.
                        </span>
                        <Button
                          type="primary"
                          shape="round"
                          icon={savingSystemShopFilters ? <LoadingOutlined /> : <SaveOutlined />}
                          loading={savingSystemShopFilters}
                          onClick={saveSystemShopFilters}
                        >
                          Save Filters
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {systemShopTab === 'tuner' && (
                  <>
                    <div className="card mb-3">
                      <div className="card-body">
                        <h6 className="mb-1">Search Tuner</h6>
                        <div className="text-muted small mb-3">
                          Advanced controls for search accuracy and ranking.
                        </div>
                        <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">
                            Search in these fields
                            <InfoHint
                              showAlways={showSearchTooltips}
                              label="Technical: qf (edismax query fields). Values: Solr field names with optional boosts (example: search_text^5 rows_outdoormodelnumber^10). Effect: higher boosts rank matches higher."
                            />
                          </label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={(cfg.system_shop_search?.query_fields || []).join('\n')}
                            onChange={(e) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_search: {
                                  ...normalizeSystemShopSearch(prev.system_shop_search),
                                  query_fields: e.target.value
                                    .split(/[\n,]+/)
                                    .map((field) => field.trim())
                                    .filter(Boolean),
                                },
                              }))
                            }
                          />
                          <div className="form-text">
                            One per line. You can boost priority with ^ (example: search_text^5).
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">
                            Exact phrase boost
                            <InfoHint
                              showAlways={showSearchTooltips}
                              label="Technical: pf (edismax phrase fields). Values: Solr field names with optional boosts. Effect: boosts results when words appear together in order."
                            />
                          </label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={(cfg.system_shop_search?.phrase_fields || []).join('\n')}
                            onChange={(e) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_search: {
                                  ...normalizeSystemShopSearch(prev.system_shop_search),
                                  phrase_fields: e.target.value
                                    .split(/[\n,]+/)
                                    .map((field) => field.trim())
                                    .filter(Boolean),
                                },
                              }))
                            }
                          />
                          <div className="form-text">
                            Adds extra boost when words appear next to each other.
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">
                            Minimum match
                            <InfoHint
                              showAlways={showSearchTooltips}
                              label="Technical: mm (minimum should match). Values: number or pattern (example: 2<75%). Effect: controls how many query words must match; lower values return more results."
                            />
                          </label>
                          <input
                            className="form-control"
                            value={cfg.system_shop_search?.min_match || ''}
                            onChange={(e) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_search: {
                                  ...normalizeSystemShopSearch(prev.system_shop_search),
                                  min_match: e.target.value,
                                },
                              }))
                            }
                          />
                          <div className="form-text">
                            Controls how many words must match.
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">
                            Match logic
                            <InfoHint
                              showAlways={showSearchTooltips}
                              label="Technical: q.op (default operator). Values: AND or OR. Effect: AND is stricter and more precise; OR is broader."
                            />
                          </label>
                          <select
                            className="form-select"
                            value={String(cfg.system_shop_search?.operator ?? 'AND')}
                            onChange={(e) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_search: {
                                  ...normalizeSystemShopSearch(prev.system_shop_search),
                                  operator: e.target.value,
                                },
                              }))
                            }
                          >
                            <option value="AND">AND</option>
                            <option value="OR">OR</option>
                            <option value="">Exclude (omit q.op)</option>
                          </select>
                          <div className="form-text">
                            AND is stricter, OR is broader. Exclude omits q.op.
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">
                            Blend score
                            <InfoHint
                              showAlways={showSearchTooltips}
                              label="Technical: tie (edismax tie-breaker). Values: 0.0 to 1.0. Effect: higher values blend scores from multiple fields more."
                            />
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            className="form-control"
                            value={cfg.system_shop_search?.tie ?? 0}
                            onChange={(e) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_search: {
                                  ...normalizeSystemShopSearch(prev.system_shop_search),
                                  tie: Number(e.target.value),
                                },
                              }))
                            }
                          />
                          <div className="form-text">
                            Blends scores from multiple fields.
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">
                            Near match distance
                            <InfoHint
                              showAlways={showSearchTooltips}
                              label="Technical: qs (query slop). Values: integer (0 or higher). Effect: allows word reordering/spacing in non-phrase matches."
                            />
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            value={cfg.system_shop_search?.qs ?? 1}
                            onChange={(e) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_search: {
                                  ...normalizeSystemShopSearch(prev.system_shop_search),
                                  qs: Number(e.target.value),
                                },
                              }))
                            }
                          />
                          <div className="form-text">
                            Allows near matches in word order.
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">
                            Phrase distance
                            <InfoHint
                              showAlways={showSearchTooltips}
                              label="Technical: ps (phrase slop). Values: integer (0 or higher). Effect: allows gaps within phrases; higher values are more forgiving."
                            />
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            value={cfg.system_shop_search?.ps ?? 2}
                            onChange={(e) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_search: {
                                  ...normalizeSystemShopSearch(prev.system_shop_search),
                                  ps: Number(e.target.value),
                                },
                              }))
                            }
                          />
                          <div className="form-text">
                            Allows small gaps inside phrases.
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">
                            Priority rules
                            <InfoHint
                              showAlways={showSearchTooltips}
                              label="Technical: bq (boost queries). Values: one Solr query per line (example: rows_outdoorbrandname:Trane^5). Effect: boosts results that match these rules."
                            />
                          </label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={(cfg.system_shop_search?.bq || []).join('\n')}
                            onChange={(e) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_search: {
                                  ...normalizeSystemShopSearch(prev.system_shop_search),
                                  bq: e.target.value
                                    .split(/[\n,]+/)
                                    .map((field) => field.trim())
                                    .filter(Boolean),
                                },
                              }))
                            }
                          />
                          <div className="form-text">
                            Adds boosts for specific values (one rule per line).
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">
                            Ranking formula
                            <InfoHint
                              showAlways={showSearchTooltips}
                              label="Technical: bf (boost functions). Values: Solr function queries (example: recip(ms(NOW,updated_at),3.16e-11,1,1)). Effect: adjusts ranking based on numeric formulas."
                            />
                          </label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={(cfg.system_shop_search?.bf || []).join('\n')}
                            onChange={(e) =>
                              setCfg((prev) => ({
                                ...prev,
                                system_shop_search: {
                                  ...normalizeSystemShopSearch(prev.system_shop_search),
                                  bf: e.target.value
                                    .split(/[\n,]+/)
                                    .map((field) => field.trim())
                                    .filter(Boolean),
                                },
                              }))
                            }
                          />
                          <div className="form-text">
                            Optional formulas for ranking (advanced).
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  </>
                )}

                {systemShopTab === 'query_types' && (
                  <>
                    <div className="card mb-3">
                      <div className="card-body">
                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
                          <div>
                            <h6 className="mb-1">Search Types</h6>
                            <div className="text-muted small">
                              Manage reusable query profiles for AHRI and Product search.
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => openQueryTypePanel('add')}
                          >
                            Add New
                          </button>
                        </div>
                        <ul className="nav nav-tabs mb-3">
                          {[
                            { key: 'all', label: 'All' },
                            { key: 'any', label: 'Any' },
                            { key: 'solr', label: 'Solr' },
                            { key: 'opensearch', label: 'OpenSearch' },
                            { key: 'shadow', label: 'Shadow' },
                          ].map((tab) => {
                            const count = tab.key === 'all'
                              ? (Array.isArray(cfg.query_types) ? cfg.query_types.length : 0)
                              : (Array.isArray(cfg.query_types)
                                ? cfg.query_types.filter((row) =>
                                    String(row?.search_engine || 'any').toLowerCase() === tab.key
                                  ).length
                                : 0);
                            return (
                              <li className="nav-item" key={`query-type-engine-tab-${tab.key}`}>
                                <button
                                  type="button"
                                  className={`nav-link ${queryTypeEngineTab === tab.key ? 'active' : ''}`}
                                  onClick={() => setQueryTypeEngineTab(tab.key)}
                                >
                                  {tab.label} ({count})
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                        <div className="table-responsive">
                          <table className="table table-sm align-middle">
                            <thead>
                              <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Label</th>
                                <th>Target</th>
                                <th>Engine</th>
                                <th>Parser</th>
                                <th style={{ width: 220 }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {queryTypeRows.map((entry) => {
                                const idx = (cfg.query_types || []).findIndex((row) => row === entry);
                                return (
                                <tr key={`query-row-${entry?.id || idx}`}>
                                  <td className="font-monospace small">{entry?.id || '-'}</td>
                                  <td className="fw-semibold">{entry?.name || '-'}</td>
                                  <td>{entry?.label || '-'}</td>
                                  <td className="text-uppercase">{entry?.target || 'ahri'}</td>
                                  <td className="text-uppercase">{entry?.search_engine || 'any'}</td>
                                  <td>{entry?.def_type || 'edismax'}</td>
                                  <td>
                                    <div className="btn-group btn-group-sm">
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => openQueryTypePanel('edit', idx)}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() => cloneQueryType(idx)}
                                      >
                                        Copy
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                          const target = entry?.target || 'ahri';
                                          setQueryTypeTest((prev) => ({
                                            ...resetQueryTypeTestOutputs(prev),
                                            target,
                                            name: entry?.name || '',
                                            input: prev.input || '',
                                          }));
                                          openQueryTypePanel('test', idx);
                                        }}
                                      >
                                        Test
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={() => removeQueryType(idx)}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )})}
                              {queryTypeRows.length === 0 && (
                                <tr>
                                  <td colSpan="7" className="text-muted text-center">
                                    No search types for this engine tab.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {(queryTypePanelMode === 'add' || queryTypePanelMode === 'edit') && (
                      <Drawer
                        title={queryTypePanelMode === 'add' ? 'Add Search Type' : 'Edit Search Type'}
                        open={queryTypePanelMode === 'add' || queryTypePanelMode === 'edit'}
                        onClose={() => setQueryTypePanelMode(null)}
                        width={queryTypeDrawerWidth}
                        placement="right"
                      >
                        <div className="schema-drawer">
                          <div
                            className="schema-resize-handle"
                            onMouseDown={(event) => {
                              queryTypeResizeRef.current = { startX: event.clientX, startWidth: queryTypeDrawerWidth };
                              setQueryTypeDrawerResizing(true);
                            }}
                          />
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">
                              {queryTypePanelMode === 'add' ? 'Add Search Type' : 'Edit Search Type'}
                            </h6>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => setQueryTypePanelMode(null)}
                            >
                              Close
                            </button>
                          </div>
                          <div className="row g-3">
                            <div className="col-12">
                              <label className="form-label">ID</label>
                              <input
                                className="form-control"
                                value={queryTypeDraft?.id || ''}
                                readOnly
                                placeholder="f47ac10b-58cc-4372-a567-0e02b2c3d479"
                              />
                              <div className="form-text">Auto-generated stable identifier used by flows.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Target</label>
                              <select
                                className="form-select"
                                value={queryTypeDraft?.target || 'ahri'}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, target: e.target.value }))}
                              >
                                <option value="ahri">AHRI</option>
                                <option value="product">Product</option>
                              </select>
                              <div className="form-text">Choose where this search type applies.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Search engine</label>
                              <select
                                className="form-select"
                                value={queryTypeDraft?.search_engine || 'any'}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, search_engine: e.target.value }))}
                              >
                                <option value="any">Any</option>
                                <option value="solr">Solr</option>
                                <option value="opensearch">OpenSearch</option>
                                <option value="shadow">Shadow</option>
                              </select>
                              <div className="form-text">Limit this type to a specific engine.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Unique name</label>
                              <input
                                className="form-control"
                                value={queryTypeDraft?.name || ''}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, name: e.target.value }))}
                              />
                              <div className="form-text">Used to reference this profile.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Label</label>
                              <input
                                className="form-control"
                                value={queryTypeDraft?.label || ''}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, label: e.target.value }))}
                              />
                              <div className="form-text">Shown in dropdowns.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Query parser (defType)</label>
                              <input
                                className="form-control"
                                value={queryTypeDraft?.def_type || ''}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, def_type: e.target.value }))}
                              />
                              <div className="form-text">Example: edismax, lucene.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">fl</label>
                              <input
                                className="form-control"
                                value={queryTypeDraft?.fl || ''}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, fl: e.target.value }))}
                              />
                              <div className="form-text">Comma-separated fields to return.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">rows</label>
                              <input
                                type="number"
                                className="form-control"
                                value={queryTypeDraft?.rows ?? ''}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, rows: e.target.value }))}
                              />
                              <div className="form-text">Overrides page size.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">wt</label>
                              <input
                                className="form-control"
                                value={queryTypeDraft?.wt || ''}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, wt: e.target.value }))}
                              />
                              <div className="form-text">Solr output format.</div>
                            </div>
                            <div className="col-12">
                              <div className="form-check mt-4">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="query-draft-debug"
                                  checked={!!queryTypeDraft?.debug}
                                  onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, debug: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="query-draft-debug">
                                  debug
                                </label>
                              </div>
                              <div className="form-text">Adds Solr debug info.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Query fields (qf)</label>
                              <textarea
                                className="form-control"
                                rows={3}
                                value={(queryTypeDraft?.query_fields || []).join('\n')}
                                onChange={(e) =>
                                  setQueryTypeDraft((prev) => ({
                                    ...prev,
                                    query_fields: e.target.value
                                      .split(/[\n,]+/)
                                      .map((field) => field.trim())
                                      .filter(Boolean),
                                  }))
                                }
                              />
                              <div className="form-text">One field per line, boosts allowed.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Phrase fields (pf)</label>
                              <textarea
                                className="form-control"
                                rows={3}
                                value={(queryTypeDraft?.phrase_fields || []).join('\n')}
                                onChange={(e) =>
                                  setQueryTypeDraft((prev) => ({
                                    ...prev,
                                    phrase_fields: e.target.value
                                      .split(/[\n,]+/)
                                      .map((field) => field.trim())
                                      .filter(Boolean),
                                  }))
                                }
                              />
                              <div className="form-text">Phrase boost fields.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Min match (mm)</label>
                              <input
                                className="form-control"
                                value={queryTypeDraft?.min_match || ''}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, min_match: e.target.value }))}
                              />
                              <div className="form-text">Example: 2&lt;75%.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Operator</label>
                              <select
                                className="form-select"
                                value={String(queryTypeDraft?.operator ?? 'AND')}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, operator: e.target.value }))}
                              >
                                <option value="AND">AND</option>
                                <option value="OR">OR</option>
                                <option value="">Exclude (omit q.op)</option>
                              </select>
                              <div className="form-text">Default boolean operator.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">Tie</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={queryTypeDraft?.tie ?? 0}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, tie: Number(e.target.value) }))}
                              />
                              <div className="form-text">Blend scores.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">QS</label>
                              <input
                                type="number"
                                className="form-control"
                                value={queryTypeDraft?.qs ?? 1}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, qs: Number(e.target.value) }))}
                              />
                              <div className="form-text">Query slop.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">PS</label>
                              <input
                                type="number"
                                className="form-control"
                                value={queryTypeDraft?.ps ?? 2}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, ps: Number(e.target.value) }))}
                              />
                              <div className="form-text">Phrase slop.</div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">PS2</label>
                              <input
                                type="number"
                                className="form-control"
                                value={queryTypeDraft?.ps2 ?? ''}
                                onChange={(e) => setQueryTypeDraft((prev) => ({ ...prev, ps2: Number(e.target.value) }))}
                              />
                              <div className="form-text">Alternative phrase slop.</div>
                            </div>
                          </div>

                          <details className="mt-3">
                            <summary className="fw-semibold">Advanced options</summary>
                            <div className="row g-3 mt-1">
                              <div className="col-12">
                                <label className="form-label">Boost queries (bq)</label>
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  value={(queryTypeDraft?.bq || []).join('\n')}
                                  onChange={(e) =>
                                    setQueryTypeDraft((prev) => ({
                                      ...prev,
                                      bq: e.target.value
                                        .split(/[\n,]+/)
                                        .map((field) => field.trim())
                                        .filter(Boolean),
                                    }))
                                  }
                                />
                                <div className="form-text">One Solr query per line.</div>
                              </div>
                              <div className="col-12">
                                <label className="form-label">Boost functions (bf)</label>
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  value={(queryTypeDraft?.bf || []).join('\n')}
                                  onChange={(e) =>
                                    setQueryTypeDraft((prev) => ({
                                      ...prev,
                                      bf: e.target.value
                                        .split(/[\n,]+/)
                                        .map((field) => field.trim())
                                        .filter(Boolean),
                                    }))
                                  }
                                />
                                <div className="form-text">Solr function queries.</div>
                              </div>
                              <div className="col-12">
                                <div className="form-check mt-4">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="query-draft-raw"
                                    checked={!!queryTypeDraft?.use_raw_q}
                                    onChange={(e) =>
                                      setQueryTypeDraft((prev) => ({ ...prev, use_raw_q: e.target.checked }))
                                    }
                                  />
                                  <label className="form-check-label" htmlFor="query-draft-raw">
                                    Use raw <code>q</code> input
                                  </label>
                                </div>
                                <div className="form-text">Send the search string directly to Solr.</div>
                              </div>
                            </div>
                          </details>

                          <div className="d-flex justify-content-end gap-2 mt-3">
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => setQueryTypePanelMode(null)}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={saveQueryTypeDraft}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      </Drawer>
                    )}

                    {queryTypePanelMode === 'test' && (
                      <div className="card mb-3">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Test Search Type</h6>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => setQueryTypePanelMode(null)}
                            >
                              Close
                            </button>
                          </div>
                          <div className="row g-3 align-items-end">
                            <div className="col-12">
                              <label className="form-label">Search text</label>
                              <div className="d-flex gap-2">
                                <input
                                  className="form-control"
                                  value={queryTypeTest.input}
                                  onChange={(e) =>
                                    setQueryTypeTest((prev) => ({ ...prev, input: e.target.value }))
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      runQueryTypeTest(1);
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={() => runQueryTypeTest(1)}
                                  disabled={queryTypeTest.loading}
                                >
                                  {queryTypeTest.loading ? 'Testing...' : 'Test Search'}
                                </button>
                              </div>
                              <div className="form-text">Press Enter to run the test.</div>
                            </div>
                            <div className="col-12 col-md-4">
                              <label className="form-label">Customer ID (optional)</label>
                              <input
                                className="form-control"
                                value={queryTypeTest.customer_id_erp || ''}
                                onChange={(e) =>
                                  setQueryTypeTest((prev) => ({
                                    ...prev,
                                    customer_id_erp: e.target.value,
                                  }))
                                }
                                placeholder="e.g. 104048"
                              />
                              <div className="form-text">
                                Used to resolve <code>{'<customer_id>'}</code> placeholders (e.g. customer part number fields).
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="d-flex flex-wrap align-items-center gap-3 text-muted small">
                                <span>
                                  Target:
                                  <span className="text-dark fw-semibold ms-1">
                                    {queryTypeTest.target === 'product' ? 'Product' : 'AHRI'}
                                  </span>
                                </span>
                                <span className="text-muted">•</span>
                                <span>
                                  Search type:
                                  <span className="text-dark fw-semibold ms-1">
                                    {(() => {
                                      const types = (cfg.query_types || []).filter((entry) =>
                                        queryTypeTest.target === 'product'
                                          ? entry?.target === 'product'
                                          : entry?.target !== 'product'
                                      );
                                      const selected = types.find((entry) => entry?.name === queryTypeTest.name);
                                      return selected?.label || selected?.name || 'Default';
                                    })()}
                                  </span>
                                </span>
                                <span className="text-muted">• Search endpoint used for this test.</span>
                              </div>
                            </div>
                            {queryTypeTest.target === 'product' && (
                              <div className="col-12">
                                <div className="row g-2">
                                  <div className="col-12 col-md-4">
                                    <label className="form-label">Search engine</label>
                                    <select
                                      className="form-select"
                                      value={queryTypeTest.search_engine || 'solr'}
                                      onChange={(e) =>
                                        setQueryTypeTest((prev) => ({
                                          ...prev,
                                          search_engine: e.target.value === 'opensearch' ? 'opensearch' : 'solr',
                                        }))
                                      }
                                    >
                                      <option value="solr">Solr</option>
                                      <option value="opensearch">OpenSearch</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className="col-12">
                              <label className="form-label">
                                {(queryTypeTest.target === 'product' && (queryTypeTest.search_engine || 'solr') === 'opensearch')
                                  ? 'Constructed OpenSearch request'
                                  : 'Constructed Solr params'}
                              </label>
                              <textarea
                                className="form-control"
                                rows={5}
                                readOnly
                                value={(() => {
                                  if (queryTypeTest.target === 'product' && (queryTypeTest.search_engine || 'solr') === 'opensearch') {
                                    const input = (queryTypeTest.input || '').trim();
                                    return JSON.stringify(
                                      {
                                        query: input || '*',
                                        page: queryTypeTest.page ?? 1,
                                        page_size: queryTypeTest.page_size ?? 10,
                                        engine: 'opensearch',
                                      },
                                      null,
                                      2
                                    );
                                  }
                                  const target = queryTypeTest.target;
                                  const types = (cfg.query_types || []).filter((entry) =>
                                    target === 'product' ? entry?.target === 'product' : entry?.target !== 'product'
                                  );
                                  const selected = types.find((entry) => entry?.name === queryTypeTest.name) || {};
                                  const useRaw = selected?.use_raw_q ?? true;
                                  const input = (queryTypeTest.input || '').trim();
                                  if (!input) return '';
                                  const pageSize = queryTypeTest.page_size ?? 10;
                                  const page = queryTypeTest.page ?? 1;
                                  const effectiveRows =
                                    selected?.rows !== undefined && selected?.rows !== null && selected?.rows !== ''
                                      ? Number(selected.rows)
                                      : pageSize;
                                  const params = {};
                                  params.q = useRaw ? input : `search_text:(${input})`;
                                  params.defType = selected?.def_type || 'edismax';
                                  if (Array.isArray(selected?.query_fields) && selected.query_fields.length) {
                                    params.qf = selected.query_fields.join(' ');
                                  }
                                  if (Array.isArray(selected?.phrase_fields) && selected.phrase_fields.length) {
                                    params.pf = selected.phrase_fields.join(' ');
                                  }
                                  if (selected?.min_match) params.mm = selected.min_match;
                                  if (selected?.operator) params['q.op'] = selected.operator;
                                  const tieValue = Number(selected?.tie);
                                  if (Number.isFinite(tieValue) && tieValue > 0) params.tie = tieValue;
                                  const qsValue = Number(selected?.qs);
                                  if (Number.isFinite(qsValue) && qsValue > 0) params.qs = Math.trunc(qsValue);
                                  const psValue = Number(selected?.ps);
                                  if (Number.isFinite(psValue) && psValue > 0) params.ps = Math.trunc(psValue);
                                  const ps2Value = Number(selected?.ps2);
                                  if (Number.isFinite(ps2Value) && ps2Value > 0) params.ps2 = Math.trunc(ps2Value);
                                  if (Array.isArray(selected?.bq) && selected.bq.length) params.bq = selected.bq.join(' ');
                                  if (Array.isArray(selected?.bf) && selected.bf.length) params.bf = selected.bf.join(' ');
                                  if (selected?.fl) params.fl = selected.fl;
                                  if (selected?.wt) params.wt = selected.wt;
                                  if (selected?.rows !== undefined && selected?.rows !== null && selected?.rows !== '') params.rows = selected.rows;
                                  params.start = Math.max(0, (page - 1) * effectiveRows);
                                  params.rows = effectiveRows;
                                  if (selected?.debug) params.debug = 'true';
                                  return JSON.stringify(params, null, 2);
                                })()}
                              />
                              <div className="form-text">
                                {(queryTypeTest.target === 'product' && (queryTypeTest.search_engine || 'solr') === 'opensearch')
                                  ? 'OpenSearch payload summary for this test.'
                                  : 'Effective Solr params for this test (0-valued tuning params and excluded q.op are omitted).'}
                              </div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">
                                {(queryTypeTest.target === 'product' && (queryTypeTest.search_engine || 'solr') === 'opensearch')
                                  ? 'Actual OpenSearch request sent'
                                  : 'Actual Solr request sent'}
                              </label>
                              <textarea
                                className="form-control"
                                rows={6}
                                readOnly
                                value={
                                  queryTypeTest.search_request
                                    ? JSON.stringify(queryTypeTest.search_request, null, 2)
                                    : ''
                                }
                              />
                              <div className="form-text">
                                Exact request details from the test response.
                              </div>
                            </div>
                          </div>
                          {queryTypeTest.successMessage && (
                            <div className="alert alert-success mt-3">{queryTypeTest.successMessage}</div>
                          )}
                          {queryTypeTest.error && (
                            <div className="alert alert-danger mt-3">{queryTypeTest.error}</div>
                          )}
                          <div className="mt-3 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2 small text-muted">
                            <span>
                              Total matches: <span className="fw-semibold">{queryTypeTest.total || 0}</span>
                            </span>
                            <div className="d-flex flex-wrap align-items-center gap-3">
                              <div className="btn-group btn-group-sm" role="group" aria-label="Field view mode">
                                <button
                                  type="button"
                                  className={`btn ${(queryTypeTest.fieldsViewMode || 'formatted') === 'formatted' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  onClick={() => setQueryTypeTest((prev) => ({ ...prev, fieldsViewMode: 'formatted' }))}
                                >
                                  Formatted
                                </button>
                                <button
                                  type="button"
                                  className={`btn ${queryTypeTest.fieldsViewMode === 'raw' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  onClick={() => setQueryTypeTest((prev) => ({ ...prev, fieldsViewMode: 'raw' }))}
                                >
                                  Raw Solr doc
                                </button>
                              </div>
                              <div className="form-check mb-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="query-test-expand-all"
                                  checked={!!queryTypeTest.expandAllFields}
                                  onChange={(e) =>
                                    setQueryTypeTest((prev) => ({ ...prev, expandAllFields: e.target.checked }))
                                  }
                                />
                                <label className="form-check-label" htmlFor="query-test-expand-all">
                                  Expand all fields
                                </label>
                              </div>
                            </div>
                          </div>
                          {queryTypeTest.results.length > 0 && (() => {
                            const rows = queryTypeTest.results || [];
                            const page = queryTypeTest.page || 1;
                            const pageSize = queryTypeTest.page_size || 10;
                            const totalPages = Math.max(1, Math.ceil((queryTypeTest.total || 0) / pageSize));
                            const isProduct = queryTypeTest.target === 'product';
                            const pick = (doc, keys) => {
                              for (const key of keys) {
                                const raw = doc?.[key];
                                const val = Array.isArray(raw) ? raw[0] : raw;
                                if (val !== null && val !== undefined && String(val).trim() !== '') {
                                  return val;
                                }
                              }
                              return '';
                            };
                            const parseJson = (value) => {
                              if (!value) return null;
                              if (typeof value === 'object') return value;
                              try {
                                return JSON.parse(value);
                              } catch {
                                return null;
                              }
                            };
                            const fieldsViewMode = queryTypeTest.fieldsViewMode || 'formatted';
                            const copyRawDoc = (doc) => {
                              const text = JSON.stringify(doc, null, 2);
                              if (navigator?.clipboard?.writeText) {
                                navigator.clipboard.writeText(text).catch(() => {});
                              }
                            };
                            const renderRawDoc = (doc, keyPrefix) => (
                              <div className="mt-2">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span className="text-muted small">Raw Solr document</span>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm py-0 px-2"
                                    style={{ fontSize: '0.7rem' }}
                                    onClick={() => copyRawDoc(doc)}
                                  >
                                    Copy JSON
                                  </button>
                                </div>
                                <pre
                                  key={`${keyPrefix}-raw`}
                                  className="border rounded p-2 bg-dark text-light mb-0"
                                  style={{ fontSize: '0.72rem', maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                >
                                  {JSON.stringify(doc, null, 2)}
                                </pre>
                              </div>
                            );
                            return (
                              <>
                                <div className="row g-3 mt-2">
                                  {rows.map((doc, idx) => {
                                    if (isProduct) {
                                      const title = pick(doc, ['name', 'productName', 'title']) || 'Unnamed product';
                                      const code = pick(doc, ['productCode', 'product_code', 'productId', 'id']);
                                      const mfr = pick(doc, ['manufacturerName', 'manufacturer_name', 'mfrName']);
                                      const part = pick(doc, ['manufacturerPartNumber', 'manufacturer_part_number', 'mfrPartNumber', 'partnumber']);
                                      const category = pick(doc, ['category']);
                                      const status = pick(doc, ['active', 'status']);
                                      const image = pick(doc, ['imageUrl', 'image_url', 'image', 'thumbnailUrl', 'thumb']);
                                      const attrs = parseJson(doc?.attributesJson);
                                      const attrKeys = attrs ? Object.keys(attrs) : [];
                                      const allEntries = Object.entries(doc || {});
                                      const formatValue = (value) => {
                                        if (value === null || value === undefined || value === '') return '—';
                                        if (Array.isArray(value)) {
                                          return value.filter((v) => v !== null && v !== undefined && v !== '').join(', ') || '—';
                                        }
                                        if (typeof value === 'object') {
                                          try {
                                            return JSON.stringify(value);
                                          } catch {
                                            return String(value);
                                          }
                                        }
                                        return String(value);
                                      };
                                      return (
                                        <div key={`test-card-${idx}`} className="col-12 col-md-6 col-xl-4">
                                          <div className="card h-100" style={{ fontSize: '0.85rem' }}>
                                            <div className="card-body p-2">
                                              <div className="d-flex gap-2 align-items-start">
                                                <div
                                                  className="bg-light d-flex align-items-center justify-content-center flex-shrink-0"
                                                  style={{ width: 96, height: 96 }}
                                                >
                                                  {image ? (
                                                    <img
                                                      src={image}
                                                      alt={title}
                                                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                    />
                                                  ) : (
                                                    <div className="text-muted small">No image</div>
                                                  )}
                                                </div>
                                                <div className="d-flex flex-column gap-1 flex-grow-1">
                                                  <div className="fw-semibold">{title}</div>
                                                  {code && <div className="text-muted small">Code: {code}</div>}
                                                  {(mfr || part) && (
                                                    <div className="text-muted small">
                                                      {mfr && <span>{mfr}</span>}
                                                      {mfr && part && <span> • </span>}
                                                      {part && <span>Part: {part}</span>}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="d-flex flex-wrap gap-2 mt-2">
                                                {category && (
                                                  <span className="badge bg-light text-dark border">
                                                    {category}
                                                  </span>
                                                )}
                                                {status !== '' && status !== null && status !== undefined && (
                                                  <span className="badge bg-light text-dark border">
                                                    {String(status) === '1' || String(status).toLowerCase() === 'true' ? 'Active' : String(status)}
                                                  </span>
                                                )}
                                                {attrKeys.length > 0 && (
                                                  <span className="badge bg-light text-dark border">
                                                    {attrKeys.length} attrs
                                                  </span>
                                                )}
                                              </div>
                                              <details className="mt-2" open={!!queryTypeTest.expandAllFields}>
                                                <summary className="text-muted small">
                                                  All fields {fieldsViewMode === 'raw' ? '(raw)' : '(formatted)'}
                                                </summary>
                                                {fieldsViewMode === 'raw' ? (
                                                  renderRawDoc(doc, `test-card-${idx}`)
                                                ) : (
                                                  <div className="mt-2">
                                                    {allEntries.length === 0 ? (
                                                      <div className="text-muted small">No fields returned.</div>
                                                    ) : (
                                                      <div className="border rounded p-2 bg-light">
                                                        {allEntries.map(([key, value]) => (
                                                          <div
                                                            key={key}
                                                            className="d-flex justify-content-between gap-2 small border-bottom py-1"
                                                          >
                                                            <span className="text-muted" style={{ wordBreak: 'break-word' }}>
                                                              {key}
                                                            </span>
                                                            <span className="text-end" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                              {formatValue(value)}
                                                            </span>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                              </details>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }
                                    const ahriId = pick(doc, ['rows_referenceid', 'referenceId', 'ahri_id', 'id']);
                                    const program = pick(doc, ['rows_programname', 'programName']);
                                    const outdoorBrand = pick(doc, ['rows_outdoorbrandname', 'outdoorBrandName', 'outdoor_brand']);
                                    const indoorBrand = pick(doc, ['rows_indoorbrandname', 'indoorBrandName', 'indoor_brand']);
                                    const outdoorModel = pick(doc, ['rows_outdoormodelnumber', 'outdoorModelNumber', 'outdoor_product_code']);
                                    const indoorModel = pick(doc, ['rows_indoormodelnumber', 'indoorModelNumber', 'indoor_product_code']);
                                    const furnaceModel = pick(doc, ['rows_furnacemodelnumber', 'furnaceModelNumber', 'furnace_product_code']);
                                    const status = pick(doc, ['rows_modelstatus', 'modelStatus']);
                                    // Variant-mode fields (doc_type === 'ahri_system_variant') -- see
                                    // _build_variant_solr_doc in backend/api/ahri.py.
                                    const seer2 = pick(doc, ['prodattr_s_seer2', 'seer2']);
                                    const hspf2 = pick(doc, ['prodattr_s_hspf2', 'hspf2']);
                                    const eer2 = pick(doc, ['prodattr_s_eer2', 'eer2']);
                                    const tonnage = pick(doc, ['prodattr_s_tonnage', 'tonnage']);
                                    const matchScore = pick(doc, ['match_score']);
                                    const isCompleteMatch = doc?.is_complete_match;
                                    const variantSource = pick(doc, ['source']);
                                    const combinationsTotal = doc?.combinations_total ?? (Array.isArray(doc?.combinations) ? doc.combinations.length : null);
                                    const allEntries = Object.entries(doc || {});
                                    const formatValue = (value) => {
                                      if (value === null || value === undefined || value === '') return '—';
                                      if (Array.isArray(value)) {
                                        return value.filter((v) => v !== null && v !== undefined && v !== '').join(', ') || '—';
                                      }
                                      if (typeof value === 'object') {
                                        try {
                                          return JSON.stringify(value);
                                        } catch {
                                          return String(value);
                                        }
                                      }
                                      return String(value);
                                    };
                                    return (
                                      <div key={`test-card-${idx}`} className="col-12 col-md-6 col-xl-4">
                                        <div className="card h-100" style={{ fontSize: '0.85rem' }}>
                                          <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-start">
                                              <div className="fw-semibold">AHRI ID: {ahriId || '—'}</div>
                                              {combinationsTotal ? (
                                                <span className="badge bg-light text-dark border" title="Approved indoor/outdoor/furnace combinations for this system">
                                                  {combinationsTotal} combo(s)
                                                </span>
                                              ) : null}
                                            </div>
                                            {program && <div className="text-muted small">Program: {program}</div>}
                                            <div className="mt-2 small">
                                              {outdoorBrand && <div>Outdoor: {outdoorBrand} {outdoorModel && `• ${outdoorModel}`}</div>}
                                              {indoorBrand && <div>Indoor: {indoorBrand} {indoorModel && `• ${indoorModel}`}</div>}
                                              {furnaceModel && <div>Furnace: {furnaceModel}</div>}
                                            </div>
                                            {(seer2 || hspf2 || eer2 || tonnage) && (
                                              <div className="mt-2 small text-muted">
                                                {seer2 && <span className="me-2">SEER2: <span className="text-dark">{seer2}</span></span>}
                                                {hspf2 && <span className="me-2">HSPF2: <span className="text-dark">{hspf2}</span></span>}
                                                {eer2 && <span className="me-2">EER2: <span className="text-dark">{eer2}</span></span>}
                                                {tonnage && <span className="me-2">Tonnage: <span className="text-dark">{tonnage}</span></span>}
                                              </div>
                                            )}
                                            <div className="d-flex flex-wrap gap-2 mt-2">
                                              {status && (
                                                <span className="badge bg-light text-dark border">
                                                  {status}
                                                </span>
                                              )}
                                              {isCompleteMatch !== undefined && (
                                                <span className={`badge border ${isCompleteMatch ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning-emphasis'}`}>
                                                  {isCompleteMatch ? 'Complete match' : 'Partial match'}
                                                </span>
                                              )}
                                              {variantSource && (
                                                <span className="badge bg-light text-dark border">{variantSource}</span>
                                              )}
                                              {matchScore !== '' && matchScore !== null && matchScore !== undefined && (
                                                <span className="badge bg-light text-dark border">score {matchScore}</span>
                                              )}
                                            </div>
                                            <details className="mt-2" open={!!queryTypeTest.expandAllFields}>
                                              <summary className="text-muted small">
                                                All fields {fieldsViewMode === 'raw' ? '(raw)' : '(formatted)'}
                                              </summary>
                                              {fieldsViewMode === 'raw' ? (
                                                renderRawDoc(doc, `test-card-fields-${idx}`)
                                              ) : (
                                                <div className="mt-2">
                                                  {allEntries.length === 0 ? (
                                                    <div className="text-muted small">No fields returned.</div>
                                                  ) : (
                                                    <div className="border rounded p-2 bg-light">
                                                      {allEntries.map(([key, value]) => (
                                                        <div
                                                          key={key}
                                                          className="d-flex justify-content-between gap-2 small border-bottom py-1"
                                                        >
                                                          <span className="text-muted" style={{ wordBreak: 'break-word' }}>
                                                            {key}
                                                          </span>
                                                          <span className="text-end" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                            {formatValue(value)}
                                                          </span>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </details>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-2">
                                  <span className="text-muted small">
                                    Page {page} of {totalPages}
                                  </span>
                                  <div className="btn-group btn-group-sm">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      disabled={page <= 1 || queryTypeTest.loading}
                                      onClick={() => runQueryTypeTest(page - 1)}
                                    >
                                      Prev
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      disabled={page >= totalPages || queryTypeTest.loading}
                                      onClick={() => runQueryTypeTest(page + 1)}
                                    >
                                      Next
                                    </button>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                          {queryTypeTest.responseRaw && (
                            <div className="mt-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="query-test-raw"
                                  checked={!!queryTypeTest.showRaw}
                                  onChange={(e) =>
                                    setQueryTypeTest((prev) => ({ ...prev, showRaw: e.target.checked }))
                                  }
                                />
                                <label className="form-check-label" htmlFor="query-test-raw">
                                  Show raw JSON response
                                </label>
                              </div>
                              {queryTypeTest.showRaw && (
                                <pre className="mt-2 p-2 border rounded bg-light small" style={{ maxHeight: 320, overflow: 'auto' }}>
                                  {queryTypeTest.responseRaw}
                                </pre>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {systemShopTab === 'query_flows' && (
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
                        <div>
                          <h6 className="mb-1">Query Flow Designer</h6>
                          <div className="text-muted small">
                            Create friendly query flows that branch by search results.
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {flowView === 'editor' ? (
                            <>
                              <button className="btn btn-outline-secondary btn-sm" onClick={() => setFlowView('list')}>
                                Back to list
                              </button>
                              <button className="btn btn-outline-secondary btn-sm" onClick={saveFlow} disabled={!flowListId}>
                                Save Flow
                              </button>
                            </>
                          ) : (
                            <button className="btn btn-outline-primary btn-sm" onClick={createFlow}>
                              Add New Flow
                            </button>
                          )}
                        </div>
                      </div>

                      {flowView === 'list' && (
                        <div className="row g-3">
                          <div className="col-12">
                            <div className="border rounded p-3">
                              {flowList.length === 0 ? (
                                <div className="text-muted small">No flows yet. Click Add New Flow to start.</div>
                              ) : (
                                <div className="row g-3">
                                  {flowList.map((flow) => (
                                    <div key={`flow-${flow.id}`} className="col-12 col-md-6 col-xl-4">
                                      <div className="border rounded p-3 h-100">
                                        <div className="fw-semibold">{flow.name}</div>
                                        <div className="text-muted small mb-2">
                                          Steps: {Array.isArray(flow.nodes) ? flow.nodes.length : 0}
                                        </div>
                                        <div className="btn-group btn-group-sm">
                                          <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                              selectFlowById(flow.id);
                                              setFlowView('editor');
                                            }}
                                          >
                                            Edit
                                          </button>
                                          <button
                                            className="btn btn-outline-primary"
                                            onClick={() => {
                                              selectFlowById(flow.id);
                                              setFlowView('editor');
                                              setTimeout(() => {
                                                document.getElementById('flow_test_query')?.focus();
                                              }, 0);
                                            }}
                                          >
                                            Test
                                          </button>
                                          <button
                                            className="btn btn-outline-danger"
                                            onClick={() => {
                                              const next = flowList.filter((f) => f.id !== flow.id);
                                              setCfg((prev) => ({ ...prev, system_shop_query_flows: next }));
                                              if (flowListId === flow.id) {
                                                setFlowListId('');
                                                setFlowName('');
                                                setFlowSteps([]);
                                                setFlowSelectedStepId('');
                                                setFlowTestResult(null);
                                                setFlowView('list');
                                              }
                                            }}
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {flowView === 'editor' && (
                        <div className="d-flex flex-column gap-3">
                        <div className="row g-3">
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Flow name</label>
                          <input
                            className="form-control"
                            value={flowName}
                            onChange={(e) => setFlowName(e.target.value)}
                            placeholder="Flow name"
                            disabled={!flowListId}
                          />
                        </div>
                        <div className="col-12 col-lg-2">
                          <label className="form-label">Engine</label>
                          <select
                            className="form-select"
                            value={flowEngine}
                            onChange={(e) => setFlowEngine(e.target.value)}
                            disabled={!flowListId}
                          >
                            <option value="any">Any</option>
                            <option value="solr">Solr</option>
                            <option value="opensearch">OpenSearch</option>
                            <option value="shadow">Shadow</option>
                          </select>
                        </div>
                        <div className="col-12 col-lg-12">
                          <div className="border rounded p-3 mt-3" style={{ maxHeight: 520, overflowY: 'auto' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="fw-semibold">Steps</div>
                                    <div className="btn-group btn-group-sm">
                                      <button className="btn btn-outline-primary" onClick={addQueryNode} disabled={!flowListId}>
                                        Add Query Step
                                      </button>
                                      <button className="btn btn-outline-primary" onClick={addConditionNode} disabled={!flowListId}>
                                        Add Condition
                                      </button>
                                      <button className="btn btn-outline-primary" onClick={addFlowNode} disabled={!flowListId}>
                                        Add Flow Step
                                      </button>
                                    </div>
                            </div>
                            {flowSteps.length === 0 && (
                              <div className="text-muted small">Add steps to start building the flow.</div>
                            )}
                            {flowSteps.map((step, idx) => (
                              <div
                                key={step.id}
                                className={`border rounded p-2 mb-2 ${flowSelectedStepId === step.id ? 'border-primary' : ''}`}
                                onClick={() => setFlowSelectedStepId(step.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="fw-semibold">
                                    {idx + 1}. {step.type === 'condition' ? 'Condition' : 'Query'}
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    {step.isStart && <span className="badge bg-primary">Start</span>}
                                    <div className="btn-group btn-group-sm">
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        disabled={idx === 0}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFlowSteps((prev) => {
                                            if (idx === 0) return prev;
                                            const next = [...prev];
                                            [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                                            return next;
                                          });
                                        }}
                                      >
                                        ↑
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        disabled={idx === flowSteps.length - 1}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFlowSteps((prev) => {
                                            if (idx === prev.length - 1) return prev;
                                            const next = [...prev];
                                            [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                                            return next;
                                          });
                                        }}
                                      >
                                        ↓
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFlowSteps((prev) => prev.filter((s) => s.id !== step.id));
                                          if (flowSelectedStepId === step.id) {
                                            setFlowSelectedStepId('');
                                          }
                                        }}
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-muted small">{flowStepLabel(step)}</div>
                              </div>
                            ))}
                          </div>
                          <div className="border rounded p-3 mt-3" style={{ minHeight: 320 }}>
                            <div className="fw-semibold mb-2">Flow Preview</div>
                            {flowSteps.length === 0 ? (
                              <div className="text-muted small">No steps yet.</div>
                            ) : (
                              <ol className="mb-0 small">
                                {flowSteps.map((step) => (
                                  <li key={`preview-${step.id}`}>
                                    {flowStepLabel(step)}
                                    {step.type === 'query' && (
                                      <span className="text-muted"> → {step.next || 'End'}</span>
                                    )}
                                    {step.type === 'condition' && (
                                      <span className="text-muted">
                                        {' '}
                                        → true: {step.nextTrue || 'End'}, false: {step.nextFalse || 'End'}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            )}
                            {flowMermaidError && (
                              <div className="alert alert-warning mt-3">{flowMermaidError}</div>
                            )}
                            {flowMermaidSvg && (
                              <div
                                className="mt-3 border rounded p-2 bg-light"
                                style={{ minHeight: 280 }}
                                dangerouslySetInnerHTML={{ __html: flowMermaidSvg }}
                              />
                            )}
                          </div>
                        </div>

                        <div className="col-12 col-lg-4">
                          <div className="border rounded p-3 h-100">
                            <div className="fw-semibold mb-2">Step Details</div>
                            {flowSelectedStepId ? (
                              (() => {
                                const current = flowSteps.find((s) => s.id === flowSelectedStepId);
                                if (!current) return <div className="text-muted small">Select a step to edit details.</div>;
                                return (
                                  <>
                                    <div className="mb-2 text-muted small">Type: {current.type}</div>
                                    {current.type === 'query' && (
                                      <>
                                        <label className="form-label">Query type</label>
                                        <select
                                          className="form-select mb-2"
                                          value={current.queryTypeId || current.queryType || ''}
                                          onChange={(e) => {
                                            const selected = systemShopQueryTypeOptions.find(
                                              (entry) => String(entry?.id || entry?.name || '') === e.target.value
                                            );
                                            setFlowSteps((prev) =>
                                              prev.map((s) => (s.id === current.id ? {
                                                ...s,
                                                queryTypeId: e.target.value,
                                                queryType: selected?.name || '',
                                              } : s))
                                            );
                                          }}
                                        >
                                          <option value="">Default</option>
                                          {systemShopQueryTypeOptions.map((entry, idx) => (
                                            <option key={`${entry?.id || entry?.name || idx}`} value={entry?.id || entry?.name || ''}>
                                              {entry?.label || entry?.name || 'Unnamed'}
                                            </option>
                                          ))}
                                        </select>
                                        <label className="form-label">Page size</label>
                                        <input
                                          className="form-control mb-2"
                                          type="number"
                                          min={1}
                                          value={current.pageSize ?? 20}
                                          onChange={(e) => {
                                            const val = Number(e.target.value) || 20;
                                            setFlowSteps((prev) =>
                                              prev.map((s) => (s.id === current.id ? { ...s, pageSize: val } : s))
                                            );
                                          }}
                                        />
                                        <label className="form-label">Next step</label>
                                        <select
                                          className="form-select mb-2"
                                          value={current.next || ''}
                                          onChange={(e) => {
                                            if (e.target.value === '__add_condition__') {
                                              const id = `node_${Date.now()}`;
                                              const newStep = {
                                                id,
                                                type: 'condition',
                                                operator: '>=',
                                                threshold: 1,
                                                conditionType: 'count',
                                                field: '',
                                                fieldOperator: 'exists',
                                                fieldValue: '',
                                                isStart: false,
                                                next: '',
                                                nextTrue: '',
                                                nextFalse: '',
                                              };
                                              setFlowSteps((prev) =>
                                                prev.map((s) => (s.id === current.id ? { ...s, next: id } : s)).concat(newStep)
                                              );
                                              setFlowSelectedStepId(id);
                                              return;
                                            }
                                            if (e.target.value === '__add_flow__') {
                                              const id = `node_${Date.now()}`;
                                              const newStep = {
                                                id,
                                                type: 'flow',
                                                flowId: '',
                                                isStart: false,
                                                next: '',
                                                nextTrue: '',
                                                nextFalse: '',
                                              };
                                              setFlowSteps((prev) =>
                                                prev.map((s) => (s.id === current.id ? { ...s, next: id } : s)).concat(newStep)
                                              );
                                              setFlowSelectedStepId(id);
                                              return;
                                            }
                                            setFlowSteps((prev) =>
                                              prev.map((s) => (s.id === current.id ? { ...s, next: e.target.value } : s))
                                            );
                                          }}
                                        >
                                          <option value="">End</option>
                                          <option value="__add_condition__">+ Add condition</option>
                                          <option value="__add_flow__">+ Add flow step</option>
                                          {flowSteps
                                            .filter((s) => s.id !== current.id)
                                            .map((s) => (
                                              <option key={`next-${s.id}`} value={s.id}>
                                                {flowStepLabel(s)}
                                              </option>
                                            ))}
                                        </select>
                                      </>
                                    )}
                                          {current.type === 'flow' && (
                                            <>
                                              <label className="form-label">Flow to run</label>
                                              <select
                                                className="form-select mb-2"
                                                value={current.flowId || ''}
                                                onChange={(e) => {
                                                  setFlowSteps((prev) =>
                                                    prev.map((s) =>
                                                      s.id === current.id ? { ...s, flowId: e.target.value } : s
                                                    )
                                                  );
                                                }}
                                              >
                                                <option value="">Select flow</option>
                                                {flowList
                                                  .filter((f) => f.id !== flowListId)
                                                  .map((flow) => (
                                                    <option key={`flow-ref-${flow.id}`} value={flow.id}>
                                                      {flow.name}
                                                    </option>
                                                  ))}
                                              </select>
                                              <label className="form-label">Next step</label>
                                              <select
                                                className="form-select mb-2"
                                                value={current.next || ''}
                                                onChange={(e) => {
                                                  if (e.target.value === '__add_condition__') {
                                                    const id = `node_${Date.now()}`;
                                                    const newStep = {
                                                      id,
                                                      type: 'condition',
                                                      operator: '>=',
                                                      threshold: 1,
                                                      conditionType: 'count',
                                                      field: '',
                                                      fieldOperator: 'exists',
                                                      fieldValue: '',
                                                      isStart: false,
                                                      next: '',
                                                      nextTrue: '',
                                                      nextFalse: '',
                                                    };
                                                    setFlowSteps((prev) =>
                                                      prev.map((s) =>
                                                        s.id === current.id ? { ...s, next: id } : s
                                                      ).concat(newStep)
                                                    );
                                                    setFlowSelectedStepId(id);
                                                    return;
                                                  }
                                                  if (e.target.value === '__add_flow__') {
                                                    const id = `node_${Date.now()}`;
                                                    const newStep = {
                                                      id,
                                                      type: 'flow',
                                                      flowId: '',
                                                      isStart: false,
                                                      next: '',
                                                      nextTrue: '',
                                                      nextFalse: '',
                                                    };
                                                    setFlowSteps((prev) =>
                                                      prev.map((s) =>
                                                        s.id === current.id ? { ...s, next: id } : s
                                                      ).concat(newStep)
                                                    );
                                                    setFlowSelectedStepId(id);
                                                    return;
                                                  }
                                                  if (e.target.value === '__add_query__') {
                                                    const id = `node_${Date.now()}`;
                                                    const newStep = {
                                                      id,
                                                      type: 'query',
                                                      queryType: '',
                                                      queryTypeId: '',
                                                      pageSize: 20,
                                                      isStart: false,
                                                      next: '',
                                                      nextTrue: '',
                                                      nextFalse: '',
                                                    };
                                                    setFlowSteps((prev) =>
                                                      prev.map((s) =>
                                                        s.id === current.id ? { ...s, next: id } : s
                                                      ).concat(newStep)
                                                    );
                                                    setFlowSelectedStepId(id);
                                                    return;
                                                  }
                                                  setFlowSteps((prev) =>
                                                    prev.map((s) => (s.id === current.id ? { ...s, next: e.target.value } : s))
                                                  );
                                                }}
                                              >
                                                <option value="">End</option>
                                                <option value="__add_condition__">+ Add condition</option>
                                                <option value="__add_flow__">+ Add flow step</option>
                                                <option value="__add_query__">+ Add query</option>
                                                {flowSteps
                                                  .filter((s) => s.id !== current.id)
                                                  .map((s) => (
                                                    <option key={`next-${s.id}`} value={s.id}>
                                                      {flowStepLabel(s)}
                                                    </option>
                                                  ))}
                                              </select>
                                            </>
                                          )}
                                          {current.type === 'condition' && (
                                      <>
                                        <label className="form-label">Condition type</label>
                                        <select
                                          className="form-select mb-2"
                                          value={current.conditionType || 'count'}
                                          onChange={(e) => {
                                            const nextType = e.target.value;
                                            setFlowSteps((prev) =>
                                              prev.map((s) =>
                                                s.id === current.id ? { ...s, conditionType: nextType } : s
                                              )
                                            );
                                          }}
                                        >
                                          <option value="count">Results count</option>
                                          <option value="field">Field condition</option>
                                        </select>
                                        {current.conditionType !== 'field' && (
                                          <div className="d-flex gap-2 mb-2">
                                            <button
                                              className="btn btn-outline-secondary btn-sm"
                                              onClick={() => {
                                                setFlowSteps((prev) =>
                                                  prev.map((s) =>
                                                    s.id === current.id
                                                      ? { ...s, operator: '>=', threshold: 1 }
                                                      : s
                                                  )
                                                );
                                              }}
                                            >
                                              Results ≥ 1
                                            </button>
                                            <button
                                              className="btn btn-outline-secondary btn-sm"
                                              onClick={() => {
                                                setFlowSteps((prev) =>
                                                  prev.map((s) =>
                                                    s.id === current.id ? { ...s, operator: '<', threshold: 1 } : s
                                                  )
                                                );
                                              }}
                                            >
                                              Results &lt; 1
                                            </button>
                                          </div>
                                        )}
                                        {current.conditionType !== 'field' && (
                                          <>
                                            <label className="form-label">Operator</label>
                                            <select
                                              className="form-select mb-2"
                                              value={current.operator || '>='}
                                              onChange={(e) => {
                                                setFlowSteps((prev) =>
                                                  prev.map((s) =>
                                                    s.id === current.id ? { ...s, operator: e.target.value } : s
                                                  )
                                                );
                                              }}
                                            >
                                              <option value=">=">&ge;</option>
                                              <option value=">">&gt;</option>
                                              <option value="<=">&le;</option>
                                              <option value="<">&lt;</option>
                                            </select>
                                            <label className="form-label">Threshold</label>
                                            <input
                                              className="form-control mb-2"
                                              type="number"
                                              min={0}
                                              value={current.threshold ?? 0}
                                              onChange={(e) => {
                                                const val = Number(e.target.value) || 0;
                                                setFlowSteps((prev) =>
                                                  prev.map((s) =>
                                                    s.id === current.id ? { ...s, threshold: val } : s
                                                  )
                                                );
                                              }}
                                            />
                                          </>
                                        )}
                                        {current.conditionType === 'field' && (
                                          <>
                                            <label className="form-label">Result field</label>
                                            <input
                                              className="form-control mb-2"
                                              value={current.field || ''}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                setFlowSteps((prev) =>
                                                  prev.map((s) => (s.id === current.id ? { ...s, field: val } : s))
                                                );
                                              }}
                                              placeholder="e.g. rows_programname"
                                            />
                                            <label className="form-label">Field operator</label>
                                            <select
                                              className="form-select mb-2"
                                              value={current.fieldOperator || 'exists'}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                setFlowSteps((prev) =>
                                                  prev.map((s) =>
                                                    s.id === current.id ? { ...s, fieldOperator: val } : s
                                                  )
                                                );
                                              }}
                                            >
                                              <option value="exists">Exists</option>
                                              <option value="equals">Equals</option>
                                              <option value="contains">Contains</option>
                                              <option value=">">&gt;</option>
                                              <option value=">=">&ge;</option>
                                              <option value="<">&lt;</option>
                                              <option value="<=">&le;</option>
                                            </select>
                                            {current.fieldOperator !== 'exists' && (
                                              <>
                                                <label className="form-label">Field value</label>
                                                <input
                                                  className="form-control mb-2"
                                                  value={current.fieldValue || ''}
                                                  onChange={(e) => {
                                                    const val = e.target.value;
                                                    setFlowSteps((prev) =>
                                                      prev.map((s) =>
                                                        s.id === current.id ? { ...s, fieldValue: val } : s
                                                      )
                                                    );
                                                  }}
                                                  placeholder="Value to compare"
                                                />
                                              </>
                                            )}
                                          </>
                                        )}
                                        <label className="form-label">If true →</label>
                                        <select
                                          className="form-select mb-2"
                                          value={current.nextTrue || ''}
                                          onChange={(e) => {
                                            if (e.target.value === '__add_condition__') {
                                              const id = `node_${Date.now()}`;
                                              const newStep = {
                                                id,
                                                type: 'condition',
                                                operator: '>=',
                                                threshold: 1,
                                                conditionType: 'count',
                                                field: '',
                                                fieldOperator: 'exists',
                                                fieldValue: '',
                                                isStart: false,
                                                next: '',
                                                nextTrue: '',
                                                nextFalse: '',
                                              };
                                              setFlowSteps((prev) =>
                                                prev.map((s) =>
                                                  s.id === current.id ? { ...s, nextTrue: id } : s
                                                ).concat(newStep)
                                              );
                                              setFlowSelectedStepId(id);
                                              return;
                                            }
                                            if (e.target.value === '__add_flow__') {
                                              const id = `node_${Date.now()}`;
                                              const newStep = {
                                                id,
                                                type: 'flow',
                                                flowId: '',
                                                isStart: false,
                                                next: '',
                                                nextTrue: '',
                                                nextFalse: '',
                                              };
                                              setFlowSteps((prev) =>
                                                prev.map((s) =>
                                                  s.id === current.id ? { ...s, nextTrue: id } : s
                                                ).concat(newStep)
                                              );
                                              setFlowSelectedStepId(id);
                                              return;
                                            }
                                            if (e.target.value === '__add_flow__') {
                                              const id = `node_${Date.now()}`;
                                              const newStep = {
                                                id,
                                                type: 'flow',
                                                flowId: '',
                                                isStart: false,
                                                next: '',
                                                nextTrue: '',
                                                nextFalse: '',
                                              };
                                              setFlowSteps((prev) =>
                                                prev.map((s) =>
                                                  s.id === current.id ? { ...s, nextFalse: id } : s
                                                ).concat(newStep)
                                              );
                                              setFlowSelectedStepId(id);
                                              return;
                                            }
                                            if (e.target.value === '__add_query__') {
                                              const id = `node_${Date.now()}`;
                                              const newStep = {
                                                id,
                                                type: 'query',
                                                queryType: '',
                                                queryTypeId: '',
                                                pageSize: 20,
                                                isStart: false,
                                                next: '',
                                                nextTrue: '',
                                                nextFalse: '',
                                              };
                                              setFlowSteps((prev) =>
                                                prev.map((s) =>
                                                  s.id === current.id ? { ...s, nextTrue: id } : s
                                                ).concat(newStep)
                                              );
                                              setFlowSelectedStepId(id);
                                              return;
                                            }
                                            setFlowSteps((prev) =>
                                              prev.map((s) =>
                                                s.id === current.id ? { ...s, nextTrue: e.target.value } : s
                                              )
                                            );
                                          }}
                                        >
                                          <option value="">End</option>
                                          <option value="__add_condition__">+ Add condition</option>
                                          <option value="__add_flow__">+ Add flow step</option>
                                          <option value="__add_query__">+ Add query</option>
                                          {flowSteps
                                            .filter((s) => s.id !== current.id)
                                            .filter((s) => !(s.type === 'query' && !s.queryType && !s.queryTypeId))
                                            .map((s) => (
                                              <option key={`true-${s.id}`} value={s.id}>
                                                {flowStepLabel(s)}
                                              </option>
                                            ))}
                                        </select>
                                        <label className="form-label">If false →</label>
                                        <select
                                          className="form-select mb-2"
                                          value={current.nextFalse || ''}
                                          onChange={(e) => {
                                            if (e.target.value === '__add_condition__') {
                                              const id = `node_${Date.now()}`;
                                              const newStep = {
                                                id,
                                                type: 'condition',
                                                operator: '>=',
                                                threshold: 1,
                                                conditionType: 'count',
                                                field: '',
                                                fieldOperator: 'exists',
                                                fieldValue: '',
                                                isStart: false,
                                                next: '',
                                                nextTrue: '',
                                                nextFalse: '',
                                              };
                                              setFlowSteps((prev) =>
                                                prev.map((s) =>
                                                  s.id === current.id ? { ...s, nextFalse: id } : s
                                                ).concat(newStep)
                                              );
                                              setFlowSelectedStepId(id);
                                              return;
                                            }
                                            if (e.target.value === '__add_query__') {
                                              const id = `node_${Date.now()}`;
                                              const newStep = {
                                                id,
                                                type: 'query',
                                                queryType: '',
                                                queryTypeId: '',
                                                pageSize: 20,
                                                isStart: false,
                                                next: '',
                                                nextTrue: '',
                                                nextFalse: '',
                                              };
                                              setFlowSteps((prev) =>
                                                prev.map((s) =>
                                                  s.id === current.id ? { ...s, nextFalse: id } : s
                                                ).concat(newStep)
                                              );
                                              setFlowSelectedStepId(id);
                                              return;
                                            }
                                            setFlowSteps((prev) =>
                                              prev.map((s) =>
                                                s.id === current.id ? { ...s, nextFalse: e.target.value } : s
                                              )
                                            );
                                          }}
                                        >
                                          <option value="">End</option>
                                          <option value="__add_condition__">+ Add condition</option>
                                          <option value="__add_flow__">+ Add flow step</option>
                                          <option value="__add_query__">+ Add query</option>
                                          {flowSteps
                                            .filter((s) => s.id !== current.id)
                                            .filter((s) => !(s.type === 'query' && !s.queryType && !s.queryTypeId))
                                            .map((s) => (
                                              <option key={`false-${s.id}`} value={s.id}>
                                                {flowStepLabel(s)}
                                              </option>
                                            ))}
                                        </select>
                                      </>
                                    )}
                                    <div className="form-check mt-2">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="flow_start_node"
                                        checked={!!current.isStart}
                                        onChange={(e) => {
                                          setFlowSteps((prev) =>
                                            prev.map((s) => ({ ...s, isStart: s.id === current.id ? e.target.checked : false }))
                                          );
                                        }}
                                      />
                                      <label className="form-check-label" htmlFor="flow_start_node">
                                        Start step
                                      </label>
                                    </div>
                                  </>
                                );
                              })()
                            ) : (
                              <div className="text-muted small">Select a step to edit details.</div>
                            )}
                          </div>
                        </div>
                        </div>

                        <div className="card mt-3">
                          <div className="card-body">
                            <div className="d-flex flex-column flex-lg-row gap-3 align-items-lg-end">
                              <div className="flex-grow-1">
                                <label className="form-label">Test with query text</label>
                                <input
                                  id="flow_test_query"
                                  className="form-control"
                                  value={flowTestQuery}
                                  onChange={(e) => setFlowTestQuery(e.target.value)}
                                  placeholder="Enter search text to run the flow"
                                />
                              </div>
                              <div style={{ minWidth: 220 }}>
                                <label className="form-label">Customer ID (optional)</label>
                                <input
                                  className="form-control"
                                  value={flowTestCustomerId}
                                  onChange={(e) => setFlowTestCustomerId(e.target.value)}
                                  placeholder="e.g. 104048"
                                />
                              </div>
                              <button
                                className="btn btn-primary"
                                onClick={runFlowTest}
                                disabled={!flowListId || !flowTestQuery.trim() || flowTestLoading}
                              >
                                {flowTestLoading ? 'Running...' : 'Run Flow'}
                              </button>
                            </div>
                            {flowTestResult && (
                              <div className="mt-3">
                                <div className="fw-semibold">Flow Results</div>
                                <div className="text-muted small">
                                  Steps executed: {flowTestResult.steps?.length || 0} | Results: {flowTestResult.result?.numFound ?? 0}
                                </div>
                                {Array.isArray(flowTestResult.steps) && flowTestResult.steps.length > 0 && (
                                  <div className="mt-3">
                                    <div className="fw-semibold">Execution trace</div>
                                    <div className="row g-2 mt-1">
                                      {flowTestResult.steps.map((step, idx) => (
                                        <div key={`flow-step-${idx}`} className="col-12">
                                          <div className="border rounded p-2">
                                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                              <span className="badge bg-light text-dark border">#{idx + 1}</span>
                                              <span className="fw-semibold text-capitalize">{step.type || 'step'}</span>
                                              {step.type === 'query' && step.query_type && (
                                                <span className="text-muted small">Type: {step.query_type}</span>
                                              )}
                                              {step.type === 'condition' && (
                                                <span className="text-muted small">
                                                  {step.operator} {step.threshold}
                                                </span>
                                              )}
                                              {typeof step.passed === 'boolean' && (
                                                <span className={`badge ${step.passed ? 'bg-success' : 'bg-danger'}`}>
                                                  {step.passed ? 'Passed' : 'Failed'}
                                                </span>
                                              )}
                                              {step.numFound !== undefined && (
                                                <span className="text-muted small">Results: {step.numFound}</span>
                                              )}
                                            </div>
                                            {step.solr_request && (
                                              <pre
                                                className="mt-2 mb-0 small bg-light border rounded p-2"
                                                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                              >
                                                {JSON.stringify(step.solr_request, null, 2)}
                                              </pre>
                                            )}
                                            {step.response && (
                                              <pre
                                                className="mt-2 mb-0 small bg-light border rounded p-2"
                                                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                              >
                                                {JSON.stringify(step.response, null, 2)}
                                              </pre>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {(() => {
                                  const docs = flowTestResult.result?.docs || [];
                                  if (!Array.isArray(docs) || docs.length === 0) return null;
                                  const lastStepWithTarget = (flowTestResult.steps || []).slice().reverse().find((s) => s?.target);
                                  const inferredTarget =
                                    lastStepWithTarget?.target ||
                                    (String(flowTestResult.result?.collection || '').includes('products_') ? 'product' : 'ahri');
                                  const isProduct = inferredTarget === 'product';
                                  const pick = (doc, keys) => {
                                    for (const key of keys) {
                                      const raw = doc?.[key];
                                      const val = Array.isArray(raw) ? raw[0] : raw;
                                      if (val !== null && val !== undefined && String(val).trim() !== '') {
                                        return val;
                                      }
                                    }
                                    return '';
                                  };
                                  const parseJson = (value) => {
                                    if (!value) return null;
                                    if (typeof value === 'object') return value;
                                    try {
                                      return JSON.parse(value);
                                    } catch {
                                      return null;
                                    }
                                  };
                                  return (
                                    <div className="row g-3 mt-2">
                                      {docs.slice(0, 6).map((doc, idx) => {
                                        if (isProduct) {
                                          const title = pick(doc, ['name', 'productName', 'title']) || 'Unnamed product';
                                          const code = pick(doc, ['productCode', 'product_code', 'productId', 'id']);
                                          const mfr = pick(doc, ['manufacturerName', 'manufacturer_name', 'mfrName']);
                                          const part = pick(doc, ['manufacturerPartNumber', 'manufacturer_part_number', 'mfrPartNumber', 'partnumber']);
                                          const category = pick(doc, ['category']);
                                          const status = pick(doc, ['active', 'status']);
                                          const image = pick(doc, ['imageUrl', 'image_url', 'image', 'thumbnailUrl', 'thumb']);
                                          const attrs = parseJson(doc?.attributesJson);
                                          const attrKeys = attrs ? Object.keys(attrs) : [];
                                          return (
                                            <div key={`flow-doc-${idx}`} className="col-12 col-md-6 col-xl-4">
                                              <div className="card h-100" style={{ fontSize: '0.85rem' }}>
                                                <div className="card-body p-2">
                                                  <div className="d-flex gap-2 align-items-start">
                                                    <div
                                                      className="bg-light d-flex align-items-center justify-content-center flex-shrink-0"
                                                      style={{ width: 96, height: 96 }}
                                                    >
                                                      {image ? (
                                                        <img
                                                          src={image}
                                                          alt={title}
                                                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                        />
                                                      ) : (
                                                        <div className="text-muted small">No image</div>
                                                      )}
                                                    </div>
                                                    <div className="d-flex flex-column gap-1 flex-grow-1">
                                                      <div className="fw-semibold">{title}</div>
                                                      {code && <div className="text-muted small">Code: {code}</div>}
                                                      {(mfr || part) && (
                                                        <div className="text-muted small">
                                                          {mfr && <span>{mfr}</span>}
                                                          {mfr && part && <span> • </span>}
                                                          {part && <span>Part: {part}</span>}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                  <div className="d-flex flex-wrap gap-2 mt-2">
                                                    {category && (
                                                      <span className="badge bg-light text-dark border">
                                                        {category}
                                                      </span>
                                                    )}
                                                    {status !== '' && status !== null && status !== undefined && (
                                                      <span className="badge bg-light text-dark border">
                                                        {String(status) === '1' || String(status).toLowerCase() === 'true' ? 'Active' : String(status)}
                                                      </span>
                                                    )}
                                                    {attrKeys.length > 0 && (
                                                      <span className="badge bg-light text-dark border">
                                                        {attrKeys.length} attrs
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        }
                                        const ahriId = pick(doc, ['rows_referenceid', 'referenceId', 'ahri_id', 'id']);
                                        const program = pick(doc, ['rows_programname', 'programName']);
                                        const outdoorBrand = pick(doc, ['rows_outdoorbrandname', 'outdoorBrandName']);
                                        const indoorBrand = pick(doc, ['rows_indoorbrandname', 'indoorBrandName']);
                                        const outdoorModel = pick(doc, ['rows_outdoormodelnumber', 'outdoorModelNumber']);
                                        const indoorModel = pick(doc, ['rows_indoormodelnumber', 'indoorModelNumber']);
                                        const furnaceModel = pick(doc, ['rows_furnacemodelnumber', 'furnaceModelNumber']);
                                        const status = pick(doc, ['rows_modelstatus', 'modelStatus']);
                                        return (
                                          <div key={`flow-doc-${idx}`} className="col-12 col-md-6 col-xl-4">
                                            <div className="card h-100" style={{ fontSize: '0.85rem' }}>
                                              <div className="card-body p-2">
                                                <div className="fw-semibold">AHRI ID: {ahriId || '—'}</div>
                                                {program && <div className="text-muted small">Program: {program}</div>}
                                                <div className="mt-2 small">
                                                  {outdoorBrand && <div>Outdoor: {outdoorBrand} {outdoorModel && `• ${outdoorModel}`}</div>}
                                                  {indoorBrand && <div>Indoor: {indoorBrand} {indoorModel && `• ${indoorModel}`}</div>}
                                                  {furnaceModel && <div>Furnace: {furnaceModel}</div>}
                                                </div>
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                  {status && (
                                                    <span className="badge bg-light text-dark border">
                                                      {status}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        </div>

                        </div>
                      )}
                    </div>
                  </div>
                )}

                {systemShopTab === 'routing' && (
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
                        <div>
                          <h6 className="mb-1">Routing Engine</h6>
                          <div className="text-muted small">
                            Evaluate routes in order, map inputs, and execute query flows.
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {routingView === 'editor' ? (
                            <>
                              <button className="btn btn-outline-secondary btn-sm" onClick={() => setRoutingView('list')}>
                                Back to list
                              </button>
                              <button className="btn btn-outline-secondary btn-sm" onClick={saveRoute} disabled={!routeDraft}>
                                Save Route
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => openRouteEditor(null)}
                              >
                                Add Route
                              </button>
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={loadRoutes}
                                disabled={routesLoading}
                              >
                                Refresh
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {routesError && (
                        <div className="alert alert-danger py-2">{routesError}</div>
                      )}

                      {routingView === 'list' && (
                        <div className="row g-3">
                          <div className="col-12">
                            <div className="border rounded p-3">
                              {routesLoading && <div className="text-muted small">Loading routes...</div>}
                              {!routesLoading && routes.length === 0 && (
                                <div className="text-muted small">
                                  No routes yet. Click Add Route to start.
                                </div>
                              )}
                              <div className="row g-3">
                                {routes.map((route) => (
                                  <div key={`route-${route.id}`} className="col-12 col-md-6 col-xl-4">
                                    <div className="border rounded p-3 h-100">
                                      <div className="fw-semibold">{route.route_name || 'Untitled route'}</div>
                                      <div className="text-muted small mb-2">
                                        Flow: {route.query_flow_name || route.query_flow_id || '-'}
                                      </div>
                                      <div className="d-flex flex-wrap gap-2 mb-2">
                                        <span className="badge bg-light text-dark border">
                                          Order {route.route_order ?? 0}
                                        </span>
                                        <span className={`badge ${route.enabled ? 'bg-success' : 'bg-secondary'}`}>
                                          {route.enabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                        <span className="badge bg-light text-dark border">
                                          {route.stop_on_match === false ? 'Continue' : 'Stop on match'}
                                        </span>
                                      </div>
                                      <div className="d-flex flex-wrap gap-2">
                                        <button
                                          className="btn btn-outline-secondary btn-sm"
                                          onClick={() => openRouteEditor(route)}
                                        >
                                          Edit
                                        </button>
                                        {routeDeleteId === route.id ? (
                                          <>
                                            <button
                                              className="btn btn-outline-danger btn-sm"
                                              onClick={() => deleteRoute(route.id)}
                                            >
                                              Confirm delete
                                            </button>
                                            <button
                                              className="btn btn-outline-secondary btn-sm"
                                              onClick={() => setRouteDeleteId(null)}
                                            >
                                              Cancel
                                            </button>
                                          </>
                                        ) : (
                                          <button
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => setRouteDeleteId(route.id)}
                                          >
                                            Delete
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {routingView === 'editor' && routeDraft && (
                        <div className="row g-3">
                          <div className="col-12 col-lg-7">
                            <div className="border rounded p-3 h-100">
                              <div className="row g-3">
                                <div className="col-12">
                                  <label className="form-label">Route name</label>
                                  <input
                                    className="form-control"
                                    value={routeDraft.route_name}
                                    onChange={(e) => updateRouteDraft('route_name', e.target.value)}
                                    placeholder="Route name"
                                  />
                                </div>
                                <div className="col-12 col-md-6">
                                  <label className="form-label">Route order</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={routeDraft.route_order}
                                    onChange={(e) => updateRouteDraft('route_order', e.target.value)}
                                  />
                                </div>
                                <div className="col-12 col-md-6">
                                  <label className="form-label">Query flow</label>
                                  <select
                                    className="form-select"
                                    value={routeDraft.query_flow_id}
                                    onChange={(e) => {
                                      const selectedId = e.target.value;
                                      const selectedFlow = flowList.find((flow) => flow.id === selectedId);
                                      updateRouteDraft('query_flow_id', selectedId);
                                      updateRouteDraft('query_flow_name', selectedFlow?.name || '');
                                    }}
                                  >
                                    <option value="">Select a flow</option>
                                    {flowList.map((flow) => (
                                      <option key={`route-flow-${flow.id}`} value={flow.id}>
                                        {flow.name}
                                      </option>
                                    ))}
                                  </select>
                                  {flowList.length === 0 && (
                                    <div className="form-text">Create a query flow first.</div>
                                  )}
                                </div>
                                <div className="col-12">
                                  <div className="d-flex flex-wrap gap-3">
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="route-enabled"
                                        checked={!!routeDraft.enabled}
                                        onChange={(e) => updateRouteDraft('enabled', e.target.checked)}
                                      />
                                      <label className="form-check-label" htmlFor="route-enabled">
                                        Enabled
                                      </label>
                                    </div>
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="route-stop"
                                        checked={!!routeDraft.stop_on_match}
                                        onChange={(e) => updateRouteDraft('stop_on_match', e.target.checked)}
                                      />
                                      <label className="form-check-label" htmlFor="route-stop">
                                        Stop on match
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                {routeFormError && (
                                  <div className="col-12">
                                    <div className="alert alert-danger py-2 mb-0">{routeFormError}</div>
                                  </div>
                                )}
                                <div className="col-12">
                                  <label className="form-label">Conditions (when_clause JSON)</label>
                                  <textarea
                                    className="form-control"
                                    rows={8}
                                    value={routeDraft.when_clause_text}
                                    onChange={(e) => updateRouteDraft('when_clause_text', e.target.value)}
                                  />
                                  <div className="form-text">
                                    Example: {"{ \"all\": [{ \"field\": \"input.queryText\", \"op\": \"contains\", \"value\": \"acme\" }] }"}
                                  </div>
                                </div>
                                <div className="col-12">
                                  <label className="form-label">Input mapping (input_mapping JSON)</label>
                                  <textarea
                                    className="form-control"
                                    rows={8}
                                    value={routeDraft.input_mapping_text}
                                    onChange={(e) => updateRouteDraft('input_mapping_text', e.target.value)}
                                  />
                                  <div className="form-text">
                                    Example: {"{ \"query\": { \"from\": \"input.queryText\", \"default\": \"\" } }"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-12 col-lg-5">
                            <div className="border rounded p-3 h-100">
                              <div className="fw-semibold mb-2">Test routing</div>
                              <label className="form-label">Input payload</label>
                              <textarea
                                className="form-control"
                                rows={6}
                                value={routeTest.input}
                                onChange={(e) => setRouteTest((prev) => ({ ...prev, input: e.target.value }))}
                              />
                              <div className="form-text">
                                Provide the request body for route execution (e.g. {"{ \"queryText\": \"101-085NL\" }"}).
                              </div>
                              {routeTest.error && (
                                <div className="alert alert-danger py-2 mt-2 mb-0">{routeTest.error}</div>
                              )}
                              <div className="mt-2 d-flex flex-wrap gap-2">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={runRouteTest}
                                  disabled={routeTest.loading}
                                >
                                  {routeTest.loading ? 'Running...' : 'Run Test'}
                                </button>
                              </div>
                              {routeTest.result && (
                                <pre className="mt-3 p-2 border rounded bg-light small" style={{ maxHeight: 360, overflow: 'auto' }}>
                                  {JSON.stringify(routeTest.result, null, 2)}
                                </pre>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {systemShopTab === 'schema' && (
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
                        <div>
                          <h6 className="mb-1">Schema Editor</h6>
                          <div className="text-muted small">
                            Manage fields, field types, dynamic fields, and copy fields per client collection.
                          </div>
                        </div>
                        <Space className="w-100 w-lg-auto">
                          <Input
                            allowClear
                            placeholder="Search schema fields, types, copy fields..."
                            value={schemaSearch}
                            onChange={(e) => setSchemaSearch(e.target.value)}
                            style={{ minWidth: 240 }}
                          />
                          <Button onClick={() => loadSchema(schemaEditorTab)} loading={schemaLoading}>
                            Reload Schema
                          </Button>
                        </Space>
                      </div>
                      {schemaError && <div className="alert alert-danger">{schemaError}</div>}
                      <Tabs
                        activeKey={schemaEditorTab}
                        onChange={(key) => setSchemaEditorTab(key)}
                        items={[
                          {
                            key: 'ahri',
                            label: 'AHRI Schema',
                            children: (
                              <>
                                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
                                  <div className="text-muted small">
                                    Collection: <code>ahri_{account?.id || 'account'}</code>
                                  </div>
                                  <Button
                                    type="primary"
                                    onClick={() =>
                                      openSchemaDrawer('ahri',
                                        schemaSectionTab === 'fieldTypes'
                                          ? 'field-type'
                                          : schemaSectionTab === 'dynamicFields'
                                          ? 'dynamic-field'
                                          : schemaSectionTab === 'copyFields'
                                          ? 'copy-field'
                                          : 'field',
                                        'add'
                                      )
                                    }
                                  >
                                    Add {schemaSectionLabels[schemaSectionTab]}
                                  </Button>
                                </div>
                                <Tabs
                                  activeKey={schemaSectionTab}
                                  onChange={(key) => setSchemaSectionTab(key)}
                                  items={[
                                    { key: 'fields', label: 'Fields', children: renderSchemaTable('ahri', 'fields') },
                                    { key: 'fieldTypes', label: 'Field Types', children: renderSchemaTable('ahri', 'fieldTypes') },
                                    { key: 'dynamicFields', label: 'Dynamic Fields', children: renderSchemaTable('ahri', 'dynamicFields') },
                                    { key: 'copyFields', label: 'Copy Fields', children: renderSchemaTable('ahri', 'copyFields') },
                                  ]}
                                />
                              </>
                            ),
                          },
                          {
                            key: 'products',
                            label: 'Products Schema',
                            children: (
                              <>
                                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
                                  <div className="text-muted small">
                                    Collection: <code>products_{account?.id || 'account'}</code>
                                  </div>
                                  <Button
                                    type="primary"
                                    onClick={() =>
                                      openSchemaDrawer('products',
                                        schemaSectionTab === 'fieldTypes'
                                          ? 'field-type'
                                          : schemaSectionTab === 'dynamicFields'
                                          ? 'dynamic-field'
                                          : schemaSectionTab === 'copyFields'
                                          ? 'copy-field'
                                          : 'field',
                                        'add'
                                      )
                                    }
                                  >
                                    Add {schemaSectionLabels[schemaSectionTab]}
                                  </Button>
                                </div>
                                <Tabs
                                  activeKey={schemaSectionTab}
                                  onChange={(key) => setSchemaSectionTab(key)}
                                  items={[
                                    { key: 'fields', label: 'Fields', children: renderSchemaTable('products', 'fields') },
                                    { key: 'fieldTypes', label: 'Field Types', children: renderSchemaTable('products', 'fieldTypes') },
                                    { key: 'dynamicFields', label: 'Dynamic Fields', children: renderSchemaTable('products', 'dynamicFields') },
                                    { key: 'copyFields', label: 'Copy Fields', children: renderSchemaTable('products', 'copyFields') },
                                  ]}
                                />
                              </>
                            ),
                          },
                        ]}
                      />
                    </div>
                  </div>
                )}
                <Drawer
                  title={filterDraft?.type === 'advanced' ? 'Edit Advanced Filter' : 'Edit Filter'}
                  placement="right"
                  width={filterDrawerWidth}
                  onClose={() => setFilterDrawerOpen(false)}
                  open={filterDrawerOpen}
                  destroyOnClose
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: -6,
                      top: 0,
                      width: 12,
                      height: '100%',
                      cursor: 'col-resize',
                    }}
                    onMouseDown={(event) => {
                      filterDrawerResizeRef.current = { startX: event.clientX, startWidth: filterDrawerWidth };
                      setFilterDrawerResizing(true);
                    }}
                  />
                  {filterDraft && (
                    <div className="d-flex flex-column gap-3">
                      <div>
                        <label className="form-label">Key</label>
                        <Input
                          value={filterDraft.key}
                          onChange={(e) => setFilterDraft((prev) => ({ ...prev, key: e.target.value }))}
                          placeholder="brand"
                        />
                      </div>
                      <div>
                        <label className="form-label">Label</label>
                        <Input
                          value={filterDraft.label}
                          onChange={(e) => setFilterDraft((prev) => ({ ...prev, label: e.target.value }))}
                          placeholder="Brand"
                        />
                      </div>
                      {filterDraft.type === 'standard' ? (
                        <>
                          <div>
                            <label className="form-label">Fields</label>
                            <Select
                              mode={availableFacetFields.error ? 'tags' : 'multiple'}
                              style={{ width: '100%' }}
                              value={filterDraft.fields}
                              onChange={(values) => setFilterDraft((prev) => ({ ...prev, fields: values }))}
                              loading={availableFacetFields.loading}
                              showSearch
                              optionFilterProp="label"
                              placeholder={
                                availableFacetFields.loading
                                  ? 'Loading available fields…'
                                  : 'Select one or more Solr facet fields'
                              }
                              options={availableFacetFields.fields.map((f) => ({
                                value: f.field,
                                // Plain string label so optionFilterProp="label" search still
                                // matches on field name/label text alone -- the record count is
                                // layered on top visually via optionRender below, not searched.
                                label: f.label ? `${f.label} (${f.field})` : f.field,
                                recordCount: getRecordCountForFields([f.field], filterValuePreview),
                              }))}
                              optionRender={(option) => (
                                <div className="d-flex justify-content-between align-items-center gap-2">
                                  <span>{option.data.label}</span>
                                  {option.data.recordCount !== null && (
                                    <span className="text-muted small">
                                      {option.data.recordCount.toLocaleString()} records
                                    </span>
                                  )}
                                </div>
                              )}
                              notFoundContent={availableFacetFields.loading ? 'Loading…' : 'No fields found'}
                            />
                            {availableFacetFields.variantMode && (
                              <div className="form-text">
                                This account uses variant-based display — only the fields Solr actually facets on for
                                variant systems are listed{availableFacetFields.brandFilterDimension ? ` (brand dimension: ${availableFacetFields.brandFilterDimension})` : ''}.
                              </div>
                            )}
                            {availableFacetFields.error && (
                              <div className="form-text text-danger">
                                Could not load available fields ({availableFacetFields.error}) — you can still type field
                                names directly.
                              </div>
                            )}
                            <div className="mt-2">
                              {filterValuePreview.loading ? (
                                <div className="form-text">Loading sample values…</div>
                              ) : filterValuePreview.error ? (
                                <div className="form-text text-warning">
                                  Could not load sample values ({filterValuePreview.error}).
                                </div>
                              ) : !filterDraft.fields.length ? (
                                <div className="form-text">Select one or more fields to preview available values.</div>
                              ) : previewValues.length === 0 ? (
                                <div className="form-text">No indexed values found yet for the selected field(s).</div>
                              ) : (
                                <div className="impqx-ahri-value-preview">
                                  <div className="form-text mb-1">Sample values from this field ({previewValues.length})</div>
                                  <div className="d-flex flex-wrap gap-1">
                                    {previewValues.slice(0, 15).map((item) => (
                                      <Tag key={item.value}>
                                        {item.value} <span className="text-muted">({item.count})</span>
                                      </Tag>
                                    ))}
                                  </div>
                                  {previewValues.length > 15 && (
                                    <Collapse
                                      ghost
                                      size="small"
                                      expandIcon={({ isActive }) => (
                                        <DownOutlined rotate={isActive ? 180 : 0} style={{ fontSize: 10 }} />
                                      )}
                                      items={[
                                        {
                                          key: 'more-values',
                                          label: (
                                            <span className="form-text">+{previewValues.length - 15} more values</span>
                                          ),
                                          children: (
                                            <div className="d-flex flex-wrap gap-1">
                                              {previewValues.slice(15).map((item) => (
                                                <Tag key={item.value}>
                                                  {item.value} <span className="text-muted">({item.count})</span>
                                                </Tag>
                                              ))}
                                            </div>
                                          ),
                                        },
                                      ]}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label">Value Type</label>
                              <select
                                className="form-select"
                                value={filterDraft.value_type}
                                onChange={(e) => setFilterDraft((prev) => ({ ...prev, value_type: e.target.value }))}
                              >
                                <option value="string">Text</option>
                                <option value="number">Number</option>
                                <option value="fraction">Fraction / Size (e.g. 1/2 inch, 1-1/2 in)</option>
                              </select>
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Sort Order</label>
                              <select
                                className="form-select"
                                value={filterDraft.sort_order}
                                onChange={(e) => setFilterDraft((prev) => ({ ...prev, sort_order: e.target.value }))}
                              >
                                {filterDraft.value_type === 'number' ? (
                                  <>
                                    <option value="asc">Value (Low → High)</option>
                                    <option value="desc">Value (High → Low)</option>
                                  </>
                                ) : filterDraft.value_type === 'fraction' ? (
                                  <>
                                    <option value="asc">Size (Smallest → Largest)</option>
                                    <option value="desc">Size (Largest → Smallest)</option>
                                  </>
                                ) : (
                                  <>
                                    <option value="asc">Alphabetical (A → Z)</option>
                                    <option value="desc">Alphabetical (Z → A)</option>
                                  </>
                                )}
                                <option value="count_asc"># of Results (Low → High)</option>
                                <option value="count_desc"># of Results (High → Low)</option>
                                <option value="custom">Custom order</option>
                              </select>
                            </div>
                          </div>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="filterMultiSelect"
                              checked={filterDraft.multi_select === true}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFilterDraft((prev) => ({ ...prev, multi_select: checked }));
                              }}
                            />
                            <label className="form-check-label" htmlFor="filterMultiSelect">
                              Allow multiple values (OR)
                            </label>
                            <div className="form-text">
                              When enabled, shoppers can select more than one value for this filter — matching
                              results include any system with at least one of the selected values. When disabled,
                              picking a new value replaces the current selection.
                            </div>
                          </div>
                          {filterDraft.sort_order === 'custom' && (
                            <div>
                              <label className="form-label mb-1">Custom Value Order</label>
                              <div className="form-text mb-2">
                                Drag a row to reorder, or use the arrows. New values discovered later are added to
                                the bottom until you place them.
                              </div>
                              {filterValuePreview.loading ? (
                                <div className="form-text">Loading sample values…</div>
                              ) : !filterDraft.fields.length ? (
                                <div className="form-text">Select one or more fields to arrange their values.</div>
                              ) : !(filterDraft.custom_value_order || []).length ? (
                                <div className="form-text">No indexed values found yet for the selected field(s).</div>
                              ) : (
                                <div className="d-flex flex-column gap-1">
                                  {(filterDraft.custom_value_order || []).map((value, index) => {
                                    const count = previewValueSet.has(value)
                                      ? previewValues.find((item) => item.value === value)?.count
                                      : undefined;
                                    return (
                                      <div
                                        key={value}
                                        draggable
                                        onDragStart={() => setDraggingCustomValue(value)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => handleCustomValueDrop(value)}
                                        className="d-flex align-items-center gap-2 px-2 py-1 border rounded"
                                      >
                                        <span style={{ cursor: 'grab' }} aria-hidden="true">⠿</span>
                                        <span className="flex-grow-1">{value}</span>
                                        {typeof count === 'number' && (
                                          <span className="text-muted">({count})</span>
                                        )}
                                        <Button
                                          size="small"
                                          onClick={() => moveCustomValue(index, -1)}
                                          disabled={index === 0}
                                        >
                                          ↑
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={() => moveCustomValue(index, 1)}
                                          disabled={index === (filterDraft.custom_value_order || []).length - 1}
                                        >
                                          ↓
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                          <Collapse
                            className="impqx-ahri-advanced-options mt-1"
                            expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
                            items={[
                              {
                                key: 'advanced',
                                label: (
                                  <span>
                                    <strong>Advanced Options</strong>
                                    {hasAdvancedValidationIssues && (
                                      <Tag color="error" className="ms-2">
                                        Needs attention
                                      </Tag>
                                    )}
                                  </span>
                                ),
                                children: (
                                  <div className="d-flex flex-column gap-4">
                                    <div>
                                      <label className="form-label mb-1">Display Map</label>
                                      <div className="form-text mb-2">
                                        Show a friendlier label for a raw value without combining it with any other
                                        value. Click a sample value below to add it, then type the label it should
                                        display.
                                      </div>
                                      {previewValues.length > 0 && (
                                        <div
                                          className="d-flex flex-wrap gap-1 mb-2"
                                          style={{ maxHeight: 220, overflowY: 'auto' }}
                                        >
                                          {previewValues.map((item) => (
                                            <Tag
                                              key={item.value}
                                              className="impqx-ahri-chip-clickable"
                                              style={{ cursor: 'pointer' }}
                                              onClick={() => handleInsertDisplayMapValue(item.value)}
                                            >
                                              {item.value}
                                            </Tag>
                                          ))}
                                        </div>
                                      )}
                                      <Input.TextArea
                                        ref={displayMapTextAreaRef}
                                        rows={4}
                                        value={filterDraft.value_display_map_raw}
                                        onChange={(e) =>
                                          setFilterDraft((prev) => ({ ...prev, value_display_map_raw: e.target.value }))
                                        }
                                        placeholder="RAW_VALUE=Friendly Label"
                                      />
                                      {unmatchedDisplayMapValues.length > 0 && (
                                        <Alert
                                          type="warning"
                                          showIcon
                                          className="mt-2"
                                          message="Some values don't match any sample value"
                                          description={`Not found in the selected field's sample data — check for typos or extra characters (e.g. stray HTML like "&reg;"): ${unmatchedDisplayMapValues.join(', ')}`}
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <label className="form-label mb-1">Group Values</label>
                                      <div className="form-text mb-2">
                                        Combine several raw values under one label shown to shoppers (the rest of the
                                        values for this field still show individually, unless disabled below). Select
                                        the values to combine, name the group, then add it.
                                      </div>
                                      {previewValues.length > 0 && (
                                        <div
                                          className="d-flex flex-wrap gap-1 mb-2"
                                          style={{ maxHeight: 220, overflowY: 'auto' }}
                                        >
                                          {previewValues.map((item) => (
                                            <Tag.CheckableTag
                                              key={item.value}
                                              checked={groupBuilderSelected.includes(item.value)}
                                              onChange={() => toggleGroupBuilderValue(item.value)}
                                            >
                                              {item.value}
                                            </Tag.CheckableTag>
                                          ))}
                                        </div>
                                      )}
                                      <div className="d-flex gap-2 mb-2">
                                        <Input
                                          value={groupBuilderLabel}
                                          onChange={(e) => setGroupBuilderLabel(e.target.value)}
                                          placeholder="Group label, e.g. Energy Star"
                                        />
                                        <Button
                                          onClick={handleAddGroup}
                                          disabled={!groupBuilderLabel.trim() || !groupBuilderSelected.length}
                                        >
                                          Add Group
                                        </Button>
                                      </div>
                                      {configuredGroups.length > 0 && (
                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                          {configuredGroups.map((group, index) => {
                                            const badValues = group.values.filter(
                                              (v) =>
                                                groupValueIssues.unmatched.includes(v) ||
                                                groupValueIssues.overlapping.includes(v)
                                            );
                                            const tag = (
                                              <Tag
                                                closable
                                                closeIcon={<CloseOutlined />}
                                                onClose={() => handleRemoveGroup(index)}
                                                color={badValues.length ? 'error' : undefined}
                                              >
                                                {group.label}: {group.values.join(', ')}
                                              </Tag>
                                            );
                                            return badValues.length ? (
                                              <Tooltip
                                                key={`${group.label}-${index}`}
                                                title={`Check these value(s): ${badValues.join(', ')}`}
                                              >
                                                {tag}
                                              </Tooltip>
                                            ) : (
                                              <span key={`${group.label}-${index}`}>{tag}</span>
                                            );
                                          })}
                                        </div>
                                      )}
                                      <Input.TextArea
                                        rows={4}
                                        value={filterDraft.value_groups_raw}
                                        onChange={(e) =>
                                          setFilterDraft((prev) => ({ ...prev, value_groups_raw: e.target.value }))
                                        }
                                        placeholder="Energy Star=Yes,true"
                                      />
                                      {(groupValueIssues.unmatched.length > 0 || groupValueIssues.overlapping.length > 0) && (
                                        <Alert
                                          type="warning"
                                          showIcon
                                          className="mt-2"
                                          message="Some group values need attention"
                                          description={
                                            <>
                                              {groupValueIssues.unmatched.length > 0 && (
                                                <div>
                                                  Not found in sample data — check for typos or extra characters:{' '}
                                                  {groupValueIssues.unmatched.join(', ')}
                                                </div>
                                              )}
                                              {groupValueIssues.overlapping.length > 0 && (
                                                <div>
                                                  Assigned to more than one group: {groupValueIssues.overlapping.join(', ')}
                                                </div>
                                              )}
                                            </>
                                          }
                                        />
                                      )}
                                      <div className="form-text mt-1">
                                        One per line: Group Label=raw_value1,raw_value2,...
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <Switch
                                        checked={filterDraft.show_ungrouped_values !== false}
                                        onChange={(checked) =>
                                          setFilterDraft((prev) => ({ ...prev, show_ungrouped_values: checked }))
                                        }
                                      />
                                      <span>Also show remaining values not included in any group</span>
                                    </div>
                                  </div>
                                ),
                              },
                            ]}
                          />
                        </>
                      ) : (
                        <div>
                          <label className="form-label">Values & Queries</label>
                          <Input.TextArea
                            rows={4}
                            value={filterDraft.values_raw}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({ ...prev, values_raw: e.target.value }))
                            }
                            placeholder="Complete=rows_ahri_complete_match:True"
                          />
                          <div className="d-flex align-items-center justify-content-between mt-2">
                            <div className="form-text">
                              One per line: Label=Solr query.
                            </div>
                            <Button size="small" onClick={runAdvancedFilterTest} loading={advancedFilterTest.loading}>
                              Test & Count
                            </Button>
                          </div>
                          {advancedFilterTest.error && (
                            <div className="alert alert-warning py-2 mt-2">{advancedFilterTest.error}</div>
                          )}
                          {advancedFilterTest.results.length > 0 && (
                            <div className="table-responsive mt-2">
                              <table className="table table-sm align-middle">
                                <thead>
                                  <tr>
                                    <th>Value</th>
                                    <th>Query</th>
                                    <th style={{ width: 120 }}>Count</th>
                                    <th style={{ width: 200 }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {advancedFilterTest.results.map((entry, idx) => (
                                    <tr key={`${entry.label}-${idx}`}>
                                      <td>{entry.label}</td>
                                      <td className="text-muted small">{entry.query || '-'}</td>
                                      <td>{typeof entry.count === 'number' ? entry.count : '-'}</td>
                                      <td className="small">
                                        {entry.error ? (
                                          <span className="text-danger">{entry.error}</span>
                                        ) : (
                                          <span className="text-success">OK</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="d-flex justify-content-end gap-2">
                        <Button onClick={() => setFilterDrawerOpen(false)}>Cancel</Button>
                        <Button type="primary" onClick={handleSaveFilterDraftClick}>
                          Save
                        </Button>
                      </div>
                    </div>
                  )}
                </Drawer>
              </>
            )}
            {settingsSection === 'exports' && (
              <div className="card mb-3">
                <div className="card-body">
                  <h5 className="card-title mb-3">AHRI Exports</h5>
                  <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${exportTab === 'create' ? 'active' : ''}`}
                        onClick={() => setExportTab('create')}
                      >
                        Create Export
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${exportTab === 'batches' ? 'active' : ''}`}
                        onClick={() => setExportTab('batches')}
                      >
                        Export Batches
                      </button>
                    </li>
                  </ul>

                  {exportTab === 'create' && (
                    <div className="row g-3">
                    <div className="col-12 col-lg-8">
                      <div className="row g-3 align-items-end">
                        <div className="col-12 col-md-6">
                          <label className="form-label">Export name</label>
                          <div className="form-control-plaintext">AHRI-PART-MATCHES</div>
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label">Format</label>
                          <select
                            className="form-select"
                            value={exportFormat}
                            onChange={(e) => setExportFormat(e.target.value)}
                          >
                            <option value="csv">CSV</option>
                            <option value="xlsx">XLSX</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-lg-4" />
                    <div className="col-12">
                      <div className="row g-3">
                        <div className="col-12 col-lg-4">
                          <div className="border rounded p-3 h-100">
                            <label className="form-label">Match status</label>
                            {[
                              { value: 'all', label: 'All' },
                              { value: 'complete', label: 'Complete matches' },
                              { value: 'partial', label: 'Partial matches' },
                              { value: 'none', label: 'No product matches' },
                            ].map((opt) => (
                              <div className="form-check" key={`match-${opt.value}`}>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`export_match_${opt.value}`}
                                  checked={exportMatchStatus.includes(opt.value)}
                                  onChange={(e) => {
                                    setExportMatchStatus((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(opt.value);
                                      else next.delete(opt.value);
                                      return Array.from(next);
                                    });
                                  }}
                                />
                                <label className="form-check-label" htmlFor={`export_match_${opt.value}`}>
                                  {opt.label}
                                </label>
                              </div>
                            ))}
                            <div className="form-text">Leave all unchecked to export all.</div>
                            {exportMatchStatus.length > 0 && (
                              <div className="d-flex flex-wrap gap-1 mt-2">
                                {exportMatchStatus.map((val) => (
                                  <span key={`match-sel-${val}`} className="badge bg-secondary">
                                    {val}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-12 col-lg-4">
                          <div className="border rounded p-3 h-100">
                            <label className="form-label">Program</label>
                            <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                              {programOptions.map((p) => (
                                <div className="form-check" key={`program-${p}`}>
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`export_program_${p}`}
                                    checked={exportProgram.includes(p)}
                                    onChange={(e) => {
                                      setExportProgram((prev) => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(p);
                                        else next.delete(p);
                                        return Array.from(next);
                                      });
                                    }}
                                  />
                                  <label className="form-check-label" htmlFor={`export_program_${p}`}>
                                    {p}
                                  </label>
                                </div>
                              ))}
                            </div>
                            <div className="form-text">Leave all unchecked to export all.</div>
                            {programOptions.length === 0 && (
                              <div className="form-text">No programs loaded yet.</div>
                            )}
                            {exportProgram.length > 0 && (
                              <div className="d-flex flex-wrap gap-1 mt-2">
                                {exportProgram.map((val) => (
                                  <span key={`program-sel-${val}`} className="badge bg-secondary">
                                    {val}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-12 col-lg-4">
                          <div className="border rounded p-3 h-100">
                            <label className="form-label">Brand</label>
                            <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                              {exportBrandList.map((b) => (
                                <div className="form-check" key={`brand-${b}`}>
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`export_brand_${b}`}
                                    checked={exportBrand.includes(b)}
                                    onChange={(e) => {
                                      setExportBrand((prev) => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(b);
                                        else next.delete(b);
                                        return Array.from(next);
                                      });
                                    }}
                                  />
                                  <label className="form-check-label" htmlFor={`export_brand_${b}`}>
                                    {b}
                                  </label>
                                </div>
                              ))}
                            </div>
                            <div className="form-text">Leave all unchecked to export all.</div>
                            {exportBrandLoading && <div className="form-text">Loading brands...</div>}
                            {!exportBrandLoading && exportBrandList.length === 0 && (
                              <div className="form-text">No brands loaded yet.</div>
                            )}
                            {exportBrand.length > 0 && (
                              <div className="d-flex flex-wrap gap-1 mt-2">
                                {exportBrand.map((val) => (
                                  <span key={`brand-sel-${val}`} className="badge bg-secondary">
                                    {val}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="form-text mt-2">
                      Includes AHRI ID, indoor/outdoor/furnace model numbers, and matched product details.
                    </div>
                    <div className="mt-2">
                      <span className="text-muted small">Matching rows:</span>{' '}
                      {exportCountLoading ? (
                        <span className="text-muted small">Calculating...</span>
                      ) : (
                        <span className="fw-semibold">{exportCount === null ? '—' : exportCount}</span>
                      )}
                    </div>
                    {exportError && <div className="alert alert-warning mt-3 mb-0">{exportError}</div>}
                  </div>
                  )}

                  {exportTab === 'batches' && (
                    <>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        <button className="btn btn-outline-primary btn-sm" onClick={loadExportJobs} disabled={exportJobsLoading}>
                          {exportJobsLoading ? 'Refreshing...' : 'Refresh'}
                        </button>
                      </div>
                      {exportJobsError && <div className="alert alert-warning">{exportJobsError}</div>}
                      <div className="table-responsive">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th>Job ID</th>
                              <th>Status</th>
                              <th>Format</th>
                              <th>Total Rows</th>
                              <th>Created</th>
                              <th>Finished</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {exportJobs.map((job) => (
                              <tr key={job.job_id}>
                                <td className="text-muted small">{job.job_id}</td>
                                <td className="text-capitalize">{job.status}</td>
                                <td className="text-uppercase">{job.format}</td>
                                <td>{typeof job.total_rows === 'number' ? job.total_rows : '-'}</td>
                                <td className="small">{job.created_at || '-'}</td>
                                <td className="small">{job.finished_at || '-'}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={job.status !== 'completed'}
                                    onClick={() => downloadExportJob(job.job_id, job.file_name)}
                                  >
                                    Download
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {exportJobs.length === 0 && (
                              <tr>
                                <td colSpan="7" className="text-muted">No export batches yet.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                  <div className="form-text mt-2">
                    Includes AHRI ID, indoor/outdoor/furnace model numbers, and matched product details.
                  </div>
                  {exportError && <div className="alert alert-warning mt-3 mb-0">{exportError}</div>}
                </div>
              </div>
            )}
            {settingsSection === 'eco_rebates' && (
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0">Eco Rebates</h6>
                    {renderSystemDefaultTag(AHRI_SECTION_SOURCE_KEYS.eco_rebates)}
                  </div>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="eco_rebates_enabled"
                          checked={!!cfg.eco_rebates?.enabled}
                          onChange={(e) => setCfg({ ...cfg, eco_rebates: { ...(cfg.eco_rebates || {}), enabled: e.target.checked } })}
                        />
                        <label className="form-check-label" htmlFor="eco_rebates_enabled">
                          Enable Eco Rebates for this site
                        </label>
                      </div>
                      <div className="form-text">
                        When enabled, the System Shop widget will display rebate information from EcoRebates for each AHRI system.
                      </div>
                    </div>
                    {!!cfg.eco_rebates?.enabled && (
                      <div className="col-12">
                        <label className="form-label">Eco Rebates Base URL</label>
                        <input
                          className="form-control"
                          placeholder="https://static.ecorebates.com/ui/widgets/demo"
                          value={cfg.eco_rebates?.base_url || ''}
                          onChange={(e) => setCfg({ ...cfg, eco_rebates: { ...(cfg.eco_rebates || {}), base_url: e.target.value } })}
                        />
                        <div className="form-text">
                          The base URL for the EcoRebates widget script. The widget will load <code>/product.js</code> from this URL.
                          Leave empty to use the default (<code>https://static.ecorebates.com/ui/widgets/demo</code>).
                        </div>
                      </div>
                    )}
                    <div className="col-12 d-flex justify-content-end">
                      <button className="btn btn-primary" onClick={save}>Save</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {settingsSection === 'system_defaults' && (
              <div className="card mb-3">
                <div className="card-body">
                  <h5 className="card-title mb-3">AHRI System Defaults</h5>
                  <div className="alert alert-info">
                    Copy settings from a selected client into global AHRI system defaults. These defaults are used
                    automatically whenever a client has no values configured for that module.
                  </div>
                  {enableCopyDefaults ? (
                    <div className="table-responsive">
                      <table className="table align-middle">
                        <thead>
                          <tr>
                            <th style={{ minWidth: 220 }}>Module</th>
                            <th style={{ minWidth: 260 }}>Source Client</th>
                            <th style={{ width: 180 }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {AHRI_SYSTEM_DEFAULT_COPY_MODULES.map((moduleItem) => (
                            <tr key={`ahri-default-copy-${moduleItem.key}`}>
                              <td className="fw-semibold">{moduleItem.label}</td>
                              <td>
                                <select
                                  className="form-select"
                                  value={systemDefaultSelections[moduleItem.key] || ''}
                                  onChange={(e) =>
                                    setSystemDefaultSelections((prev) => ({
                                      ...prev,
                                      [moduleItem.key]: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="">Select client</option>
                                  {systemDefaultClients.map((clientItem) => (
                                    <option key={`ahri-default-client-${moduleItem.key}-${clientItem.id}`} value={String(clientItem.id)}>
                                      {clientItem.name || `Account ${clientItem.id}`} (#{clientItem.id})
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  disabled={
                                    !systemDefaultSelections[moduleItem.key] || !!systemDefaultCopying[moduleItem.key]
                                  }
                                  onClick={() => copyAhriSystemDefaultFromClient(moduleItem.key)}
                                >
                                  {systemDefaultCopying[moduleItem.key] ? 'Copying...' : 'Copy to System'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-muted small">
                      Copy defaults is disabled for this account.
                    </div>
                  )}
                </div>
              </div>
            )}
            {settingsSection === 'schemas' && (
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0">Program Schemas</h6>
                      {renderSystemDefaultTag(AHRI_SECTION_SOURCE_KEYS.schemas)}
                    </div>
                    <button
                      className="btn btn-sm btn-primary"
                    onClick={()=>{
                        setSchemaProg('');
                        setSchemaCollection('');
                        setSchemaLines('');
                        setSchemaProductFields([]);
                        setSchemaSelectedFields([]);
                        const nextTree = createEmptyWhereTree();
                        setQbTree(nextTree);
                        setWhereJsonDirty(false);
                        setWhereJsonError('');
                        setWhereJsonText(JSON.stringify(nextTree, null, 2));
                        setDrawerOpen(true);
                      }}
                    >
                      Add Program Schema
                    </button>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Program</th>
                          <th>Collection</th>
                          <th>Fields</th>
                          <th>Product Lookup Fields</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(cfg.program_schemas || []).map((s, i) => (
                          <tr key={i}>
                            <td><code>{s.program}</code></td>
                            <td>{s.collection || <span className="text-muted">default</span>}</td>
                            <td style={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={(s.fields || []).join(', ')}>
                              {(s.fields || []).join(', ')}
                            </td>
                            <td style={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={(s.product_lookup_fields || []).join(', ')}>
                              {s.product_lookup_fields && s.product_lookup_fields.length > 0 ? s.product_lookup_fields.join(', ') : <span className="text-muted">None</span>}
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={()=>{
                                  setSchemaProg(s.program || '');
                                  setSchemaCollection(s.collection || '');
                                  const lines = (s.fields || []).map(f => `${f}: `).join('\n');
                                  setSchemaLines(lines);
                                  setSchemaProductFields(Array.isArray(s.product_lookup_fields) ? s.product_lookup_fields : []);
                                  setSchemaSelectedFields(Array.isArray(s.fields) ? s.fields : []);
                                  const existingTree = s.where_tree && typeof s.where_tree === 'object'
                                    ? normalizeWhereTree(s.where_tree)
                                    : createEmptyWhereTree();
                                  setQbTree(existingTree);
                                  setWhereJsonDirty(false);
                                  setWhereJsonError('');
                                  setWhereJsonText(JSON.stringify(existingTree, null, 2));
                                  setDrawerOpen(true);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-success me-2"
                                onClick={async ()=>{
                                  const program = s.program || '';
                                  setVerifyProgram(program);
                                  setVerifyOpen(true);
                                  setVerifyLoading(true);
                                  setVerifyError('');
                                  setVerifyData(null);
                                  try {
                                    const r = await fetch(`${API_BASE_URL}/api/v1/ahri/verify`, {
                                      method: 'POST',
                                      headers,
                                      body: JSON.stringify({ program, access_token_override: (cfg.access_token_override || '') })
                                    });
                                    const j = await r.json().catch(()=>({ detail: 'Invalid JSON' }));
                                    if (!r.ok) {
                                      setVerifyError(j?.detail || `HTTP ${r.status}`);
                                    } else {
                                      setVerifyData(j);
                                      if (j && j.success === false && j.error) {
                                        setVerifyError(j.error);
                                      }
                                    }
                                  } catch (e) {
                                    setVerifyError(String(e));
                                  } finally {
                                    setVerifyLoading(false);
                                  }
                                }}
                              >
                                Verify
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  const list = (cfg.program_schemas || []).filter(x => (x.program || '').toLowerCase() !== (s.program || '').toLowerCase());
                                  setCfg({ ...cfg, program_schemas: list });
                                  try {
                                    setSchemasText(JSON.stringify(list, null, 2));
                                  } catch {}
                                }}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                        {(!cfg.program_schemas || cfg.program_schemas.length === 0) && (
                          <tr><td colSpan="5" className="text-muted">No program schemas defined.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            <div className="d-flex gap-2 mb-3">
              {settingsSection === 'exports' && (
                <button className="btn btn-primary" onClick={exportAhriPartMatches} disabled={exportLoading}>
                  {exportLoading ? 'Exporting...' : 'Export'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <SettingsHistoryPanel
        open={settingsHistoryOpen}
        onClose={() => setSettingsHistoryOpen(false)}
        apiBaseUrl={API_BASE_URL}
        headers={headers}
        onRestored={loadConfig}
        sectionKeys={AHRI_SECTION_SOURCE_KEYS[settingsSection]}
        sectionLabel={AHRI_SETTINGS_SECTION_LABELS[settingsSection]}
      />

      {activeTab === 'sync' && (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <div>
                  <h5 className="mb-1">AHRI Data Sync</h5>
                  <div className="text-muted small">
                    Pulls the latest certificate data from the AHRI directory for the checked program(s), writes it to the AHRI database, then re-indexes it into Solr — including <code>ahri_system_variant</code> documents when variant sync is enabled.
                  </div>
                </div>
                <div className="btn-group">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => { loadJobs(); loadSolrImportState(); }}
                  >
                    Refresh
                  </button>
                  {activeJob && (
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => cancelJob(activeJob.job_id)}
                      disabled={activeJob.status === 'cancelling'}
                    >
                      {activeJob.status === 'cancelling' ? 'Stopping…' : 'Stop Sync'}
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={startSync}
                    disabled={!selectedPrograms.length || !!activeJob}
                  >
                    {activeJob ? 'Sync in progress…' : 'Start Sync'}
                  </button>
                </div>
              </div>

              {(() => {
                const isRunning = activeJob?.status === 'running';
                const statusColors = {
                  running: 'bg-primary', pending: 'bg-warning text-dark', queued: 'bg-warning text-dark',
                  cancelling: 'bg-danger', completed: 'bg-success', failed: 'bg-danger', cancelled: 'bg-secondary',
                };
                const displayStatus = activeJob?.status || jobs[0]?.status || 'idle';
                const statusBadge = statusColors[displayStatus] || 'bg-secondary';
                const upserted = Number(activeJob?.items_upserted ?? jobs[0]?.items_upserted ?? 0);
                const pages = Number(activeJob?.pages_completed ?? jobs[0]?.pages_completed ?? 0);
                const total = Number(activeJob?.total ?? jobs[0]?.total ?? 0);
                const showRatio = total > 0 && upserted <= total;
                const pct = showRatio ? Math.min(100, Math.round((upserted / total) * 100)) : null;

                const variantsEnabled = !!cfg.solr?.sync_variants_on_import;
                const docCounts = solrImportState.doc_counts || {};
                const certDocs = docCounts.ahri_system_docs;
                const variantDocs = docCounts.ahri_system_variant_docs;
                const variantsSynced = Number(solrImportState.variants_synced || 0);
                const variantsSkipped = Number(solrImportState.variants_skipped || 0);
                const variantsFailedBatches = Number(solrImportState.variants_sync_failed_batches || 0);
                const solrRunning = ['running', 'queued'].includes(solrImportState.status);

                return (
                  <div className="row g-3">
                    <div className="col-sm-6 col-lg-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small mb-1">Status</div>
                        <div className="d-flex align-items-center gap-2">
                          {isRunning && <div className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />}
                          <span className={`badge ${statusBadge} text-capitalize`}>{displayStatus}</span>
                        </div>
                        {activeJob?.job_id && (
                          <div className="small text-muted mt-1" title={activeJob.job_id}>Job {activeJob.job_id.slice(0, 8)}…</div>
                        )}
                        {(activeJob?.started_at || jobs[0]?.started_at) && (
                          <div className="small text-muted mt-1">
                            Started: {new Date(activeJob?.started_at || jobs[0]?.started_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small mb-1">Certificates Synced</div>
                        <div className="fw-semibold">
                          {upserted.toLocaleString()}{showRatio ? ` / ${total.toLocaleString()}` : ''}
                        </div>
                        <div className="small text-muted mt-1">{pages.toLocaleString()} page{pages === 1 ? '' : 's'} fetched</div>
                        {(showRatio || (isRunning && upserted > 0)) && (
                          <div className="progress mt-2" style={{ height: '4px' }}>
                            <div
                              className={`progress-bar ${!showRatio ? 'progress-bar-striped progress-bar-animated' : ''}`}
                              role="progressbar"
                              style={{ width: showRatio ? `${pct}%` : '100%' }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small mb-1 d-flex align-items-center gap-1">
                          Solr Index
                          {solrRunning && <span className="badge bg-primary-subtle text-primary-emphasis border">syncing</span>}
                        </div>
                        <div className="fw-semibold">
                          {Number(certDocs ?? 0).toLocaleString()} <span className="fw-normal text-muted small">certificates</span>
                        </div>
                        <div className="fw-semibold">
                          {Number(variantDocs ?? 0).toLocaleString()} <span className="fw-normal text-muted small">ahri_system_variant docs</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small mb-1">AHRI System Variants</div>
                        {variantsEnabled ? (
                          <>
                            <div className="fw-semibold text-success">{variantsSynced.toLocaleString()} synced</div>
                            <div className="small text-muted mt-1">{variantsSkipped.toLocaleString()} skipped (unchanged)</div>
                            {variantsFailedBatches > 0 && (
                              <div className="small text-danger mt-1">{variantsFailedBatches} failed batch{variantsFailedBatches === 1 ? '' : 'es'}</div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="fw-semibold text-muted">Disabled</div>
                            <div className="small text-muted mt-1">
                              Enable "Sync variants on import" under Solr settings to keep <code>ahri_system_variant</code> docs current.
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-start">
                <div className="col-lg-8">
                  <label className="form-label">Programs to Sync</label>
                  <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {programOptions.map((prog) => (
                      <div className="form-check" key={prog}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`prog-chk-${prog}`}
                          checked={selectedPrograms.includes(prog)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPrograms((prev) => prev.includes(prog) ? prev : [...prev, prog]);
                            } else {
                              setSelectedPrograms((prev) => prev.filter((p) => p !== prog));
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor={`prog-chk-${prog}`}>
                          {prog}
                        </label>
                      </div>
                    ))}
                    {programOptions.length === 0 && (
                      <span className="text-muted small">No programs configured.</span>
                    )}
                  </div>
                  <div className="form-text">
                    Check one or more programs to sync. Leaving all checked runs the sync for every program schema.
                  </div>
                </div>
                <div className="col-lg-4 d-flex flex-column gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={handleSelectAllPrograms}>
                    Select All
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={handleClearPrograms}>
                    Clear Selection
                  </button>
                  <div className="small text-muted">
                    Sync will only run for the checked programs.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 mb-3">
            <button className="btn btn-outline-success" onClick={startEmbeddings} disabled={!cfg.vectorize_enabled} title={!cfg.vectorize_enabled ? 'Enable vectorizing in settings to allow embeddings' : ''}>Start Embeddings</button>
            <div className="form-check ms-2">
              <input className="form-check-input" type="checkbox" id="embdbg" checked={embDebug} onChange={(e)=>setEmbDebug(e.target.checked)} />
              <label className="form-check-label" htmlFor="embdbg">Debug</label>
            </div>
            <div className="form-check ms-auto">
              <input className="form-check-input" type="checkbox" id="poll" checked={polling} onChange={(e)=>setPolling(e.target.checked)} />
              <label className="form-check-label" htmlFor="poll">Auto-refresh (5s)</label>
            </div>
          </div>

          <div className="alert alert-info mb-3">
            <strong>What this does:</strong> Fetches certificate records from the AHRI directory for the checked program(s) and writes them to the AHRI database, then runs a delta Solr re-index of the affected records. When "Sync variants on import" is enabled (Settings &gt; Solr), the same pass also re-syncs each affected certificate's <code>ahri_system_variant</code> documents (one per matched indoor/outdoor/furnace combination) so storefront search and filters stay current — it does not regenerate variants from product mapping changes; use the Product Attributes Sync job for that.
          </div>

          <div className="card">
            <div className="card-body">
              <h6 className="mb-3">Recent Jobs</h6>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Job ID</th>
                      <th>Status</th>
                      <th>Last Activity</th>
                      <th className="text-end">Pages Fetched</th>
                      <th className="text-end">Records Written</th>
                      <th className="text-end">Page Size</th>
                      <th>Started</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(j => {
                      const canCancel = j.status === 'pending' || j.status === 'running';
                      const canDelete = !canCancel;
                      const statusColors = { completed: 'bg-success', running: 'bg-primary', pending: 'bg-warning text-dark', queued: 'bg-warning text-dark', cancelled: 'bg-secondary', cancelling: 'bg-danger', failed: 'bg-danger' };
                      const badgeClass = statusColors[j.status] || 'bg-secondary';
                      const shortId = (j.job_id || '').slice(0, 8);
                      // Parse last "pull" log line for active-job detail
                      const em = j.error_message || '';
                      const logLines = em.split(/\r?\n/).filter(Boolean);
                      const pullLine = [...logLines].reverse().find(l => l.startsWith('pull '));
                      const pullMatch = pullLine ? pullLine.match(/program=(\S+)\s+status=(\S+)\s+outdoor=(\S+)\s+indoor=(\S+)\s+page=(\d+)/) : null;
                      return (
                        <tr key={j.job_id}>
                          <td>
                            <code title={j.job_id} style={{ cursor: 'default' }}>{shortId}…</code>
                          </td>
                          <td>
                            <span className={`badge ${badgeClass}`}>{j.status}</span>
                          </td>
                          <td>
                            {pullMatch ? (
                              <div className="d-flex flex-wrap gap-1">
                                <span className="badge bg-primary-subtle text-primary-emphasis border">Program: {pullMatch[1]}</span>
                                <span className="badge bg-secondary-subtle text-secondary-emphasis border">Page: {pullMatch[5]}</span>
                                {pullMatch[3] !== 'None' && <span className="badge bg-info-subtle text-info-emphasis border">Outdoor: {pullMatch[3]}</span>}
                                {pullMatch[4] !== 'None' && <span className="badge bg-info-subtle text-info-emphasis border">Indoor: {pullMatch[4]}</span>}
                              </div>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                          <td className="text-end">{j.pages_completed != null ? Number(j.pages_completed).toLocaleString() : '—'}</td>
                          <td className="text-end fw-semibold">{j.items_upserted != null ? Number(j.items_upserted).toLocaleString() : '—'}</td>
                          <td className="text-end text-muted">{j.page_size || '—'}</td>
                          <td className="text-muted small text-nowrap">{j.started_at ? new Date(j.started_at).toLocaleString() : '—'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-outline-danger" disabled={!canCancel} onClick={() => cancelJob(j.job_id)}>Cancel</button>
                              <button className="btn btn-sm btn-outline-secondary" disabled={!canDelete} onClick={() => deleteJob(j.job_id)}>Delete</button>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                disabled={!j.error_message}
                                onClick={() => { setLogText(j.error_message || ''); setLogOpen(true); }}
                                title="View progress log"
                              >
                                Log
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Log Modal */}
      <div className={`modal fade ${logOpen ? 'show d-block' : ''}`} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Job Log</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={()=>setLogOpen(false)} />
            </div>
            <div className="modal-body">
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{logText || 'No log available'}</pre>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={()=>setLogOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      </div>
      {/* Verify Modal */}
      <div className={`modal fade ${verifyOpen ? 'show d-block' : ''}`} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Verify Program {verifyProgram ? `- ${verifyProgram}` : ''}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={()=>setVerifyOpen(false)} />
            </div>
            <div className="modal-body">
              {verifyLoading && <div className="text-muted">Verifying...</div>}
              {!verifyLoading && (
                <>
                  {verifyError && (
                    <div className="alert alert-danger">
                      {verifyError}
                    </div>
                  )}
                  {verifyData && (
                    <>
                      <div className="mb-2">
                        <div className="row g-2">
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Success</div>
                                <div className="fw-bold">{String(verifyData.success)}</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Total Count</div>
                                <div className="fw-bold">{verifyData.totalCount ?? '—'}</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Returned (Page 1)</div>
                                <div className="fw-bold">{verifyData.returned ?? (Array.isArray(verifyData.items) ? verifyData.items.length : '—')}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <div className="row g-2">
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Auth Used</div>
                                <div className="fw-bold">
                                  {(verifyData?.debug?.auth_used) || (verifyData?.auth_used) || '—'}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Auth Source</div>
                                <div className="fw-bold">
                                  {(verifyData?.debug?.auth_source) || (verifyData?.auth_source) || '—'}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-4">
                            <div className="card">
                              <div className="card-body">
                                <div className="small text-muted">Transport</div>
                                <div className="fw-bold">
                                  {(verifyData?.debug?.transport) || (verifyData?.transport) || '—'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {Array.isArray(verifyData.items) && verifyData.items.length > 0 && (
                        <div className="mb-3">
                          <h6>Items (preview)</h6>
                          <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 320, overflow: 'auto' }}>
{JSON.stringify(verifyData.items.slice(0, 10), null, 2)}
                          </pre>
                        </div>
                      )}
                      <div className="accordion" id="verifyAcc">
                        <div className="accordion-item">
                          <h2 className="accordion-header" id="v-gql">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#v-gql-c" aria-expanded="false" aria-controls="v-gql-c">
                              GraphQL Queries
                            </button>
                          </h2>
                          <div id="v-gql-c" className="accordion-collapse collapse" aria-labelledby="v-gql" data-bs-parent="#verifyAcc">
                            <div className="accordion-body">
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <div className="small text-muted">Inline Query</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{(verifyData?.request_inline?.query) || (verifyData?.debug?.request_inline?.query) || '(none)'}
                                  </pre>
                                </div>
                                <div className="col-md-6">
                                  <div className="small text-muted">Variables Query</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{(verifyData?.request_vars?.query) || (verifyData?.debug?.request_vars?.query) || '(none)'}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="accordion-item">
                          <h2 className="accordion-header" id="v-req">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#v-req-c" aria-expanded="false" aria-controls="v-req-c">
                              Request Bodies
                            </button>
                          </h2>
                          <div id="v-req-c" className="accordion-collapse collapse" aria-labelledby="v-req" data-bs-parent="#verifyAcc">
                            <div className="accordion-body">
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <div className="small text-muted">Inline</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{JSON.stringify(verifyData.request_inline || (verifyData.debug && verifyData.debug.request_inline) || {}, null, 2)}
                                  </pre>
                                </div>
                                <div className="col-md-6">
                                  <div className="small text-muted">Variables</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{JSON.stringify(verifyData.request_vars || (verifyData.debug && verifyData.debug.request_vars) || {}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="accordion-item">
                          <h2 className="accordion-header" id="v-resp">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#v-resp-c" aria-expanded="false" aria-controls="v-resp-c">
                              Responses
                            </button>
                          </h2>
                          <div id="v-resp-c" className="accordion-collapse collapse" aria-labelledby="v-resp" data-bs-parent="#verifyAcc">
                            <div className="accordion-body">
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <div className="small text-muted">Inline</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{JSON.stringify((verifyData.debug && verifyData.debug.response) || verifyData.inline_response || {}, null, 2)}
                                  </pre>
                                </div>
                                <div className="col-md-6">
                                  <div className="small text-muted">Variables</div>
                                  <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
{JSON.stringify(verifyData.response_vars || {}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                              {verifyData.inline_status && (
                                <div className="mt-2 small text-muted">Inline status: {verifyData.inline_status}</div>
                              )}
                              {verifyData.vars_status && (
                                <div className="mt-1 small text-muted">Vars status: {verifyData.vars_status}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={()=>setVerifyOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      </div>
      {/* Confirm Save Schema Modal */}
      <div className={`modal fade ${confirmSchemaOpen ? 'show d-block' : ''}`} tabIndex="-1" role="dialog">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Save</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={()=>setConfirmSchemaOpen(false)} />
            </div>
            <div className="modal-body">
              <div className="mb-2">Save schema for program <code>{pendingSchema?.prog}</code>?</div>
              {pendingSchema && (
                <ul className="small mb-0">
                  <li>Collection: <code>{pendingSchema.entry.collection || 'default'}</code></li>
                  <li>Fields: {Array.isArray(pendingSchema.entry.fields) ? pendingSchema.entry.fields.length : 0}</li>
                  <li>Product Lookup Fields: {Array.isArray(pendingSchema.entry.product_lookup_fields) ? pendingSchema.entry.product_lookup_fields.length : 0}</li>
                  <li>Where Builder: {pendingSchema.entry.where_tree ? 'configured' : 'none'}</li>
                </ul>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={()=>setConfirmSchemaOpen(false)}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async ()=>{
                  if (!pendingSchema) { setConfirmSchemaOpen(false); return; }
                  const { prog, entry } = pendingSchema;
                  const list = Array.isArray(cfg.program_schemas) ? [...cfg.program_schemas] : [];
                  const idx = list.findIndex(x => (x.program || '').toLowerCase() === prog.toLowerCase());
                  if (idx >= 0) list[idx] = { ...list[idx], ...entry }; else list.push(entry);
                  const newCfg = { ...cfg, program_schemas: list };
                  setCfg(newCfg);
                  try { setSchemasText(JSON.stringify(list, null, 2)); } catch {}
                  // persist immediately so collection is saved to DB
                  try {
                    const r = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, {
                      method: 'PUT',
                      headers,
                      body: JSON.stringify(newCfg)
                    });
                    if (!r.ok) {
                      const j = await r.json().catch(()=>({}));
                      alert(`Save failed: ${j?.detail || r.status}`);
                    }
                  } catch (e) {
                    alert(`Save failed: ${String(e)}`);
                  }
                  setConfirmSchemaOpen(false);
                  setPendingSchema(null);
                  setDrawerOpen(false);
                }}
              >
                Confirm Save
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Delete AHRI Data by Brand Modal */}
      <div className={`modal fade ${brandDeleteModalOpen ? 'show d-block' : ''}`} tabIndex="-1" role="dialog">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Delete AHRI data for brand <code>{brandPurgeName}</code>
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={closeBrandDeleteModal} disabled={brandPurging} />
            </div>
            <div className="modal-body">
              {brandImpactLoading && <div className="text-muted">Loading impact…</div>}
              {!brandImpactLoading && brandImpactError && (
                <div className="alert alert-danger mb-0">Failed to load impact: {brandImpactError}</div>
              )}
              {!brandImpactLoading && !brandImpactError && brandImpact && brandDeleteStep === 1 && (
                <>
                  <div className="alert alert-warning">
                    This action is <strong>permanent and cannot be undone</strong>. It will remove data from PostgreSQL and Solr, and prune matched references from products.
                  </div>
                  <ul className="mb-3">
                    <li><strong>{brandImpact.total_rows}</strong> AHRI record{brandImpact.total_rows === 1 ? '' : 's'} will be deleted</li>
                    <li><strong>{brandImpact.complete_matches}</strong> complete match{brandImpact.complete_matches === 1 ? '' : 'es'} will be lost</li>
                    <li><strong>{brandImpact.partial_matches}</strong> partial match{brandImpact.partial_matches === 1 ? '' : 'es'} will be lost</li>
                    <li><strong>{brandImpact.no_matches}</strong> unmatched record{brandImpact.no_matches === 1 ? '' : 's'} will be removed</li>
                    <li><strong>{brandImpact.pending_review_rows}</strong> record{brandImpact.pending_review_rows === 1 ? '' : 's'} with pending mapping review will be discarded</li>
                    <li><strong>{brandImpact.distinct_programs}</strong> program{brandImpact.distinct_programs === 1 ? '' : 's'} affected</li>
                    <li><strong>{brandImpact.affected_products}</strong> product{brandImpact.affected_products === 1 ? '' : 's'} will have this brand's AHRI matches unlinked</li>
                  </ul>
                  {brandImpact.total_rows === 0 && (
                    <div className="alert alert-info mb-0">No AHRI records were found for this brand.</div>
                  )}
                </>
              )}
              {!brandImpactLoading && !brandImpactError && brandImpact && brandDeleteStep === 2 && (
                <>
                  <div className="alert alert-danger">
                    Type the brand name <strong>{brandPurgeName}</strong> below to confirm permanent deletion of {brandImpact.total_rows} record{brandImpact.total_rows === 1 ? '' : 's'}.
                  </div>
                  <input
                    className="form-control"
                    placeholder="Type brand name to confirm"
                    value={brandConfirmText}
                    onChange={(e) => setBrandConfirmText(e.target.value)}
                    autoFocus
                  />
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={closeBrandDeleteModal} disabled={brandPurging}>
                Cancel
              </button>
              {brandDeleteStep === 1 && (
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={brandImpactLoading || !!brandImpactError || !brandImpact || brandImpact.total_rows === 0}
                  onClick={() => setBrandDeleteStep(2)}
                >
                  Continue
                </button>
              )}
              {brandDeleteStep === 2 && (
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={brandPurging || brandConfirmText.trim().toLowerCase() !== (brandPurgeName || '').trim().toLowerCase()}
                  onClick={handleBrandPurge}
                >
                  {brandPurging ? 'Deleting…' : 'Permanently Delete'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Slide-out drawer for program schema (right to left) */}
      <div className={`offcanvas offcanvas-end ${drawerOpen ? 'show' : ''}`} style={{ visibility: drawerOpen ? 'visible' : 'hidden', width: `${drawerWidthPct}vw`, maxWidth: '75vw' }}>
        {/* Resize handle */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '6px',
            cursor: 'col-resize',
            background: 'rgba(0,0,0,0.05)'
          }}
          onMouseDown={() => setResizing(true)}
          title="Drag to resize (50% - 75%)"
        />
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">Add Program Schema</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={()=>setDrawerOpen(false)}></button>
        </div>
        <div className="offcanvas-body">
          <div className="mb-3">
            <label className="form-label">Program</label>
            <input className="form-control" value={schemaProg} onChange={e=>setSchemaProg(e.target.value)} placeholder="e.g., ULE" />
          </div>
          <div className="mb-3">
            <label className="form-label">Collection (optional)</label>
            <input className="form-control" value={schemaCollection} onChange={e=>setSchemaCollection(e.target.value)} placeholder="e.g., uleResults" />
          </div>
          <div className="mb-2 small text-muted">Paste fields and types (one per line): fieldName: Type</div>
          <div className="mb-3">
            <textarea className="form-control" rows={8} value={schemaLines} onChange={e=>setSchemaLines(e.target.value)} placeholder={`referenceId: Long\nmodelStatus: String`} />
          </div>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">Fields to Fetch</label>
              {schemaFieldOptions.length > 0 && (
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSchemaSelectedFields([...schemaFieldOptions])}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSchemaSelectedFields([])}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            <div className="border rounded p-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
              {schemaFieldOptions.length === 0 ? (
                <div className="text-muted small">Add fields above to manage selection.</div>
              ) : (
                schemaFieldOptions.map((field) => {
                  const inputId = `schema-select-field-${field}`;
                  return (
                    <div className="form-check" key={field}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={inputId}
                        checked={schemaSelectedFields.includes(field)}
                        onChange={() =>
                          setSchemaSelectedFields((prev) =>
                            prev.includes(field)
                              ? prev.filter((f) => f !== field)
                              : [...prev, field]
                          )
                        }
                      />
                      <label className="form-check-label" htmlFor={inputId}>
                        {field}
                      </label>
                    </div>
                  );
                })
              )}
            </div>
            <div className="form-text">
              Only the selected fields will be requested from the AHRI API for this program.
            </div>
          </div>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">Product Lookup &amp; Mapping Fields</label>
              {schemaFieldOptions.length > 0 && (
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSchemaProductFields([...schemaFieldOptions])}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSchemaProductFields([])}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            <div className="border rounded p-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
              {schemaFieldOptions.length === 0 ? (
                <div className="text-muted small">Add fields above to enable product lookup selection.</div>
              ) : (
                schemaFieldOptions.map((field) => {
                  const inputId = `schema-product-field-${field}`;
                  return (
                    <div className="form-check" key={field}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={inputId}
                        checked={schemaProductFields.includes(field)}
                        onChange={() =>
                          setSchemaProductFields((prev) =>
                            prev.includes(field)
                              ? prev.filter((f) => f !== field)
                              : [...prev, field]
                          )
                        }
                      />
                      <label className="form-check-label" htmlFor={inputId}>
                        {field}
                      </label>
                    </div>
                  );
                })
              )}
            </div>
            <div className="form-text">
              These fields are used when looking up or mapping products derived from this program’s data.
            </div>
          </div>
          
          {/* Visual Where Builder */}
          <hr />
          <h6>Where Builder (AND / OR, nested groups)</h6>
          <div className="mb-2 small text-muted">Build advanced where conditions. These will be used as a GraphQL where clause. If provided, simple equality filters for outdoor/indoor/status will be omitted.</div>
          {(() => {
            const ops = ['eq','ne','gt','gte','lt','lte','in','contains','startsWith','endsWith'];
            const parseFieldNames = () => {
              const names = new Set();
              (schemaLines || '').split('\n').forEach(line => {
                const m = String(line||'').match(/^\s*([A-Za-z_][A-Za-z0-9_]*)\s*:/);
                if (m && m[1]) names.add(m[1]);
              });
              return Array.from(names);
            };
            const candidateFields = parseFieldNames();
            const updateGroupAt = (path, mutator) => {
              setQbTree(prev => {
                const copy = JSON.parse(JSON.stringify(prev || { logic:'AND', conditions:[], groups:[] }));
                let node = copy;
                for (const idx of path) {
                  if (!node.groups) node.groups = [];
                  node = node.groups[idx];
                }
                mutator(node);
                return copy;
              });
            };
            const renderGroup = (group, path = []) => (
              <div className="border rounded p-2 mb-2" style={{ borderColor: '#ddd' }}>
                <div className="d-flex align-items-center mb-2">
                  <label className="me-2">Logic</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: 120 }}
                    value={group.logic || 'AND'}
                    onChange={e => updateGroupAt(path, g => { g.logic = e.target.value; })}
                  >
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                  {path.length > 0 && (
                    <button
                      className="btn btn-sm btn-outline-danger ms-auto"
                      onClick={() => {
                        const parentPath = path.slice(0, -1);
                        const idx = path[path.length - 1];
                        updateGroupAt(parentPath, p => {
                          p.groups.splice(idx, 1);
                        });
                      }}
                    >Remove group</button>
                  )}
                </div>
                <div className="mb-2">
                  {(group.conditions || []).map((c, ci) => (
                    <div className="d-flex align-items-center mb-2" key={ci}>
                      <select
                        className="form-select form-select-sm me-2"
                        value={c.field || ''}
                        onChange={e => updateGroupAt(path, g => { g.conditions[ci].field = e.target.value; })}
                        style={{ maxWidth: 240 }}
                      >
                        <option value="">Select field...</option>
                        {candidateFields.map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                      <select
                        className="form-select form-select-sm me-2"
                        style={{ maxWidth: 140 }}
                        value={c.op || 'eq'}
                        onChange={e => updateGroupAt(path, g => { g.conditions[ci].op = e.target.value; })}
                      >
                        {ops.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <input
                        className="form-control form-control-sm me-2"
                        value={c.value ?? ''}
                        placeholder={c.op === 'in' ? 'v1, v2, v3' : 'value'}
                        onChange={e => updateGroupAt(path, g => { g.conditions[ci].value = e.target.value; })}
                      />
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => updateGroupAt(path, g => { g.conditions.splice(ci, 1); })}
                      >✕</button>
                    </div>
                  ))}
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => updateGroupAt(path, g => { (g.conditions = g.conditions || []).push({ field: '', op: 'eq', value: '' }); })}
                  >+ Condition</button>
                </div>
                <div className="ms-3">
                  {(group.groups || []).map((sg, gi) => (
                    <div key={gi}>
                      {renderGroup(sg, [...path, gi])}
                    </div>
                  ))}
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => updateGroupAt(path, g => { (g.groups = g.groups || []).push({ logic: 'AND', conditions: [], groups: [] }); })}
                  >+ Group</button>
                </div>
              </div>
            );
            return (
              <div className="mb-3">
                {renderGroup(qbTree, [])}
                <div className="d-flex justify-content-between">
                <div className="small text-muted">{hasWhereContent(qbTree) ? 'Conditions configured' : 'No conditions'}</div>
                  <button
                    className="btn btn-sm btn-outline-warning"
                  onClick={() => {
                    const resetTree = createEmptyWhereTree();
                    setQbTree(resetTree);
                    setWhereJsonDirty(false);
                    setWhereJsonError('');
                    setWhereJsonText(JSON.stringify(resetTree, null, 2));
                  }}
                  >
                    Reset Builder
                  </button>
                </div>
              </div>
            );
          })()}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">Where Builder JSON</label>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={()=>{
                    try {
                      const parsed = JSON.parse(whereJsonText || '{}');
                      const normalized = normalizeWhereTree(parsed);
                      setQbTree(normalized);
                      setWhereJsonDirty(false);
                      setWhereJsonError('');
                      setWhereJsonText(JSON.stringify(normalized, null, 2));
                    } catch (err) {
                      setWhereJsonError(String(err.message || err));
                    }
                  }}
                >
                  Apply JSON
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={()=>{
                    const normalized = normalizeWhereTree(qbTree);
                    setWhereJsonDirty(false);
                    setWhereJsonError('');
                    setWhereJsonText(JSON.stringify(normalized, null, 2));
                  }}
                >
                  Reset JSON
                </button>
              </div>
            </div>
            <textarea
              className={`form-control ${whereJsonError ? 'is-invalid' : ''}`}
              rows={8}
              value={whereJsonText}
              onChange={(e)=>{
                setWhereJsonDirty(true);
                setWhereJsonText(e.target.value);
              }}
            />
            <div className="form-text">
              View or edit the raw where-tree JSON if you prefer direct control. Click &ldquo;Apply JSON&rdquo; to update the visual builder.
            </div>
            {whereJsonError && <div className="invalid-feedback d-block">{whereJsonError}</div>}
          </div>
          <div className="d-flex gap-2 mt-3">
            <button
              className="btn btn-primary"
              onClick={()=>{
                const prog = (schemaProg || '').trim();
                if (!prog) { alert('Program is required'); return; }
                const fieldsFromLines = [];
                (schemaLines || '').split('\n').forEach(line=>{
                  const m = String(line||'').match(/^\s*([A-Za-z_][A-Za-z0-9_]*)\s*:/);
                  if (m && m[1]) fieldsFromLines.push(m[1]);
                });
                if (fieldsFromLines.length === 0) { alert('No valid fields found'); return; }
                const fetchFields = schemaSelectedFields.length > 0 ? schemaSelectedFields : fieldsFromLines;
                if (fetchFields.length === 0) { alert('Select at least one field to fetch'); return; }
                const cleanedProductFields = Array.from(new Set(schemaProductFields.map(f => (f || '').trim()))).filter(Boolean);
                const entry = {
                  program: prog,
                  collection: (schemaCollection || '').trim(),
                  fields: fetchFields,
                  product_lookup_fields: cleanedProductFields,
                };
                // attach where_tree if it has content
                if (hasWhereContent(qbTree)) {
                  entry['where_tree'] = qbTree;
                }
                setPendingSchema({ prog, entry });
                setConfirmSchemaOpen(true);
              }}
            >
              Save
            </button>
            <button className="btn btn-outline-secondary" onClick={()=>setDrawerOpen(false)}>Cancel</button>
          </div>
        </div>
      </div>
      {activeTab === 'mapping_review' && (
        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
              <div>
                <h5 className="mb-1">AHRI Match Review</h5>
                <div className="text-muted small">
                  Low-confidence matches surfaced by the scorer. Approve to merge into
                  <code className="mx-1">product_mapping</code>, reject to drop.
                </div>
              </div>
            </div>
            <MappingReviewPanel
              apiBaseUrl={API_BASE_URL}
              getAuthHeaders={getAuthHeaders}
            />
          </div>
        </div>
      )}
      {activeTab === 'system_shop_events' && (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <div>
                  <h5 className="mb-1">AHRI System Shop Events</h5>
                  <div className="text-muted small">
                    Review webhook events, filter by date/search, and inspect payload and headers for debugging.
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={loadSystemShopEventEndpoints}
                  >
                    Refresh Endpoints
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={loadSystemShopEvents}
                    disabled={!systemShopSelectedEndpointId || systemShopEventsLoading}
                  >
                    {systemShopEventsLoading ? 'Loading…' : 'Refresh Events'}
                  </button>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-lg-4">
                  <label className="form-label">Endpoint</label>
                  <select
                    className="form-select"
                    value={systemShopSelectedEndpointId}
                    onChange={(e) => {
                      setSystemShopSelectedEndpointId(e.target.value);
                      setSystemShopSelectedEvent(null);
                      setSystemShopEventDetailOpen(false);
                    }}
                  >
                    <option value="">Select endpoint</option>
                    {(systemShopEventEndpointOptions.length > 0
                      ? systemShopEventEndpointOptions
                      : systemShopEventEndpoints
                    ).map((endpoint) => (
                      <option key={endpoint.id} value={String(endpoint.id)}>
                        {endpoint.name || endpoint.endpoint_key || `Endpoint ${endpoint.id}`}
                      </option>
                    ))}
                  </select>
                  <div className="form-text">
                    {systemShopEventEndpointOptions.length > 0
                      ? `Showing ${systemShopEventEndpointOptions.length} System Shop endpoint(s).`
                      : `${systemShopEventEndpoints.length || 0} endpoint(s) available.`}
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">Start</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={systemShopEventStartAt}
                    onChange={(e) => setSystemShopEventStartAt(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">End</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={systemShopEventEndAt}
                    onChange={(e) => setSystemShopEventEndAt(e.target.value)}
                  />
                </div>
                <div className="col-12 col-lg-2">
                  <label className="form-label">Search</label>
                  <input
                    className="form-control"
                    value={systemShopEventSearch}
                    placeholder="payload/header text"
                    onChange={(e) => setSystemShopEventSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        loadSystemShopEvents();
                      }
                    }}
                  />
                </div>
              </div>

              <div className="border rounded p-3 mt-3">
                <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2 mb-3">
                  <div className="fw-semibold">Event Webhooks</div>
                  <div className="small text-muted">
                    Configure which AHRI System Shop events should publish outbound webhooks for this endpoint.
                  </div>
                </div>
                <div className="row g-2">
                  {SYSTEM_SHOP_WEBHOOK_EVENT_TYPES.map((entry) => {
                    const sub = (systemShopWebhookSubs || []).find((row) => row?.event_type === entry.key);
                    const checked = sub ? !!sub.enabled : false;
                    return (
                      <div key={entry.key} className="col-12 col-md-6">
                        <label className="d-flex align-items-center justify-content-between border rounded px-3 py-2">
                          <span className="small">{entry.label}</span>
                          <input
                            type="checkbox"
                            className="form-check-input ms-2"
                            checked={checked}
                            disabled={!systemShopSelectedEndpointId || systemShopWebhookSubSaving === entry.key}
                            onChange={(e) => toggleSystemShopWebhookEvent(entry.key, e.target.checked)}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="row g-2 mt-1">
                <div className="col-12 col-md-4">
                  <div className="border rounded p-2 h-100">
                    <div className="small text-muted">Pending Deliveries</div>
                    <div className="fw-semibold">{systemShopWebhookDeliveryStats.pending}</div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="border rounded p-2 h-100">
                    <div className="small text-muted">Delivered</div>
                    <div className="fw-semibold text-success">{systemShopWebhookDeliveryStats.delivered}</div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="border rounded p-2 h-100">
                    <div className="small text-muted">Failed</div>
                    <div className="fw-semibold text-danger">{systemShopWebhookDeliveryStats.failed}</div>
                  </div>
                </div>
              </div>

              <div className="border rounded p-3 mt-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="fw-semibold">Delivery Details</div>
                  <div className="small text-muted">Latest outbound webhook attempts</div>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 180 }}>Event Type</th>
                        <th style={{ minWidth: 110 }}>Status</th>
                        <th style={{ minWidth: 90 }}>Attempts</th>
                        <th style={{ minWidth: 150 }}>Next Attempt</th>
                        <th style={{ minWidth: 120 }}>HTTP</th>
                        <th style={{ minWidth: 220 }}>Last Error</th>
                        <th style={{ minWidth: 160 }}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemShopWebhookDeliveries.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-muted py-3">
                            No delivery records yet.
                          </td>
                        </tr>
                      )}
                      {systemShopWebhookDeliveries.map((row) => {
                        const status = String(row?.status || '').toLowerCase();
                        const badgeClass = status === 'delivered'
                          ? 'bg-success-subtle text-success-emphasis'
                          : (status === 'failed'
                            ? 'bg-danger-subtle text-danger-emphasis'
                            : 'bg-secondary-subtle text-secondary-emphasis');
                        return (
                          <tr key={row.id}>
                            <td className="text-break">{row.event_type || '-'}</td>
                            <td>
                              <span className={`badge ${badgeClass}`}>{row.status || 'unknown'}</span>
                            </td>
                            <td>{`${row.attempt_count || 0}/${row.max_attempts || 0}`}</td>
                            <td>{row.next_attempt_at || '-'}</td>
                            <td>{row.response_status_code ?? '-'}</td>
                            <td className="text-break">{row.last_error || '-'}</td>
                            <td>{row.created_at || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {systemShopEventsError && (
                <div className="alert alert-danger mt-3 mb-0">
                  {systemShopEventsError}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 120 }}>Received</th>
                      <th style={{ minWidth: 110 }}>Status</th>
                      <th style={{ minWidth: 240 }}>Payload Preview</th>
                      <th style={{ minWidth: 240 }}>Error</th>
                      <th style={{ width: 120 }} className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systemShopEvents.length === 0 && !systemShopEventsLoading && (
                      <tr>
                        <td colSpan={5} className="text-muted py-3">
                          {systemShopSelectedEndpointId
                            ? 'No events found for the selected filters.'
                            : 'Select an endpoint to view events.'}
                        </td>
                      </tr>
                    )}
                    {systemShopEvents.map((event) => {
                      const normalizedStatus = String(event?.status || '').toLowerCase();
                      const badgeClass = normalizedStatus === 'processed' || normalizedStatus === 'success'
                        ? 'bg-success-subtle text-success-emphasis'
                        : (normalizedStatus === 'failed' || normalizedStatus === 'error'
                          ? 'bg-danger-subtle text-danger-emphasis'
                          : 'bg-secondary-subtle text-secondary-emphasis');
                      const payloadPreview = (() => {
                        try {
                          const raw = event?.payload;
                          if (!raw) return '';
                          const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
                          return text.length > 180 ? `${text.slice(0, 180)}…` : text;
                        } catch (err) {
                          return '';
                        }
                      })();
                      return (
                        <tr key={event.id}>
                          <td>{event.received_at || '-'}</td>
                          <td>
                            <span className={`badge ${badgeClass}`}>
                              {event.status || 'unknown'}
                            </span>
                          </td>
                          <td className="text-break">{payloadPreview || '-'}</td>
                          <td className="text-break">{event.error || '-'}</td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                setSystemShopSelectedEvent(event);
                                setSystemShopEventDetailOpen(true);
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <Drawer
            title={`Event Details${systemShopSelectedEvent?.id ? ` #${systemShopSelectedEvent.id}` : ''}`}
            placement="right"
            onClose={() => setSystemShopEventDetailOpen(false)}
            open={systemShopEventDetailOpen}
            width={Math.min(980, Math.max(560, Math.round((typeof window === 'undefined' ? 1440 : window.innerWidth) * 0.5)))}
          >
            {systemShopSelectedEvent ? (
              <div className="d-flex flex-column gap-3">
                <div className="border rounded p-3">
                  <div className="small text-muted mb-1">Received At</div>
                  <div>{systemShopSelectedEvent.received_at || '-'}</div>
                </div>
                <div className="border rounded p-3">
                  <div className="small text-muted mb-1">Status</div>
                  <div>{systemShopSelectedEvent.status || '-'}</div>
                </div>
                <div className="border rounded p-3">
                  <div className="small text-muted mb-1">Error</div>
                  <div className="text-break">{systemShopSelectedEvent.error || '-'}</div>
                </div>
                <div className="border rounded p-3">
                  <div className="small text-muted mb-2">Payload</div>
                  <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(systemShopSelectedEvent.payload || {}, null, 2)}
                  </pre>
                </div>
                <div className="border rounded p-3">
                  <div className="small text-muted mb-2">Headers</div>
                  <pre className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(systemShopSelectedEvent.headers || {}, null, 2)}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="text-muted">No event selected.</div>
            )}
          </Drawer>
        </>
      )}
      {activeTab === 'mapping' && (
        <>
        <div className="mb-4">
          <h4 className="mb-1">Product Mapping</h4>
          <p className="text-muted mb-0">
            Match AHRI certificates to products, then keep attributes, match status, and generated
            system combinations in sync. Recommended order: run a mapping engine first, then
            Attribute Sync / Match Recheck as needed, and only run Variant Backfill after a schema
            or logic change.
          </p>
        </div>

        {[
          {
            heading: '1. Mapping Engine',
            note: 'Pick one — only one mapping job can run at a time',
            tools: [
              {
                key: 'v2',
                icon: 'bi-cpu',
                title: 'Mapping V2',
                badge: { text: 'Recommended', className: 'text-bg-primary' },
                description: 'Multi-threaded regex matcher with right-trim fallback and cancel support.',
                whenToUse: 'Default choice for running or re-running product mapping in the background.',
                active: mappingV2Active,
                status: mappingV2Status,
              },
              {
                key: 'v3',
                icon: 'bi-flask',
                title: 'Mapping V3',
                badge: { text: 'Beta', className: 'text-bg-warning' },
                description: 'Bulk exact-SQL match first, Solr scored search only for the misses, then reconciles.',
                whenToUse: 'Trial engine running alongside V1/V2 — use to evaluate match quality, not yet a replacement.',
                active: mappingV3Active,
                status: mappingV3Status,
              },
              {
                key: 'v1',
                icon: 'bi-archive',
                title: 'Mapping V1',
                badge: { text: 'Legacy', className: 'text-bg-secondary' },
                description: 'The original single-threaded regex matcher. Also includes a single-AHRI-ID quick check tool.',
                whenToUse: 'Only if you need the original engine\'s behavior or the quick-check debug tool.',
                active: mappingActive,
                status: mappingStatus,
              },
            ],
          },
          {
            heading: '2. Maintenance & Sync',
            note: 'Run after a mapping engine has matched products',
            tools: [
              {
                key: 'attr-sync',
                icon: 'bi-arrow-repeat',
                title: 'Product Attributes Sync',
                description: 'Pulls the latest attributes from matched products into AHRI records and Solr facets.',
                whenToUse: 'Run whenever product attributes change and search/faceting needs to reflect it.',
                active: attrSyncActive,
                status: attrSyncStatus,
              },
              {
                key: 'match-check',
                icon: 'bi-check2-square',
                title: 'Match Status Recheck',
                description: 'Re-evaluates complete vs. partial match flags from the current product mapping and resyncs Solr.',
                whenToUse: 'Run after a manual mapping edit so complete/partial labels catch up without a full re-mapping.',
                active: matchCheckActive,
                status: matchCheckStatus,
              },
            ],
          },
          {
            heading: '3. Reconciliation',
            note: 'Heavier, run rarely',
            tools: [
              {
                key: 'variant-backfill',
                icon: 'bi-arrow-clockwise',
                title: 'Variant Backfill',
                badge: { text: 'Rare', className: 'text-bg-dark' },
                description: 'Regenerates every AHRI system variant row (availability + product name/image/description) from current data.',
                whenToUse: 'Run once after a variant-related schema/logic change, or if existing combinations look stale.',
                active: variantBackfillActive,
                status: variantBackfillStatus,
              },
            ],
          },
        ].map((group) => (
          <div className="mb-4" key={group.heading}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="text-uppercase text-muted small fw-bold mb-0">{group.heading}</h6>
              <span className="text-muted small">{group.note}</span>
            </div>
            <div className="row g-2">
              {group.tools.map((tool) => (
                <div className="col-md-6 col-lg-4" key={tool.key}>
                  <button
                    type="button"
                    onClick={() => setMappingSubTab(tool.key)}
                    className={`btn w-100 h-100 text-start p-3 ${mappingSubTab === tool.key ? 'border-primary border-2 bg-primary-subtle' : 'border'}`}
                    style={{ borderRadius: 10 }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="fw-semibold text-dark">
                        <i className={`bi ${tool.icon} me-2`} />
                        {tool.title}
                      </span>
                      {tool.active ? (
                        <span className="badge text-bg-success">Running</span>
                      ) : tool.badge ? (
                        <span className={`badge ${tool.badge.className}`}>{tool.badge.text}</span>
                      ) : null}
                    </div>
                    <div className="small text-muted mb-1">{tool.description}</div>
                    <div className="small text-muted fst-italic">{tool.whenToUse}</div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {mappingSubTab === 'v2' && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <div>
                  <h5 className="mb-1">AHRI Product Mapping V2 (Background Job)</h5>
                  <div className="text-muted small">
                    Runs the V2 matcher (regex + right-trim fallback) in the background with multi-threading and cancel support.
                  </div>
                </div>
                <div className="btn-group">
                  <button className="btn btn-outline-secondary" onClick={loadMappingV2State} disabled={mappingV2Loading}>
                    {mappingV2Loading ? 'Refreshing…' : 'Refresh'}
                  </button>
                  <button
                    className="btn btn-outline-dark"
                    onClick={()=>{
                      const lines = Array.isArray(mappingV2State?.log) ? mappingV2State.log : [];
                      setLogText(lines.length ? lines.join('\n') : 'No log entries yet.');
                      setLogOpen(true);
                    }}
                    disabled={!Array.isArray(mappingV2State?.log) || mappingV2State.log.length === 0}
                  >
                    View Log
                  </button>
                  {mappingV2Active && (
                    <button
                      className="btn btn-outline-danger"
                      onClick={stopProductMappingV2}
                      disabled={mappingV2Stopping}
                    >
                      {mappingV2Stopping ? 'Stopping…' : 'Stop Mapping V2'}
                    </button>
                  )}
                  <button
                    className="btn btn-outline-primary"
                    onClick={startProductMappingV2Baseline}
                    disabled={mappingV2BaselineStarting || mappingV2Active || mappingV2Starting}
                    title="Profile ~500 AHRI rows and record Solr/DB timing (P0 baseline)"
                  >
                    {mappingV2BaselineStarting ? 'Starting baseline…' : 'Run baseline (500)'}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={startProductMappingV2}
                    disabled={
                      mappingV2Starting
                      || mappingV2Active
                      || (mappingV2ScopeSelection === 'subset' && mappingV2IdsParsedCount === 0)
                    }
                  >
                    {mappingV2Active ? 'Mapping V2 in progress…' : 'Start Mapping V2'}
                  </button>
                </div>
              </div>
              {mappingV2Error && <div className="alert alert-danger">{mappingV2Error}</div>}
              {!mappingV2Active && (
                <div className="alert alert-secondary py-2 d-flex align-items-center gap-2 mb-3">
                  <span className="badge bg-secondary">Not running</span>
                  <span className="small">
                    {mappingV2Status
                      ? `No mapping job is currently running. The stats below are from the last run (ended ${mappingV2Status}).`
                      : 'No mapping job has been run yet.'}
                  </span>
                </div>
              )}
              <div className="row g-3 mb-3">
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Status</div>
                    <div className="fw-semibold text-capitalize">
                      {mappingV2Active ? (
                        mappingV2Status
                      ) : (
                        <span className="text-muted">{mappingV2Status ? `${mappingV2Status} (not running)` : 'idle'}</span>
                      )}
                    </div>
                    {mappingV2State?.job_id && (
                      <div className="small text-muted mt-1">
                        Job: <code>{mappingV2State.job_id}</code>
                      </div>
                    )}
                    <div className="small text-muted mt-1">
                      Scope:{' '}
                      {mappingV2ScopeState === 'baseline'
                        ? 'P0 baseline sample (~500 rows)'
                        : mappingV2ScopeState === 'subset'
                          ? 'Selected AHRI IDs'
                          : 'Complete AHRI data'}
                    </div>
                    <div className="small text-muted">
                      Threads: {mappingV2State?.mapping_threads || cfg.mapping_threads || 2}
                    </div>
                    {mappingV2Active && mappingV2State?.updated_at && (
                      <div className="small text-muted mt-1">
                        Last update: {new Date(mappingV2State.updated_at).toLocaleTimeString()}
                      </div>
                    )}
                    <div className="small text-muted">
                      Trim: {mappingV2State?.max_trim ?? cfg.mapping_v2_max_trim ?? 4} / Min len:{' '}
                      {mappingV2State?.min_length ?? cfg.mapping_v2_min_length ?? 4}
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Processed</div>
                    <div className="fw-semibold">{mappingV2Stats.processed} / {mappingV2Stats.total || '—'}</div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Matches</div>
                    <div className="fw-semibold">{mappingV2Matches}</div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Complete Matches</div>
                    <div className="fw-semibold">{mappingV2CompleteMatches}</div>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>
                    {mappingV2Active ? 'Progress' : 'Progress (last run, not running)'}
                    {mappingV2Active && (
                      <span className="ms-2 text-primary">
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" style={{ width: '0.65rem', height: '0.65rem', borderWidth: '0.15em' }} />
                        {mappingV2State?.mapping_threads > 1 ? `${mappingV2State.mapping_threads} threads active` : 'running'}
                      </span>
                    )}
                  </span>
                  <span>{mappingV2Stats.percent}%</span>
                </div>
                <div className={`progress${mappingV2Active ? '' : ' opacity-50'}`} style={{ height: 6 }}>
                  <div
                    className={`progress-bar${mappingV2Active ? ' progress-bar-striped progress-bar-animated' : ' bg-secondary'}`}
                    role="progressbar"
                    style={{ width: `${mappingV2Active && mappingV2Stats.percent === 0 ? 100 : mappingV2Stats.percent}%` }}
                    aria-valuenow={mappingV2Stats.percent}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>
              </div>
              {mappingV2State?.perf && (
                <div className="card mb-3 border-info">
                  <div className="card-body py-3">
                    <h6 className="mb-2">Performance (P0 baseline)</h6>
                    <div className="row g-2 small">
                      <div className="col-6 col-md-4 col-lg-3">
                        <span className="text-muted">Throughput</span>
                        <div className="fw-semibold">{mappingV2State.perf.rows_per_sec ?? '—'} rows/sec</div>
                      </div>
                      <div className="col-6 col-md-4 col-lg-3">
                        <span className="text-muted">Solr queries</span>
                        <div className="fw-semibold">{mappingV2State.perf.solr_queries ?? 0}</div>
                      </div>
                      <div className="col-6 col-md-4 col-lg-3">
                        <span className="text-muted">Cache hits</span>
                        <div className="fw-semibold">{mappingV2State.perf.cache_hits ?? 0}</div>
                      </div>
                      <div className="col-6 col-md-4 col-lg-3">
                        <span className="text-muted">Solr time</span>
                        <div className="fw-semibold">{mappingV2State.perf.solr_ms ?? 0} ms</div>
                      </div>
                      <div className="col-6 col-md-4 col-lg-3">
                        <span className="text-muted">Avg Solr / query</span>
                        <div className="fw-semibold">{mappingV2State.perf.avg_solr_ms_per_query ?? '—'} ms</div>
                      </div>
                      <div className="col-6 col-md-4 col-lg-3">
                        <span className="text-muted">DB ahri_data</span>
                        <div className="fw-semibold">{mappingV2State.perf.db_ahri_ms ?? 0} ms</div>
                      </div>
                      <div className="col-6 col-md-4 col-lg-3">
                        <span className="text-muted">DB products</span>
                        <div className="fw-semibold">{mappingV2State.perf.db_products_ms ?? 0} ms</div>
                      </div>
                      <div className="col-6 col-md-4 col-lg-3">
                        <span className="text-muted">AHRI Solr flush</span>
                        <div className="fw-semibold">{mappingV2State.perf.ahri_solr_flush_ms ?? 0} ms</div>
                      </div>
                      <div className="col-6 col-md-4 col-lg-3">
                        <span className="text-muted">Product Solr flush</span>
                        <div className="fw-semibold">{mappingV2State.perf.product_solr_flush_ms ?? 0} ms</div>
                      </div>
                    </div>
                    {mappingV2State.perf.last_batch && Object.keys(mappingV2State.perf.last_batch).length > 0 && (
                      <div className="mt-2 text-muted small">
                        Last batch ({mappingV2State.perf.last_batch.rows} rows):{' '}
                        {mappingV2State.perf.last_batch.elapsed_ms} ms total — worker {mappingV2State.perf.last_batch.worker_wall_ms} ms,
                        DB ahri {mappingV2State.perf.last_batch.db_ahri_ms} ms, DB products {mappingV2State.perf.last_batch.db_products_ms} ms
                      </div>
                    )}
                    {mappingV2State.perf_config && (
                      <div className="mt-2 text-muted small">
                        Config: field={mappingV2State.perf_config.mapping_field},
                        programs={JSON.stringify(mappingV2State.perf_config.lookup_fields_by_program || {})}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {mappingV2ScopeState === 'baseline' && mappingV2State?.baseline_meta && (
                <div className="alert alert-info mb-3">
                  <div className="fw-semibold mb-1">Baseline profiling run</div>
                  <div className="small">
                    Sample {mappingV2State.baseline_meta.sample_size} rows (offset {mappingV2State.baseline_meta.offset} of {mappingV2State.baseline_meta.total_rows}).
                    Use Performance metrics below to find Solr vs DB bottlenecks.
                  </div>
                </div>
              )}
              {mappingV2ScopeState === 'subset' && (
                <div className="alert alert-warning mb-3">
                  <div className="fw-semibold mb-1">Targeted mapping run</div>
                  <div className="small">
                    Evaluating {mappingV2RequestedIdsTotal} AHRI ID{mappingV2RequestedIdsTotal === 1 ? '' : 's'}
                    {mappingV2RequestedIdsMore > 0 ? ' (preview below)' : ''}
                  </div>
                  {mappingV2RequestedIds.length > 0 && (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {mappingV2RequestedIds.map((id) => (
                        <code key={id} className="bg-light px-2 py-1 rounded">{id}</code>
                      ))}
                      {mappingV2RequestedIdsMore > 0 && (
                        <span className="text-muted small align-self-center">
                          +{mappingV2RequestedIdsMore} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">Mapping V2 Scope & Settings</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="mapping-v2-scope"
                          id="mapping-v2-scope-full"
                          value="full"
                          checked={mappingV2ScopeSelection === 'full'}
                          onChange={() => setMappingV2ScopeSelection('full')}
                          disabled={mappingV2Active}
                        />
                        <label className="form-check-label" htmlFor="mapping-v2-scope-full">
                          Map the entire AHRI dataset
                        </label>
                      </div>
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="mapping-v2-scope"
                          id="mapping-v2-scope-subset"
                          value="subset"
                          checked={mappingV2ScopeSelection === 'subset'}
                          onChange={() => setMappingV2ScopeSelection('subset')}
                          disabled={mappingV2Active}
                        />
                        <label className="form-check-label" htmlFor="mapping-v2-scope-subset">
                          Map only specific AHRI IDs
                        </label>
                      </div>
                      {mappingV2ScopeSelection === 'subset' && (
                        <div>
                          <label className="form-label">AHRI IDs (comma or newline separated)</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={mappingV2IdsInput}
                            onChange={(e)=>setMappingV2IdsInput(e.target.value)}
                            placeholder="215271984, 215271985, 215271986"
                            disabled={mappingV2Active}
                          />
                          <div className="form-text">
                            {mappingV2IdsParsedCount > 0
                              ? `${mappingV2IdsParsedCount} ID${mappingV2IdsParsedCount === 1 ? '' : 's'} detected.`
                              : 'Enter at least one AHRI ID.'}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <div className="row g-3">
                        <div className="col-sm-6">
                          <label className="form-label">Mapping Threads</label>
                          <input
                            type="number"
                            className="form-control"
                            min={1}
                            max={10}
                            value={cfg.mapping_threads ?? 2}
                            onChange={(e) => handleMappingThreadsChange(e.target.value)}
                            disabled={mappingV2Active}
                          />
                          <div className="form-text">Use 1-10 threads. Higher values increase Solr load.</div>
                        </div>
                        <div className="col-sm-3">
                          <label className="form-label">Max Trim</label>
                          <input
                            type="number"
                            className="form-control"
                            min={0}
                            max={8}
                            value={cfg.mapping_v2_max_trim ?? 4}
                            onChange={(e) => handleMappingV2MaxTrimChange(e.target.value)}
                            disabled={mappingV2Active}
                          />
                          <div className="form-text">Trim from right if no hit.</div>
                        </div>
                        <div className="col-sm-3">
                          <label className="form-label">Min Length</label>
                          <input
                            type="number"
                            className="form-control"
                            min={1}
                            max={8}
                            value={cfg.mapping_v2_min_length ?? 4}
                            onChange={(e) => handleMappingV2MinLengthChange(e.target.value)}
                            disabled={mappingV2Active}
                          />
                          <div className="form-text">Stop trimming below this length.</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
                    <h6 className="mb-0">Recent Mapping V2 Jobs</h6>
                    <div className="btn-group">
                      <button className="btn btn-outline-secondary btn-sm" onClick={loadMappingV2Jobs} disabled={mappingV2JobsLoading}>
                        {mappingV2JobsLoading ? 'Refreshing…' : 'Refresh History'}
                      </button>
                    </div>
                  </div>
                  {mappingV2JobsError && <div className="alert alert-danger">{mappingV2JobsError}</div>}
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Job ID</th>
                          <th>Status</th>
                          <th>Processed</th>
                          <th>Matches</th>
                          <th>Complete</th>
                          <th>Threads</th>
                          <th>Trim</th>
                          <th>Started</th>
                          <th>Finished</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mappingV2Jobs.length === 0 && (
                          <tr>
                            <td colSpan="9" className="text-muted text-center py-3">No jobs found</td>
                          </tr>
                        )}
                        {mappingV2Jobs.map((job) => (
                          <tr key={job.job_id}>
                            <td style={{ maxWidth: 140 }} className="text-truncate"><code>{job.job_id}</code></td>
                            <td className="text-capitalize">{job.status}</td>
                            <td>{job.processed} / {job.total || '—'}</td>
                            <td>{job.matches}</td>
                            <td>{job.complete_matches || 0}</td>
                            <td>{job.mapping_threads || '—'}</td>
                            <td>{job.max_trim ?? '—'} / {job.min_length ?? '—'}</td>
                            <td>{job.started_at ? new Date(job.started_at).toLocaleString() : '—'}</td>
                            <td>{job.finished_at ? new Date(job.finished_at).toLocaleString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {mappingSubTab === 'v3' && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <div>
                  <h5 className="mb-1">AHRI Product Mapping V3 (Beta)</h5>
                  <div className="text-muted small">
                    Bulk-first matcher: exact SQL match against products first, Solr scored search only for
                    misses, then a reconcile pass. Ships alongside V1/V2 during trial.
                  </div>
                </div>
                <div className="btn-group">
                  <button className="btn btn-outline-secondary" onClick={loadMappingV3State} disabled={mappingV3Loading}>
                    {mappingV3Loading ? 'Refreshing…' : 'Refresh'}
                  </button>
                  {mappingV3Active && (
                    <button
                      className="btn btn-outline-danger"
                      onClick={stopProductMappingV3}
                      disabled={mappingV3Stopping}
                    >
                      {mappingV3Stopping ? 'Stopping…' : 'Stop Mapping V3'}
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={startProductMappingV3}
                    disabled={mappingV3Starting || mappingV3Active}
                  >
                    {mappingV3Active ? 'Mapping V3 in progress…' : 'Start Mapping V3'}
                  </button>
                </div>
              </div>
              {mappingV3Error && <div className="alert alert-danger">{mappingV3Error}</div>}
              <div className="row g-3 mb-3">
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Status</div>
                    <div className="fw-semibold text-capitalize">
                      {mappingV3Status || 'idle'}
                      {mappingV3Active && (
                        <span
                          className="spinner-border spinner-border-sm ms-2 text-primary"
                          role="status"
                          aria-hidden="true"
                          style={{ width: '0.65rem', height: '0.65rem', borderWidth: '0.15em' }}
                        />
                      )}
                    </div>
                    {mappingV3State?.job_id && (
                      <div className="small text-muted mt-1">
                        Job: <code>{mappingV3State.job_id}</code>
                      </div>
                    )}
                    {mappingV3State?.updated_at && !Number.isNaN(new Date(mappingV3State.updated_at).getTime()) && (
                      <div className="small text-muted mt-1">
                        Last update: {new Date(mappingV3State.updated_at).toLocaleTimeString()}
                      </div>
                    )}
                    {mappingV3Status === 'failed' && mappingV3State?.error && (
                      <div className="small text-danger mt-1">Error: {mappingV3State.error}</div>
                    )}
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Current Phase</div>
                    <div className="fw-semibold">
                      {MAPPING_V3_PHASE_LABELS[mappingV3Phase] || mappingV3Phase || '—'}
                      {mappingV3PhaseStep && (
                        <span className="text-muted fw-normal ms-1">
                          ({mappingV3PhaseStep.index} of {mappingV3PhaseStep.total})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Rows Touched</div>
                    <div className="fw-semibold">{mappingV3TouchedCount}</div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Reindex</div>
                    <div className="fw-semibold text-capitalize">
                      {mappingV3State?.reindex_ok === false ? 'Failed' : mappingV3State?.reindex_ok === true ? 'OK' : '—'}
                    </div>
                  </div>
                </div>
              </div>
              {mappingV3Active && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between small text-muted mb-1">
                    <span>
                      {MAPPING_V3_PHASE_LABELS[mappingV3Phase] || mappingV3Phase || 'Running'}
                      {mappingV3PhaseStep && ` — phase ${mappingV3PhaseStep.index} of ${mappingV3PhaseStep.total}`}
                    </span>
                    <span>
                      {mappingV3CurrentPhaseProgress
                        ? `${mappingV3CurrentPhaseProgress.processed} / ${mappingV3CurrentPhaseProgress.total}`
                        : 'Working…'}
                    </span>
                  </div>
                  <div className="progress" style={{ height: '0.5rem' }}>
                    <div
                      className={`progress-bar${mappingV3CurrentPhaseProgress ? '' : ' progress-bar-striped progress-bar-animated'}`}
                      role="progressbar"
                      style={{ width: `${mappingV3CurrentPhaseProgress ? mappingV3CurrentPhaseProgress.percent : 100}%` }}
                      aria-valuenow={mappingV3CurrentPhaseProgress ? mappingV3CurrentPhaseProgress.percent : undefined}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                </div>
              )}
              {Object.keys(mappingV3PhaseResults).length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>Phase</th>
                        <th>Distinct / Processed</th>
                        <th>Bulk Exact Matched</th>
                        <th>Matched</th>
                        <th>Rows Updated</th>
                        <th>Reverse-Verify Cleared</th>
                        <th>Status Changed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(mappingV3PhaseResults).map(([phaseName, phaseResult]) => {
                        const row = mappingV3RowValues(phaseName, phaseResult);
                        return (
                          <tr key={phaseName}>
                            <td>{MAPPING_V3_PHASE_LABELS[phaseName] || phaseName}</td>
                            {row.note ? (
                              <td colSpan={6} className="text-muted fst-italic">
                                Skipped — {row.note}
                              </td>
                            ) : (
                              <>
                                <td>{row.distinct}</td>
                                <td>{row.bulkExact}</td>
                                <td>{row.matched}</td>
                                <td>{row.rowsUpdated}</td>
                                <td>{row.reverseCleared}</td>
                                <td>{row.statusChanged}</td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {mappingSubTab === 'v1' && (
        <div className="card">
          <div className="card-body">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
              <div>
                <h5 className="mb-1">AHRI Product Mapping</h5>
                <div className="text-muted small">
                  Uses program-specific lookup fields to locate products (via regex) and store the relationships for Solr faceting.
                </div>
              </div>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={loadMappingState}
                  disabled={mappingLoading}
                >
                  {mappingLoading ? 'Refreshing…' : 'Refresh'}
                </button>
                <button
                  className="btn btn-outline-dark"
                  onClick={()=>{
                    const lines = Array.isArray(mappingState?.log) ? mappingState.log : [];
                    setLogText(lines.length ? lines.join('\n') : 'No log entries yet');
                    setLogOpen(true);
                  }}
                  disabled={!Array.isArray(mappingState?.log) || mappingState.log.length === 0}
                >
                  View Log
                </button>
                {mappingActive && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={stopProductMapping}
                    disabled={mappingStopping}
                  >
                    {mappingStopping ? 'Stopping…' : 'Stop Mapping'}
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={startProductMapping}
                  disabled={
                    mappingStarting
                    || mappingActive
                    || (mappingScopeSelection === 'subset' && mappingIdsParsedCount === 0)
                  }
                >
                  {mappingActive ? 'Mapping in progress…' : 'Start Mapping'}
                </button>
              </div>
            </div>
            {mappingError && (
              <div className="alert alert-danger">{mappingError}</div>
            )}
            <div className="row g-3 mb-3">
              <div className="col-sm-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Status</div>
                  <div className="fw-semibold text-capitalize">{mappingStatus || 'idle'}</div>
                  {mappingState?.job_id && (
                    <div className="small text-muted mt-1">
                      Job: <code>{mappingState.job_id}</code>
                    </div>
                  )}
                  <div className="small text-muted mt-1">
                    Scope: {mappingScopeState === 'subset' ? 'Selected AHRI IDs' : 'Complete AHRI data'}
                  </div>
                  <div className="small text-muted">
                    Threads: {mappingState?.mapping_threads || cfg.mapping_threads || 2}
                  </div>
                  {mappingActive && mappingState?.updated_at && (
                    <div className="small text-muted mt-1">
                      Last update: {new Date(mappingState.updated_at).toLocaleTimeString()}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Processed</div>
                  <div className="fw-semibold">{mappingStats.processed} / {mappingStats.total || '—'}</div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Matches</div>
                  <div className="fw-semibold">{mappingMatches}</div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Complete Matches</div>
                  <div className="fw-semibold">{mappingCompleteMatches}</div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Products Linked</div>
                  <div className="fw-semibold">{mappingUpdatedProducts}</div>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <div className="d-flex justify-content-between small text-muted mb-1">
                <span>
                  Progress
                  {mappingActive && (
                    <span className="ms-2 text-primary">
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" style={{ width: '0.65rem', height: '0.65rem', borderWidth: '0.15em' }} />
                      {(mappingState?.mapping_threads || cfg.mapping_threads || 2) > 1 ? `${mappingState?.mapping_threads || cfg.mapping_threads || 2} threads active` : 'running'}
                    </span>
                  )}
                </span>
                <span>{mappingStats.percent}%</span>
              </div>
              <div className="progress" style={{ height: 6 }}>
                <div
                  className={`progress-bar${mappingActive ? ' progress-bar-striped progress-bar-animated' : ''}`}
                  role="progressbar"
                  style={{ width: `${mappingActive && mappingStats.percent === 0 ? 100 : mappingStats.percent}%` }}
                  aria-valuenow={mappingStats.percent}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
                  <div>
                    <h6 className="mb-1">Mapping V2 — Quick Check</h6>
                    <div className="text-muted small">
                      Run the new AHRI→product mapping (regex + right-trim fallback) for a single AHRI ID and review the attempted queries.
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      type="button"
                      onClick={()=>{
                        setMapV2Id('');
                        setMapV2Result(null);
                        setMapV2Error('');
                      }}
                      disabled={mapV2Loading}
                    >
                      Clear
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      type="button"
                      onClick={runMappingV2}
                      disabled={mapV2Loading}
                    >
                      {mapV2Loading ? 'Running…' : 'Run Lookup'}
                    </button>
                  </div>
                </div>
                {mapV2Error && <div className="alert alert-danger mb-3">{mapV2Error}</div>}
                <div className="row g-3 mb-3">
                  <div className="col-sm-6 col-md-4 col-lg-3">
                    <label className="form-label">AHRI ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={mapV2Id}
                      onChange={(e)=>setMapV2Id(e.target.value)}
                      placeholder="e.g. 216721334"
                      disabled={mapV2Loading}
                    />
                  </div>
                  <div className="col-sm-6 col-md-4 col-lg-3">
                    <label className="form-label">Max Trim (chars)</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      max={8}
                      value={cfg.mapping_v2_max_trim ?? 4}
                      onChange={(e) => handleMappingV2MaxTrimChange(e.target.value)}
                      disabled={mapV2Loading}
                    />
                    <div className="form-text">Trim from right if no hit (default 4).</div>
                  </div>
                  <div className="col-sm-6 col-md-4 col-lg-3">
                    <label className="form-label">Min Length</label>
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      max={8}
                      value={cfg.mapping_v2_min_length ?? 4}
                      onChange={(e) => handleMappingV2MinLengthChange(e.target.value)}
                      disabled={mapV2Loading}
                    />
                    <div className="form-text">Stop trimming when length below this (default 4).</div>
                  </div>
                </div>
                {mapV2Result && (
                  <div className="border rounded p-3">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <h6 className="mb-0">Result</h6>
                      <span className={`badge ${mapV2Result.complete_match ? 'bg-success' : 'bg-warning text-dark'}`}>
                        {mapV2Result.complete_match ? 'Complete match' : 'Partial'}
                      </span>
                      <span className="badge bg-light text-dark">Program: {mapV2Result.program || 'n/a'}</span>
                      <span className="badge bg-light text-dark">Mapping field: {mapV2Result.mapping_field}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={()=>setMapV2ShowDebug((v)=>!v)}
                      >
                        {mapV2ShowDebug ? 'Hide debug' : 'Show debug'}
                      </button>
                    </div>
                    <div className="small text-muted mb-3">
                      Lookup fields: {(mapV2Result.lookup_fields || []).join(', ') || '—'}
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <h6 className="mb-2">Mapping</h6>
                        {Object.keys(mapV2Result.mapping_result || {}).length === 0 ? (
                          <div className="text-muted small">No products matched.</div>
                        ) : (
                          <ul className="list-group list-group-flush">
                            {Object.entries(mapV2Result.mapping_result || {}).map(([k, v]) => (
                              <li key={k} className="list-group-item d-flex justify-content-between align-items-center px-0">
                                <span className="text-muted">{k}</span>
                                <code className="bg-light px-2 py-1 rounded">{v}</code>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="col-md-6">
                        <h6 className="mb-2">Matched Products</h6>
                        {Array.isArray(mapV2Result.matched_products) && mapV2Result.matched_products.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table table-sm mb-0">
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Slot</th>
                                  <th>Product Code</th>
                                  <th>Manufacturer Part #</th>
                                  <th>Name</th>
                                </tr>
                              </thead>
                              <tbody>
                                {mapV2Result.matched_products.map((mp, idx) => {
                                  const doc = mp.doc || {};
                                  const mfrPart = Array.isArray(doc.manufacturerPartNumber) ? doc.manufacturerPartNumber[0] : doc.manufacturerPartNumber;
                                  const slotNum = mp.slot || 1;
                                  return (
                                    <tr key={idx}>
                                      <td>{mp.field || '—'}</td>
                                      <td>
                                        <span className="badge bg-secondary">{slotNum === 1 ? 'Primary' : `Alt ${slotNum}`}</span>
                                      </td>
                                      <td><code className="bg-light px-2 py-1 rounded">{mp.product_code || '—'}</code></td>
                                      <td>{mfrPart || '—'}</td>
                                      <td style={{ maxWidth: 240 }} className="text-truncate">{doc.name || '—'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-muted small">No product docs returned.</div>
                        )}
                      </div>
                    </div>
                    <div className="mt-3">
                      <h6 className="mb-2">Attempt Log</h6>
                      {Array.isArray(mapV2Result?.debug?.attempts) && mapV2Result.debug.attempts.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th>Raw</th>
                                <th>Queries tried</th>
                                <th>Hit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {mapV2Result.debug.attempts.map((at, idx) => (
                                <tr key={idx}>
                                  <td style={{ maxWidth: 140 }} className="text-truncate">{at.field || '—'}</td>
                                  <td style={{ maxWidth: 160 }} className="text-truncate">{at.raw_value || '—'}</td>
                                  <td style={{ maxWidth: 360 }}>
                                    {(at.attempts || []).map((attempt, i) => (
                                      <div key={i} className="small mb-2">
                                        <div className="text-muted mb-1">Attempt {i + 1}: {attempt.value}</div>
                                        {(attempt.queries_detail || attempt.queries || []).map((qd, qi) => {
                                          const qText = typeof qd === 'string' ? qd : qd.q;
                                          const nf = typeof qd === 'object' ? qd.num_found : undefined;
                                          const ret = typeof qd === 'object' ? qd.returned : undefined;
                                          const err = typeof qd === 'object' ? qd.error : undefined;
                                          const sample = typeof qd === 'object' ? qd.sample : null;
                                          return (
                                            <div key={qi} className="mb-1">
                                              <code className="bg-light px-1 py-0 d-inline-block mb-1">{qText}</code>
                                              <div className="text-muted">
                                                {err ? (
                                                  <span className="text-danger">Error: {err}</span>
                                                ) : (
                                                  <>
                                                    <span>numFound: {nf ?? 'n/a'}</span>
                                                    <span className="ms-2">returned: {ret ?? 'n/a'}</span>
                                                    {sample && (sample.productCode || sample.manufacturerPartNumber || sample.name) && (
                                                      <span className="ms-2">
                                                        sample: <code className="bg-light px-1 py-0">{sample.productCode || '—'}</code>
                                                        {sample.manufacturerPartNumber ? ` / ${sample.manufacturerPartNumber}` : ''}
                                                      </span>
                                                    )}
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ))}
                                  </td>
                                  <td>
                                    {(() => {
                                      const hitAttempt = (at.attempts || []).find((a) => a.hit_query);
                                      if (hitAttempt) {
                                        return (
                                          <div className="small">
                                            <div>Query: <code className="bg-light px-1 py-0">{hitAttempt.hit_query}</code></div>
                                            <div className="text-muted">Hits: {hitAttempt.hit_count || 0}</div>
                                          </div>
                                        );
                                      }
                                      return <span className="text-muted small">—</span>;
                                    })()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-muted small">No attempts recorded.</div>
                      )}
                    </div>
                    {mapV2ShowDebug && (
                      <div className="mt-3">
                        <h6 className="mb-2">Debug (raw)</h6>
                        <pre className="bg-light p-2 small" style={{ maxHeight: 320, overflow: 'auto' }}>
{JSON.stringify(mapV2Result.debug || {}, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Started</div>
                  <div>{mappingState?.started_at ? new Date(mappingState.started_at).toLocaleString() : '—'}</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Last Update</div>
                  <div>{mappingState?.updated_at ? new Date(mappingState.updated_at).toLocaleString() : '—'}</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small mb-1">Last AHRI ID</div>
                  <div>{mappingState?.last_ahri_id || '—'}</div>
                </div>
              </div>
            </div>
            {mappingScopeState === 'subset' && (
              <div className="alert alert-warning mb-3">
                <div className="fw-semibold mb-1">Targeted mapping run</div>
                <div className="small">
                  Evaluating {mappingRequestedIdsTotal} AHRI ID{mappingRequestedIdsTotal === 1 ? '' : 's'}
                  {mappingRequestedIdsMore > 0 ? ' (preview below)' : ''}
                </div>
                {mappingRequestedIds.length > 0 && (
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {mappingRequestedIds.map((id) => (
                      <code key={id} className="bg-light px-2 py-1 rounded">{id}</code>
                    ))}
                    {mappingRequestedIdsMore > 0 && (
                      <span className="text-muted small align-self-center">
                        +{mappingRequestedIdsMore} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="card mb-3">
              <div className="card-body">
                <h6 className="mb-3">Mapping Scope</h6>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="mapping-scope"
                    id="mapping-scope-full"
                    value="full"
                    checked={mappingScopeSelection === 'full'}
                    onChange={() => setMappingScopeSelection('full')}
                    disabled={mappingActive}
                  />
                  <label className="form-check-label" htmlFor="mapping-scope-full">
                    Map the entire AHRI dataset
                  </label>
                </div>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="mapping-scope"
                    id="mapping-scope-subset"
                    value="subset"
                    checked={mappingScopeSelection === 'subset'}
                    onChange={() => setMappingScopeSelection('subset')}
                    disabled={mappingActive}
                  />
                  <label className="form-check-label" htmlFor="mapping-scope-subset">
                    Map only specific AHRI IDs
                  </label>
                </div>
                {mappingScopeSelection === 'subset' && (
                  <div>
                    <label className="form-label">AHRI IDs (comma or newline separated)</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={mappingIdsInput}
                      onChange={(e)=>setMappingIdsInput(e.target.value)}
                      placeholder="215271984, 215271985, 215271986"
                      disabled={mappingActive}
                    />
                    <div className="form-text">
                      {mappingIdsParsedCount > 0
                        ? `${mappingIdsParsedCount} ID${mappingIdsParsedCount === 1 ? '' : 's'} detected.`
                        : 'Enter at least one AHRI ID.'}
                    </div>
                  </div>
                )}
                <div className="row g-3 mt-3">
                  <div className="col-sm-6 col-md-4 col-lg-3">
                    <label className="form-label">Mapping Threads</label>
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      max={10}
                      value={cfg.mapping_threads ?? 2}
                      onChange={(e) => handleMappingThreadsChange(e.target.value)}
                      disabled={mappingActive}
                    />
                    <div className="form-text">
                      Use 1-10 threads for mapping. Higher values increase Solr load.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
                  <h6 className="mb-0">Recent Mapping Jobs</h6>
                  <div className="btn-group">
                    <button className="btn btn-outline-secondary btn-sm" onClick={loadMappingJobs} disabled={mappingJobsLoading}>
                      {mappingJobsLoading ? 'Refreshing…' : 'Refresh History'}
                    </button>
                  </div>
                </div>
                {mappingJobsError && <div className="alert alert-danger">{mappingJobsError}</div>}
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Job ID</th>
                        <th>Status</th>
                        <th>Processed</th>
                        <th>Matches</th>
                        <th>Products</th>
                        <th>Started</th>
                        <th>Finished</th>
                        <th style={{ width: 160 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappingJobs.map((job) => (
                        <tr key={job.job_id}>
                          <td><code style={{ fontSize: '0.8rem' }}>{job.job_id}</code></td>
                          <td className="text-capitalize">{job.status || '—'}</td>
                          <td>{job.processed ?? 0}{typeof job.total === 'number' ? ` / ${job.total}` : ''}</td>
                          <td>{job.matches ?? 0}</td>
                          <td>{job.updated_products ?? 0}</td>
                          <td>{job.started_at ? new Date(job.started_at).toLocaleString() : '—'}</td>
                          <td>{job.finished_at ? new Date(job.finished_at).toLocaleString() : <span className="text-muted">—</span>}</td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button className="btn btn-outline-primary" onClick={() => viewMappingJobLog(job.job_id)}>
                                View Log
                              </button>
                              <button className="btn btn-outline-danger" onClick={() => deleteMappingJob(job.job_id)}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(!mappingJobs || mappingJobs.length === 0) && (
                        <tr>
                          <td colSpan="8" className="text-muted text-center">No jobs recorded yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="alert alert-info mb-0">
              Product mapping evaluates each AHRI record, uses the configured lookup fields per program schema, runs a regex search against the product Solr collection (`partnumber_regex`), and stores the matches back into both AHRI Solr (dynamic fields) and the product records (`ahri_ids`). This enables faceting/searching across both collections by shared identifiers.
            </div>
          </div>
        </div>
        )}

        {mappingSubTab === 'attr-sync' && (
          <>
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <div>
                  <h5 className="mb-1">AHRI Product Attributes Sync</h5>
                  <div className="text-muted small">
                    Syncs product attributes to AHRI records. Run this after updating product attributes to update AHRI data and Solr.
                  </div>
                </div>
                <div className="btn-group">
                  <button className="btn btn-outline-secondary" onClick={loadAttrSyncState} disabled={attrSyncLoading}>
                    {attrSyncLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                  {attrSyncActive && (
                    <button
                      className="btn btn-outline-danger"
                      onClick={stopAttrSync}
                      disabled={attrSyncStopping}
                    >
                      {attrSyncStopping ? 'Stopping…' : 'Stop Sync'}
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={startAttrSync}
                    disabled={attrSyncStarting || attrSyncActive}
                  >
                    {attrSyncActive ? 'Sync in progress…' : 'Start Attributes Sync'}
                  </button>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label small fw-medium">Sync Mode</label>
                  <select
                    className="form-select"
                    value={attrSyncMode}
                    onChange={(e) => setAttrSyncMode(e.target.value)}
                    disabled={attrSyncActive}
                  >
                    <option value="replace">Replace all product attributes (faster)</option>
                    <option value="upsert">Upsert (retain existing, update changed)</option>
                  </select>
                  <div className="form-text">
                    Upsert keeps existing attributes that are not present in matched products.
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">Batch Size (max 1000)</label>
                  <input
                    type="number"
                    className="form-control"
                    min={1}
                    max={1000}
                    value={attrSyncBatchSize}
                    onChange={(e) => {
                      const next = Number(e.target.value || 1);
                      if (Number.isNaN(next)) {
                        setAttrSyncBatchSize(1);
                      } else {
                        setAttrSyncBatchSize(Math.min(1000, Math.max(1, next)));
                      }
                    }}
                    onBlur={() => {
                      setAttrSyncBatchSize((prev) => {
                        const next = Number(prev || 1);
                        if (Number.isNaN(next)) return 100;
                        return Math.min(1000, Math.max(1, next));
                      });
                    }}
                    disabled={attrSyncActive}
                  />
                  <div className="form-text">Controls how many AHRI records are processed per batch.</div>
                </div>
                <div className="col-12 d-flex flex-wrap gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="attr-sync-clear"
                      checked={attrSyncClearExisting}
                      onChange={(e) => setAttrSyncClearExisting(e.target.checked)}
                      disabled={attrSyncActive}
                    />
                    <label className="form-check-label" htmlFor="attr-sync-clear">
                      Clear all existing product attributes before sync
                    </label>
                    <div className="form-text">
                      Clears matched attributes first, then applies new values from matched products.
                    </div>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="attr-sync-mapped-only"
                      checked={attrSyncMappedOnly}
                      onChange={(e) => setAttrSyncMappedOnly(e.target.checked)}
                      disabled={attrSyncActive}
                    />
                    <label className="form-check-label" htmlFor="attr-sync-mapped-only">
                      Sync only mapped attributes (Attribute Import)
                    </label>
                    <div className="form-text">
                      Restrict updates to target attributes configured in Product Attribute Import.
                    </div>
                  </div>
                </div>
              </div>
              {attrSyncError && <div className="alert alert-danger">{attrSyncError}</div>}
              <div className="row g-3 mb-3">
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Status</div>
                    <div className="fw-semibold text-capitalize">{attrSyncStatus || 'idle'}</div>
                    {attrSyncState?.job_id && (
                      <div className="small text-muted mt-1">
                        Job: <code>{attrSyncState.job_id}</code>
                      </div>
                    )}
                    <div className="small text-muted mt-1">
                      Scope: {attrSyncState?.scope === 'subset' ? 'Selected AHRI IDs' : 'All with product mappings'}
                    </div>
                    {attrSyncState?.sync_mode && (
                      <div className="small text-muted mt-1">
                        Mode: {attrSyncState.sync_mode}{attrSyncState.clear_existing ? ' (clear existing)' : ''}
                      </div>
                    )}
                    {typeof attrSyncState?.batch_size === 'number' && (
                      <div className="small text-muted mt-1">
                        Batch size: {attrSyncState.batch_size}
                        {attrSyncState.mapped_only ? ' (mapped only)' : ''}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Processed</div>
                    <div className="fw-semibold">{attrSyncState?.processed || 0} / {attrSyncState?.total || '—'}</div>
                    {attrSyncState?.total > 0 && (
                      <div className="progress mt-2" style={{ height: '4px' }}>
                        <div 
                          className="progress-bar" 
                          role="progressbar" 
                          style={{ width: `${Math.min(100, (attrSyncState?.processed / attrSyncState?.total) * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Updated</div>
                    <div className="fw-semibold text-success">{attrSyncState?.updated || 0}</div>
                    <div className="small text-muted">AHRI records updated with attributes</div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Errors</div>
                    <div className={`fw-semibold ${(attrSyncState?.errors || 0) > 0 ? 'text-danger' : ''}`}>{attrSyncState?.errors || 0}</div>
                    {attrSyncState?.error && (
                      <div className="small text-danger mt-1">{attrSyncState.error}</div>
                    )}
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">AHRI System Variants</div>
                    <div className="fw-semibold">{attrSyncState?.debug?.variants_regenerated || 0} regenerated</div>
                    <div className="small text-muted mt-1">{attrSyncState?.debug?.variants_synced || 0} synced to Solr</div>
                    {(attrSyncState?.debug?.variants_errors || 0) > 0 && (
                      <div className="small text-danger mt-1">{attrSyncState.debug.variants_errors} variant errors</div>
                    )}
                  </div>
                </div>
              </div>
              {attrSyncState?.started_at && (
                <div className="small text-muted mb-2">
                  Started: {new Date(attrSyncState.started_at).toLocaleString()}
                  {attrSyncState?.finished_at && (
                    <span className="ms-3">Finished: {new Date(attrSyncState.finished_at).toLocaleString()}</span>
                  )}
                </div>
              )}
              <div className="alert alert-info mb-0">
                <strong>What this does:</strong> This job reads AHRI records that have product mappings, fetches the latest attributes from the matched products in your Products table, and updates:
                <ul className="mb-0 mt-2">
                  <li>The <code>matched_product_attributes</code> field in the AHRI database</li>
                  <li>The <code>ahri_system_variants</code> table (regenerated for every AHRI record whose attributes changed)</li>
                  <li>The product attribute facets in AHRI Solr (<code>prodattr_s_*</code>, <code>prodattr_d_*</code>, <code>prodattr_b_*</code>)</li>
                </ul>
                Run this job whenever you update product attributes and want those changes reflected in AHRI search and faceting.
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <div>
                  <h5 className="mb-1">AHRI Variant Resync (auto)</h5>
                  <div className="text-muted small">
                    Runs automatically after a bulk product import or a single product edit touches attributes --
                    refreshes <code>ahri_system_variants</code> and pushes the updated docs to Solr. Nothing to
                    start here; this panel just shows the most recent run.
                  </div>
                </div>
                <div className="btn-group">
                  <button className="btn btn-outline-secondary" onClick={loadVariantResyncState} disabled={variantResyncLoading}>
                    {variantResyncLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
              </div>
              {variantResyncError && <div className="alert alert-danger">{variantResyncError}</div>}
              {!variantResyncState?.status ? (
                <div className="text-muted small">No AHRI variant resync has run yet for this account.</div>
              ) : (
                <>
                  <div className="row g-3 mb-3">
                    <div className="col-sm-6 col-lg-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small mb-1">Status</div>
                        <div className="fw-semibold text-capitalize">
                          {variantResyncActive && (
                            <span className="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true" />
                          )}
                          {variantResyncStatus || 'idle'}
                        </div>
                        {variantResyncState?.job_id && (
                          <div className="small text-muted mt-1">
                            Job: <code>{variantResyncState.job_id}</code>
                          </div>
                        )}
                        {variantResyncState?.trigger && (
                          <div className="small text-muted mt-1">
                            Trigger: {variantResyncState.trigger === 'bulk_import' ? 'Bulk product import' : 'Product edit'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small mb-1">Processed</div>
                        <div className="fw-semibold">{variantResyncState?.processed || 0} / {variantResyncState?.total || '—'}</div>
                        {variantResyncState?.total > 0 && (
                          <div className="progress mt-2" style={{ height: '4px' }}>
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{ width: `${Math.min(100, (variantResyncState?.processed / variantResyncState?.total) * 100)}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small mb-1">Regenerated</div>
                        <div className="fw-semibold text-success">{variantResyncState?.regenerated || 0}</div>
                        <div className="small text-muted">Variants regenerated and synced to Solr</div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small mb-1">Errors</div>
                        <div className={`fw-semibold ${(variantResyncState?.errors || 0) > 0 ? 'text-danger' : ''}`}>
                          {variantResyncState?.errors || 0}
                        </div>
                        {variantResyncState?.error && (
                          <div className="small text-danger mt-1">{variantResyncState.error}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  {variantResyncState?.started_at && (
                    <div className="small text-muted mb-0">
                      Started: {new Date(variantResyncState.started_at).toLocaleString()}
                      {variantResyncState?.finished_at && (
                        <span className="ms-3">Finished: {new Date(variantResyncState.finished_at).toLocaleString()}</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          </>

        )}
        {mappingSubTab === 'match-check' && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <div>
                  <h5 className="mb-1">AHRI Match Status Recheck</h5>
                  <div className="text-muted small">
                    Re-evaluates complete vs partial matches based on available model numbers in each AHRI record.
                  </div>
                </div>
                <div className="btn-group">
                  <button className="btn btn-outline-secondary" onClick={loadMatchCheckState} disabled={matchCheckLoading}>
                    {matchCheckLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                  {matchCheckActive && (
                    <button
                      className="btn btn-outline-danger"
                      onClick={stopMatchCheck}
                      disabled={matchCheckStopping}
                    >
                      {matchCheckStopping ? 'Stopping…' : 'Stop Check'}
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={startMatchCheck}
                    disabled={matchCheckStarting || matchCheckActive}
                  >
                    {matchCheckActive ? 'Check in progress…' : 'Start Match Check'}
                  </button>
                </div>
              </div>
              {matchCheckError && <div className="alert alert-danger">{matchCheckError}</div>}
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label small fw-medium">Batch Size (max 1000)</label>
                  <input
                    type="number"
                    className="form-control"
                    min={1}
                    max={1000}
                    value={matchCheckBatchSize}
                    onChange={(e) => {
                      const next = Number(e.target.value || 1);
                      if (Number.isNaN(next)) {
                        setMatchCheckBatchSize(1);
                      } else {
                        setMatchCheckBatchSize(Math.min(1000, Math.max(1, next)));
                      }
                    }}
                    onBlur={() => {
                      setMatchCheckBatchSize((prev) => {
                        const next = Number(prev || 1);
                        if (Number.isNaN(next)) return 200;
                        return Math.min(1000, Math.max(1, next));
                      });
                    }}
                    disabled={matchCheckActive}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">Solr Commit Strategy</label>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="match-check-commit-each-batch"
                      checked={matchCheckCommitEachBatch}
                      onChange={(e) => setMatchCheckCommitEachBatch(e.target.checked)}
                      disabled={matchCheckActive}
                    />
                    <label className="form-check-label" htmlFor="match-check-commit-each-batch">
                      Commit after each batch (uncheck to commit once at the end)
                    </label>
                  </div>
                  <div className="form-text">
                    Disable to speed up large runs. Results appear after the final commit.
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Status</div>
                    <div className="fw-semibold text-capitalize">{matchCheckStatus || 'idle'}</div>
                    {matchCheckState?.job_id && (
                      <div className="small text-muted mt-1">
                        Job: <code>{matchCheckState.job_id}</code>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Processed</div>
                    <div className="fw-semibold">{matchCheckState?.processed || 0} / {matchCheckState?.total || '—'}</div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Updated</div>
                    <div className="fw-semibold text-success">{matchCheckState?.updated || 0}</div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Errors</div>
                    <div className={`fw-semibold ${(matchCheckState?.errors || 0) > 0 ? 'text-danger' : ''}`}>{matchCheckState?.errors || 0}</div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Batch Size</div>
                    <div className="fw-semibold">{matchCheckState?.batch_size || matchCheckBatchSize}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {mappingSubTab === 'variant-backfill' && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <div>
                  <h5 className="mb-1">AHRI System Variant Backfill</h5>
                  <div className="text-muted small">
                    Regenerates every <code>ahri_system_variants</code> row (and the denormalized
                    product name/image/description/availability fields on it) from the current
                    product mapping and inventory. Run this once after a variant-related schema
                    or logic change, or any time existing combinations look stale.
                  </div>
                </div>
                <div className="btn-group">
                  <button className="btn btn-outline-secondary" onClick={loadVariantBackfillProgress} disabled={variantBackfillLoading}>
                    {variantBackfillLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={startVariantBackfill}
                    disabled={variantBackfillStarting || variantBackfillActive}
                  >
                    {variantBackfillActive ? 'Backfill running…' : 'Run Variant Backfill'}
                  </button>
                </div>
              </div>
              {variantBackfillError && <div className="alert alert-danger">{variantBackfillError}</div>}
              <div className="row g-3 mb-3">
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Status</div>
                    <div className="fw-semibold text-capitalize">{variantBackfillStatus || 'pending'}</div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Processed</div>
                    <div className="fw-semibold">{variantBackfillProgress?.processed_count || 0}</div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Errors</div>
                    <div className={`fw-semibold ${(variantBackfillProgress?.error_count || 0) > 0 ? 'text-danger' : ''}`}>
                      {variantBackfillProgress?.error_count || 0}
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small mb-1">Last AHRI ID</div>
                    <div className="fw-semibold text-truncate" title={variantBackfillProgress?.last_ahri_id || ''}>
                      {variantBackfillProgress?.last_ahri_id || '—'}
                    </div>
                  </div>
                </div>
              </div>
              {variantBackfillProgress?.started_at && (
                <div className="small text-muted mb-2">
                  Started: {new Date(variantBackfillProgress.started_at).toLocaleString()}
                  {variantBackfillProgress?.completed_at && (
                    <span className="ms-3">Completed: {new Date(variantBackfillProgress.completed_at).toLocaleString()}</span>
                  )}
                </div>
              )}
              <div className="alert alert-info mb-0">
                <strong>What this does:</strong> Walks every AHRI certificate for this account and
                re-runs the same variant-generation logic used after a mapping change --
                re-populating each combination's availability (<code>availability_json</code>) and
                denormalized product name/image/description columns. Resumable: calling it again
                after an interruption picks up from the last processed AHRI ID, not from the start.
                To force a full re-run of certificates already processed by a prior backfill
                (e.g. after a schema/logic change like this one), an operator needs to clear this
                account&apos;s row in <code>ahri_variant_backfill_progress</code> first -- there is
                no reset control here yet. Once this completes, run a Full Solr Import
                from the AHRI Search page (with &quot;sync variant docs&quot; enabled in Settings →
                Solr) to push the refreshed data to search.
              </div>
            </div>
          </div>
        )}
        </>
      )}
      <Drawer
        title={`${schemaDrawerMode === 'edit' ? 'Edit' : 'Add'} ${schemaDrawerType.replace('-', ' ')}`}
        open={schemaDrawerOpen}
        onClose={() => setSchemaDrawerOpen(false)}
        width={schemaDrawerWidth}
        placement="right"
      >
        <div className="schema-drawer">
          <div
            className="schema-resize-handle"
            onMouseDown={(event) => {
              schemaResizeRef.current = { startX: event.clientX, startWidth: schemaDrawerWidth };
              setSchemaDrawerResizing(true);
            }}
          />
          <Form form={schemaForm} layout="vertical">
          {(schemaDrawerType === 'field' || schemaDrawerType === 'dynamic-field') && (
            <>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Type is required' }]}>
                <Input placeholder="string, text_general, pdouble..." />
              </Form.Item>
              <Space size="large" style={{ marginBottom: 16 }}>
                <Form.Item name="stored" valuePropName="checked" label="Stored">
                  <Switch />
                </Form.Item>
                <Form.Item name="indexed" valuePropName="checked" label="Indexed">
                  <Switch />
                </Form.Item>
                <Form.Item name="multiValued" valuePropName="checked" label="MultiValued">
                  <Switch />
                </Form.Item>
                <Form.Item name="docValues" valuePropName="checked" label="DocValues">
                  <Switch />
                </Form.Item>
              </Space>
            </>
          )}
          {schemaDrawerType === 'field-type' && (
            <>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="class" label="Class" rules={[{ required: true, message: 'Class is required' }]}>
                <Input placeholder="solr.TextField" />
              </Form.Item>
            </>
          )}
          {schemaDrawerType === 'copy-field' && (
            <>
              <Form.Item name="source" label="Source" rules={[{ required: true, message: 'Source is required' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="dest" label="Dest" rules={[{ required: true, message: 'Dest is required' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="maxChars" label="Max chars (optional)">
                <Input type="number" />
              </Form.Item>
            </>
          )}
          <Form.Item name="json" label="Advanced JSON (optional)">
            <Input.TextArea rows={6} placeholder="Paste full schema JSON to override the form." />
          </Form.Item>
            <Space>
              <Button type="primary" onClick={submitSchemaDrawer}>
                Save
              </Button>
              <Button onClick={() => setSchemaDrawerOpen(false)}>Cancel</Button>
            </Space>
          </Form>
        </div>
      </Drawer>
      {drawerOpen && <div className="offcanvas-backdrop fade show" onClick={()=>setDrawerOpen(false)}></div>}
      <style>{`
        .info-hint {
          position: relative;
          display: inline-flex;
          color: #6c757d;
          cursor: help;
        }
        .info-hint-bubble {
          position: absolute;
          left: 50%;
          top: 100%;
          transform: translate(-50%, 8px);
          background: #1f2937;
          color: #ffffff;
          padding: 8px 10px;
          border-radius: 6px;
          font-size: 12px;
          line-height: 1.4;
          width: max-content;
          max-width: 260px;
          opacity: 0;
          pointer-events: none;
          z-index: 1000;
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
        }
        .info-hint:hover .info-hint-bubble,
        .info-hint:focus-within .info-hint-bubble {
          opacity: 1;
        }
        .info-hint--always .info-hint-bubble {
          position: static;
          transform: none;
          margin-top: 6px;
          width: 100%;
          max-width: 100%;
          background: #f8f9fa;
          color: #495057;
          border: 1px solid #e9ecef;
          box-shadow: none;
          opacity: 1;
          pointer-events: auto;
        }
        .schema-drawer {
          position: relative;
          height: 100%;
        }
        .schema-resize-handle {
          position: absolute;
          left: -6px;
          top: 0;
          width: 12px;
          height: 100%;
          cursor: col-resize;
        }
        .shop-filters-section {
          border: 1px solid #edf0f3;
          border-radius: 14px;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
        }
        .shop-filters-section .card-body {
          padding: 1.25rem 1.5rem;
        }
        .shop-filters-section-title {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-size: 0.95rem;
          font-weight: 600;
          color: #1f2937;
        }
        .shop-filters-section-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 10px;
          background: #eef2ff;
          color: #4f46e5;
          flex-shrink: 0;
        }
        .setting-toggle {
          padding: 0.65rem 0;
          border-bottom: 1px solid #f1f3f5;
        }
        .setting-toggle:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        .setting-toggle--indent {
          margin-left: 1.25rem;
          padding-left: 1rem;
          border-left: 2px solid #eef0f3;
        }
        .setting-toggle-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }
        .setting-toggle-label {
          font-weight: 500;
          color: #1f2937;
          margin: 0;
          cursor: pointer;
          min-width: 0;
          flex: 1 1 auto;
        }
        .setting-toggle-row .ant-switch {
          flex-shrink: 0;
        }
        .setting-toggle-desc {
          margin-top: 0.2rem;
          max-width: 640px;
        }
        .shop-filters-content {
          padding-bottom: 96px;
        }
        .shop-filter-card {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
          border: 1px solid #edf0f3;
          border-radius: 10px;
          padding: 0.75rem 0.85rem;
          margin-bottom: 0.6rem;
          background: #fff;
        }
        .shop-filter-card:last-child {
          margin-bottom: 0;
        }
        .shop-filter-card--dragging {
          background: #eef2ff;
          border-color: #c7d2fe;
        }
        .shop-filter-card-drag {
          color: #9ca3af;
          padding-top: 0.15rem;
          cursor: grab;
          flex-shrink: 0;
        }
        .shop-filter-card-body {
          flex: 1;
          min-width: 0;
        }
        .shop-filter-card-top {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
        }
        .shop-filter-card-label {
          font-weight: 600;
          color: #1f2937;
          word-break: break-word;
        }
        .shop-filter-card-type {
          font-size: 0.75rem;
          font-weight: 600;
          color: #4f46e5;
          background: #eef2ff;
          border-radius: 999px;
          padding: 0.1rem 0.6rem;
          flex-shrink: 0;
        }
        .shop-filter-card-key {
          font-size: 0.78rem;
          color: #6b7280;
          margin-top: 0.15rem;
          word-break: break-all;
        }
        .shop-filter-card-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.6rem;
        }
        .shop-filter-card-actions .btn {
          flex: 1;
        }
        .shop-filters-save-bar {
          position: sticky;
          bottom: 16px;
          display: flex;
          justify-content: flex-end;
          z-index: 20;
          pointer-events: none;
        }
        .shop-filters-save-bar-inner {
          pointer-events: auto;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 999px;
          padding: 0.5rem 0.75rem 0.5rem 1.1rem;
          box-shadow: 0 12px 32px rgba(16, 24, 40, 0.16);
        }
        .shop-filters-save-hint {
          font-size: 0.8rem;
          color: #6b7280;
          white-space: nowrap;
        }
        @media (max-width: 575.98px) {
          .shop-filters-save-bar {
            bottom: 12px;
            justify-content: stretch;
          }
          .shop-filters-save-bar-inner {
            width: 100%;
            border-radius: 14px;
            justify-content: space-between;
          }
          .shop-filters-section .card-body {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default AhriData;


